import { useState, useRef, useEffect, useMemo, type ReactNode } from "react";
import { createPortal } from "react-dom";
import WeeklyCalendarView, { type CalendarEvent } from "@/components/WeeklyCalendarView";
import { expandBc360Event, bc360OccurrenceKey } from "@shared/bc360CalendarRecurrence";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { resolveS360Name } from "@/lib/s360Name";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { toast } from "sonner";
import {
  Users, DollarSign, Plus, Copy, Check, BarChart3,
  RefreshCw, BookOpen, Zap, Filter, X, Eye, Clock,
  Building2, Link2, Activity, Award, Shield, ChevronDown, ChevronRight,
  CheckCircle2, AlertCircle, ArrowRight, Wifi, WifiOff,
  Pencil, Trash2, Save, TrendingUp, FileText, Star,
  Layers, Globe, UserCheck, Mail, Phone, Lock, Unlock,
  CreditCard, Calendar, PauseCircle, XCircle, Loader2, Settings, KeyRound, EyeOff,
  Megaphone, Bell, AlertTriangle, Info, ExternalLink, CalendarCheck, ArrowUpCircle, MessageSquare,
  Ban, RotateCcw, Download, ToggleLeft, ToggleRight, Network, Briefcase, Repeat2, Pin, PinOff, Upload, LogOut,
  Search, Table2, Menu, Image as ImageIcon, Video, LayoutTemplate, GalleryHorizontal, ChevronsUpDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  buildCommissionExportCsv,
  currentMonthPayoutRange,
  filterCommissionRows,
  summarizeCommissionRows,
  type CommissionStatusFilter,
} from "@/lib/commissionSearch";
import AdminLock from "@/components/AdminLock";
import LeadDetailDrawer from "@/components/LeadDetailDrawer";
import AgentDetailDrawer from "@/components/AgentDetailDrawer";
import { IdentityVerificationPanel } from "@/components/IdentityVerificationPanel";
import AgreementPortalPanel from "@/components/AgreementPortalPanel";
import ClientDocumentReview from "@/components/ClientDocumentReview";
import { CertificationPipeline } from "@/components/CertificationPipeline";
import { AdminMarketingMaterials } from "@/components/AdminMarketingMaterials";
import { AnnouncementMedia } from "@/components/AnnouncementMedia";
import { AdminFlywheelTab } from "@/components/AdminFlywheelTab";
import { GraceFaqAdminTab } from "@/components/GraceFaqAdminTab";

// ─── Consultation Calendar URLs (shared with RepDashboard) ───────────────────
const ADMIN_CONSULTATION_CALENDARS: Record<string, { label: string; url: string; color: string }[]> = {
  "Fundable360":        [{ label: "Fundable360 Consultation",        url: "https://api.wibiz.ai/widget/booking/jN4jUeNAZmQ7BvM1aGZF", color: "#84ab4a" }],
  "Scale360":           [{ label: "Scale360 Consultation",           url: "https://api.wibiz.ai/widget/booking/O6uW585cnEvZAB2fNRmE", color: "#E8620A" }],
  "Share360":           [{ label: "Share360 Nonprofit Consultation", url: "https://api.wibiz.ai/widget/booking/eZS9bJf0uzV5aXN48jC8", color: "#0FA8A8" }],
  "Share360 Business":  [{ label: "Share360 Business Consultation",  url: "https://api.wibiz.ai/widget/booking/xBgDVk2OWeq0DePGpuY8", color: "#1A6FD4" }],
  "Share360 Nonprofit": [{ label: "Share360 Nonprofit Consultation", url: "https://api.wibiz.ai/widget/booking/eZS9bJf0uzV5aXN48jC8", color: "#0FA8A8" }],
  "Legacy360":          [{ label: "Legacy360 Living Trust",          url: "https://api.wibiz.ai/widget/booking/28XGN7D3DWQ4OSO2UWLn", color: "#C8920A" }],
  "Media360":           [{ label: "Media360 Consultation",           url: "https://api.wibiz.ai/widget/booking/f7Bih3G3zfHl0y4osogZ", color: "#7B4FD4" }],
};

// ─── Vertical Pipeline Definitions ───────────────────────────────────────────
// Each vertical has its own pipeline(s) with exact GHL stage names

const VERTICALS = [
  {
    key: "scale360",
    label: "Scale360",
    color: "#E8620A",
    bg: "rgba(232,98,10,0.08)",
    icon: TrendingUp,
    verticalName: "Scale360",
    pipelineId: "mzgOuQhHmkDuixcpiSbj",
    pipelines: [
      {
        id: "mzgOuQhHmkDuixcpiSbj",
        name: "Scale360 Partner Funnel",
        verticalName: "Scale360",
        // Stages fetched live from GHL — confirmed from live GHL API 2026-05-04
        stages: ["Lead Captured", "Audit Submitted", "Follow-Up Active", "Payment Received", "Onboarding Submitted", "Active Client", "Lost / No Response"],
        terminalStages: ["Active Client", "Lost / No Response"],
      },
      {
        id: "HmZA3KEdITefCLNZwg1H",
        name: "Scale360 Enterprise",
        verticalName: "Scale360 Enterprise",
        // Stages fetched live from GHL — confirmed from live GHL API 2026-05-04
        stages: ["New Enquiry", "Discovery Call Scheduled", "Proposal Sent", "Closed Won", "Closed Lost"],
        terminalStages: ["Closed Won", "Closed Lost"],
      },
    ],
  },
  {
    key: "fundable360",
    label: "Fundable360",
    color: "#84ab4a",
    bg: "rgba(132,171,74,0.08)",
    icon: FileText,
    verticalName: "Fundable360",
    pipelineId: "UtddGTWZhyTcaRSWEGKm",
    pipelines: [
      {
        id: "UtddGTWZhyTcaRSWEGKm",
        name: "Pipeline A — Enrollment Process",
        verticalName: "Fundable360",
        stages: ["New Lead", "Appointment Booked", "Appointment Completed", "Sent Information", "Booked Follow Up", "Enrolled", "Onboarded Credit Building", "Does Not Have Enrollment Fees", "Inactive/Lost"],
        terminalStages: ["Inactive/Lost"],
        adminOnlyStages: ["Does Not Have Enrollment Fees"],
      },
      {
        id: "Qh2eqgohaTaCIITZYOyQ",
        name: "Pipeline B — Funding",
        verticalName: "Fundable360",
        // Stages fetched live from GHL — local list is fallback only
        stages: ["In Review", "In Progress", "In Funding", "Funded", "Closed-No Funding", "On Hold"],
        terminalStages: ["Funded", "Closed-No Funding"],
      },
    ],
  },
  {
    key: "share360",
    label: "Share360",
    color: "#1A6FD4",
    bg: "rgba(26,111,212,0.08)",
    icon: Globe,
    verticalName: "Share360",
    pipelineId: "czZqnGQZru1yUcz7vHM6",
    pipelines: [
      {
        id: "czZqnGQZru1yUcz7vHM6",
        name: "Pipeline A — Nonprofit Track",
        // CRITICAL: verticalName used for strict lead filtering — NEVER mix with Business leads
        verticalName: "Share360 Nonprofit",
        stages: ["New Lead", "Appointment Booked", "Booked Follow Up", "Charity Documents", "Charity Package", "Collateral Review", "Onboarded Receiving Donations", "Inactive"],
        terminalStages: ["Onboarded Receiving Donations", "Inactive"],
      },
      {
        id: "le4RyNh4YBlLMVeVslHt",
        name: "Pipeline B — Business Track",
        // CRITICAL: verticalName used for strict lead filtering — NEVER mix with Nonprofit leads
        verticalName: "Share360 Business",
        stages: ["New Lead", "Appointment Booked", "Statements", "Rate Review", "Merch Info Form", "Application", "Underwriting Review/Request", "VAR/Deployment", "Onboarded Processing", "Declined", "Inactive"],
        terminalStages: ["Onboarded Processing", "Declined", "Inactive"],
      },
      {
        id: "share360-ra",
        name: "Pipeline C — Registered Agents (RAs)",
        verticalName: "Share360",
        stages: ["Application Submitted", "Registration Paid", "Statements", "Background Check", "Certification Approved", "Subscription Active"],
        terminalStages: ["Subscription Active"],
        note: "Monthly subscription tracking required for RAs",
      },
    ],
  },
  {
    key: "legacy360",
    label: "Legacy360",
    color: "#C8920A",
    bg: "rgba(200,146,10,0.08)",
    icon: Award,
    verticalName: "Legacy360",
    pipelineId: "AtnyZ5hxQGhpBwTO8gro",
    pipelines: [
      {
        id: "AtnyZ5hxQGhpBwTO8gro",
        name: "Living Trust",
        // Discriminator for Legacy360's 4 pipelines, which all share verticalName
        // "Legacy360" — see legacy360SubTypeKeyForLead below. Living Trust is the
        // default bucket for any lead that doesn't match a more specific pipeline.
        l360LeadTypeKey: "living_trust",
        stages: ["New Lead", "Appointment Booked", "Consultation Completed", "Payment Received", "Documents Drafted", "Documents Signed", "Trust Funded", "Trust Recorded"],
        terminalStages: ["Trust Recorded"],
      },
      {
        id: "hoIcCAyZ8Wg0wPPNzosy",
        name: "125 Plan",
        l360LeadTypeKey: "125plan",
        // Pipeline ID supplied by client 2026-07-30. Stage order per client spec.
        stages: ["New Lead", "Appointment Booked", "Proposal Presentation", "Onboarding", "Inactive"],
        terminalStages: ["Onboarding", "Inactive"],
      },
      {
        id: "LHgTWf4RYWr6SZlbe94h",
        name: "New Agents",
        hidden: true,
        l360LeadTypeKey: "new_agent",
        // Sourced from the certifications table (Legacy360-certified associates),
        // not from GHL opportunities — see isCertificationPipeline in VerticalKanbanTab.
        // Admin-only per client decision 2026-07-30 (not shown on the Rep dashboard).
        dataSource: "certifications",
        stages: ["Information Submitted"],
        terminalStages: ["Information Submitted"],
      },
      {
        id: "EKY9Qps5kZDkRBbLjL4t",
        name: "Advantage",
        l360LeadTypeKey: "advantage",
        // Pipeline ID supplied by client 2026-07-30. Stage names are a RECOMMENDATION
        // based on the form's captured fields (license status/type, background-check
        // willingness, contract willingness) pending client confirmation in Wibiz —
        // see memory/project_legacy360_three_pipelines.md. Update if renamed.
        stages: ["Information Submitted", "License & Background Review", "Interview Scheduled", "Contract Offered", "Contracted (Active Agent)", "Not a Fit / Inactive"],
        terminalStages: ["Contracted (Active Agent)", "Not a Fit / Inactive"],
      },
    ],
  },
  {
    key: "media360",
    label: "Media360",
    color: "#7B4FD4",
    bg: "rgba(123,79,212,0.08)",
    icon: Star,
    verticalName: "Media360",
    pipelineId: "MKZZiFgADSTL2iNuOuvw",
    pipelines: [
      {
        id: "MKZZiFgADSTL2iNuOuvw",
        name: "Media360 Pipeline",
        stages: ["Form Submitted", "Strategy Session Scheduled", "Strategy Session Completed", "Proposal Sent", "Active Client", "Closed/Lost"],
        terminalStages: ["Active Client", "Closed/Lost"],
      },
    ],
  },
] as const;

type VerticalKey = typeof VERTICALS[number]["key"];
type TabKey = VerticalKey | "reps" | "commissions" | "verticals" | "sync" | "team" | "ra_subscriptions" | "identity" | "agreements" | "client_docs" | "certifications" | "quiz_questions" | "materials" | "settings" | "share360_enrollment" | "training" | "appointments" | "announcements" | "flywheel" | "associates_landing" | "verticallinks" | "bc360_calendar" | "attribution" | "webhook_logs" | "role_upgrades" | "grace_faq" | "grace_phone" | "all_leads" | "billing" | "fundable360_financing";

// ─── Constants ────────────────────────────────────────────────────────────────
const VERTICAL_COLORS: Record<string, string> = {
  Scale360: "#E8620A", Fundable360: "#84ab4a", Share360: "#1A6FD4",
  "Share360 Business": "#1A6FD4", "Share360 Nonprofit": "#0FA8A8",
  Legacy360: "#C8920A", Media360: "#7B4FD4", Build360: "#0FA8A8", Protect360: "#7B4FD4",
};

const PLAN_LABELS: Record<string, string> = { lite: "Lite", standard: "Standard", pro: "Pro" };

// BC360 internal stage keys (used in DB) — for the old pipeline view
const STAGES = [
  { key: "lead_captured", label: "Lead Captured", color: "#1A6FD4", bg: "rgba(26,111,212,0.1)" },
  { key: "audit_submitted", label: "Audit Submitted", color: "#C8920A", bg: "rgba(200,146,10,0.1)" },
  { key: "follow_up_active", label: "Follow-Up Active", color: "#E8620A", bg: "rgba(232,98,10,0.1)" },
  { key: "payment_received", label: "Payment Received", color: "#0FA8A8", bg: "rgba(15,168,168,0.1)" },
  { key: "onboarding_submitted", label: "Onboarding Submitted", color: "#7B4FD4", bg: "rgba(123,79,212,0.1)" },
  { key: "active_client", label: "Active Client", color: "#84ab4a", bg: "rgba(132,171,74,0.1)" },
  { key: "lost_cold", label: "Lost / Cold", color: "#D93025", bg: "rgba(217,48,37,0.1)" },
] as const;

const COMMISSION_GRID: Record<string, Record<string, { enrollment: string; recurring: string; note?: string; paymentLink?: string }>> = {
  Fundable360: {
    Agency: { enrollment: "30% ($900)", recurring: "30% of funding splits", note: "Enrollment fee $2,995", paymentLink: "https://api.wibiz.ai/payment-link/69b8cdf8942c7dd36efd0b07" },
    "Senior Associate": { enrollment: "25% ($750)", recurring: "25% of funding splits" },
    Associate: { enrollment: "20% ($600)", recurring: "20% of funding splits" },
  },
  Scale360: {
    Agency: { enrollment: "40% of setup fee", recurring: "40% of subscriptions", note: "BC360 retains 50% of net" },
    "Senior Associate": { enrollment: "25% of setup fee", recurring: "25% of subscriptions" },
    Associate: { enrollment: "20% of setup fee", recurring: "20% of subscriptions" },
  },
  Legacy360: {
    Agency: { enrollment: "30% ($900)", recurring: "N/A", note: "Living Trust — enrollment fee $2,995", paymentLink: "https://api.wibiz.ai/payment-link/69b8ce33f35c540e14832dfb" },
    "Senior Associate": { enrollment: "25% ($750)", recurring: "N/A" },
    Associate: { enrollment: "20% ($600)", recurring: "N/A" },
  },
  Share360: {
    Agency: { enrollment: "PENDING", recurring: "PENDING", note: "Rates not yet confirmed — paid directly by banking partners" },
    "Senior Associate": { enrollment: "PENDING", recurring: "PENDING" },
    Associate: { enrollment: "PENDING", recurring: "PENDING" },
  },
  Media360: {
    Agency: { enrollment: "N/A", recurring: "N/A", note: "No commission — featured placement only" },
    "Senior Associate": { enrollment: "N/A", recurring: "N/A" },
    Associate: { enrollment: "N/A", recurring: "N/A" },
  },
};

const ALL_VERTICALS = ["Scale360", "Fundable360", "Share360", "Legacy360", "Media360"];
const MANUAL_COMMISSION_VERTICALS = ["Fundable360", "Scale360", "Share360", "Legacy360"] as const;
const EMPTY_MANUAL_COMMISSION_FORM = {
  repCode: "",
  verticalName: "" as "" | (typeof MANUAL_COMMISSION_VERTICALS)[number],
  amount: "",
  description: "",
  clientName: "",
  businessName: "",
};

type CommissionAssociate = {
  id: number;
  repCode: string;
  firstName: string | null;
  lastName: string | null;
  status: string;
};

function AssociateCommissionSearch({
  associates,
  value,
  onChange,
}: {
  associates: CommissionAssociate[];
  value: string;
  onChange: (repCode: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = associates.find(associate => associate.repCode === value);
  const name = (associate: CommissionAssociate) =>
    [associate.firstName, associate.lastName].filter(Boolean).join(" ") || associate.repCode;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label="Search active associates"
          className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left text-sm focus:outline-none focus:border-[#84ab4a] focus:ring-2 focus:ring-[#84ab4a]/20"
        >
          <span className={selected ? "truncate text-gray-900" : "truncate text-gray-400"}>
            {selected ? `${name(selected)} (${selected.repCode})` : "Search an active associate"}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        <Command>
          <CommandInput placeholder="Search by name or associate ID..." />
          <CommandList>
            <CommandEmpty>No active associate found.</CommandEmpty>
            <CommandGroup>
              {associates.map(associate => (
                <CommandItem
                  key={associate.id}
                  value={`${name(associate)} ${associate.repCode}`}
                  onSelect={() => {
                    onChange(associate.repCode);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${value === associate.repCode ? "opacity-100" : "opacity-0"}`}
                  />
                  <span className="truncate">{name(associate)}</span>
                  <span className="ml-auto pl-3 font-mono text-xs text-gray-400">
                    {associate.repCode}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function fmt(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

function fmtCommission(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

// ─── Stage Badge ──────────────────────────────────────────────────────────────
function StageBadge({ stage, ghlStageName }: { stage: string; ghlStageName?: string | null }) {
  const s = STAGES.find((x) => x.key === stage);
  // GHL is source of truth for stage names — prefer ghlStageName when available
  const displayLabel = ghlStageName || s?.label || stage;
  const bg = s?.bg ?? "#F3F4F6";
  const color = s?.color ?? "#6B7280";
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: bg, color }}>
      {displayLabel}
    </span>
  );
}

// ─── Agent Level Badge ────────────────────────────────────────────────────────
function AgentLevelBadge({ level }: { level: string }) {
  const colors: Record<string, string> = {
    Agency: "bg-yellow-100 text-yellow-800 border border-yellow-200",
    "Senior Associate": "bg-blue-100 text-blue-800 border border-blue-200",
    Associate: "bg-purple-100 text-purple-800 border border-purple-200",
  };
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${colors[level] ?? "bg-gray-100 text-gray-600"}`}>
      {level}
    </span>
  );
}

// ─── Rep Onboarding Form ──────────────────────────────────────────────────────
function RepOnboardingForm({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    vertical: "Fundable360", agentLevel: "Associate" as "Agency" | "Senior Associate" | "Associate",
    uplineRepCode: "", mgaRepCode: "", notes: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedVerticals, setSelectedVerticals] = useState<string[]>([]);
  const [createdInfo, setCreatedInfo] = useState<{ repCode: string; verifyLink?: string } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const toggleVertical = (v: string) => setSelectedVerticals(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  const copyField = (val: string, key: string) => { navigator.clipboard.writeText(val); setCopied(key); setTimeout(() => setCopied(null), 2000); };

  const createRep = trpc.rep.create.useMutation({
    onSuccess: (data) => {
      toast.success("Rep registered successfully!");
      setCreatedInfo({
        repCode: (data as any).repCode ?? "",
        verifyLink: (data as any).verifyPortalUrl,
      });
    },
    onError: (e) => toast.error(e.message),
  });

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.firstName.trim()) errs.firstName = "Required";
    if (!form.lastName.trim()) errs.lastName = "Required";
    if (!form.email.trim()) errs.email = "Required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    createRep.mutate({
      ...form,
      allowedVerticals: selectedVerticals.length > 0 ? selectedVerticals : null,
    });
  };

  // Step 1 success screen: show rep code + verify link (NO credentials yet)
  if (createdInfo) {
    const infoRepCode = createdInfo.repCode;
    const infoVerifyLink = createdInfo.verifyLink;
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-xl p-3">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="font-bold text-sm">Agent Registered — Step 1 Complete</p>
            <p className="text-xs text-green-600">A welcome email with the rep code and identity verification link has been queued for delivery. Credentials will only be sent after identity is approved.</p>
          </div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Rep Code (sent in Step 1 email)</p>
          <div className="flex items-center justify-between bg-white border rounded-lg px-3 py-2">
            <span className="font-mono text-sm font-bold text-gray-800">{infoRepCode}</span>
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => copyField(infoRepCode, "repCode")}>
              {copied === "repCode" ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
            </Button>
          </div>
          {infoVerifyLink && (
            <>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Identity Verification Link (auto-sent in Step 1 email)</p>
              <div className="flex items-center justify-between bg-white border rounded-lg px-3 py-2">
                <span className="font-mono text-xs text-blue-700 truncate max-w-[260px]">{infoVerifyLink}</span>
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => copyField(infoVerifyLink, "verifyLink")}>
                  {copied === "verifyLink" ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </>
          )}
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1">
          <p className="text-xs font-bold text-blue-800">What happens next:</p>
          <ol className="text-[11px] text-blue-700 space-y-0.5 list-decimal list-inside">
            <li>The Step 1 welcome email is sent with rep code + verify link</li>
            <li>Agent uploads their ID via the verification link</li>
            <li>You review and approve in the Identity Verification panel</li>
            <li>On approval, credentials are auto-generated and sent for the Step 4 access email</li>
          </ol>
        </div>
        <div className="flex gap-2 justify-end">
          <Button className="bg-[#84ab4a] hover:bg-[#6e9038] text-white border-0" onClick={onSuccess}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">First Name *</label>
          <Input value={form.firstName} onChange={(e) => setForm(f => ({ ...f, firstName: e.target.value }))} className={errors.firstName ? "border-red-400" : ""} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Last Name *</label>
          <Input value={form.lastName} onChange={(e) => setForm(f => ({ ...f, lastName: e.target.value }))} className={errors.lastName ? "border-red-400" : ""} />
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Email *</label>
        <Input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className={errors.email ? "border-red-400" : ""} />
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Phone</label>
        <Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Primary Vertical</label>
          <Select value={form.vertical} onValueChange={(v) => setForm(f => ({ ...f, vertical: v }))}>
            <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ALL_VERTICALS.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Agent Level</label>
          <Select value={form.agentLevel} onValueChange={(v) => setForm(f => ({ ...f, agentLevel: v as any }))}>
            <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Agency">Agency</SelectItem>
              <SelectItem value="Senior Associate">Senior Associate</SelectItem>
              <SelectItem value="Associate">Associate</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Upline Rep Code</label>
          <Input value={form.uplineRepCode} onChange={(e) => setForm(f => ({ ...f, uplineRepCode: e.target.value }))} placeholder="e.g. BC-360" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Agency Rep Code</label>
          <Input value={form.mgaRepCode} onChange={(e) => setForm(f => ({ ...f, mgaRepCode: e.target.value }))} placeholder="e.g. BC-360" />
        </div>
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-3.5 h-3.5 text-blue-600" />
          <label className="text-xs font-bold text-blue-800">Approved Verticals</label>
          <span className="text-[10px] text-blue-500 ml-auto">Leave unchecked = no access</span>
        </div>
        <p className="text-[10px] text-blue-600 mb-2">Select which verticals this rep is authorized to sell. Reps can only see referral links and submit leads for approved verticals.</p>
        <div className="grid grid-cols-2 gap-1.5">
          {ALL_VERTICALS.map(v => (
            <label key={v} className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={selectedVerticals.includes(v)} onChange={() => toggleVertical(v)} className="rounded" />
              <span className="text-xs text-gray-700">{v}</span>
            </label>
          ))}
        </div>
        {selectedVerticals.length === 0 && (
          <p className="text-[10px] text-orange-600 mt-1.5 font-semibold">No verticals selected — rep will have no access until approved</p>
        )}
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
        <Input value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes..." />
      </div>
      <div className="flex gap-2 justify-end pt-2">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" className="bg-[#84ab4a] hover:bg-[#6e9038] text-white border-0" disabled={createRep.isPending}>
          {createRep.isPending ? "Registering..." : "Register Rep"}
        </Button>
      </div>
    </form>
  );
}

// ─── Lead Detail Modal ────────────────────────────────────────────────────────
function LeadDetailModal({ leadId, onClose }: { leadId: number; onClose: () => void }) {
  const leadQuery = trpc.lead.getById.useQuery({ id: leadId });
  const historyQuery = trpc.lead.history.useQuery({ leadId });
  const utils = trpc.useUtils();
  const updateStage = trpc.lead.updateStage.useMutation({
    onSuccess: () => {
      toast.success("Stage updated and synced to Wibiz Dashboard");
      utils.lead.list.invalidate();
      utils.lead.getById.invalidate({ id: leadId });
      utils.lead.pipelineStats.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
  const [newStage, setNewStage] = useState("");
  const lead = leadQuery.data;
  if (!lead) return null;
  // Find the vertical config for this lead to show correct stages
  const verticalConfig = VERTICALS.find(v => v.verticalName === lead.verticalName || lead.verticalName?.startsWith(v.label));
  const availableStages = verticalConfig?.pipelines[0]?.stages ?? STAGES.map(s => s.label);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          {(() => { const { primary, secondary } = resolveS360Name(lead as any); return (<>
            <h3 className="font-bold text-lg text-gray-900">{primary}</h3>
            <p className="text-sm text-gray-500">{secondary ?? lead.businessName ?? lead.email}</p>
          </>); })()}
        </div>
        <StageBadge stage={lead.stage} ghlStageName={(lead as any).ghlStageName} />
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        {[
          ["Email", lead.email], ["Phone", lead.phone],
          ["Business", lead.businessName], ["Vertical", lead.verticalName],
          ["Rep Code", lead.repCode], ["Plan", lead.plan ? PLAN_LABELS[lead.plan] : "—"],
          ["Setup Fee", lead.setupFee ? fmt(Number(lead.setupFee)) : "—"],
          ["Monthly Fee", lead.monthlyFee ? fmt(Number(lead.monthlyFee)) : "—"],
          ["Pipeline Stage", (lead as any).ghlStageName || lead.stage || "—"],
          ["Wibiz Contact ID", lead.ghlContactId || "Pending sync"],
          ["Source", lead.source?.startsWith("ghl_") ? null : lead.source],
          ["Booked", new Date(lead.createdAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })],
        ].map(([label, value]) => (
          <div key={label} className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{label}</div>
            <div className="font-medium text-gray-800 truncate">{value || "—"}</div>
          </div>
        ))}
      </div>
      {lead.referralLink && (
        <div className="bg-[#0D1E35] rounded-xl p-3">
          <div className="text-xs text-[#84ab4a] font-bold uppercase tracking-wide mb-1">Referral Link</div>
          <div className="font-mono text-xs text-white/70 break-all">{lead.referralLink}</div>
        </div>
      )}
      {/* Application / Booking form details — shown for any lead with captured form data */}
      {(lead.f360BusinessName || lead.f360Industry || lead.f360Revenue || lead.f360CreditStatus || lead.f360TimeInBusiness || lead.f360MainGoal || lead.f360AdditionalInfo || lead.f360AffiliateRefId || lead.f360ReferrerAgentId || lead.referrerAgentName || lead.referrerAgentEmail) && (
        <div className="border border-[#84ab4a]/20 rounded-xl p-4 bg-[#84ab4a]/3">
          <div className="text-xs font-bold text-[#84ab4a] uppercase tracking-wide mb-3">
            {lead.verticalName ? `${lead.verticalName} Application Details` : "Booking Form Details"}
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {[
              ["Business Name", lead.f360BusinessName || lead.businessName],
              ["Industry / Type", lead.f360Industry],
              ["Monthly Revenue", lead.f360Revenue],
              ["Credit Status", lead.f360CreditStatus],
              ["Time in Business", lead.f360TimeInBusiness],
              ["Affiliate Ref ID", lead.f360AffiliateRefId],
              ["Agent ID (ref)", lead.f360AgentId || lead.f360RefId || lead.f360ReferrerAgentId],
              ["Referrer Name", lead.referrerAgentName],
              ["Referrer Email", lead.referrerAgentEmail],
            ].filter(([, v]) => v).map(([label, value]) => (
              <div key={label as string} className="bg-white rounded-lg p-3">
                <div className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{label}</div>
                <div className="font-medium text-gray-800 truncate">{value || "—"}</div>
              </div>
            ))}
          </div>
          {lead.f360MainGoal && (
            <div className="mt-3 bg-white rounded-lg p-3">
              <div className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">Main Goal</div>
              <div className="text-sm text-gray-800">{lead.f360MainGoal}</div>
            </div>
          )}
          {lead.f360AdditionalInfo && (
            <div className="mt-3 bg-white rounded-lg p-3">
              <div className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">Additional Info</div>
              <div className="text-sm text-gray-800">{lead.f360AdditionalInfo}</div>
            </div>
          )}
        </div>
      )}
      {/* Admin-only stage update */}
      <div className="border border-[#84ab4a]/20 rounded-xl p-4 bg-[#84ab4a]/3">
        <div className="flex items-center gap-2 mb-3">
          <Shield className="w-3.5 h-3.5 text-[#84ab4a]" />
          <div className="text-xs font-bold text-gray-600 uppercase tracking-wide">Admin: Update Stage</div>
          <span className="text-[10px] bg-[#84ab4a]/10 text-[#84ab4a] px-2 py-0.5 rounded-full font-bold ml-auto">ADMIN ONLY</span>
        </div>
        <p className="text-xs text-gray-400 mb-3">Stage changes sync automatically to Wibiz Dashboard pipeline and trigger Make.com automations.</p>
        <div className="flex gap-2">
          <Select value={newStage} onValueChange={setNewStage}>
            <SelectTrigger className="flex-1 text-sm">
              <SelectValue placeholder="Select new stage..." />
            </SelectTrigger>
            <SelectContent>
              {STAGES.map((s) => (
                <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            disabled={!newStage || updateStage.isPending}
            onClick={() => newStage && updateStage.mutate({ leadId, stage: newStage as any, changedBy: "admin", isAdmin: true })}
            className="bg-[#84ab4a] hover:bg-[#6e9038] text-white border-0"
          >
            {updateStage.isPending ? "Syncing..." : "Update & Sync"}
          </Button>
        </div>
      </div>
      {historyQuery.data && historyQuery.data.length > 0 && (
        <div>
          <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Stage History</div>
          <div className="space-y-2">
            {historyQuery.data.slice(0, 5).map((h) => (
              <div key={h.id} className="flex items-center gap-2 text-xs text-gray-500">
                <Clock className="w-3 h-3 flex-shrink-0" />
                <span>{h.fromStage?.replace(/_/g, " ")} → <strong>{h.toStage.replace(/_/g, " ")}</strong></span>
                <span className="text-xs text-gray-300 ml-1">by {h.changedBy}</span>
                <span className="ml-auto text-gray-400">{new Date(h.createdAt).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Commission Grid Table ────────────────────────────────────────────────────
function CommissionGridTable() {
  const [selectedVertical, setSelectedVertical] = useState("Fundable360");
  const verticals = Object.keys(COMMISSION_GRID);
  const grid = COMMISSION_GRID[selectedVertical];
  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden mb-6">
      <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-700">Commission Rate Reference</span>
        <div className="flex gap-1.5">
          {verticals.map((v) => (
            <button
              key={v}
              onClick={() => setSelectedVertical(v)}
              className={`text-xs px-2.5 py-1 rounded-full font-semibold transition-all ${selectedVertical === v ? "text-white" : "text-gray-500 hover:bg-gray-100"}`}
              style={selectedVertical === v ? { background: VERTICAL_COLORS[v] ?? "#84ab4a" } : {}}
            >
              {v}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Agent Level</th>
              <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Enrollment</th>
              <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Recurring</th>
              <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Notes</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(grid).map(([level, rates]) => (
              <tr key={level} className="border-b border-gray-50">
                <td className="px-5 py-3 font-semibold text-gray-800">{level}</td>
                <td className="px-5 py-3 font-mono text-sm font-bold" style={{ color: VERTICAL_COLORS[selectedVertical] ?? "#84ab4a" }}>{rates.enrollment}</td>
                <td className="px-5 py-3 font-mono text-sm text-gray-600">{rates.recurring}</td>
                <td className="px-5 py-3 text-xs text-gray-400">
                  {rates.note ?? "—"}
                  {rates.paymentLink && (
                    <a href={rates.paymentLink} target="_blank" rel="noopener noreferrer"
                      className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-green-100 text-green-700 hover:bg-green-200 font-semibold text-[10px] border border-green-200">
                      👉 Pay Enrollment Fee
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Legacy360 has one verticalName ("Legacy360") shared by 3 distinct GHL pipelines.
// ghlPipelineId (set by CRM sync — server/routers.ts syncPipelineFromGhl) is the
// authoritative signal since it requires no GHL custom-field configuration —
// this is what lets the 125 Plan form use a plain native GHL form instead of the
// paid "Send Data via Webhook" action. l360LeadType (set by /api/legacy360/webhook,
// when that push path IS used) is the fallback. Mirrors the same helper in
// RepDashboard.tsx — keep both in sync if pipeline keys change.
type Legacy360PipelineKey = "living_trust" | "125plan" | "new_agent" | "advantage";
const LEGACY360_PIPELINE_ID_MAP: Record<string, Legacy360PipelineKey> = {
  "AtnyZ5hxQGhpBwTO8gro": "living_trust",
  "hoIcCAyZ8Wg0wPPNzosy": "125plan",
  "LHgTWf4RYWr6SZlbe94h": "new_agent",
  "EKY9Qps5kZDkRBbLjL4t": "advantage",
};
// Derived from the map above so the two can never drift apart. Needed because
// the GHL stage lookup takes a pipeline ID, while lead routing works in keys.
const LEGACY360_KEY_TO_PIPELINE_ID = Object.fromEntries(
  Object.entries(LEGACY360_PIPELINE_ID_MAP).map(([id, key]) => [key, id])
) as Record<Legacy360PipelineKey, string>;
function legacy360SubTypeKeyForLead(lead: { l360LeadType?: string | null; ghlPipelineId?: string | null }): Legacy360PipelineKey {
  const byPipelineId = lead.ghlPipelineId ? LEGACY360_PIPELINE_ID_MAP[lead.ghlPipelineId] : undefined;
  if (byPipelineId) return byPipelineId;
  if (lead.l360LeadType === "legacy360_125plan") return "125plan";
  if (lead.l360LeadType === "legacy360_new_agent") return "new_agent";
  if (lead.l360LeadType === "lagecy360_advantage") return "advantage";
  return "living_trust";
}

// Legacy360's "Schedule a Consultation" calendar, keyed by pipeline sub-type —
// ADMIN_CONSULTATION_CALENDARS is keyed by verticalName, and all 4 Legacy360
// pipelines share verticalName "Legacy360", so a verticalName-only lookup always
// resolved to Living Trust's calendar regardless of the lead's actual pipeline.
// 125 Plan's calendar ID was supplied by the client 2026-07-31 (Rt2jjmae3NaN5m53YxAi).
const LEGACY360_CONSULTATION_CALENDARS: Record<Legacy360PipelineKey, { label: string; url: string; color: string }[]> = {
  living_trust: [
    { label: "Legacy360 Living Trust", url: "https://api.wibiz.ai/widget/booking/28XGN7D3DWQ4OSO2UWLn", color: "#C8920A" },
  ],
  "125plan": [
    { label: "Legacy360 - Section 125 Plan Discovery Meeting", url: "https://api.wibiz.ai/widget/booking/Rt2jjmae3NaN5m53YxAi", color: "#C8920A" },
  ],
  new_agent: [],
  advantage: [],
};

function consultationCalendarsForLead(lead: { verticalName?: string | null; l360LeadType?: string | null; ghlPipelineId?: string | null }): { label: string; url: string; color: string }[] {
  if (lead.verticalName === "Legacy360") {
    return LEGACY360_CONSULTATION_CALENDARS[legacy360SubTypeKeyForLead(lead)] ?? [];
  }
  return ADMIN_CONSULTATION_CALENDARS[lead.verticalName ?? ""] ?? [];
}

// ─── Vertical Kanban Tab ──────────────────────────────────────────────────────
function VerticalKanbanTab({
  vertical,
  leads,
  onSelectLead,
  onDeleteLead,
}: {
  vertical: typeof VERTICALS[number];
  leads: any[];
  onSelectLead: (id: number) => void;
  onDeleteLead: (id: number) => void;
}) {
  const [activePipelineIdx, setActivePipelineIdx] = useState(0);
  // Clamp index to valid range — prevents TypeError when switching from a multi-pipeline
  // vertical (e.g. Share360 at index 2) to a single-pipeline one (e.g. Legacy360).
  const safeIdx = Math.min(activePipelineIdx, vertical.pipelines.length - 1);
  const pipeline = vertical.pipelines[safeIdx];

  const verticalLeads = leads.filter(l =>
    l.verticalName === vertical.verticalName ||
    l.verticalName?.startsWith(vertical.label)
  );

  // Fetch live stages from GHL for real pipeline IDs; skip virtual pipeline IDs.
  // Legacy360 New Agents is sourced from the certifications table, not GHL opportunities
  // (see certRosterQuery below) — treat it as virtual here too so it skips the GHL fetch.
  const isCertificationPipeline = (pipeline as any).dataSource === "certifications";
  const VIRTUAL_PIPELINE_IDS = ["share360-ra"];
  const isRealGhlPipeline = !VIRTUAL_PIPELINE_IDS.includes(pipeline.id) && !isCertificationPipeline;
  const ghlStagesQuery = trpc.lead.getGhlStages.useQuery(
    { pipelineId: pipeline.id },
    { enabled: isRealGhlPipeline, staleTime: 5 * 60 * 1000 }
  );
  const activeStageNames: string[] =
    (isRealGhlPipeline && ghlStagesQuery.data && ghlStagesQuery.data.length > 0)
      ? ghlStagesQuery.data.map((s: { id: string; name: string }) => s.name)
      : [...pipeline.stages];

  // Legacy360 New Agents roster — Legacy360-certified associates (passed the
  // certification quiz), sourced from BC360's own certifications table rather
  // than a GHL opportunity sync. Admin-only view (not shown on the Rep dashboard).
  const certRosterQuery = trpc.certification.adminList.useQuery(
    { vertical: "Legacy360", status: "passed" },
    { enabled: isCertificationPipeline }
  );

  // Map leads to GHL stage names using the ghlStageName field or fall back to the known stage key → display name map
  const STAGE_KEY_TO_DISPLAY: Record<string, string> = {
    lead_captured: "New Lead",
    audit_submitted: "Appointment Booked",
    follow_up_active: "Initial Outreach",
    payment_received: "Enrolled",
    onboarding_submitted: "Documents In Review",
    active_client: "Funded",
    lost_cold: "Inactive / Lost",
  };
  const getLeadGhlStage = (lead: any): string => {
    if (lead.ghlStageName) return lead.ghlStageName;
    if (lead.stage && STAGE_KEY_TO_DISPLAY[lead.stage]) return STAGE_KEY_TO_DISPLAY[lead.stage];
    return lead.stage?.replace(/_/g, " ") || "New Lead";
  };

  // For multi-pipeline verticals, filter leads to the correct pipeline.
  // Share360 uses explicit verticalName on each pipeline ("Share360 Nonprofit" / "Share360 Business")
  // for strict separation — never rely on stage-name overlap across pipelines for this.
  const leadsInPipeline = (() => {
    if (vertical.pipelines.length === 1) return verticalLeads;
    // Legacy360: discriminate by l360LeadType, not verticalName (all 3 Legacy360
    // pipelines share verticalName "Legacy360" — see legacy360SubTypeKeyForLead).
    const l360Key = (pipeline as any).l360LeadTypeKey as string | undefined;
    if (l360Key) {
      return verticalLeads.filter(l => legacy360SubTypeKeyForLead(l as any) === l360Key);
    }
    const pipelineVerticalName = (pipeline as any).verticalName as string | undefined;
    if (pipelineVerticalName) {
      // Strict filter: only leads whose verticalName exactly matches this pipeline's verticalName.
      // Also include generic "Share360" leads (no sub-type yet) in the first pipeline tab only.
      return verticalLeads.filter(l => {
        if (l.verticalName === pipelineVerticalName) return true;
        // Leads with the parent verticalName (e.g. "Share360") or no subtype go to first pipeline
        if (safeIdx === 0 && (l.verticalName === vertical.verticalName || !l.verticalName)) return true;
        return false;
      });
    }
    // Fallback for other multi-pipeline verticals: filter by stage name
    const stageSet = new Set(activeStageNames);
    return verticalLeads.filter(l => {
      const stage = getLeadGhlStage(l);
      if (stageSet.size > 0 && stageSet.has(stage)) return true;
      if (!l.ghlStageName && safeIdx === 0) return true;
      return false;
    });
  })();

  function exportCsv() {
    const headers = ["ID", "First Name", "Last Name", "Email", "Phone", "Business", "Stage", "Rep Code", "Vertical", "Created"];
    const rows = verticalLeads.map(l => [
      l.id, l.firstName ?? "", l.lastName ?? "", l.email ?? "", l.phone ?? "",
      l.businessName ?? "", (l.ghlStageName || l.stage) ?? "", l.repCode ?? "", l.verticalName ?? "",
      new Date(l.createdAt).toLocaleDateString(),
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `${vertical.label}_leads_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  return (
    <div>
      {/* Tab header with export */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-900">{vertical.label} Leads</h2>
          <p className="text-sm text-gray-400">{verticalLeads.length} total leads in this vertical</p>
        </div>
        <button
          onClick={exportCsv}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 text-sm font-semibold text-gray-600 hover:border-[#84ab4a]/50 hover:text-[#84ab4a] shadow-sm transition-colors"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>
      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        {[
          { label: "Total Leads", value: verticalLeads.length, icon: Users, color: vertical.color },
          { label: "Enrolled / Active", value: verticalLeads.filter(l => ["active_client", "payment_received"].includes(l.stage)).length, icon: CheckCircle2, color: "#84ab4a" },
          { label: "In Progress", value: verticalLeads.filter(l => ["audit_submitted", "follow_up_active", "onboarding_submitted"].includes(l.stage)).length, icon: Activity, color: "#C8920A" },
          { label: "Lost / Inactive", value: verticalLeads.filter(l => l.stage === "lost_cold").length, icon: AlertCircle, color: "#D93025" },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: color }} />
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold font-mono text-gray-900">{value}</div>
                <div className="text-xs text-gray-400 mt-0.5 font-medium">{label}</div>
              </div>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
                <Icon className="w-4.5 h-4.5" style={{ color }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pipeline selector (if multiple pipelines) */}
      {vertical.pipelines.length > 1 && (
        <div className="flex gap-2 mb-4">
          {vertical.pipelines.map((p, i) => (p as any).hidden ? null : (
            <button
              key={p.id}
              onClick={() => setActivePipelineIdx(i)}
              className={`text-sm px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2 ${activePipelineIdx === i ? "text-white shadow-sm" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              style={activePipelineIdx === i ? { background: vertical.color } : {}}
            >
              {p.name}
              {(p as any).internalOnly && (
                <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full">Internal</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Pipeline note */}
      {(pipeline as any).note && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-xs text-amber-800 font-medium">
          📋 {(pipeline as any).note}
        </div>
      )}

      {/* Calendar booking note */}
      <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5 text-xs text-blue-800">
        <span className="font-bold">📅 Lead Capture:</span> Leads are captured automatically when a prospect books an appointment on the {vertical.label} calendar. Records are created in the CRM tagged with the vertical and rep ID.
      </div>

      {/* GHL stages loading indicator */}
      {isRealGhlPipeline && ghlStagesQuery.isLoading && (
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading live stages from Wibiz...
        </div>
      )}

      {/* New Agents — certification roster (not a lead/opportunity board) */}
      {isCertificationPipeline ? (
        <div>
          {certRosterQuery.isLoading ? (
            <div className="py-8 text-center text-gray-400 text-sm">Loading certified associates...</div>
          ) : (certRosterQuery.data ?? []).length === 0 ? (
            <div className="bg-white/50 border border-dashed rounded-xl p-8 text-center" style={{ borderColor: `${vertical.color}30` }}>
              <div className="text-sm text-gray-400">No Legacy360-certified associates yet</div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2.5 px-1">
                <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: vertical.color }}>Information Submitted</span>
                <span className="text-xs font-bold rounded-full px-1.5 py-0.5 ml-1" style={{ background: `${vertical.color}15`, color: vertical.color }}>
                  {(certRosterQuery.data ?? []).length}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {(certRosterQuery.data ?? []).map((cert: any) => (
                  <div key={cert.id} className="bg-white border border-gray-100 rounded-xl p-3">
                    <div className="font-semibold text-xs text-gray-800 truncate">
                      {[cert.repFirstName, cert.repLastName].filter(Boolean).join(" ") || cert.repCode}
                    </div>
                    <div className="text-xs text-gray-400 truncate mt-0.5">{cert.repCode}</div>
                    {cert.completedAt && (
                      <div className="text-[10px] text-gray-400 mt-1">
                        Certified {new Date(cert.completedAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-3" style={{ minWidth: `${activeStageNames.length * 200}px` }}>
          {activeStageNames.map((stageName) => {
            const stageLeads = leadsInPipeline.filter(l => {
              const lStage = getLeadGhlStage(l);
              return lStage === stageName || l.stage?.replace(/_/g, " ") === stageName.toLowerCase().replace(/ /g, "_");
            });
            const isTerminal = (pipeline as any).terminalStages?.includes(stageName);
            const isAdminOnly = (pipeline as any).adminOnlyStages?.includes(stageName);
            return (
              <div key={stageName} className="w-48 flex-shrink-0">
                <div className="flex items-center justify-between mb-2.5 px-1">
                  <div className="flex items-center gap-1 min-w-0">
                    <span className="text-[11px] font-bold uppercase tracking-wide truncate" style={{ color: vertical.color }}>
                      {stageName}
                    </span>
                    {isAdminOnly && (
                      <span className="text-[9px] bg-red-100 text-red-600 px-1 py-0.5 rounded font-bold flex-shrink-0">ADMIN</span>
                    )}
                  </div>
                  <span className="text-xs font-bold rounded-full px-1.5 py-0.5 ml-1 flex-shrink-0" style={{ background: `${vertical.color}15`, color: vertical.color }}>
                    {stageLeads.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {stageLeads.map((lead) => (
                    <div
                      key={lead.id}
                      className="bg-white border border-gray-100 rounded-xl p-3 hover:border-gray-300 hover:shadow-sm transition-all group relative"
                    >
                      <div className="cursor-pointer" onClick={() => onSelectLead(lead.id)}>
                        {(() => {
                          const isBiz = lead.verticalName === "Share360 Business";
                          const isNP  = lead.verticalName === "Share360 Nonprofit";
                          // GHL workflows append " - {repCode}" to company/org names — strip it for display
                          const stripRC = (s: string | null | undefined): string | null => {
                            if (!s) return null;
                            if (lead.repCode && s.endsWith(` - ${lead.repCode}`)) return s.slice(0, -(` - ${lead.repCode}`).length);
                            return s;
                          };
                          const contactFull = [lead.firstName, lead.lastName].filter(Boolean).join(" ");
                          const rawOrgBiz = isBiz ? (lead.sh360BusinessName || lead.businessName)
                                          : isNP  ? (lead.sh360OrgName || lead.businessName)
                                          : null;
                          const cleanedOrg = stripRC(rawOrgBiz);
                          const cleanedContact = stripRC(contactFull) || null;
                          const primaryName = (isBiz || isNP)
                            ? (cleanedOrg || cleanedContact || lead.email?.split("@")[0] || "Unknown")
                            : (cleanedContact || stripRC(lead.businessName) || lead.email?.split("@")[0] || "Unknown");
                          // Secondary:
                          //   Business  → contact person (firstName+lastName), fall back to email
                          //   Nonprofit → sh360BusinessName ("Business Contact Name"), fall back to contact name, then email
                          //   Others    → businessName or email
                          const secondaryLine = isBiz
                            ? (cleanedContact && cleanedContact !== primaryName ? cleanedContact : lead.email)
                            : isNP
                            ? (stripRC(lead.sh360BusinessName) || (cleanedContact && cleanedContact !== primaryName ? cleanedContact : null) || lead.email)
                            : (stripRC(lead.businessName) || lead.email);
                          return (
                            <>
                              <div className="font-semibold text-xs text-gray-800 truncate mb-0.5">{primaryName}</div>
                              <div className="text-xs text-gray-400 truncate mb-1.5">{secondaryLine}</div>
                              {lead.repCode && (
                                <span className="text-[10px] font-mono text-gray-400 block">{lead.repCode}</span>
                              )}
                            </>
                          );
                        })()}
                        {lead.plan && (
                          <div className="mt-1 text-[10px] font-semibold text-gray-500">{PLAN_LABELS[lead.plan] ?? lead.plan}</div>
                        )}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); onDeleteLead(lead.id); }}
                        className="absolute top-1.5 right-1.5 p-1 text-gray-200 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all"
                        title="Delete lead"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {stageLeads.length === 0 && (
                    <div className="bg-white/50 border border-dashed rounded-xl p-4 text-center" style={{ borderColor: `${vertical.color}30` }}>
                      <div className="text-xs text-gray-300">Empty</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
}

// ─── CRM Sync Tab ─────────────────────────────────────────────────────────────
const SYNC_PIPELINES = [
  { label: "Fundable360 Pipeline A", pipelineId: "UtddGTWZhyTcaRSWEGKm", verticalName: "Fundable360", color: "#84ab4a" },
  { label: "Fundable360 Pipeline B", pipelineId: "Qh2eqgohaTaCIITZYOyQ", verticalName: "Fundable360", color: "#84ab4a" },
  { label: "Scale360 Partner Funnel", pipelineId: "mzgOuQhHmkDuixcpiSbj", verticalName: "Scale360", color: "#E8620A" },
  { label: "Scale360 Enterprise", pipelineId: "HmZA3KEdITefCLNZwg1H", verticalName: "Scale360 Enterprise", color: "#E8620A" },
  { label: "Share360 Nonprofit", pipelineId: "czZqnGQZru1yUcz7vHM6", verticalName: "Share360 Nonprofit", color: "#0FA8A8" },
  { label: "Share360 Business", pipelineId: "le4RyNh4YBlLMVeVslHt", verticalName: "Share360 Business", color: "#1A6FD4" },
  { label: "Legacy360 Living Trust", pipelineId: "AtnyZ5hxQGhpBwTO8gro", verticalName: "Legacy360", color: "#C8920A" },
  { label: "Legacy360 125 Plan", pipelineId: "hoIcCAyZ8Wg0wPPNzosy", verticalName: "Legacy360", color: "#C8920A" },
  { label: "Legacy360 Advantage", pipelineId: "EKY9Qps5kZDkRBbLjL4t", verticalName: "Legacy360", color: "#C8920A" },
  { label: "Media360", pipelineId: "MKZZiFgADSTL2iNuOuvw", verticalName: "Media360", color: "#7B4FD4" },
];

function PipelineSyncSection({ utils }: { utils: ReturnType<typeof trpc.useUtils> }) {
  const [syncState, setSyncState] = useState<Record<string, { loading: boolean; result?: string; error?: string }>>({});
  const syncMutation = trpc.crm.syncPipelineFromGhl.useMutation();

  const handleSync = async (pipelineId: string, verticalName: string, label: string) => {
    setSyncState(s => ({ ...s, [pipelineId]: { loading: true } }));
    try {
      const result = await syncMutation.mutateAsync({ pipelineId, verticalName });
      const msg = result.total === 0
        ? `No opportunities found in Wibiz for this pipeline`
        : `Synced ${result.upserted} leads (${result.total} total${result.skipped > 0 ? `, ${result.skipped} skipped` : ""})`;
      setSyncState(s => ({ ...s, [pipelineId]: { loading: false, result: msg } }));
      // Refresh both lead list and cached stage names for this pipeline
      utils.lead.list.invalidate();
      utils.lead.getGhlStages.invalidate({ pipelineId });
      toast.success(`${label}: ${result.total === 0 ? "0 records found" : `${result.upserted} leads synced`}`);
    } catch (err: any) {
      setSyncState(s => ({ ...s, [pipelineId]: { loading: false, error: err?.message ?? "Sync failed" } }));
      toast.error(`${label}: sync failed`);
    }
  };

  const handleSyncAll = async () => {
    for (const p of SYNC_PIPELINES) {
      await handleSync(p.pipelineId, p.verticalName, p.label);
    }
  };

  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
      <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
        <div>
          <span className="text-sm font-semibold text-gray-700">Sync Leads from Wibiz Pipelines</span>
          <p className="text-xs text-gray-400 mt-0.5">Pull all opportunities from each pipeline directly into the BC360 lead database.</p>
        </div>
        <Button size="sm" onClick={handleSyncAll} disabled={Object.values(syncState).some(s => s.loading)} className="bg-[#84ab4a] hover:bg-[#6e9038] text-white">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Sync All Pipelines
        </Button>
      </div>
      <div className="divide-y divide-gray-50">
        {SYNC_PIPELINES.map(p => {
          const state = syncState[p.pipelineId];
          return (
            <div key={p.pipelineId} className="flex items-center gap-4 px-5 py-3.5">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: p.color }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-800">{p.label}</div>
                <div className="text-xs text-gray-400 font-mono truncate">{p.pipelineId}</div>
                {state?.result && <div className="text-xs text-green-600 mt-0.5">{state.result}</div>}
                {state?.error && <div className="text-xs text-red-500 mt-0.5">{state.error}</div>}
              </div>
              <Button
                size="sm" variant="outline"
                onClick={() => handleSync(p.pipelineId, p.verticalName, p.label)}
                disabled={state?.loading}
                className="flex-shrink-0"
              >
                {state?.loading ? <><RefreshCw className="w-3 h-3 mr-1.5 animate-spin" /> Syncing...</> : <><RefreshCw className="w-3 h-3 mr-1.5" /> Sync</>}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Share360 Display Fix Panel ───────────────────────────────────────────────
function Share360DisplayFixPanel() {
  const [result, setResult] = useState<{ total: number; synced: number; skipped: number; errors: number } | null>(null);
  const utils = trpc.useUtils();
  const mutation = trpc.lead.bulkSyncShare360Fields.useMutation({
    onSuccess: (data) => {
      setResult(data);
      // Invalidate the list cache so every dashboard immediately shows fresh
      // sh360OrgName / sh360BusinessName values — eliminates the stale-cache
      // contact-name flash on next page load or drawer open.
      utils.lead.list.invalidate();
      if (data.errors > 0) {
        toast.warning(`Share360 sync done — ${data.synced} updated, ${data.errors} errors`);
      } else {
        toast.success(`Share360 sync complete — ${data.synced} leads updated`);
      }
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="bg-white border border-blue-100 rounded-2xl shadow-sm overflow-hidden mb-6">
      <div className="px-5 py-3.5 border-b border-blue-50 flex items-center justify-between">
        <div>
          <span className="text-sm font-semibold text-gray-700">Fix Share360 Display Fields</span>
          <p className="text-xs text-gray-400 mt-0.5">
            Re-fetches org/business name and role fields from Wibiz for all Share360 leads.
            Safe to run — only updates Share360 custom fields, never overwrites contact names or attribution.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="bg-[#1A6FD4] hover:bg-[#155cb8] text-white flex-shrink-0 ml-4"
        >
          {mutation.isPending
            ? <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Syncing...</>
            : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Sync Share360 Fields</>}
        </Button>
      </div>
      {result && (
        <div className="px-5 py-3 flex flex-wrap gap-4 text-xs text-gray-500">
          <span><span className="font-semibold text-gray-700">{result.total}</span> total leads</span>
          <span className="text-green-600"><span className="font-semibold">{result.synced}</span> updated</span>
          <span><span className="font-semibold">{result.skipped}</span> skipped (no change needed)</span>
          {result.errors > 0 && <span className="text-red-500"><span className="font-semibold">{result.errors}</span> errors</span>}
        </div>
      )}
    </div>
  );
}

// ─── Document GHL Sync Backfill Panel ────────────────────────────────────────
const DOC_SOURCES = [
  { value: "client_portal",       label: "Client Portal Uploads",          desc: "Bank statements, business IDs, and other docs uploaded by clients" },
  { value: "rep_identity",        label: "Rep Driver License / ID",         desc: "Identity verification docs uploaded by associates" },
  { value: "share360_enrollment", label: "Share360 Enrollment Packet & W-9", desc: "Signed enrollment and W-9 PDFs from associate onboarding" },
] as const;

type DocSource = typeof DOC_SOURCES[number]["value"];

function DocSyncPanel() {
  const [dryRun, setDryRun] = useState(true);
  const [limit, setLimit] = useState(10);
  const [sources, setSources] = useState<DocSource[]>(["client_portal", "rep_identity", "share360_enrollment"]);
  const [result, setResult] = useState<any>(null);

  const backfillMutation = trpc.docSync.backfill.useMutation({
    onSuccess: (data) => {
      setResult(data);
      const { summary } = data;
      if (summary.dryRun) {
        toast.success(`Dry run complete — ${summary.toWrite} would be synced, ${summary.alreadySynced} already done`);
      } else {
        const written = summary.toWrite - summary.errors;
        toast.success(`Sync complete — ${written} notes posted to WiBiz${summary.errors ? `, ${summary.errors} failed` : ""}`);
      }
    },
    onError: (e) => toast.error(e.message),
  });

  function toggleSource(src: DocSource) {
    setSources(prev => prev.includes(src) ? prev.filter(s => s !== src) : [...prev, src]);
  }

  const actionLabel = dryRun ? "Preview (dry run)" : `Sync ${limit} records to WiBiz`;
  const canRun = sources.length > 0 && !backfillMutation.isPending;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-6">
      {/* Header */}
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-bold text-gray-800">Document Sync to WiBiz Notes</span>
        <span className="text-[10px] bg-blue-100 text-blue-700 font-bold px-2 py-0.5 rounded-full uppercase">Admin only</span>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Posts uploaded document links as notes on the matching WiBiz contact record.
        Only adds notes — does <strong>not</strong> modify tags, fields, or pipeline stage.
        Safe to re-run: already-synced records are automatically skipped.
      </p>

      {/* Source checkboxes */}
      <div className="flex flex-col gap-2 mb-4">
        {DOC_SOURCES.map(src => (
          <label key={src.value} className="flex items-start gap-2.5 cursor-pointer select-none group">
            <input
              type="checkbox"
              checked={sources.includes(src.value)}
              onChange={() => toggleSource(src.value)}
              className="mt-0.5 w-3.5 h-3.5 flex-shrink-0"
            />
            <span className="text-xs text-gray-700 leading-tight">
              <span className="font-medium">{src.label}</span>
              <span className="text-gray-400 ml-1">— {src.desc}</span>
            </span>
          </label>
        ))}
      </div>

      {/* Controls row */}
      <div className="flex flex-wrap gap-4 items-end mb-4">
        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
          <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} className="w-3.5 h-3.5" />
          Dry run (preview only — no WiBiz writes)
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-600">
          Records per run
          <input
            type="number" min={1} max={500} value={limit}
            onChange={e => setLimit(Math.min(500, Math.max(1, parseInt(e.target.value) || 10)))}
            className="w-16 border border-gray-200 rounded px-2 py-1 text-xs font-mono"
          />
        </label>
        <button
          onClick={() => { setResult(null); backfillMutation.mutate({ dryRun, limit, sources }); }}
          disabled={!canRun}
          className={`px-4 py-1.5 text-white text-xs font-semibold rounded-lg transition-colors ${
            !canRun ? "bg-gray-300 cursor-not-allowed"
            : dryRun ? "bg-gray-700 hover:bg-gray-800"
            : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {backfillMutation.isPending ? "Running…" : actionLabel}
        </button>
      </div>

      {!dryRun && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
          <strong>Live mode:</strong> This will post notes to WiBiz contacts. Always run a dry run first to confirm what will be synced.
        </p>
      )}

      {/* Results */}
      {result && (
        <>
          {/* Summary bar */}
          <div className="flex flex-wrap gap-4 text-xs mb-3 font-mono">
            <span className="text-gray-500">Total: <b>{result.summary.total}</b></span>
            <span className={result.summary.dryRun ? "text-blue-700" : "text-green-700"}>
              {result.summary.dryRun ? "Would sync" : "Synced"}: <b>{result.summary.toWrite}</b>
            </span>
            <span className="text-gray-400">Already done: <b>{result.summary.alreadySynced}</b></span>
            {result.summary.skippedNoUrl > 0 && <span className="text-gray-400">No URL: <b>{result.summary.skippedNoUrl}</b></span>}
            {result.summary.errors > 0 && <span className="text-red-600">Errors: <b>{result.summary.errors}</b></span>}
            {result.summary.dryRun && <span className="text-amber-600 font-bold">DRY RUN — nothing written</span>}
          </div>

          {result.summary.toWrite === 0 && result.summary.alreadySynced === result.summary.total && result.summary.total > 0 && (
            <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-3">
              All records in this batch are already synced.
            </p>
          )}

          {/* Row list */}
          {result.results.length > 0 && (
            <div className="max-h-72 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-50 text-[11px] font-mono">
              {result.results.map((r: any, i: number) => (
                <div
                  key={i}
                  className={`px-3 py-2 flex gap-2 items-start ${
                    r.action === "error" ? "bg-red-50"
                    : r.action === "write" ? (r.dryRun ? "bg-blue-50/40" : "bg-green-50/40")
                    : "bg-gray-50/50"
                  }`}
                >
                  {/* Source badge */}
                  <span className={`flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded font-bold ${
                    r.source === "client_portal" ? "bg-purple-100 text-purple-700"
                    : r.source === "rep_identity" ? "bg-orange-100 text-orange-700"
                    : "bg-teal-100 text-teal-700"
                  }`}>
                    {r.source === "client_portal" ? "client" : r.source === "rep_identity" ? "id" : "s360"}
                  </span>
                  {/* Doc type */}
                  <span className="text-gray-500 w-28 truncate flex-shrink-0">{r.docType}</span>
                  {/* Filename */}
                  <span className="text-gray-400 flex-1 truncate">{r.fileName}</span>
                  {/* Entity */}
                  <span className="text-gray-300 w-20 truncate flex-shrink-0">{r.entityType}:{r.entityId}</span>
                  {/* Action */}
                  <span className={`flex-shrink-0 font-bold ${
                    r.action === "write" ? (r.dryRun ? "text-blue-600" : "text-green-700")
                    : r.action === "error" ? "text-red-600"
                    : "text-gray-400"
                  }`}>{r.action === "skip_already_synced" ? "already done" : r.action === "skip_no_url" ? "no url" : r.action}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── GHL Upline Name Backfill Panel ──────────────────────────────────────────
function UplineSyncPanel() {
  const [dryRun, setDryRun] = useState(true);
  const [onlyBlank, setOnlyBlank] = useState(true);
  const [limit, setLimit] = useState(20);
  const [fields, setFields] = useState<"upline" | "mga">("upline");
  const [result, setResult] = useState<any>(null);
  const isMga = fields === "mga";

  const bulkMutation = trpc.ghlUplineSync.bulkBackfill.useMutation({
    onSuccess: (data) => {
      setResult(data);
      toast.success(`${data.summary.dryRun ? "Dry-run" : "Bulk sync"} complete — ${data.summary.toWrite} to write, ${data.summary.skipped} skipped, ${data.summary.errors} errors`);
    },
    onError: (e) => toast.error(e.message),
  });

  const actionLabel = dryRun ? "Dry Run (preview)" : `Write ${limit} contacts`;
  const actionColor = dryRun ? "bg-gray-700 hover:bg-gray-800" : "bg-amber-600 hover:bg-amber-700";

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-6">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-bold text-gray-800">WiBiz Hierarchy Field Backfill</span>
        <span className="text-[10px] bg-amber-100 text-amber-700 font-bold px-2 py-0.5 rounded-full uppercase">Admin only</span>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        {isMga ? (
          <>
            Writes <code className="bg-gray-100 px-1 rounded">mga_name</code>, <code className="bg-gray-100 px-1 rounded">mga_email</code> and <code className="bg-gray-100 px-1 rounded">mga_phone</code> to agent contacts — the agency above each agent.
            {" "}<code className="bg-gray-100 px-1 rounded">mga_email</code> is what agent email automations CC. Agency-level agents are skipped (no agency above them).
          </>
        ) : (
          <>Writes <code className="bg-gray-100 px-1 rounded">upline_name</code>, <code className="bg-gray-100 px-1 rounded">upline_email</code> and <code className="bg-gray-100 px-1 rounded">upline_phone</code> to WiBiz contacts.</>
        )}
        {" "}Only those fields — no tags, no other writes.
        <strong className="text-amber-700 ml-1">
          Before running bulk: confirm no WiBiz workflow triggers on "{isMga ? "mga_email" : "upline_name"} field changed".
        </strong>
      </p>

      <div className="flex flex-wrap gap-4 mb-4 items-end">
        <label className="flex items-center gap-2 text-xs text-gray-600">
          Fields
          <select
            value={fields}
            onChange={e => { setFields(e.target.value as "upline" | "mga"); setResult(null); }}
            className="border border-gray-200 rounded px-2 py-1 text-xs"
          >
            <option value="upline">Upline (upline_*)</option>
            <option value="mga">Agency (mga_*)</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
          <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} className="w-3.5 h-3.5" />
          Dry run (no writes)
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
          <input type="checkbox" checked={onlyBlank} onChange={e => setOnlyBlank(e.target.checked)} className="w-3.5 h-3.5" />
          Only blank fields
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-600">
          Limit
          <input
            type="number" min={1} max={200} value={limit}
            onChange={e => setLimit(Math.min(200, Math.max(1, parseInt(e.target.value) || 20)))}
            className="w-16 border border-gray-200 rounded px-2 py-1 text-xs font-mono"
          />
        </label>
        <button
          onClick={() => { setResult(null); bulkMutation.mutate({ dryRun, onlyBlank, limit, fields }); }}
          disabled={bulkMutation.isPending}
          className={`px-4 py-1.5 text-white text-xs font-semibold rounded-lg transition-colors ${actionColor}`}
        >
          {bulkMutation.isPending ? "Running…" : actionLabel}
        </button>
      </div>

      {result && (
        <>
          <div className="flex gap-4 text-xs mb-3 font-mono">
            <span className="text-gray-500">Total: <b>{result.summary.total}</b></span>
            <span className={result.summary.dryRun ? "text-gray-600" : "text-green-700"}>
              {result.summary.dryRun ? "Would write" : "Written"}: <b>{result.summary.toWrite}</b>
            </span>
            <span className="text-gray-400">Skipped: <b>{result.summary.skipped}</b></span>
            {result.summary.errors > 0 && <span className="text-red-600">Errors: <b>{result.summary.errors}</b></span>}
            {result.summary.dryRun && <span className="text-amber-600 font-bold">DRY RUN — nothing written</span>}
          </div>
          <div className="max-h-64 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-50 text-[11px] font-mono">
            {result.results.map((r: any, i: number) => (
              <div key={i} className={`px-3 py-2 flex gap-3 items-start ${r.action === "error" ? "bg-red-50" : r.action === "write" ? (r.dryRun ? "bg-blue-50/40" : "bg-green-50/40") : "bg-gray-50/60"}`}>
                <span className="text-gray-400 w-24 truncate flex-shrink-0">{r.agentId}</span>
                <span className="text-gray-300 w-28 truncate flex-shrink-0">{r.ghlContactId}</span>
                <span className="text-gray-400 flex-1 truncate">
                  {r.oldUplineName ? `"${r.oldUplineName}"` : <em>blank</em>} → <b>{r.newUplineName || "—"}</b>
                </span>
                <span className={`flex-shrink-0 font-bold ${
                  r.action === "write" ? (r.dryRun ? "text-blue-600" : "text-green-700")
                  : r.action === "error" ? "text-red-600"
                  : "text-gray-400"
                }`}>{r.action}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CrmSyncTab({ copyToClipboard, copiedCode }: { copyToClipboard: (text: string, code: string) => void; copiedCode: string | null }) {
  const eventsQuery = trpc.crm.recentEvents.useQuery();
  const healthQuery = trpc.crm.healthCheck.useQuery();
  const utils = trpc.useUtils();
  const [testing, setTesting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const testConnection = async () => {
    setTesting(true);
    try {
      await utils.crm.healthCheck.invalidate();
      await new Promise(r => setTimeout(r, 1000));
      toast.success("Connection test complete — check status cards below");
    } finally {
      setTesting(false);
    }
  };

  const sendTestWebhook = async () => {
    setSendingTest(true);
    try {
      const res = await fetch("/api/ghl/webhook", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "ContactCreate",
          contact: {
            id: `test-${Date.now()}`,
            firstName: "Test",
            lastName: "Webhook",
            email: `test-webhook-${Date.now()}@bc360test.internal`,
            tags: [],
            customFields: [],
          },
        }),
      });
      if (res.ok) {
        toast.success("Test webhook sent — a new event should appear in Recent Sync Events");
        await utils.crm.recentEvents.invalidate();
      } else {
        toast.error("Test webhook failed — check server logs");
      }
    } catch {
      toast.error("Test webhook request failed");
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Wibiz Dashboard Sync</h2>
          <p className="text-sm text-gray-400">Webhook endpoints, pipeline routing, and sync event log</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={sendTestWebhook} disabled={sendingTest}>
            {sendingTest ? <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Sending...</> : <><Zap className="w-3.5 h-3.5 mr-1.5" /> Send Test Event</>}
          </Button>
          <Button size="sm" variant="outline" onClick={testConnection} disabled={testing}>
            {testing ? <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Testing...</> : <><Wifi className="w-3.5 h-3.5 mr-1.5" /> Test Connection</>}
          </Button>
        </div>
      </div>
      <PipelineSyncSection utils={utils} />
      <Share360DisplayFixPanel />
      {/* BC360 inbound webhook */}
      <div className="bg-[#0D1E35] rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-2 h-2 rounded-full bg-green-400" />
          <span className="text-xs font-bold text-white/60 uppercase tracking-wider">BC360 Inbound Webhook</span>
        </div>
        <p className="text-xs text-white/40 mb-3">Wibiz Dashboard → BC360. Configure this URL in your Wibiz automation to push contact events into BC360.</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-white/6 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-[#84ab4a] truncate">
            {`${window.location.origin}/api/ghl/webhook`}
          </code>
          <button
            onClick={() => copyToClipboard(`${window.location.origin}/api/ghl/webhook`, "bc360-webhook")}
            className="flex-shrink-0 bg-[#84ab4a] text-white px-3 py-2 rounded-lg text-xs font-semibold hover:bg-[#6e9038] transition-colors"
          >
            {copiedCode === "bc360-webhook" ? "Copied!" : "Copy URL"}
          </button>
        </div>
      </div>
      {/* Scale360 outbound webhook */}
      <div className="bg-[#1a1a2e] border border-[#E8620A]/20 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-2 h-2 rounded-full bg-[#E8620A]" />
          <span className="text-xs font-bold text-[#E8620A]/80 uppercase tracking-wider">Scale360 Inbound Webhook</span>
        </div>
        <p className="text-xs text-white/40 mb-3">BC360 → Scale360 Partner Funnel pipeline. Scale360 leads are routed through this webhook.</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-white/6 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-[#E8620A] truncate">
            https://services.leadconnectorhq.com/hooks/4NIo95IzKUVayysfr0PX/webhook-trigger/rtyVGCkTkdKFWtp4RF1C
          </code>
          <button
            onClick={() => copyToClipboard("https://services.leadconnectorhq.com/hooks/4NIo95IzKUVayysfr0PX/webhook-trigger/rtyVGCkTkdKFWtp4RF1C", "scale360-webhook")}
            className="flex-shrink-0 bg-[#E8620A] text-white px-3 py-2 rounded-lg text-xs font-semibold hover:bg-[#c9530a] transition-colors"
          >
            {copiedCode === "scale360-webhook" ? "Copied!" : "Copy URL"}
          </button>
        </div>
        <p className="text-xs text-white/30 mt-2">Pipeline ID: mzgOuQhHmkDuixcpiSbj</p>
      </div>
      {/* Scale360 Setup Complete trigger */}
      <Scale360SetupCompletePanel />
      {/* Integration status cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Webhook Endpoint", status: "active", desc: "POST /api/webhook" },
          { label: "Lead Auto-Creation", status: "active", desc: "Contact events → BC360 leads" },
          { label: "Stage Sync", status: (healthQuery.data as any)?.ok ? "active" : "pending", desc: "BC360 → Wibiz pipeline" },
          { label: "Make.com Automation", status: "pending", desc: "Scale360 workflow trigger" },
        ].map(({ label, status, desc }) => (
          <div key={label} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-2 h-2 rounded-full ${status === "active" ? "bg-green-500" : "bg-yellow-400"}`} />
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">{status}</span>
            </div>
            <div className="font-semibold text-gray-900 text-sm">{label}</div>
            <div className="text-xs text-gray-400 mt-0.5">{desc}</div>
          </div>
        ))}
      </div>
      {/* Recent events */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">Recent Sync Events</span>
          <button onClick={() => utils.crm.recentEvents.invalidate()} className="text-xs text-[#84ab4a] hover:underline">Refresh</button>
        </div>
        {eventsQuery.data && eventsQuery.data.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Event Type</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Contact ID</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Rep Code</th>
                <th className="text-center px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Status</th>
                <th className="text-right px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Time</th>
              </tr>
            </thead>
            <tbody>
              {eventsQuery.data.map((e) => (
                <tr key={e.id} className="border-b border-gray-50">
                  <td className="px-5 py-3 font-mono text-xs text-gray-700">{e.eventType}</td>
                  <td className="px-5 py-3 font-mono text-xs text-gray-400">{e.ghlContactId ?? "—"}</td>
                  <td className="px-5 py-3 font-mono text-xs font-bold text-[#84ab4a]">{e.repCode ?? "—"}</td>
                  <td className="px-5 py-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${e.processed ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                      {e.processed ? "Processed" : "Pending"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right text-xs text-gray-400">{new Date(e.createdAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="py-16 text-center text-gray-400">
            <Zap className="w-10 h-10 mx-auto mb-3 opacity-20" />
            <p className="font-medium">No sync events yet</p>
            <p className="text-sm mt-1">Events will appear here once your Wibiz Dashboard starts sending webhooks</p>
          </div>
        )}
      </div>
      <div className="mt-4 text-center">
        <Link href="/ghl-guide">
          <button className="text-sm text-[#84ab4a] hover:underline font-semibold flex items-center gap-1.5 mx-auto">
            <BookOpen className="w-4 h-4" /> View Full Wibiz Setup Guide
          </button>
        </Link>
      </div>
    </div>
  );
}

// ─── Edit Rep Form ────────────────────────────────────────────────────────────
function EditRepForm({ rep, onSave, onCancel, isPending }: { rep: any; onSave: (data: any) => void; onCancel: () => void; isPending: boolean }) {
  const [form, setForm] = useState({
    firstName: rep.firstName ?? "",
    lastName: rep.lastName ?? "",
    email: rep.email ?? "",
    phone: rep.phone ?? "",
    vertical: rep.vertical ?? "",
    agentLevel: rep.agentLevel ?? "Associate",
    status: rep.status ?? "active",
    notes: rep.notes ?? "",
  });
  const initialAllowed = (() => { try { return rep.allowedVerticals ? JSON.parse(rep.allowedVerticals) : null; } catch { return null; } })();
  const [allowAll, setAllowAll] = useState(initialAllowed === null);
  const [selectedVerticals, setSelectedVerticals] = useState<string[]>(initialAllowed ?? ALL_VERTICALS);
  const toggleVertical = (v: string) => setSelectedVerticals(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ ...form, allowedVerticals: allowAll ? null : selectedVerticals });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">First Name</label>
          <Input value={form.firstName} onChange={(e) => setForm(f => ({ ...f, firstName: e.target.value }))} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Last Name</label>
          <Input value={form.lastName} onChange={(e) => setForm(f => ({ ...f, lastName: e.target.value }))} />
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
        <Input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} />
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Phone</label>
        <Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Agent Level</label>
          <Select value={form.agentLevel} onValueChange={(v) => setForm(f => ({ ...f, agentLevel: v }))}>
            <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Agency">Agency</SelectItem>
              <SelectItem value="Senior Associate">Senior Associate</SelectItem>
              <SelectItem value="Associate">Associate</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Status</label>
          <Select value={form.status} onValueChange={(v) => setForm(f => ({ ...f, status: v }))}>
            <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Primary Vertical</label>
        <Select value={form.vertical} onValueChange={(v) => setForm(f => ({ ...f, vertical: v }))}>
          <SelectTrigger className="text-sm"><SelectValue placeholder="Select vertical..." /></SelectTrigger>
          <SelectContent>
            {ALL_VERTICALS.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {/* Vertical Access Control */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Shield className="w-3.5 h-3.5 text-blue-600" />
            <label className="text-xs font-bold text-blue-800">Approved Verticals</label>
          </div>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={allowAll} onChange={(e) => setAllowAll(e.target.checked)} className="rounded" />
            <span className="text-xs text-blue-600">All verticals</span>
          </label>
        </div>
        {!allowAll && (
          <div className="grid grid-cols-2 gap-1.5">
            {ALL_VERTICALS.map(v => (
              <label key={v} className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={selectedVerticals.includes(v)} onChange={() => toggleVertical(v)} className="rounded" />
                <span className="text-xs text-gray-700">{v}</span>
              </label>
            ))}
          </div>
        )}
        {allowAll && <p className="text-xs text-blue-500">Rep can access all verticals</p>}
        {!allowAll && selectedVerticals.length === 0 && (
          <p className="text-[10px] text-orange-600 mt-1 font-semibold">⚠ No verticals selected — rep will have no access</p>
        )}
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
        <Input value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes..." />
      </div>
      <div className="flex gap-2 justify-end pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="bg-[#84ab4a] hover:bg-[#6e9038] text-white border-0" disabled={isPending}>
          <Save className="w-3.5 h-3.5 mr-1.5" />
          {isPending ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </form>
  );
}

// ─── Team Members Admin Tab ─────────────────────────────────────────────────
function TeamMembersAdminTab() {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", assignedVertical: "Scale360,Fundable360,Share360,Legacy360,Media360", role: "team_member" as "team_member" | "product_manager" });
  const [editingMember, setEditingMember] = useState<any | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [ghlSetupCard, setGhlSetupCard] = useState<any | null>(null); // newly created member to show GHL setup card
  const [ghlIdInputs, setGhlIdInputs] = useState<Record<number, string>>({}); // per-member GHL ID input state
  const [editingGhlIdFor, setEditingGhlIdFor] = useState<number | null>(null); // which member's Staff ID is being edited
  const [resetPwFor, setResetPwFor] = useState<number | null>(null); // which member's password reset is open
  const [pwInputs, setPwInputs] = useState<Record<number, string>>({}); // per-member new password input
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const utils = trpc.useUtils();

  const membersQuery = trpc.teamMembers.list.useQuery();
  const createMember = trpc.teamMembers.create.useMutation({
    onSuccess: (data) => {
      toast.success("Team member created");
      utils.teamMembers.list.invalidate();
      setShowForm(false);
      setGhlSetupCard(data); // show GHL setup card with credentials
      setForm({ name: "", email: "", password: "", assignedVertical: "Scale360", role: "team_member" });
    },
    onError: (e) => toast.error(e.message),
  });
  const updateMember = trpc.teamMembers.update.useMutation({
    onSuccess: () => { toast.success("Team member updated"); utils.teamMembers.list.invalidate(); setEditingMember(null); },
    onError: (e) => toast.error(e.message),
  });
  const setGhlUserId = trpc.teamMembers.setGhlUserId.useMutation({
    onSuccess: () => { toast.success("Wibiz User ID saved"); utils.teamMembers.list.invalidate(); setGhlIdInputs({}); setEditingGhlIdFor(null); },
    onError: (e) => toast.error(e.message),
  });
  const resetPassword = trpc.teamMembers.update.useMutation({
    onSuccess: () => { toast.success("Password updated"); utils.teamMembers.list.invalidate(); setResetPwFor(null); setPwInputs({}); },
    onError: (e) => toast.error(e.message),
  });
  const deleteMember = trpc.teamMembers.delete.useMutation({
    onSuccess: () => { toast.success("Team member removed"); utils.teamMembers.list.invalidate(); setDeleteConfirm(null); },
    onError: (e) => toast.error(e.message),
  });
  const toggleActive = trpc.teamMembers.update.useMutation({
    onSuccess: () => { utils.teamMembers.list.invalidate(); },
  });

  const members = membersQuery.data ?? [];

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Team Members</h2>
          <p className="text-sm text-gray-500 mt-0.5">BC360 staff with scoped vertical access. Login at <span className="font-mono text-indigo-600">/team</span></p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="https://app.wibiz.ai/settings/team"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors"
          >
            <Globe className="w-3.5 h-3.5" /> Wibiz Staff
          </a>
          <Button onClick={() => setShowForm(!showForm)} className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2">
            <Plus className="w-4 h-4" /> Add Team Member
          </Button>
        </div>
      </div>

      {/* GHL Setup Card — shown after creating a new member */}
      {ghlSetupCard && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 shadow-sm">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-2 text-amber-800 font-bold text-sm">
                <AlertCircle className="w-4 h-4" />
                Wibiz Staff Setup Required
              </div>
              <p className="text-xs text-amber-700 mt-0.5">Copy these details and manually add this person in Wibiz → Settings → My Staff → Add User. Then paste their Wibiz User ID below.</p>
            </div>
            <button onClick={() => setGhlSetupCard(null)} className="text-amber-500 hover:text-amber-700"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            {[
              { label: "Full Name", value: ghlSetupCard.name, field: "name" },
              { label: "Email", value: ghlSetupCard.email, field: "email" },
              { label: "Temp Password", value: ghlSetupCard.plainPassword, field: "password" },
              { label: "Assigned Vertical(s)", value: ghlSetupCard.assignedVertical, field: "vertical" },
            ].map(({ label, value, field }) => (
              <div key={field} className="bg-white rounded-lg border border-amber-200 p-3">
                <div className="text-[10px] font-bold text-amber-600 uppercase tracking-wider mb-1">{label}</div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-mono text-gray-800 truncate">{value}</span>
                  <button
                    onClick={() => copyToClipboard(value, field)}
                    className="flex-shrink-0 text-amber-500 hover:text-amber-700"
                  >
                    {copiedField === field ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-lg border border-amber-200 p-3">
            <div className="text-[10px] font-bold text-amber-600 uppercase tracking-wider mb-2">Wibiz Setup Steps</div>
            <ol className="text-xs text-gray-700 space-y-1">
              <li className="flex gap-2"><span className="font-bold text-amber-600">1.</span> Go to Wibiz → Settings → My Staff → Add User</li>
              <li className="flex gap-2"><span className="font-bold text-amber-600">2.</span> Enter the name, email, and password above. Set role to <strong>User</strong>, type <strong>Account</strong>.</li>
              <li className="flex gap-2"><span className="font-bold text-amber-600">3.</span> Assign to location: <strong>BizConnect360</strong></li>
              <li className="flex gap-2"><span className="font-bold text-amber-600">4.</span> Go to Calendars → find the <strong>{ghlSetupCard.assignedVertical}</strong> calendar → Edit → Staff → add this user</li>
              <li className="flex gap-2"><span className="font-bold text-amber-600">5.</span> Copy the Wibiz User ID from their profile URL and paste it below</li>
            </ol>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Input
              placeholder="Paste Wibiz User ID (e.g. kqrUv69IWJSYFgN2KKxB)"
              value={ghlIdInputs[ghlSetupCard.id] ?? ""}
              onChange={e => setGhlIdInputs(prev => ({ ...prev, [ghlSetupCard.id]: e.target.value }))}
              className="flex-1 text-sm font-mono"
            />
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap"
              disabled={!ghlIdInputs[ghlSetupCard.id]?.trim() || setGhlUserId.isPending}
              onClick={() => {
                const ghlId = ghlIdInputs[ghlSetupCard.id]?.trim();
                if (ghlId) {
                  setGhlUserId.mutate({ id: ghlSetupCard.id, ghlUserId: ghlId }, {
                    onSuccess: () => setGhlSetupCard(null),
                  });
                }
              }}
            >
              {setGhlUserId.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save Wibiz ID
            </Button>
            <Button size="sm" variant="outline" onClick={() => setGhlSetupCard(null)}>Skip for now</Button>
          </div>
        </div>
      )}

      {/* Add form */}
      {showForm && (
        <div className="bg-white rounded-2xl border border-indigo-100 p-5 shadow-sm">
          <div className="text-sm font-bold text-gray-700 mb-4">New Team Member</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Full Name</label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Jane Smith" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Email</label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jane@bizconnect360.com" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Temporary Password</label>
              <Input value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Min 6 characters" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Role</label>
              <select
                value={form.role}
                onChange={e => setForm(f => ({ ...f, role: e.target.value as any }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-400"
              >
                <option value="team_member">Team Member</option>
                <option value="product_manager">Product Manager</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-500 mb-2">Assigned Verticals</label>
              <div className="flex flex-wrap gap-2">
                {["Scale360", "Fundable360", "Share360", "Legacy360", "Media360"].map(v => {
                  const selected = form.assignedVertical.split(",").map(s => s.trim()).filter(Boolean);
                  const isChecked = selected.includes(v);
                  return (
                    <label key={v} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold cursor-pointer transition-all ${isChecked ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-600 border-gray-200 hover:border-indigo-300"}`}>
                      <input type="checkbox" className="sr-only" checked={isChecked} onChange={() => {
                        const cur = form.assignedVertical.split(",").map(s => s.trim()).filter(Boolean);
                        const next = isChecked ? cur.filter(x => x !== v) : [...cur, v];
                        setForm(f => ({ ...f, assignedVertical: next.join(",") }));
                      }} />
                      {v}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={() => createMember.mutate(form)} disabled={createMember.isPending || !form.name || !form.email || !form.password} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {createMember.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
            </Button>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Members table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {membersQuery.isLoading ? (
          <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...</div>
        ) : members.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <UserCheck className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <div className="text-sm">No team members yet. Add your first team member above.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Name</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Email</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Vertical</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Wibiz Status</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Status</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {members.map((m: any) => (
                <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold ${m.role === "product_manager" ? "bg-purple-100 text-purple-700" : "bg-indigo-100 text-indigo-600"}`}>{m.name?.[0]?.toUpperCase() ?? "?"}</div>
                      {editingMember?.id === m.id ? (
                        <Input
                          value={editingMember.name ?? ""}
                          onChange={e => setEditingMember((prev: any) => ({ ...prev, name: e.target.value }))}
                          placeholder="Full name"
                          className="h-7 text-sm w-40"
                          autoFocus
                        />
                      ) : (
                        <div>
                          <span className="text-sm font-semibold text-gray-800">{m.name}</span>
                          {m.role === "product_manager" && (
                            <div className="text-[9px] font-bold text-purple-600 uppercase tracking-wider flex items-center gap-0.5 mt-0.5">
                              <Briefcase className="w-2.5 h-2.5" /> Product Manager
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3"><div className="flex items-center gap-1 text-xs text-gray-500"><Mail className="w-3 h-3" />{m.email}</div></td>
                  <td className="px-4 py-3">
                    {editingMember?.id === m.id ? (
                      <div className="flex flex-wrap gap-1">
                        {["Scale360", "Fundable360", "Share360", "Legacy360", "Media360"].map(v => {
                          const selected = (editingMember.assignedVertical ?? "").split(",").map((s: string) => s.trim()).filter(Boolean);
                          const isChecked = selected.includes(v);
                          return (
                            <label key={v} className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold cursor-pointer transition-all ${isChecked ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-500 border-gray-200"}`}>
                              <input type="checkbox" className="sr-only" checked={isChecked} onChange={() => {
                                const cur = (editingMember.assignedVertical ?? "").split(",").map((s: string) => s.trim()).filter(Boolean);
                                const next = isChecked ? cur.filter((x: string) => x !== v) : [...cur, v];
                                setEditingMember((prev: any) => ({ ...prev, assignedVertical: next.join(",") }));
                              }} />
                              {v}
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {(m.assignedVertical ?? "").split(",").map((s: string) => s.trim()).filter(Boolean).map((v: string) => (
                          <span key={v} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">{v}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {m.ghlUserId && editingGhlIdFor !== m.id ? (
                      <div className="flex items-center gap-1">
                        <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-700">
                          <CheckCircle2 className="w-3 h-3" /> Synced
                        </span>
                        <button onClick={() => copyToClipboard(m.ghlUserId, `ghl-${m.id}`)} className="text-gray-400 hover:text-gray-600" title={m.ghlUserId}>
                          {copiedField === `ghl-${m.id}` ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                        </button>
                        <button onClick={() => { setEditingGhlIdFor(m.id); setGhlIdInputs(prev => ({ ...prev, [m.id]: m.ghlUserId })); }} className="text-gray-400 hover:text-indigo-600" title="Edit Staff ID">
                          <Pencil className="w-3 h-3" />
                        </button>
                      </div>
                    ) : editingGhlIdFor === m.id ? (
                      <div className="flex items-center gap-1">
                        <Input
                          placeholder="Wibiz User ID"
                          value={ghlIdInputs[m.id] ?? ""}
                          onChange={e => setGhlIdInputs(prev => ({ ...prev, [m.id]: e.target.value }))}
                          className="h-6 text-[10px] font-mono w-36 px-2"
                          autoFocus
                        />
                        <button onClick={() => { const id = ghlIdInputs[m.id]?.trim(); if (id) setGhlUserId.mutate({ id: m.id, ghlUserId: id }); }} disabled={!ghlIdInputs[m.id]?.trim() || setGhlUserId.isPending} className="text-indigo-600 hover:text-indigo-800 disabled:opacity-40">
                          <Save className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setEditingGhlIdFor(null)} className="text-gray-400 hover:text-gray-600"><X className="w-3 h-3" /></button>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                          <AlertCircle className="w-3 h-3" /> Pending Wibiz Setup
                        </span>
                        <div className="flex items-center gap-1">
                          <Input
                            placeholder="Paste Wibiz User ID"
                            value={ghlIdInputs[m.id] ?? ""}
                            onChange={e => setGhlIdInputs(prev => ({ ...prev, [m.id]: e.target.value }))}
                            className="h-6 text-[10px] font-mono w-36 px-2"
                          />
                          <button onClick={() => { const id = ghlIdInputs[m.id]?.trim(); if (id) setGhlUserId.mutate({ id: m.id, ghlUserId: id }); }} disabled={!ghlIdInputs[m.id]?.trim() || setGhlUserId.isPending} className="text-indigo-600 hover:text-indigo-800 disabled:opacity-40">
                            <Save className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleActive.mutate({ id: m.id, status: m.status === "active" ? "inactive" : "active" })}
                      className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full transition-colors ${
                        m.status === "active" ? "bg-green-50 text-green-700 hover:bg-green-100" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                      }`}
                    >
                      {m.status === "active" ? <><Unlock className="w-3 h-3" /> Active</> : <><Lock className="w-3 h-3" /> Inactive</>}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {editingMember?.id === m.id ? (
                        <>
                          <Button
                            size="sm"
                            className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                            onClick={() => {
                              const trimmedName = (editingMember.name ?? "").trim();
                              if (!trimmedName) { toast.error("Name cannot be blank"); return; }
                              updateMember.mutate({ id: m.id, name: trimmedName, assignedVertical: editingMember.assignedVertical });
                            }}
                            disabled={updateMember.isPending || !(editingMember.name ?? "").trim()}
                          >
                            <Save className="w-3 h-3 mr-1" /> Save
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditingMember(null)}>Cancel</Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditingMember(m)}><Pencil className="w-3 h-3" /></Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50" title="Reset password" onClick={() => setResetPwFor(resetPwFor === m.id ? null : m.id)}><KeyRound className="w-3 h-3" /></Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteConfirm(m.id)}><Trash2 className="w-3 h-3" /></Button>
                        </>
                      )}
                      {resetPwFor === m.id && (
                        <div className="flex items-center gap-1 mt-1.5">
                          <Input
                            type="password"
                            placeholder="New password"
                            value={pwInputs[m.id] ?? ""}
                            onChange={e => setPwInputs(prev => ({ ...prev, [m.id]: e.target.value }))}
                            className="h-6 text-[10px] w-28 px-2"
                            autoFocus
                          />
                          <button
                            onClick={() => { const pw = pwInputs[m.id]?.trim(); if (pw && pw.length >= 6) resetPassword.mutate({ id: m.id, password: pw }); else toast.error("Min 6 characters"); }}
                            disabled={!pwInputs[m.id]?.trim() || resetPassword.isPending}
                            className="text-amber-600 hover:text-amber-800 disabled:opacity-40"
                          >
                            {resetPassword.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                          </button>
                          <button onClick={() => { setResetPwFor(null); setPwInputs({}); }} className="text-gray-400 hover:text-gray-600"><X className="w-3 h-3" /></button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Delete confirm */}
      <Dialog open={deleteConfirm !== null} onOpenChange={open => { if (!open) setDeleteConfirm(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-red-600 flex items-center gap-2"><Trash2 className="w-5 h-5" /> Remove Team Member</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600 mt-2">This will revoke their access to the team portal. This action cannot be undone.</p>
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => deleteConfirm && deleteMember.mutate({ id: deleteConfirm })} disabled={deleteMember.isPending}>Remove</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
// ─── Share360 Doc Links — per-rep, fetches fresh R2 signed URLs on demand ────────
function Share360DocLinks({ repId, hasDoc }: { repId: number; hasDoc: boolean }) {
  const [fetch, setFetch] = useState(false);
  const query = trpc.share360.adminGetDocUrls.useQuery({ repId }, { enabled: fetch, staleTime: 300_000 });

  if (!hasDoc) return null;

  if (!fetch) {
    return (
      <div className="mb-3">
        <button
          className="text-xs text-[#1A6FD4] underline flex items-center gap-1 hover:text-blue-800"
          onClick={() => setFetch(true)}
        >
          <FileText className="w-3 h-3" /> Load Doc Links
        </button>
      </div>
    );
  }

  if (query.isLoading) {
    return (
      <div className="mb-3 flex items-center gap-1 text-xs text-gray-400">
        <Loader2 className="w-3 h-3 animate-spin" /> Fetching secure links…
      </div>
    );
  }

  const { enrollmentPacket, w9 } = query.data ?? {};

  if (!enrollmentPacket && !w9) {
    return (
      <div className="mb-3 text-xs text-amber-600 flex items-center gap-1">
        <AlertCircle className="w-3 h-3" /> Docs not yet available — PDF may still be processing. Try again in a minute.
      </div>
    );
  }

  return (
    <div className="mb-3 flex flex-wrap gap-3">
      {enrollmentPacket && (
        <a href={enrollmentPacket} target="_blank" rel="noopener noreferrer" className="text-xs text-[#1A6FD4] underline flex items-center gap-1">
          <FileText className="w-3 h-3" /> Enrollment Packet (signed)
        </a>
      )}
      {w9 && (
        <a href={w9} target="_blank" rel="noopener noreferrer" className="text-xs text-[#1A6FD4] underline flex items-center gap-1">
          <FileText className="w-3 h-3" /> W-9 (signed)
        </a>
      )}
      <button
        className="text-xs text-gray-400 underline flex items-center gap-1 hover:text-gray-600"
        onClick={() => query.refetch()}
        disabled={query.isFetching}
      >
        <RefreshCw className="w-3 h-3" /> Refresh Links
      </button>
    </div>
  );
}

// ─── Per-row lazy R2 links for DocuSeal submissions (main PDF + all attachments) ─
function SubmissionDocLink({ submissionId, status }: { submissionId: number; status: string }) {
  const [fetch, setFetch] = useState(false);
  const query = trpc.share360.getSubmissionAllR2Docs.useQuery(
    { submissionId },
    { enabled: fetch, staleTime: 300_000 }
  );

  if (status !== "completed") return <span className="text-gray-300 text-xs">—</span>;

  if (!fetch) {
    return (
      <button
        onClick={() => setFetch(true)}
        className="text-[#1A6FD4] text-xs underline flex items-center gap-0.5 hover:text-blue-800 whitespace-nowrap"
      >
        <Download className="w-3 h-3" /> Load PDFs
      </button>
    );
  }

  if (query.isLoading) {
    return <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />;
  }

  const docs = query.data?.docs ?? [];
  if (docs.length) {
    return (
      <div className="flex flex-col gap-0.5">
        {docs.map((doc, i) => (
          <a key={i} href={doc.url} target="_blank" rel="noopener noreferrer"
            className="text-[#84ab4a] flex items-center gap-0.5 hover:underline whitespace-nowrap font-semibold text-xs">
            <Download className="w-3 h-3" /> {doc.name}
          </a>
        ))}
      </div>
    );
  }

  return (
    <button onClick={() => query.refetch()} className="text-red-400 text-xs underline whitespace-nowrap">
      Retry
    </button>
  );
}

// ─── DocuSeal All Submissions Panel ──────────────────────────────────────────────
function DocusealSubmissionsPanel() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [savingId, setSavingId] = useState<number | null>(null);
  const [savedIds, setSavedIds] = useState<Set<number>>(new Set());
  // Accumulated pages — adminListSubmissions is cursor-paginated (nextAfter)
  // instead of a single fixed-size snapshot, so "Load More" can reach
  // submissions older than the default first page.
  const [loadedSubmissions, setLoadedSubmissions] = useState<any[]>([]);
  const [nextAfter, setNextAfter] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const utils = trpc.useUtils();
  const query = trpc.share360.adminListSubmissions.useQuery({ limit: 200 }, {
    enabled: open,
    staleTime: 60_000,
  });
  // Search runs against the complete synchronized database catalogue rather
  // than filtering pages already loaded in this browser.
  const debouncedSearch = useDebouncedValue(search, 400);
  const searchTerm = debouncedSearch.trim();
  const isSearching = searchTerm.length >= 2;
  const searchQuery = trpc.share360.adminSearchSubmissions.useQuery(
    { query: searchTerm, limit: 100 },
    { enabled: open && isSearching, staleTime: 60_000, placeholderData: prev => prev },
  );
  const syncIndexMutation = trpc.share360.adminSyncSubmissionIndex.useMutation({
    onSuccess: data => {
      toast.success(`Document index updated: ${data.indexedCount.toLocaleString()} submissions searchable.`);
      query.refetch();
      if (isSearching) searchQuery.refetch();
    },
    onError: e => toast.error(e.message ?? "Document synchronization failed."),
  });
  useEffect(() => {
    if (query.data) {
      setLoadedSubmissions(query.data.submissions);
      setNextAfter(query.data.nextAfter);
    }
  }, [query.data]);
  const handleLoadMore = async () => {
    if (nextAfter == null || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await utils.share360.adminListSubmissions.fetch({ limit: 200, after: nextAfter });
      setLoadedSubmissions(prev => [...prev, ...page.submissions]);
      setNextAfter(page.nextAfter);
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to load more submissions.");
    } finally {
      setLoadingMore(false);
    }
  };
  const saveDocMutation = trpc.share360.saveSubmissionAsDocument.useMutation({
    onSuccess: (res, vars) => {
      setSavedIds(prev => new Set(prev).add(vars.submissionId));
      setSavingId(null);
      if (res.alreadySaved) toast.success("Already saved to documents.");
      else { toast.success("Saved to client documents — visible in Team Portal."); utils.clientPortal.adminListDocuments.invalidate(); }
    },
    onError: (e, vars) => { setSavingId(null); toast.error(e.message ?? "Failed to save document"); },
  });

  // When searching, the server result IS the list - it already covers history
  // the browser never loaded, so intersecting it with loadedSubmissions would
  // reintroduce exactly the bug this replaced.
  const submissions = isSearching ? (searchQuery.data?.submissions ?? []) : loadedSubmissions;

  const STATUS_STYLES: Record<string, string> = {
    completed:  "bg-green-50 text-green-700 border border-green-100",
    pending:    "bg-amber-50 text-amber-700 border border-amber-100",
    expired:    "bg-red-50 text-red-600 border border-red-100",
    declined:   "bg-gray-100 text-gray-600 border border-gray-200",
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl mb-5 overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition-colors"
        onClick={() => setOpen(v => !v)}
      >
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#1A6FD4]" />
          <span className="text-sm font-bold text-gray-800">All DocuSeal Submissions</span>
          {loadedSubmissions.length > 0 && (
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
              {loadedSubmissions.length}{nextAfter != null ? "+" : ""}
            </span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-gray-100 px-5 py-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <Input
                placeholder="Search all history by email, name, template, or submission ID…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="h-8 text-sm pr-8"
              />
              {isSearching && searchQuery.isFetching && (
                <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-gray-400" />
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={() => (isSearching ? searchQuery.refetch() : query.refetch())}
              disabled={isSearching ? searchQuery.isFetching : query.isFetching}
            >
              {(isSearching ? searchQuery.isFetching : query.isFetching)
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <RefreshCw className="w-3.5 h-3.5" />}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs whitespace-nowrap"
              onClick={() => syncIndexMutation.mutate()}
              disabled={syncIndexMutation.isPending}
              title="Synchronize every document record into the searchable index"
            >
              {syncIndexMutation.isPending
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Syncing all…</>
                : <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Sync all</>}
            </Button>
          </div>

          {search.trim().length === 1 && (
            <p className="text-[10px] text-amber-600 mb-2">Enter at least 2 characters to search.</p>
          )}

          {(isSearching ? searchQuery.isLoading : query.isLoading) ? (
            <div className="py-8 text-center text-gray-400 text-sm flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              {isSearching ? "Searching all submission history…" : "Loading submissions…"}
            </div>
          ) : submissions.length === 0 ? (
            <div className="py-8 text-center text-gray-400 text-sm">
              {isSearching ? (
                <>
                  <div>No submissions match "{searchTerm}".</div>
                  <div className="text-[10px] mt-1">
                    Searched all {(searchQuery.data?.indexedCount ?? 0).toLocaleString()} synchronized submissions.
                  </div>
                </>
              ) : "No submissions found."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] font-bold uppercase text-gray-400 border-b border-gray-100">
                    <th className="text-left pb-2 pr-3">ID</th>
                    <th className="text-left pb-2 pr-3">Template</th>
                    <th className="text-left pb-2 pr-3">Submitter</th>
                    <th className="text-left pb-2 pr-3">Status</th>
                    <th className="text-left pb-2 pr-3">Created</th>
                    <th className="text-left pb-2 pr-3">Completed</th>
                    <th className="text-left pb-2 pr-3">Doc</th>
                    <th className="text-left pb-2">Save</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {submissions.map((s: any) => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="py-2 pr-3 font-mono text-gray-500">{s.id}</td>
                      <td className="py-2 pr-3 text-gray-700 max-w-[140px] truncate">{s.templateName ?? `#${s.templateId}`}</td>
                      <td className="py-2 pr-3 max-w-[180px]">
                        {s.submitterName && <div className="text-gray-800 truncate">{s.submitterName}</div>}
                        <div className="text-gray-400 truncate">{s.submitterEmail ?? "—"}</div>
                      </td>
                      <td className="py-2 pr-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold capitalize ${STATUS_STYLES[s.status] ?? "bg-gray-100 text-gray-500"}`}>
                          {s.status}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-gray-500 whitespace-nowrap">
                        {s.createdAt ? new Date(s.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }) : "—"}
                      </td>
                      <td className="py-2 pr-3 text-gray-500 whitespace-nowrap">
                        {s.completedAt ? new Date(s.completedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }) : "—"}
                      </td>
                      <td className="py-2 pr-3">
                        <SubmissionDocLink submissionId={s.id} status={s.status} />
                      </td>
                      <td className="py-2">
                        {s.status === "completed" && s.submitterEmail ? (
                          savedIds.has(s.id) ? (
                            <span className="text-[10px] font-semibold text-green-600 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Saved
                            </span>
                          ) : (
                            <button
                              disabled={savingId === s.id}
                              onClick={() => {
                                setSavingId(s.id);
                                saveDocMutation.mutate({
                                  submissionId: s.id,
                                  submitterEmail: s.submitterEmail!,
                                  documentUrl: s.documentUrl ?? undefined,
                                  templateName: s.templateName ?? undefined,
                                });
                              }}
                              className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-lg bg-[#84ab4a]/10 text-[#84ab4a] hover:bg-[#84ab4a]/20 disabled:opacity-50 transition-colors whitespace-nowrap"
                            >
                              {savingId === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                              {savingId === s.id ? "Saving…" : "Save to Docs"}
                            </button>
                          )
                        ) : (
                          <span className="text-[10px] text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-between mt-3">
                <p className="text-[10px] text-gray-400">
                  {isSearching ? (
                    <>
                      Showing {submissions.length.toLocaleString()} of {(searchQuery.data?.total ?? submissions.length).toLocaleString()} matches
                      {searchQuery.data?.lastSyncedAt
                        ? ` across the index updated ${new Date(searchQuery.data.lastSyncedAt).toLocaleString()}.`
                        : " across the synchronized document index."}
                    </>
                  ) : (
                    <>Showing the most recent submissions. Search covers full history, no need to load first.</>
                  )}
                </p>
                {/* Load More pages the browse list only. While searching it
                    would be meaningless: the server already scanned past it. */}
                {!isSearching && nextAfter != null && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs flex-shrink-0"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                    {loadingMore ? "Loading…" : "Load More"}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Share360 Enrollment Admin Tab ────────────────────────────────────────────────
function Share360EnrollmentAdminTab() {
  const utils = trpc.useUtils();
  const pendingQuery = trpc.share360.getPendingApprovals.useQuery(undefined, { refetchInterval: 60_000 });
  const confirmPaymentMutation = trpc.share360.confirmPayment.useMutation({
    onSuccess: () => { utils.share360.getPendingApprovals.invalidate(); toast.success("Payment confirmed!"); },
    onError: (e: any) => toast.error(e.message),
  });
  const confirmBgCheckMutation = trpc.share360.confirmBackgroundCheck.useMutation({
    onSuccess: () => { utils.share360.getPendingApprovals.invalidate(); toast.success("Background check verified!"); },
    onError: (e: any) => toast.error(e.message),
  });
  const approveMutation = trpc.share360.adminApprove.useMutation({
    onSuccess: () => { utils.share360.getPendingApprovals.invalidate(); toast.success("Enrollment approved!"); },
    onError: (e: any) => toast.error(e.message),
  });
  const rejectMutation = trpc.share360.adminReject.useMutation({
    onSuccess: () => { utils.share360.getPendingApprovals.invalidate(); toast.success("Enrollment rejected."); },
    onError: (e: any) => toast.error(e.message),
  });
  const suspendMutation = trpc.share360.adminSuspend.useMutation({
    onSuccess: () => { utils.share360.getPendingApprovals.invalidate(); toast.success("Access suspended."); },
    onError: (e: any) => toast.error(e.message),
  });
  const restoreMutation = trpc.share360.adminRestore.useMutation({
    onSuccess: () => { utils.share360.getPendingApprovals.invalidate(); toast.success("Access restored!"); },
    onError: (e: any) => toast.error(e.message),
  });
  const merchantOverrideMutation = trpc.share360.adminSetMerchantOverride.useMutation({
    onSuccess: () => { utils.share360.getPendingApprovals.invalidate(); toast.success("Merchant count override saved."); },
    onError: (e: any) => toast.error(e.message),
  });
  const [merchantOverrides, setMerchantOverrides] = useState<Record<number, string>>({});
  const markEnrolledMutation = trpc.share360.adminMarkEnrolled.useMutation({
    onSuccess: () => { utils.share360.getPendingApprovals.invalidate(); allRepsQuery.refetch(); toast.success("Associate marked as enrolled/approved."); },
    onError: (e: any) => toast.error(e.message),
  });
  const allRepsQuery = trpc.rep.list.useQuery(undefined, { staleTime: 60_000 });
  const [legacySearch, setLegacySearch] = useState("");
  const [legacyRepId, setLegacyRepId] = useState<number | null>(null);
  const [backfillResult, setBackfillResult] = useState<{ provisioned: number; skipped: number; failed: number; total: number } | null>(null);
  const backfillMutation = trpc.share360.adminBackfillStripeSubscriptions.useMutation({
    onSuccess: (data: { provisioned: number; skipped: number; failed: number; total: number }) => {
      setBackfillResult(data);
      if (data.provisioned > 0) {
        toast.success(`Provisioned ${data.provisioned} subscription${data.provisioned !== 1 ? "s" : ""}`);
      } else {
        toast.success("All approved agents already have Stripe subscriptions");
      }
    },
    onError: (e: any) => toast.error(e.message),
  });

  const enrolledRepIds = new Set((pendingQuery.data ?? []).map((r: any) => r.id));
  const legacyMatches = legacySearch.trim().length >= 2
    ? (allRepsQuery.data ?? []).filter((r: any) =>
        !enrolledRepIds.has(r.id) &&
        (`${r.firstName} ${r.lastName} ${r.repCode}`.toLowerCase().includes(legacySearch.toLowerCase()))
      ).slice(0, 8)
    : [];

  const STATUS_LABELS: Record<string, { label: string; color: string; step: number }> = {
    not_started:       { label: "Not Started",        color: "#9CA3AF", step: 0 },
    docs_submitted:    { label: "Docs Submitted",     color: "#8B5CF6", step: 1 },
    payment_pending:   { label: "Payment Pending",    color: "#F59E0B", step: 2 },
    payment_confirmed: { label: "Payment Confirmed",  color: "#3B82F6", step: 2 },
    background_check:  { label: "Background Check",   color: "#06B6D4", step: 3 },
    certified:         { label: "Certified",          color: "#10B981", step: 4 },
    pending_approval:  { label: "Pending Approval",   color: "#F59E0B", step: 4 },
    approved:          { label: "Approved",           color: "#22C55E", step: 5 },
    rejected:          { label: "Rejected",           color: "#EF4444", step: 5 },
    suspended:         { label: "Suspended",          color: "#6B7280", step: 0 },
  };

  // Application pipeline steps (new order: Docs → Payment → Background Check → Certified → Approved)
  const STEPS = ["Docs", "Payment", "Background Check", "Certified", "Approved"];

  const reps = pendingQuery.data ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Share360 RA Enrollment</h2>
          <p className="text-sm text-gray-400 mt-0.5">
            Review and action all active RA applications. Steps: Docs → Payment → Background Check → Certification → Final Approval.
          </p>
        </div>
        <div className="text-xs text-gray-400 bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5">
          Payment webhook: <span className="font-mono text-blue-600">/api/share360/payment-webhook</span>
        </div>
      </div>
      {/* ── All DocuSeal Submissions (collapsible) ── */}
      <DocusealSubmissionsPanel />

      {/* ── Stripe Subscription Backfill ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-bold text-gray-800">Stripe Subscription Backfill</span>
              <span className="text-[10px] bg-blue-100 text-blue-700 font-bold px-2 py-0.5 rounded-full uppercase">Admin only</span>
            </div>
            <p className="text-xs text-gray-500">
              Provisions a $24.95/month Stripe subscription for every approved Share360 agent who is missing one. Safe to run multiple times — agents who already have a subscription are skipped.
            </p>
            {backfillResult && (
              <p className="text-xs mt-2 text-gray-700">
                Last run: <span className="font-semibold text-green-700">{backfillResult.provisioned} provisioned</span>{" · "}
                <span className="text-gray-500">{backfillResult.skipped} already had one</span>
                {backfillResult.failed > 0 && <span className="text-red-600"> · {backfillResult.failed} failed</span>}
                {" · "}{backfillResult.total} total approved
              </p>
            )}
          </div>
          <button
            onClick={() => backfillMutation.mutate()}
            disabled={backfillMutation.isPending}
            className="flex-shrink-0 px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
          >
            {backfillMutation.isPending ? "Running…" : "Run Backfill"}
          </button>
        </div>
      </div>

      {/* ── Legacy / Manual Enrollment Override ── */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Shield className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-bold text-amber-800">Manual Enrollment Override</span>
          <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">Legacy / Admin-only</span>
        </div>
        <p className="text-xs text-amber-700 mb-3">For associates who completed Share360 enrollment before this system existed. Marks them as fully approved, granting immediate access.</p>
        <div className="flex gap-2 relative">
          <div className="flex-1 relative">
            <Input
              placeholder="Search by name or rep code..."
              value={legacySearch}
              onChange={e => { setLegacySearch(e.target.value); setLegacyRepId(null); }}
              className="h-8 text-sm bg-white"
            />
            {legacyMatches.length > 0 && (
              <div className="absolute top-full left-0 right-0 z-10 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 overflow-hidden">
                {legacyMatches.map((r: any) => (
                  <button
                    key={r.id}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center justify-between"
                    onClick={() => { setLegacyRepId(r.id); setLegacySearch(`${r.firstName} ${r.lastName} (${r.repCode})`); }}
                  >
                    <span>{r.firstName} {r.lastName}</span>
                    <span className="text-xs text-gray-400">{r.repCode} · {r.share360EnrollmentStatus ?? "not started"}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <Button
            size="sm"
            className="bg-amber-600 hover:bg-amber-700 text-white border-0 h-8"
            disabled={!legacyRepId || markEnrolledMutation.isPending}
            onClick={() => { if (legacyRepId) markEnrolledMutation.mutate({ repId: legacyRepId }); }}
          >
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark Enrolled
          </Button>
        </div>
      </div>

      {pendingQuery.isLoading ? (
        <div className="py-12 text-center text-gray-400">Loading...</div>
      ) : reps.length === 0 ? (
        <div className="py-12 text-center">
          <Globe className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-400 text-sm">No active Share360 enrollment applications.</p>
          <p className="text-xs text-gray-300 mt-1">Applications appear here once an agent initiates enrollment from their dashboard.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reps.map((rep: any) => {
            const statusInfo = STATUS_LABELS[rep.share360EnrollmentStatus] ?? { label: rep.share360EnrollmentStatus, color: "#9CA3AF", step: 0 };
            const stepIdx = statusInfo.step;
            return (
              <div key={rep.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-semibold text-gray-900">{rep.firstName} {rep.lastName}</div>
                    <div className="text-xs text-gray-500">{rep.repCode} · {rep.agentLevel} · {rep.email}</div>
                  </div>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: `${statusInfo.color}18`, color: statusInfo.color }}>
                    {statusInfo.label}
                  </span>
                </div>

                {/* Progress bar */}
                <div className="flex items-center gap-1 mb-4">
                  {STEPS.map((step, i) => (
                    <div key={step} className="flex items-center gap-1 flex-1">
                      <div className="flex-1 h-1.5 rounded-full" style={{ background: i < stepIdx ? "#1A6FD4" : i === stepIdx - 1 ? "#1A6FD4" : "#E5E7EB" }} />
                      {i === STEPS.length - 1 && null}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mb-4 text-[10px] font-semibold">
                  {STEPS.map((step, i) => (
                    <div key={step} className="flex-1 text-center" style={{ color: i < stepIdx ? "#1A6FD4" : "#9CA3AF" }}>{step}</div>
                  ))}
                </div>

                {/* Timestamps */}
                <div className="grid grid-cols-3 gap-2 mb-4 text-xs text-gray-500">
                  {rep.share360EnrollmentPaidAt && (
                    <div className="bg-gray-50 rounded-lg p-2">
                      <div className="text-[10px] font-bold text-gray-400 uppercase mb-0.5">Paid</div>
                      {new Date(rep.share360EnrollmentPaidAt).toLocaleDateString()}
                    </div>
                  )}
                  {rep.share360BackgroundCheckAt && (
                    <div className="bg-gray-50 rounded-lg p-2">
                      <div className="text-[10px] font-bold text-gray-400 uppercase mb-0.5">BG Check</div>
                      {new Date(rep.share360BackgroundCheckAt).toLocaleDateString()}
                    </div>
                  )}
                  {rep.share360ApprovedAt && (
                    <div className="bg-gray-50 rounded-lg p-2">
                      <div className="text-[10px] font-bold text-gray-400 uppercase mb-0.5">Approved</div>
                      {new Date(rep.share360ApprovedAt).toLocaleDateString()}
                    </div>
                  )}
                </div>

                {/* Monthly fee billing status (approved RAs only). rep.share360Waived
                    comes from getShare360PendingApprovals's server-side waiver check —
                    MUST be consulted before the raw paidAt/nextDue fields, which can be
                    stale for a waived rep (e.g. a leftover value from before the waiver
                    was earned) and would otherwise render a misleading due date. */}
                {rep.share360EnrollmentStatus === "approved" && (() => {
                  if (rep.share360Waived) {
                    return (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-3 text-xs bg-green-50 text-green-700 border border-green-100">
                        <DollarSign className="w-3.5 h-3.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span className="font-semibold">$24.95 Monthly Fee — </span>
                          Waived ({rep.share360MerchantCount ?? 0} onboarded merchant{rep.share360MerchantCount === 1 ? "" : "s"})
                        </div>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Waived</span>
                      </div>
                    );
                  }
                  const feePaidAt  = rep.share360MonthlyFeePaidAt  ? new Date(rep.share360MonthlyFeePaidAt)  : null;
                  const feeNextDue = rep.share360MonthlyFeeNextDue ? new Date(rep.share360MonthlyFeeNextDue) : null;
                  const paidUp     = feeNextDue && feeNextDue > new Date();
                  return (
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-3 text-xs ${paidUp ? "bg-green-50 text-green-700 border border-green-100" : "bg-amber-50 text-amber-700 border border-amber-100"}`}>
                      <DollarSign className="w-3.5 h-3.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="font-semibold">$24.95 Monthly Fee — </span>
                        {paidUp
                          ? <>Paid · Next due {feeNextDue!.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</>
                          : feePaidAt
                            ? <>Overdue · Last paid {feePaidAt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</>
                            : <>Never paid — fee pending</>
                        }
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${paidUp ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                        {paidUp ? "Current" : "Due"}
                      </span>
                    </div>
                  );
                })()}

                {/* Doc links — fetches fresh R2 signed URLs; no DocuSeal login required */}
                <Share360DocLinks repId={rep.id} hasDoc={!!rep.share360EnrollmentDocUrl} />

                {/* Action buttons — contextual per status */}
                <div className="flex flex-wrap gap-2">
                  {(rep.share360EnrollmentStatus === "docs_submitted" || rep.share360EnrollmentStatus === "payment_pending") && (
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="text-xs text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Docs received — awaiting payment
                      </div>
                      <Button size="sm" className="bg-[#1A6FD4] text-white border-0" onClick={() => confirmPaymentMutation.mutate({ repId: rep.id })} disabled={confirmPaymentMutation.isPending}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Confirm $49.95 Payment
                      </Button>
                    </div>
                  )}

                  {rep.share360EnrollmentStatus === "payment_confirmed" && (
                    <Button size="sm" className="bg-[#06B6D4] text-white border-0" onClick={() => confirmBgCheckMutation.mutate({ repId: rep.id })} disabled={confirmBgCheckMutation.isPending}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Verify Background Check Complete
                    </Button>
                  )}

                  {/* Background check: admin can manually approve/reject (in case quiz is waived) */}
                  {rep.share360EnrollmentStatus === "background_check" && (
                    <>
                      <div className="text-xs text-cyan-600 bg-cyan-50 px-3 py-1.5 rounded-lg flex items-center gap-1 mr-2">
                        <Clock className="w-3 h-3" /> Awaiting certification quiz from agent
                      </div>
                      <Button size="sm" className="bg-green-600 text-white border-0" onClick={() => approveMutation.mutate({ repId: rep.id, approvedBy: "admin" })} disabled={approveMutation.isPending}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Final Approval
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => rejectMutation.mutate({ repId: rep.id })} disabled={rejectMutation.isPending}>
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                    </>
                  )}

                  {rep.share360EnrollmentStatus === "pending_approval" && (
                    <>
                      <Button size="sm" className="bg-green-600 text-white border-0" onClick={() => approveMutation.mutate({ repId: rep.id, approvedBy: "admin" })} disabled={approveMutation.isPending}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Final Approval
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => rejectMutation.mutate({ repId: rep.id })} disabled={rejectMutation.isPending}>
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                    </>
                  )}

                  {rep.share360EnrollmentStatus === "approved" && (
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" className="text-orange-600 border-orange-200 hover:bg-orange-50" onClick={() => { if (confirm(`Suspend ${rep.firstName} ${rep.lastName} for non-payment? This will lock their dashboard and trigger a payment failure notification email.`)) suspendMutation.mutate({ repId: rep.id, reason: "payment_failed" }); }} disabled={suspendMutation.isPending}>
                        <PauseCircle className="w-3.5 h-3.5 mr-1" /> Suspend — Non-Payment
                      </Button>
                      <Button size="sm" variant="outline" className="text-gray-500 border-gray-200 hover:bg-gray-50 text-xs" onClick={() => { if (confirm(`Suspend ${rep.firstName} ${rep.lastName}? (generic reason, no payment email)`)) suspendMutation.mutate({ repId: rep.id, reason: "other" }); }} disabled={suspendMutation.isPending}>
                        Suspend (Other)
                      </Button>
                    </div>
                  )}

                  {rep.share360EnrollmentStatus === "suspended" && (
                    <Button size="sm" className="bg-green-600 text-white border-0" onClick={() => restoreMutation.mutate({ repId: rep.id })} disabled={restoreMutation.isPending}>
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore Access
                    </Button>
                  )}
                </div>

                {/* Merchant count override (approved + suspended RAs) */}
                {(rep.share360EnrollmentStatus === "approved" || rep.share360EnrollmentStatus === "suspended") && (
                  <div className="mt-3 flex items-center gap-2 pt-3 border-t border-gray-100">
                    <span className="text-xs text-gray-500 whitespace-nowrap">Merchant count override:</span>
                    <Input
                      type="number"
                      min={0}
                      placeholder="Auto"
                      value={merchantOverrides[rep.id] ?? (rep.share360MerchantCountOverride != null ? String(rep.share360MerchantCountOverride) : "")}
                      onChange={e => setMerchantOverrides(prev => ({ ...prev, [rep.id]: e.target.value }))}
                      className="h-7 text-xs w-24"
                    />
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => {
                      const val = merchantOverrides[rep.id];
                      merchantOverrideMutation.mutate({ repId: rep.id, count: val === "" ? null : Number(val) });
                    }} disabled={merchantOverrideMutation.isPending}>
                      <Save className="w-3 h-3 mr-1" /> Save
                    </Button>
                    {rep.share360MerchantCountOverride != null && (
                      <span className="text-[10px] text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">Override active: {rep.share360MerchantCountOverride}</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── RA Subscriptions Admin Tab ──────────────────────────────────────────────────
// ─── Share360 Monthly Support Fee — Payment History ───────────────────────────
function MonthlyFeePaymentsTable() {
  const query = trpc.share360.listMonthlyFeeStatuses.useQuery(undefined, { staleTime: 60_000 });
  const rows = query.data ?? [];

  const fmt = (ms: number | null) =>
    ms ? new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

  const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
    waived:     { label: "Waived",     cls: "bg-purple-50 text-purple-700 border border-purple-100" },
    current:    { label: "Current",    cls: "bg-green-50 text-green-700 border border-green-100" },
    overdue:    { label: "Overdue",    cls: "bg-amber-50 text-amber-700 border border-amber-100" },
    never_paid: { label: "Never Paid", cls: "bg-red-50 text-red-600 border border-red-100" },
  };

  const waived   = rows.filter((r: any) => r.status === "waived").length;
  const current  = rows.filter((r: any) => r.status === "current").length;
  const overdue  = rows.filter((r: any) => r.status === "overdue").length;
  const neverPaid= rows.filter((r: any) => r.status === "never_paid").length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-gray-900">$24.95 Monthly Support Fee — Payment Status</h3>
          <p className="text-xs text-gray-400 mt-0.5">Auto-updated when Wibiz fires the monthly fee webhook · Approved RAs only</p>
        </div>
        <button
          onClick={() => query.refetch()}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 px-3 py-1.5 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${query.isFetching ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Total Approved RAs", value: rows.length,  color: "#0FA8A8" },
          { label: "Fee Waived (≥3 merchants)", value: waived,    color: "#7B4FD4" },
          { label: "Paid & Current",     value: current,  color: "#10B981" },
          { label: "Overdue / Never Paid",value: overdue + neverPaid, color: "#F59E0B" },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-xl p-3.5 shadow-sm border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: color }} />
            <div className="text-2xl font-bold font-mono" style={{ color }}>{value}</div>
            <div className="text-[11px] text-gray-400 mt-0.5 font-medium">{label}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {query.isLoading ? (
          <div className="flex items-center justify-center py-10 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <DollarSign className="w-7 h-7 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No approved Share360 RAs yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Associate</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Rep Code</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Email</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Last Paid</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Next Due</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Merchants</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((r: any) => {
                const badge = STATUS_BADGE[r.status] ?? STATUS_BADGE.never_paid;
                const initials = `${r.firstName?.[0] ?? ""}${r.lastName?.[0] ?? ""}`.toUpperCase() || "?";
                return (
                  <tr key={r.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-teal-50 flex items-center justify-center text-[10px] font-bold text-teal-600 flex-shrink-0">
                          {initials}
                        </div>
                        <span className="text-sm font-semibold text-gray-800 truncate">
                          {[r.firstName, r.lastName].filter(Boolean).join(" ") || "—"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono text-gray-500">{r.repCode ?? "—"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-500">{r.email ?? "—"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-700 font-medium">{fmt(r.lastPaidAt)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium ${r.nextDueAt && r.nextDueAt < Date.now() && r.status !== "waived" && r.billingActive ? "text-red-600" : "text-gray-700"}`}>
                        {(r.status === "waived" || !r.billingActive) ? "N/A" : fmt(r.nextDueAt)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-bold text-gray-800">{r.merchantCount}</span>
                        <span className="text-xs text-gray-400">/ 3 needed</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${badge.cls}`}>
                        {badge.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

function RaSubscriptionsAdminTab() {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", plan: "Standard", amount: 97, billingDay: 1, pipelineStage: "Application Submitted", notes: "" });
  const utils = trpc.useUtils();

  const subsQuery = trpc.raSubscriptions.list.useQuery();
  const createSub = trpc.raSubscriptions.create.useMutation({
    onSuccess: () => { toast.success("RA subscription created"); utils.raSubscriptions.list.invalidate(); setShowForm(false); },
    onError: (e) => toast.error(e.message),
  });
  const updateStatus = trpc.raSubscriptions.updateStatus.useMutation({
    onSuccess: () => { utils.raSubscriptions.list.invalidate(); toast.success("Status updated"); },
  });
  const deleteSub = trpc.raSubscriptions.delete.useMutation({
    onSuccess: () => { utils.raSubscriptions.list.invalidate(); toast.success("Subscription removed"); },
  });

  const subs = subsQuery.data ?? [];
  const active = subs.filter((s: any) => s.status === "active").length;
  const paused = subs.filter((s: any) => s.status === "paused").length;
  const pending = subs.filter((s: any) => s.status === "pending").length;
  const totalMRR = subs.filter((s: any) => s.status === "active").reduce((acc: number, s: any) => acc + (s.amount ?? 0), 0);

  const STATUS_STYLES: Record<string, string> = {
    active: "bg-green-50 text-green-700",
    paused: "bg-yellow-50 text-yellow-700",
    cancelled: "bg-red-50 text-red-700",
    pending: "bg-blue-50 text-blue-700",
  };

  const RA_STAGES = ["Application Submitted", "Registration Paid", "Statements", "Background Check", "Certification Approved", "Subscription Active"];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Share360 Associate Subscriptions</h2>
          <p className="text-sm text-gray-500 mt-0.5">Track monthly subscriptions for Associates in the Share360 vertical</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="bg-[#0FA8A8] hover:bg-[#0d9090] text-white gap-2">
          <Plus className="w-4 h-4" /> Add Associate
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Associates", value: subs.length, color: "#0FA8A8" },
          { label: "Active", value: active, color: "#10B981" },
          { label: "Paused", value: paused, color: "#F59E0B" },
          { label: "Monthly MRR", value: new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(totalMRR), color: "#8B5CF6" },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">{label}</div>
            <div className="text-2xl font-bold" style={{ color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Add form */}
      {showForm && (
        <div className="bg-white rounded-2xl border border-teal-100 p-5 shadow-sm">
          <div className="text-sm font-bold text-gray-700 mb-4">Register New Associate</div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Full Name</label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="John Doe" /></div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Email</label><Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="john@email.com" /></div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Phone</label><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+1 555 000 0000" /></div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Plan</label>
              <Select value={form.plan} onValueChange={v => setForm(f => ({ ...f, plan: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Starter", "Standard", "Pro"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Monthly Amount ($)</label><Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: Number(e.target.value) }))} /></div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Billing Day (1-28)</label><Input type="number" min={1} max={28} value={form.billingDay} onChange={e => setForm(f => ({ ...f, billingDay: Number(e.target.value) }))} /></div>
            <div className="col-span-2"><label className="block text-xs font-semibold text-gray-500 mb-1">Pipeline Stage</label>
              <Select value={form.pipelineStage} onValueChange={v => setForm(f => ({ ...f, pipelineStage: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{RA_STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><label className="block text-xs font-semibold text-gray-500 mb-1">Notes</label><Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes" /></div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={() => createSub.mutate(form)} disabled={createSub.isPending} className="bg-[#0FA8A8] hover:bg-[#0d9090] text-white">
              {createSub.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Register Associate
            </Button>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Monthly Support Fee — Payment History (auto-populated from Wibiz webhook) */}
      <MonthlyFeePaymentsTable />

      {/* Subscriptions table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Manual Subscription Records</p>
        </div>
        {subsQuery.isLoading ? (
          <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...</div>
        ) : subs.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <CreditCard className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <div className="text-sm">No manual records yet.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Associate Name</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Contact</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Plan</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Amount</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Stage</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Status</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Billing Day</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {subs.map((s: any) => (
                <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-teal-50 flex items-center justify-center text-[10px] font-bold text-teal-600">{s.name?.[0]?.toUpperCase() ?? "?"}</div>
                      <span className="text-sm font-semibold text-gray-800">{s.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-0.5">
                      {s.email && <div className="text-xs text-gray-500 flex items-center gap-1"><Mail className="w-3 h-3" />{s.email}</div>}
                      {s.phone && <div className="text-xs text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" />{s.phone}</div>}
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className="text-xs font-semibold text-gray-700">{s.plan}</span></td>
                  <td className="px-4 py-3"><span className="text-sm font-bold text-gray-900">${(s.amount ?? 0).toLocaleString()}<span className="text-xs text-gray-400">/mo</span></span></td>
                  <td className="px-4 py-3"><span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">{s.pipelineStage ?? "—"}</span></td>
                  <td className="px-4 py-3">
                    <Select value={s.status} onValueChange={v => updateStatus.mutate({ id: s.id, status: v as any })}>
                      <SelectTrigger className={`h-7 text-xs w-28 ${STATUS_STYLES[s.status] ?? ""}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="paused">Paused</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Calendar className="w-3 h-3" /> Day {s.billingDay ?? 1}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => deleteSub.mutate({ id: s.id })}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

/// ─── Admin Settings Tab ─────────────────────────────────────────────────
const CALENDAR_SETTINGS = [
  { key: "ghl_calendar_training", label: "Training Calendar ID", description: "Calendar ID for agent training session bookings. Shown in the agent Training tab." },
  { key: "ghl_calendar_scale360", label: "Scale360 Strategy Call Calendar ID", description: "Calendar ID for Scale360 strategy call bookings." },
  { key: "ghl_calendar_fundable360", label: "Fundable360 Consultation Calendar ID", description: "Calendar ID for Fundable360 consultation bookings." },
  { key: "ghl_calendar_share360", label: "Share360 Consultation Calendar ID", description: "Calendar ID for Share360 consultation bookings." },
];

// ─── Admin Appointments Tab ────────────────────────────────────────────────────────────────────────────────────
const CALENDAR_NAMES: Record<string, string> = {
  "2ImDJVXE23GfW6WmVsMD": "BC360 General",
  "7LTivsApkZroz3LlZSiP": "Fundable360",
  "BNSDF3gHtSZuzgLMkijr": "Scale360",
  "FqI0SWCgqb5629AyScY6": "Share360",
  "MokOx22qfOnONZ4u9BKx": "Legacy360",
  "PCiM3Ok9GCLeycWuv8UK": "Media360",
};

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  confirmed:  { label: "Confirmed",  bg: "bg-green-100",  text: "text-green-700" },
  cancelled:  { label: "Cancelled",  bg: "bg-red-100",    text: "text-red-700" },
  showed:     { label: "Showed",     bg: "bg-blue-100",   text: "text-blue-700" },
  noshow:     { label: "No Show",    bg: "bg-yellow-100", text: "text-yellow-700" },
  invalid:    { label: "Invalid",    bg: "bg-gray-100",   text: "text-gray-500" },
  rescheduled:{ label: "Rescheduled",bg: "bg-purple-100", text: "text-purple-700" },
};

const VERTICALS_FILTER = [
  { value: "", label: "All Verticals" },
  { value: "general",     label: "BC360 General" },
  { value: "Fundable360", label: "Fundable360" },
  { value: "Scale360",    label: "Scale360" },
  { value: "Share360",    label: "Share360" },
  { value: "Legacy360",   label: "Legacy360" },
  { value: "Media360",    label: "Media360" },
];

const STATUSES_FILTER = [
  { value: "",           label: "All Statuses" },
  { value: "confirmed",  label: "Confirmed" },
  { value: "cancelled",  label: "Cancelled" },
  { value: "showed",     label: "Showed" },
  { value: "noshow",     label: "No Show" },
  { value: "rescheduled",label: "Rescheduled" },
];

// ─── BizConnect360 Calendar Admin Tab ─────────────────────────────────────────
const EVENT_TYPES = ["Training", "Q&A", "Webinar", "Workshop", "Other"];
const SYNC_CALENDARS = [
  { label: "BC360 General",  id: "2ImDJVXE23GfW6WmVsMD" },
  { label: "Fundable360",    id: "7LTivsApkZroz3LlZSiP" },
  { label: "Scale360",       id: "BNSDF3gHtSZuzgLMkijr" },
  { label: "Share360",       id: "FqI0SWCgqb5629AyScY6" },
  { label: "Legacy360",      id: "MokOx22qfOnONZ4u9BKx" },
  { label: "Media360",       id: "PCiM3Ok9GCLeycWuv8UK" },
];

/** Convert a UTC epoch-ms value to a local datetime-local string for <input> */
function msToDatetimeLocal(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
/** Convert a datetime-local string (local time) to UTC epoch ms */
function datetimeLocalToMs(s: string): number {
  return new Date(s).getTime();
}

// Expand recurring base events into individual occurrences for the weekly calendar view,
// excluding any occurrence hidden via bc360Calendar.hideOccurrence.
// The list below the calendar still uses raw base events for edit/delete.
// Shares its expansion logic with the server (@shared/bc360CalendarRecurrence) so the
// admin preview and the associate-facing calendar never disagree about what's excluded.
function expandEventsForCalendar(events: any[], hiddenKeys: Set<string>): any[] {
  const windowStart = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const windowEnd   = Date.now() + 90 * 24 * 60 * 60 * 1000;
  return events.flatMap(ev => expandBc360Event(ev, windowStart, windowEnd, hiddenKeys));
}

function AdminBc360CalendarTab() {
  const utils = trpc.useUtils();
  const { data: events = [], isLoading, refetch } = trpc.bc360Calendar.listAll.useQuery();
  const createMut  = trpc.bc360Calendar.create.useMutation({ onSuccess: () => { toast.success("Event created"); utils.bc360Calendar.listAll.invalidate(); setShowForm(false); }, onError: (e) => toast.error(`Create failed: ${e.message}`) });
  const updateMut  = trpc.bc360Calendar.update.useMutation({ onSuccess: () => { toast.success("Event updated"); utils.bc360Calendar.listAll.invalidate(); setShowForm(false); }, onError: (e) => toast.error(`Update failed: ${e.message}`) });
  const deleteMut  = trpc.bc360Calendar.delete.useMutation({ onSuccess: () => { toast.success("Event deleted"); utils.bc360Calendar.listAll.invalidate(); setConfirmDelete(null); }, onError: (e) => toast.error(`Delete failed: ${e.message}`) });
  const syncMut    = trpc.bc360Calendar.syncFromGhl.useMutation({ onSuccess: (r) => { toast.success(`Synced ${r.upserted} of ${r.total} events`); utils.bc360Calendar.listAll.invalidate(); setSyncCalId(""); } });

  const { data: hiddenOccurrences = [] } = trpc.bc360Calendar.listHiddenOccurrences.useQuery();
  const hideOccurrenceMut = trpc.bc360Calendar.hideOccurrence.useMutation({
    onSuccess: () => {
      toast.success("Occurrence hidden from Associate calendars");
      utils.bc360Calendar.listHiddenOccurrences.invalidate();
      utils.bc360Calendar.list.invalidate();
      setOccurrenceChoice(null);
    },
    onError: (e) => toast.error(`Hide failed: ${e.message}`),
  });
  const restoreOccurrenceMut = trpc.bc360Calendar.restoreOccurrence.useMutation({
    onSuccess: () => {
      toast.success("Occurrence restored");
      utils.bc360Calendar.listHiddenOccurrences.invalidate();
      utils.bc360Calendar.list.invalidate();
    },
    onError: (e) => toast.error(`Restore failed: ${e.message}`),
  });
  const hiddenKeySet = useMemo(
    () => new Set((hiddenOccurrences as any[]).map(h => bc360OccurrenceKey(h.seriesEventId, h.occurrenceStartTimeUtc))),
    [hiddenOccurrences]
  );

  const [showForm, setShowForm]         = useState(false);
  const [editEvent, setEditEvent]       = useState<any>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [syncCalId, setSyncCalId]       = useState("");
  const [occurrenceChoice, setOccurrenceChoice] = useState<any>(null);

  const currentYear = new Date().getFullYear();
  const [blockYear, setBlockYear]       = useState(currentYear + 1);
  const [blockDryRun, setBlockDryRun]   = useState(true);
  const [blockResult, setBlockResult]   = useState<any>(null);
  const blockHolidaysMut = trpc.bc360Calendar.blockUsHolidays.useMutation({
    onSuccess: (data) => {
      setBlockResult(data);
      if (!data.dryRun) {
        toast.success(`Blocked ${data.blocked?.length ?? 0} holiday slots across ${data.calendarCount} calendars.`);
      }
    },
    onError: (e) => toast.error(`Holiday blocking failed: ${e.message}`),
  });

  const emptyForm = { title: "", description: "", joinLink: "", eventType: "Training", startLocal: "", endLocal: "", isPublished: true, recurrenceRule: "none", recurrenceEndLocal: "", timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone };
  const [form, setForm] = useState(emptyForm);

  function openCreate() { setEditEvent(null); setForm(emptyForm); setShowForm(true); }
  function openEdit(ev: any) {
    setEditEvent(ev);
    setForm({
      title: ev.title,
      description: ev.description ?? "",
      joinLink: ev.joinLink ?? "",
      eventType: ev.eventType,
      startLocal: msToDatetimeLocal(ev.startTimeUtc),
      endLocal:   msToDatetimeLocal(ev.endTimeUtc),
      isPublished: ev.isPublished,
      recurrenceRule: ev.recurrenceRule ?? "none",
      timeZone: ev.timeZone ?? "",
      recurrenceEndLocal: ev.recurrenceEndDate ? msToDatetimeLocal(ev.recurrenceEndDate) : "",
    });
    setShowForm(true);
  }
  function handleSave() {
    if (!form.timeZone) { toast.error("Confirm the organizer timezone before saving this event."); return; }
    if (!form.title.trim() || !form.startLocal || !form.endLocal) { toast.error("Title and start/end times are required"); return; }
    const payload = {
      timeZone:          form.timeZone,
      title:             form.title.trim(),
      description:       form.description || undefined,
      joinLink:          form.joinLink || undefined,
      eventType:         form.eventType,
      startTimeUtc:      datetimeLocalToMs(form.startLocal),
      endTimeUtc:        datetimeLocalToMs(form.endLocal),
      isPublished:       form.isPublished,
      recurrenceRule:    form.recurrenceRule as "none" | "daily" | "weekly" | "monthly",
      recurrenceEndDate: form.recurrenceRule !== "none" && form.recurrenceEndLocal
        ? datetimeLocalToMs(form.recurrenceEndLocal)
        : undefined,
    };
    if (editEvent) { updateMut.mutate({ id: editEvent.id, ...payload }); }
    else { createMut.mutate(payload); }
  }

  // Grace-view: same expanded published data the LLM queries
  const { data: graceEvents = [] } = trpc.bc360Calendar.list.useQuery();
  const [graceSearch, setGraceSearch] = useState("");

  const now = Date.now();
  const upcoming = (events as any[]).filter(e => e.endTimeUtc > now).sort((a: any, b: any) => a.startTimeUtc - b.startTimeUtc);
  const past     = (events as any[]).filter(e => e.endTimeUtc <= now).sort((a: any, b: any) => b.startTimeUtc - a.startTimeUtc);

  const graceFiltered = (graceEvents as any[]).filter(e => {
    if (!graceSearch.trim()) return true;
    const q = graceSearch.toLowerCase();
    return (e.title ?? "").toLowerCase().includes(q) || (e.eventType ?? "").toLowerCase().includes(q);
  }).sort((a: any, b: any) => a.startTimeUtc - b.startTimeUtc);

  const renderEventRow = (ev: any) => (
    <div key={ev.id} className={`border rounded-xl overflow-hidden ${ev.isPublished ? "border-gray-200 bg-white shadow-sm" : "border-dashed border-gray-200 bg-gray-50/50"}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Date badge */}
        <div className="w-10 h-10 rounded-lg bg-[#84ab4a]/10 flex flex-col items-center justify-center flex-shrink-0">
          <span className="text-[9px] font-bold uppercase text-[#84ab4a] leading-none">
            {new Date(ev.startTimeUtc).toLocaleDateString("en-US", { month: "short" })}
          </span>
          <span className="text-base font-bold text-[#84ab4a] leading-none">
            {new Date(ev.startTimeUtc).getDate()}
          </span>
        </div>
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900 truncate">{ev.title}</span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#84ab4a]/10 text-[#84ab4a]">{ev.eventType}</span>
            {ev.recurrenceRule && ev.recurrenceRule !== "none" && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-500 flex items-center gap-1">
                <Repeat2 className="w-2.5 h-2.5" />{ev.recurrenceRule.charAt(0).toUpperCase() + ev.recurrenceRule.slice(1)}
              </span>
            )}
            {!ev.isPublished && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-400">Draft</span>}
          </div>
          <div className="text-xs text-gray-400 mt-0.5">
            {new Date(ev.startTimeUtc).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" })}
            {" – "}
            {new Date(ev.endTimeUtc).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
          </div>
          {ev.description && <div className="text-xs text-gray-500 mt-0.5 truncate">{ev.description}</div>}
          {ev.joinLink && (
            <a href={ev.joinLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#84ab4a] underline underline-offset-2 mt-0.5">
              <ExternalLink className="w-3 h-3" /> Join Link
            </a>
          )}
        </div>
        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => openEdit(ev)} title="Edit" className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
            <Pencil className="w-3.5 h-3.5" />
          </button>
          {ev.isPublished ? (
            <button
              onClick={() => updateMut.mutate({ id: ev.id, isPublished: false })}
              title="Cancel (unpublish)"
              className="p-1.5 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
            >
              <EyeOff className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              onClick={() => updateMut.mutate({ id: ev.id, isPublished: true })}
              title="Restore (publish)"
              className="p-1.5 rounded-lg text-gray-400 hover:text-[#84ab4a] hover:bg-[#84ab4a]/10 transition-colors"
            >
              <Eye className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={() => setConfirmDelete(ev.id)} title="Delete permanently" className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">BizConnect360 Calendar</h2>
          <p className="text-sm text-gray-400">Manage events visible to all Associates. Create manually or sync from Wibiz.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#84ab4a] text-white text-sm font-semibold hover:bg-[#6d9138] transition-colors shadow-sm">
            <Plus className="w-4 h-4" /> New Event
          </button>
        </div>
      </div>

      {/* GHL Sync */}
      <div className="bg-[#0D1E35] border border-[#84ab4a]/20 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <CalendarCheck className="w-4 h-4 text-[#84ab4a]" />
          <span className="text-sm font-bold text-white">Sync from Wibiz Calendar</span>
        </div>
        <p className="text-xs text-white/50 mb-3">Pull upcoming events from a Wibiz training calendar and import them into the BizConnect360 Calendar. Existing events (matched by event ID) are updated in place.</p>
        <div className="flex items-center gap-2">
          <select
            value={syncCalId}
            onChange={e => setSyncCalId(e.target.value)}
            className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm text-white appearance-none"
          >
            <option value="" className="text-gray-900">Select a calendar…</option>
            {SYNC_CALENDARS.map(c => (
              <option key={c.id} value={c.id} className="text-gray-900">{c.label}</option>
            ))}
          </select>
          <button
            disabled={!syncCalId || syncMut.isPending}
            onClick={() => syncMut.mutate({ calendarId: syncCalId })}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#84ab4a] text-white text-sm font-semibold hover:bg-[#6d9138] disabled:opacity-40 transition-colors"
          >
            {syncMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Sync
          </button>
        </div>
      </div>

      {/* US Holiday Blocking */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            <span className="text-base">🇺🇸</span>
          </div>
          <div className="flex-1">
            <div className="text-sm font-bold text-gray-900">US Holiday Blocking</div>
            <div className="text-xs text-gray-400">Block US federal holidays on all Wibiz booking calendars so clients cannot book on those days.</div>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Year</label>
              <select
                value={blockYear}
                onChange={e => { setBlockYear(Number(e.target.value)); setBlockResult(null); }}
                className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-[#84ab4a]/60 focus:ring-2 focus:ring-[#84ab4a]/20"
              >
                {[currentYear, currentYear + 1, currentYear + 2].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer pb-1">
              <input
                type="checkbox"
                checked={blockDryRun}
                onChange={e => { setBlockDryRun(e.target.checked); setBlockResult(null); }}
                className="w-4 h-4 rounded accent-[#84ab4a]"
              />
              <span className="text-sm text-gray-700 font-medium">Dry run (preview only, no changes)</span>
            </label>
            <button
              onClick={() => blockHolidaysMut.mutate({ year: blockYear, dryRun: blockDryRun })}
              disabled={blockHolidaysMut.isPending}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 ${
                blockDryRun
                  ? "bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100"
                  : "bg-[#84ab4a] text-white hover:bg-[#6d9138]"
              }`}
            >
              {blockHolidaysMut.isPending
                ? <><Loader2 className="w-4 h-4 animate-spin" /> {blockDryRun ? "Previewing…" : "Blocking…"}</>
                : <><CalendarCheck className="w-4 h-4" /> {blockDryRun ? `Preview ${blockYear} Holidays` : `Block ${blockYear} Holidays`}</>
              }
            </button>
          </div>

          {blockResult && (
            <div className="space-y-3">
              {/* Summary */}
              <div className={`rounded-xl px-4 py-3 text-sm font-medium flex items-center gap-2 ${blockResult.dryRun ? "bg-blue-50 text-blue-800 border border-blue-200" : "bg-green-50 text-green-800 border border-green-200"}`}>
                {blockResult.dryRun
                  ? <><Info className="w-4 h-4 flex-shrink-0" /> Dry run — {blockResult.holidays.length} holidays × {blockResult.calendarCount} calendars = {blockResult.totalBlocksToCreate} slots would be created. No changes made.</>
                  : <><CheckCircle2 className="w-4 h-4 flex-shrink-0" /> Blocked {blockResult.blocked?.length} slots across {blockResult.calendarCount} calendars for {blockResult.holidays.length} holidays. {blockResult.errors?.length > 0 ? `${blockResult.errors.length} errors.` : "All successful."}</>
                }
              </div>
              {/* Holiday list */}
              <div className="border border-gray-100 rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-500">Date</th>
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-500">Holiday</th>
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-500">{blockResult.dryRun ? "Status" : "Calendars Blocked"}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {(blockResult.holidays as any[]).map((h: any) => {
                      const successCount = blockResult.dryRun ? blockResult.calendarCount :
                        (blockResult.blocked?.filter((b: any) => b.date === h.date).length ?? 0);
                      return (
                        <tr key={h.date} className="hover:bg-gray-50/50">
                          <td className="px-4 py-2.5 font-mono text-gray-600">{h.date}</td>
                          <td className="px-4 py-2.5 font-medium text-gray-900">🇺🇸 {h.name}</td>
                          <td className="px-4 py-2.5">
                            {blockResult.dryRun
                              ? <span className="text-blue-600">{blockResult.calendarCount} calendars</span>
                              : <span className={`font-semibold ${successCount === blockResult.calendarCount ? "text-green-600" : "text-amber-600"}`}>{successCount}/{blockResult.calendarCount}</span>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {blockResult.errors?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700">
                  <strong>{blockResult.errors.length} errors:</strong>{" "}
                  {blockResult.errors.slice(0, 3).map((e: any) => `${e.date} (${e.calendarId.slice(-6)})`).join(", ")}
                  {blockResult.errors.length > 3 && ` + ${blockResult.errors.length - 3} more`}
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-gray-400 leading-relaxed">
            Blocks all {blockYear} US federal holidays on all configured Wibiz training and consultation calendars.
            Run the dry run first to verify the holiday list, then uncheck "Dry run" and click again to apply.
          </p>
        </div>
      </div>

      {/* Weekly Calendar View — recurring events expanded for display, hidden occurrences excluded */}
      <WeeklyCalendarView
        events={expandEventsForCalendar(events as any[], hiddenKeySet) as CalendarEvent[]}
        isLoading={isLoading}
        onEventClick={(ev) => {
          // A clicked occurrence of a recurring series offers a choice (hide this
          // occurrence vs. edit/unpublish the whole series) instead of jumping
          // straight to the series editor. Non-recurring events edit directly,
          // unchanged from prior behavior.
          if ((ev as any).isRecurrenceOccurrence) {
            setOccurrenceChoice(ev);
            return;
          }
          const base = (events as any[]).find(e => e.id === ev.id);
          if (base) openEdit(base);
        }}
        onSlotClick={(startMs: number) => {
          const startLocal = new Date(startMs).toISOString().slice(0, 16);
          const endLocal = new Date(startMs + 60 * 60 * 1000).toISOString().slice(0, 16);
          setEditEvent(null);
          setForm({ ...emptyForm, startLocal, endLocal });
          setShowForm(true);
        }}
      />

      {/* ── Grace Preview — exact expanded data the LLM queries ── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <Eye className="w-4 h-4 text-[#84ab4a]" />
              Dawn Preview
            </h3>
            <p className="text-xs text-gray-400">Exact expanded event list Dawn queries. Search to verify any event name.</p>
          </div>
          <input
            type="text"
            placeholder="Search events…"
            value={graceSearch}
            onChange={e => setGraceSearch(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-48 focus:outline-none focus:ring-1 focus:ring-[#84ab4a]"
          />
        </div>
        {graceFiltered.length === 0 ? (
          <div className="text-center py-6 text-sm text-gray-400 border border-dashed border-gray-200 rounded-xl">
            {graceSearch ? `No events matching "${graceSearch}"` : "No upcoming published events"}
          </div>
        ) : (
          <div className="space-y-1.5">
            {graceFiltered.map((ev: any, i: number) => {
              const dt = new Date(ev.startTimeUtc);
              return (
                <div key={`${ev.id}-${i}`} className="flex items-start gap-3 px-4 py-2.5 border border-gray-100 rounded-xl bg-white text-sm">
                  <div className="w-9 h-9 rounded-lg bg-[#84ab4a]/10 flex flex-col items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[8px] font-bold uppercase text-[#84ab4a] leading-none">
                      {dt.toLocaleDateString("en-US", { month: "short" })}
                    </span>
                    <span className="text-sm font-bold text-[#84ab4a] leading-none">{dt.getDate()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">{ev.title}</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#84ab4a]/10 text-[#84ab4a]">{ev.eventType}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {dt.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" })}
                    </div>
                    {ev.description && <div className="text-xs text-gray-400 mt-0.5 truncate">{ev.description}</div>}
                    {ev.joinLink && (
                      <a href={ev.joinLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#84ab4a] underline underline-offset-2 mt-0.5">
                        <ExternalLink className="w-3 h-3" /> Join Link
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Base Events (editable) ── */}
      <div className="space-y-3">
        <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
          <CalendarCheck className="w-4 h-4 text-gray-400" />
          Base Events
          <span className="text-xs font-normal text-gray-400">— edit, unpublish, or delete</span>
        </h3>
        {isLoading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-300" /></div>
        ) : upcoming.length === 0 && past.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-400">No events yet. Create one or sync from Wibiz.</div>
        ) : (
          <div className="space-y-6">
            {upcoming.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Upcoming ({upcoming.length})</p>
                <div className="space-y-2">{upcoming.map(renderEventRow)}</div>
              </div>
            )}
            {past.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Past ({past.length})</p>
                <div className="space-y-2 opacity-60">{past.map(renderEventRow)}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Hidden Occurrences — single dates skipped out of a recurring series; series stays published */}
      {(hiddenOccurrences as any[]).length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
            <EyeOff className="w-4 h-4 text-gray-400" />
            Hidden Occurrences
            <span className="text-xs font-normal text-gray-400">— excluded from Associate calendars; the series stays published</span>
          </h3>
          <div className="space-y-2">
            {(hiddenOccurrences as any[]).map((h) => {
              const series = (events as any[]).find(e => e.id === h.seriesEventId);
              return (
                <div key={h.id} className="flex items-center justify-between gap-3 px-4 py-2.5 border border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-800 truncate">{series?.title ?? `Event #${h.seriesEventId}`}</div>
                    <div className="text-xs text-gray-400">
                      {new Date(h.occurrenceStartTimeUtc).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" })}
                    </div>
                  </div>
                  <button
                    onClick={() => restoreOccurrenceMut.mutate({ seriesEventId: h.seriesEventId, occurrenceStartTimeUtc: h.occurrenceStartTimeUtc })}
                    disabled={restoreOccurrenceMut.isPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#84ab4a] hover:bg-[#84ab4a]/10 transition-colors flex-shrink-0 disabled:opacity-50"
                  >
                    <Eye className="w-3.5 h-3.5" /> Restore
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      <Dialog open={showForm} onOpenChange={v => { if (!v) setShowForm(false); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#84ab4a]" />
              {editEvent ? "Edit Event" : "New Calendar Event"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Title *</label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Fundable360 Training — Q3" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Event Type</label>
                <select value={form.eventType} onChange={e => setForm(f => ({ ...f, eventType: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
                  {EVENT_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.isPublished} onChange={e => setForm(f => ({ ...f, isPublished: e.target.checked }))} className="w-4 h-4 accent-[#84ab4a]" />
                  <span className="text-sm text-gray-700">Published</span>
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Start (your device time) *</label>
                <input type="datetime-local" value={form.startLocal} onChange={e => setForm(f => ({ ...f, startLocal: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">End (your device time) *</label>
                <input type="datetime-local" value={form.endLocal} onChange={e => setForm(f => ({ ...f, endLocal: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Organizer timezone</label>
              <Input value={form.timeZone} onChange={e => setForm(f => ({ ...f, timeZone: e.target.value }))} placeholder="America/Chicago" />
              <p className="text-xs text-gray-500 mt-1">Recurring meetings keep their clock time in this timezone. Everyone sees meeting times in their own device timezone.</p>
              {!form.timeZone && <p className="text-xs text-amber-700 mt-1">This older event has no saved organizer timezone. Confirm it before changing the schedule.</p>}
              <button type="button" className="text-xs text-blue-600 mt-1" onClick={() => setForm(f => ({ ...f, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }))}>Use my device timezone</button>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Join Link (URL)</label>
              <Input value={form.joinLink} onChange={e => setForm(f => ({ ...f, joinLink: e.target.value }))} placeholder="https://zoom.us/j/..." />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} placeholder="Optional event details…" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none" />
            </div>
            <div className={`grid gap-3 ${form.recurrenceRule !== "none" ? "grid-cols-2" : "grid-cols-1"}`}>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Repeat</label>
                <select
                  value={form.recurrenceRule}
                  onChange={e => setForm(f => ({ ...f, recurrenceRule: e.target.value, recurrenceEndLocal: "" }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="none">Does not repeat</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              {form.recurrenceRule !== "none" && (
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Repeat until (optional)</label>
                  <input
                    type="datetime-local"
                    value={form.recurrenceEndLocal}
                    onChange={e => setForm(f => ({ ...f, recurrenceEndLocal: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              )}
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={handleSave} disabled={createMut.isPending || updateMut.isPending} className="flex-1 bg-[#84ab4a] hover:bg-[#6d9138] text-white font-semibold">
                {createMut.isPending || updateMut.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editEvent ? "Save Changes" : "Create Event"}
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)} className="flex-1">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recurring occurrence clicked — choose scope of the change */}
      <Dialog open={occurrenceChoice !== null} onOpenChange={v => { if (!v) setOccurrenceChoice(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{occurrenceChoice?.title}</DialogTitle>
          </DialogHeader>
          {occurrenceChoice && (
            <p className="text-sm text-gray-500 mb-1">
              {new Date(occurrenceChoice.occurrenceStartTimeUtc).toLocaleString("en-US", { weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
            </p>
          )}
          <p className="text-sm text-gray-600 mb-4">This is one occurrence of a recurring event. Choose what to change.</p>
          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              disabled={hideOccurrenceMut.isPending}
              onClick={() => occurrenceChoice && hideOccurrenceMut.mutate({
                seriesEventId: occurrenceChoice.seriesEventId,
                occurrenceStartTimeUtc: occurrenceChoice.occurrenceStartTimeUtc,
              })}
              className="justify-center"
            >
              {hideOccurrenceMut.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
              Hide this occurrence
            </Button>
            <Button
              onClick={() => {
                const base = (events as any[]).find(e => e.id === occurrenceChoice?.seriesEventId);
                setOccurrenceChoice(null);
                if (base) openEdit(base);
              }}
              className="justify-center bg-[#84ab4a] hover:bg-[#6d9138] text-white"
            >
              Edit or unpublish entire series
            </Button>
            <Button variant="ghost" onClick={() => setOccurrenceChoice(null)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={confirmDelete !== null} onOpenChange={v => { if (!v) setConfirmDelete(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Delete Event?</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600 mb-4">This will permanently remove the event from the BizConnect360 Calendar. Associates will no longer see it.</p>
          <div className="flex gap-2">
            <Button variant="destructive" onClick={() => confirmDelete !== null && deleteMut.mutate({ id: confirmDelete })} disabled={deleteMut.isPending} className="flex-1">
              {deleteMut.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Delete
            </Button>
            <Button variant="outline" onClick={() => setConfirmDelete(null)} className="flex-1">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminAppointmentsTab() {
  const [filterVertical, setFilterVertical] = useState("");
  const [filterStatus,   setFilterStatus]   = useState("");
  const [expandedId,     setExpandedId]     = useState<string | null>(null);

  // Pull live from GHL API — no local DB caching
  const { data: verticalGroups = [], isLoading, refetch } = trpc.ghlAppointments.getAllAdmin.useQuery({
    vertical: filterVertical || undefined,
  }, { refetchInterval: 120_000 });

  // Flatten all appointments from all vertical groups
  const allAppointments: any[] = (verticalGroups as any[]).flatMap((g: any) =>
    (g.appointments ?? []).map((a: any) => ({ ...a, vertical: g.vertical }))
  );

  // Apply status filter client-side
  const filtered = filterStatus
    ? allAppointments.filter(a => a.status === filterStatus)
    : allAppointments;

  const now = Date.now();
  const upcoming = filtered.filter(b => new Date(b.startTime).getTime() > now)
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  const past = filtered.filter(b => new Date(b.startTime).getTime() <= now)
    .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

  const renderBookingRow = (b: any, isPast: boolean) => {
    const sc = STATUS_CONFIG[b.status] ?? STATUS_CONFIG.confirmed;
    const isExpanded = expandedId === b.id;
    const startMs = new Date(b.startTime).getTime();
    const endMs = new Date(b.endTime).getTime();
    return (
      <div
        key={b.id}
        className={`border rounded-xl overflow-hidden transition-all ${
          isPast ? "border-gray-100 bg-gray-50/50" : "border-gray-200 bg-white shadow-sm"
        }`}
      >
        {/* Row header */}
        <button
          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
          onClick={() => setExpandedId(isExpanded ? null : b.id)}
        >
          {/* Date badge */}
          <div className={`w-10 h-10 rounded-lg flex flex-col items-center justify-center flex-shrink-0 ${
            isPast ? "bg-gray-100" : "bg-[#0FA8A8]/10"
          }`}>
            <span className={`text-[9px] font-bold uppercase leading-none ${
              isPast ? "text-gray-400" : "text-[#0FA8A8]"
            }`}>
              {new Date(b.startTime).toLocaleDateString("en-US", { month: "short" })}
            </span>
            <span className={`text-base font-bold leading-none ${
              isPast ? "text-gray-400" : "text-[#0FA8A8]"
            }`}>
              {new Date(b.startTime).getDate()}
            </span>
          </div>
          {/* Title + meta */}
          <div className="flex-1 min-w-0">
            <div className={`text-sm font-semibold truncate ${
              isPast ? "text-gray-500" : "text-gray-900"
            }`}>{b.title || b.contact?.name || "Training Session"}</div>
            <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-2 flex-wrap">
              <span>{CALENDAR_NAMES[b.calendarId] ?? b.vertical}</span>
              {b.contact?.name && <><span>·</span><span className="font-medium text-gray-600">{b.contact.name}</span></>}
              <span>·</span>
              <span>
                {new Date(b.startTime).toLocaleString("en-US", {
                  month: "short", day: "numeric", year: "numeric",
                  hour: "numeric", minute: "2-digit"
                })}
              </span>
            </div>
          </div>
          {/* Status badge */}
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
            sc.bg + " " + sc.text
          }`}>{sc.label}</span>
          {/* Expand chevron */}
          <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${
            isExpanded ? "rotate-180" : ""
          }`} />
        </button>
        {/* Expanded detail */}
        {isExpanded && (
          <div className="px-4 pb-4 pt-1 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {(b.contact?.name || b.contact?.firstName) && (
              <div>
                <span className="text-xs text-gray-400 block mb-0.5">Attendee</span>
                <span className="font-medium text-gray-800">{b.contact.name || `${b.contact.firstName ?? ""} ${b.contact.lastName ?? ""}`.trim()}</span>
              </div>
            )}
            {b.contact?.email && (
              <div>
                <span className="text-xs text-gray-400 block mb-0.5">Email</span>
                <a href={`mailto:${b.contact.email}`} className="font-medium text-blue-600 hover:underline">{b.contact.email}</a>
              </div>
            )}
            {b.contact?.phone && (
              <div>
                <span className="text-xs text-gray-400 block mb-0.5">Phone</span>
                <a href={`tel:${b.contact.phone}`} className="font-medium text-gray-800">{b.contact.phone}</a>
              </div>
            )}
            {b.address && (
              <div>
                <span className="text-xs text-gray-400 block mb-0.5">Location</span>
                <span className="font-medium text-gray-800">{b.address}</span>
              </div>
            )}
            <div>
              <span className="text-xs text-gray-400 block mb-0.5">Time</span>
              <span className="font-medium text-gray-800">
                {new Date(b.startTime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                {" – "}
                {new Date(b.endTime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
              </span>
            </div>
            {b.assignedUserId && (
              <div>
                <span className="text-xs text-gray-400 block mb-0.5">Assigned Staff ID</span>
                <span className="font-medium text-gray-600 text-xs font-mono">{b.assignedUserId}</span>
              </div>
            )}
            {b.notes && (
              <div className="sm:col-span-2">
                <span className="text-xs text-gray-400 block mb-0.5">Notes</span>
                <span className="text-gray-700">{b.notes}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-1">Training Appointments</h2>
        <p className="text-sm text-gray-500">All booked training sessions across all verticals and agents. Pulled live from Wibiz — refreshes every 2 minutes. Click any row to expand details.</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Select value={filterVertical} onValueChange={setFilterVertical}>
          <SelectTrigger className="w-44 text-sm">
            <SelectValue placeholder="All Verticals" />
          </SelectTrigger>
          <SelectContent>
            {VERTICALS_FILTER.map(v => (
              <SelectItem key={v.value} value={v.value || "__all__"}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 text-sm">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            {STATUSES_FILTER.map(s => (
              <SelectItem key={s.value} value={s.value || "__all__"}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="text-xs">
          Refresh
        </Button>
        {(filterVertical || filterStatus) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setFilterVertical(""); setFilterStatus(""); }}
            className="text-gray-400 hover:text-gray-700"
          >
            <X className="w-3.5 h-3.5 mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total",     value: filtered.length,                                       color: "#0FA8A8" },
          { label: "Upcoming",  value: upcoming.length,                                       color: "#84ab4a" },
          { label: "Past",      value: past.length,                                           color: "#6366F1" },
          { label: "Cancelled", value: filtered.filter((b: any) => b.status === "cancelled").length, color: "#E8620A" },
        ].map(s => (
          <div key={s.label} className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm">
            <div className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Upcoming bookings */}
      {isLoading ? (
        <div className="py-12 text-center text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          <p className="text-sm">Loading appointments…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-gray-400 bg-white border border-gray-100 rounded-2xl">
          <Calendar className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">No appointments found</p>
          <p className="text-xs mt-1">No training appointments found in Wibiz for the selected filters.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {upcoming.length > 0 && (
            <div>
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Upcoming ({upcoming.length})</div>
              <div className="space-y-2">{upcoming.map(b => renderBookingRow(b, false))}</div>
            </div>
          )}
          {past.length > 0 && (
            <div>
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Past ({past.length})</div>
              <div className="space-y-2">{past.map(b => renderBookingRow(b, true))}</div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}

// ─── Admin Training Calendar Tab ─────────────────────────────────────────────────────
function AdminTrainingTab() {const utils = trpc.useUtils();
  const allSettings = trpc.settings.getAll.useQuery();
  const updateSetting = trpc.settings.update.useMutation({
    onSuccess: () => {
      utils.settings.getAll.invalidate();
      utils.settings.get.invalidate();
      toast.success("Training calendar updated");
    },
    onError: (e) => toast.error(e.message),
  });

  const getValue = (key: string) =>
    allSettings.data?.find((s: { key: string; value: string | null }) => s.key === key)?.value ?? "";

  const [calId, setCalId] = useState("");
  const [initialized, setInitialized] = useState(false);

  if (!initialized && allSettings.data) {
    setCalId(getValue("ghl_calendar_training"));
    setInitialized(true);
  }

  const embedUrl = calId ? `https://api.wibiz.ai/widget/calendar/${calId}` : null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-1">Training Calendar</h2>
        <p className="text-sm text-gray-500">Set the Calendar ID for agent training sessions. Agents see this calendar as a view-only embed on their Overview and Training tabs.</p>
      </div>

      {/* Calendar ID input */}
      <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-4 h-4 text-[#84ab4a]" />
          <span className="font-semibold text-gray-800 text-sm">Training Calendar ID</span>
        </div>
        <div className="flex gap-3">
          <Input
            value={calId}
            onChange={(e) => setCalId(e.target.value)}
            placeholder="e.g. O6uW585cnEvZAB2fNRmE"
            className="flex-1 text-sm"
          />
          <Button
            size="sm"
            onClick={() => updateSetting.mutate({ key: "ghl_calendar_training", value: calId || null })}
            disabled={updateSetting.isPending}
            className="bg-[#84ab4a] hover:bg-[#6d8f3a] text-white"
          >
            {updateSetting.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span className="ml-1.5">Save</span>
          </Button>
        </div>
        <p className="text-xs text-gray-400 mt-2">In your Wibiz Dashboard → Calendars → select calendar → Edit. The ID appears in the URL or settings panel.</p>
      </div>

      {/* Live preview */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-[#84ab4a]" />
            <span className="text-sm font-semibold text-gray-700">Calendar Preview (Agent View)</span>
          </div>
          {embedUrl && (
            <a href={embedUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:underline flex items-center gap-1">
              <Link2 className="w-3 h-3" /> Open in new tab
            </a>
          )}
        </div>
        {!embedUrl ? (
          <div className="py-16 text-center text-gray-400">
            <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Enter a Calendar ID above to preview the agent view.</p>
          </div>
        ) : (
          <iframe src={embedUrl} style={{ width: "100%", height: "600px", border: "none" }} title="Training Calendar Preview" />
        )}
      </div>

      {/* How-to guide */}
      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
        <div className="flex items-start gap-3">
          <Settings className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-blue-900 mb-1">How training sessions work</div>
            <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
              <li>BC360 schedules sessions directly in the Wibiz calendar linked above</li>
              <li>Agents see the calendar as a read-only embed — they cannot book or modify sessions</li>
              <li>The same calendar appears on the agent's Overview tab (compact) and Training tab (full size)</li>
              <li>To update the calendar, simply change the Calendar ID above and save</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Announcement Media Field (upload image OR paste a video link) ────────────
// Preview-only detection (mirrors server-side detectAnnouncementMediaType in
// routers.ts) — the server is the source of truth for what's actually stored;
// this only picks which embed to show while the admin is editing the form.
function detectMediaPreviewType(url: string): "youtube" | "vimeo" | "video_url" {
  if (/(?:youtube\.com\/watch\?v=|youtube\.com\/shorts\/|youtu\.be\/)/i.test(url)) return "youtube";
  if (/vimeo\.com\//i.test(url)) return "vimeo";
  return "video_url";
}

function AnnouncementMediaField({
  mediaUrl, mediaKind, uploading, onUpload, onVideoUrlChange, onClear,
}: {
  mediaUrl: string;
  mediaKind: "" | "image" | "video";
  uploading: boolean;
  onUpload: (file: File) => void;
  onVideoUrlChange: (url: string) => void;
  onClear: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Featured Media (optional)</label>
      <div className="flex flex-wrap items-center gap-2">
        <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }} />
        <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
          <span className="ml-1.5">Upload Image</span>
        </Button>
        <span className="text-xs text-gray-400">or</span>
        <div className="flex items-center gap-1.5 flex-1 min-w-[220px]">
          <Video className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <input
            type="text"
            placeholder="Paste a video link (YouTube, Vimeo, or app.wibiz.ai media URL)"
            value={mediaKind === "video" ? mediaUrl : ""}
            onChange={(e) => onVideoUrlChange(e.target.value)}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40"
          />
        </div>
        {mediaUrl && (
          <button type="button" onClick={onClear} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors" title="Remove media">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {mediaUrl && (
        <div className="mt-2 max-w-xs">
          <AnnouncementMedia
            mediaUrl={mediaUrl}
            mediaType={mediaKind === "image" ? "image" : detectMediaPreviewType(mediaUrl)}
            title="Preview"
            className="mb-0 h-40"
          />
        </div>
      )}
    </div>
  );
}

// ─── Admin Announcements Tab ──────────────────────────────────────────────────────────────────────────────────────
const ANN_PRIORITY_CONFIG = {
  urgent: { label: "Urgent", bg: "bg-red-50", border: "border-red-200", badge: "bg-red-100 text-red-700", icon: AlertTriangle, iconColor: "text-red-500" },
  high:   { label: "High",   bg: "bg-amber-50", border: "border-amber-200", badge: "bg-amber-100 text-amber-700", icon: Bell, iconColor: "text-amber-500" },
  normal: { label: "Info",   bg: "bg-white", border: "border-gray-100", badge: "bg-blue-50 text-blue-600", icon: Info, iconColor: "text-blue-400" },
};

function AdminAnnouncementsTab() {
  const utils = trpc.useUtils();
  const { data: announcements = [], isLoading } = trpc.announcements.listAll.useQuery();
  const createMutation = trpc.announcements.create.useMutation({
    onSuccess: () => { utils.announcements.listAll.invalidate(); toast.success("Announcement published!"); setShowForm(false); setForm({ title: "", body: "", priority: "normal", pinned: false, displayDate: "", mediaUrl: "", mediaKind: "" }); },
    onError: (e) => toast.error(e.message),
  });
  const updateMutation = trpc.announcements.update.useMutation({
    onSuccess: () => { utils.announcements.listAll.invalidate(); toast.success("Announcement updated"); setEditingId(null); },
    onError: (e) => toast.error(e.message),
  });
  const togglePinMutation = trpc.announcements.togglePin.useMutation({
    onSuccess: () => { utils.announcements.listAll.invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const deleteMutation = trpc.announcements.delete.useMutation({
    onSuccess: () => { utils.announcements.listAll.invalidate(); toast.success("Announcement deleted"); },
    onError: (e) => toast.error(e.message),
  });

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ title: "", body: "", priority: "normal" as "normal" | "high" | "urgent", pinned: false, displayDate: "", mediaUrl: "", mediaKind: "" as "" | "image" | "video" });
  const [editForm, setEditForm] = useState({ title: "", body: "", priority: "normal" as "normal" | "high" | "urgent", isActive: true, displayDate: "", mediaUrl: "", mediaKind: "" as "" | "image" | "video" });
  const [mediaUploading, setMediaUploading] = useState(false);

  const startEdit = (ann: any) => {
    setEditingId(ann.id);
    const dd = ann.displayDate ? new Date(ann.displayDate).toISOString().slice(0, 16) : "";
    const mediaKind: "" | "image" | "video" = ann.mediaType === "image" ? "image" : ann.mediaType && ann.mediaType !== "none" ? "video" : "";
    setEditForm({ title: ann.title, body: ann.body, priority: ann.priority, isActive: ann.isActive, displayDate: dd, mediaUrl: ann.mediaUrl ?? "", mediaKind });
  };

  const uploadAnnouncementImage = async (file: File, target: "create" | "edit") => {
    setMediaUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/upload/announcement-media", { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Upload failed");
      if (target === "create") setForm(f => ({ ...f, mediaUrl: data.url, mediaKind: "image" }));
      else setEditForm(f => ({ ...f, mediaUrl: data.url, mediaKind: "image" }));
      toast.success("Image uploaded");
    } catch (err: any) {
      toast.error(err.message ?? "Upload failed");
    }
    setMediaUploading(false);
  };

  const handleCreate = () => {
    createMutation.mutate({
      title: form.title,
      body: form.body,
      priority: form.priority,
      pinned: form.pinned,
      displayDate: form.displayDate ? new Date(form.displayDate).toISOString() : null,
      mediaUrl: form.mediaUrl || null,
      mediaTypeOverride: form.mediaKind === "image" ? "image" : undefined,
    });
  };

  const handleUpdate = (ann: any) => {
    updateMutation.mutate({
      id: ann.id,
      title: editForm.title,
      body: editForm.body,
      priority: editForm.priority,
      isActive: editForm.isActive,
      displayDate: editForm.displayDate ? new Date(editForm.displayDate).toISOString() : null,
      mediaUrl: editForm.mediaUrl || null,
      mediaTypeOverride: editForm.mediaKind === "image" ? "image" : undefined,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-1">Announcements</h2>
          <p className="text-sm text-gray-500">Create and manage announcements that are pushed to all agents on their dashboard overview.</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#84ab4a] hover:bg-[#6d8f3a] text-white gap-2">
          <Plus className="w-4 h-4" /> New Announcement
        </Button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="bg-white border border-[#84ab4a]/30 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Megaphone className="w-4 h-4 text-[#84ab4a]" />
            <span className="font-semibold text-gray-800">New Announcement</span>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Title</label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. System Maintenance on March 25" className="text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Message</label>
              <textarea
                value={form.body}
                onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                placeholder="Write your announcement here..."
                rows={4}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40 focus:border-[#84ab4a] resize-none"
              />
            </div>
            <div className="flex flex-wrap gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Priority</label>
                <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value as any }))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40">
                  <option value="normal">Info (Normal)</option>
                  <option value="high">High Priority</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Display Date (optional)</label>
                <input type="datetime-local" value={form.displayDate} onChange={e => setForm(f => ({ ...f, displayDate: e.target.value }))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40" />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={form.pinned} onChange={e => setForm(f => ({ ...f, pinned: e.target.checked }))} className="rounded" />
                  <Pin className="w-3.5 h-3.5 text-gray-400" /> Pin to top
                </label>
              </div>
            </div>
            <AnnouncementMediaField
              mediaUrl={form.mediaUrl}
              mediaKind={form.mediaKind}
              uploading={mediaUploading}
              onUpload={(file) => uploadAnnouncementImage(file, "create")}
              onVideoUrlChange={(url) => setForm(f => ({ ...f, mediaUrl: url, mediaKind: url ? "video" : "" }))}
              onClear={() => setForm(f => ({ ...f, mediaUrl: "", mediaKind: "" }))}
            />
            <div className="flex gap-2 pt-1">
              <Button onClick={handleCreate} disabled={!form.title || !form.body || createMutation.isPending} className="bg-[#84ab4a] hover:bg-[#6d8f3a] text-white">
                {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
                <span className="ml-1.5">Publish</span>
              </Button>
              <Button variant="outline" onClick={() => { setShowForm(false); setForm({ title: "", body: "", priority: "normal", pinned: false, displayDate: "", mediaUrl: "", mediaKind: "" }); }}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Announcements list */}
      {isLoading ? (
        <div className="py-12 text-center text-gray-400"><Loader2 className="w-6 h-6 mx-auto animate-spin opacity-40" /></div>
      ) : announcements.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-2xl p-12 text-center">
          <Megaphone className="w-10 h-10 mx-auto mb-3 text-gray-200" />
          <p className="text-gray-500 font-medium">No announcements yet</p>
          <p className="text-sm text-gray-400 mt-1">Create your first announcement to push it to all agents.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {(announcements as any[]).map((ann) => {
            const cfg = ANN_PRIORITY_CONFIG[ann.priority as keyof typeof ANN_PRIORITY_CONFIG] ?? ANN_PRIORITY_CONFIG.normal;
            const IconComp = cfg.icon;
            const isEditing = editingId === ann.id;
            const displayDateStr = ann.displayDate
              ? new Date(ann.displayDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
              : new Date(ann.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
            return (
              <div key={ann.id} className={`rounded-2xl border shadow-sm overflow-hidden ${cfg.bg} ${cfg.border} ${!ann.isActive ? "opacity-50" : ""}`}>
                <div className={`px-5 py-3 flex items-center gap-3 border-b ${cfg.border}`}>
                  <IconComp className={`w-4 h-4 flex-shrink-0 ${cfg.iconColor}`} />
                  {ann.pinned && <Pin className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" aria-label="Pinned" />}
                  <span className="font-semibold text-gray-900 text-sm flex-1">{ann.title}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${cfg.badge}`}>{cfg.label}</span>
                  {!ann.isActive && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Hidden</span>}
                  <span className="text-xs text-gray-400">{displayDateStr}</span>
                  <div className="flex items-center gap-1 ml-2">
                    <button onClick={() => togglePinMutation.mutate({ id: ann.id })} className={`p-1.5 rounded-lg hover:bg-white/60 transition-colors ${ann.pinned ? "text-amber-500" : "text-gray-400 hover:text-amber-500"}`} title={ann.pinned ? "Unpin" : "Pin to top"}>
                      {ann.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => isEditing ? setEditingId(null) : startEdit(ann)} className="p-1.5 rounded-lg hover:bg-white/60 text-gray-400 hover:text-gray-700 transition-colors" title="Edit">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => updateMutation.mutate({ id: ann.id, isActive: !ann.isActive })} className="p-1.5 rounded-lg hover:bg-white/60 text-gray-400 hover:text-gray-700 transition-colors" title={ann.isActive ? "Hide" : "Show"}>
                      {ann.isActive ? <PauseCircle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => { if (confirm("Delete this announcement?")) deleteMutation.mutate({ id: ann.id }); }} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors" title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {isEditing ? (
                  <div className="px-5 py-4 space-y-3">
                    <Input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} className="text-sm" />
                    <textarea value={editForm.body} onChange={e => setEditForm(f => ({ ...f, body: e.target.value }))} rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40 resize-none" />
                    <div className="flex flex-wrap items-center gap-3">
                      <select value={editForm.priority} onChange={e => setEditForm(f => ({ ...f, priority: e.target.value as any }))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none">
                        <option value="normal">Info (Normal)</option>
                        <option value="high">High Priority</option>
                        <option value="urgent">Urgent</option>
                      </select>
                      <div>
                        <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Display Date</label>
                        <input type="datetime-local" value={editForm.displayDate} onChange={e => setEditForm(f => ({ ...f, displayDate: e.target.value }))} className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none" />
                      </div>
                      <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                        <input type="checkbox" checked={editForm.isActive} onChange={e => setEditForm(f => ({ ...f, isActive: e.target.checked }))} className="rounded" />
                        Active (visible to agents)
                      </label>
                    </div>
                    <AnnouncementMediaField
                      mediaUrl={editForm.mediaUrl}
                      mediaKind={editForm.mediaKind}
                      uploading={mediaUploading}
                      onUpload={(file) => uploadAnnouncementImage(file, "edit")}
                      onVideoUrlChange={(url) => setEditForm(f => ({ ...f, mediaUrl: url, mediaKind: url ? "video" : "" }))}
                      onClear={() => setEditForm(f => ({ ...f, mediaUrl: "", mediaKind: "" }))}
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleUpdate(ann)} disabled={updateMutation.isPending} className="bg-[#84ab4a] hover:bg-[#6d8f3a] text-white">
                        {updateMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        <span className="ml-1">Save</span>
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="px-5 py-4">
                    <AnnouncementMedia mediaUrl={ann.mediaUrl} mediaType={ann.mediaType} title={ann.title} />
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{ann.body}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Admin Associates Landing Page Content Tab ──────────────────────────────
// Content-only editor for /associates (client/src/pages/AssociateLanding.tsx):
// logo, hero headline/subtext, and the 6 perk cards' title/description.
// Layout/design/icons are NOT editable here by deliberate design (2026-07-22
// client ask: keep the design, just make the content changeable). Every
// field has a placeholder showing the live default, and an empty field on
// save falls back to that default (server-side, see
// server/_core/associateLandingContent.ts's sanitizeAssociateLandingContent).
const ASSOCIATE_LANDING_DEFAULT_LOGO_URL = "/associates/bizconnect360-logo.png";
const ASSOCIATE_LANDING_DEFAULT_HEADLINE = "Build a business that's actually yours.";
const ASSOCIATE_LANDING_DEFAULT_SUBTEXT = "Be a part of the BizConnect360 community and get real infrastructure, proven systems, and a team behind you — so you're building something lasting, never alone.";
const ASSOCIATE_LANDING_DEFAULT_PERKS = [
  { title: "Zero Barrier To Entry", description: "100% free to start. No licensing, no fine print, no upfront cost — just a real opportunity, open and waiting for you to take it." },
  { title: "Relationships That Compound", description: "An ecosystem built around trusted relationships and meaningful introductions — you'll never spend time prospecting again." },
  { title: "Real Technology & Systems", description: "A dedicated portal, ready-to-use materials, and tools built to support you — not busywork bolted on after the fact." },
  { title: "Training & Development", description: "Live coaching and ongoing education from people who've actually built this — not a PDF you're left to figure out alone." },
  { title: "Unlimited Income Potential", description: "There's no ceiling here. What you build — and what it's worth — is entirely up to you." },
  { title: "A Community, Not A Competition", description: "Join a network of like-minded people building alongside you — sharing what works instead of guarding it." },
];

type AssociateLandingForm = {
  logoUrl: string;
  heroHeadline: string;
  heroSubtext: string;
  perks: { title: string; description: string }[];
};

const EMPTY_ASSOCIATE_LANDING_FORM: AssociateLandingForm = {
  logoUrl: "",
  heroHeadline: "",
  heroSubtext: "",
  perks: ASSOCIATE_LANDING_DEFAULT_PERKS.map(() => ({ title: "", description: "" })),
};

function AdminAssociateLandingTab() {
  const utils = trpc.useUtils();
  const { data: content, isLoading } = trpc.associateLanding.getContent.useQuery();
  const [form, setForm] = useState<AssociateLandingForm>(EMPTY_ASSOCIATE_LANDING_FORM);
  const hydrated = useRef(false);
  const [logoUploading, setLogoUploading] = useState(false);

  // Hydrate local form state from the server exactly once per page load —
  // an admin's in-progress edits must never be clobbered by a background
  // refetch (e.g. on window refocus).
  useEffect(() => {
    if (hydrated.current || !content) return;
    hydrated.current = true;
    setForm({
      logoUrl: content.logoUrl ?? "",
      heroHeadline: content.heroHeadline ?? "",
      heroSubtext: content.heroSubtext ?? "",
      perks: content.perks && content.perks.length === ASSOCIATE_LANDING_DEFAULT_PERKS.length
        ? content.perks
        : EMPTY_ASSOCIATE_LANDING_FORM.perks,
    });
  }, [content]);

  const updateMutation = trpc.associateLanding.updateContent.useMutation({
    onSuccess: () => { utils.associateLanding.getContent.invalidate(); toast.success("Associates landing page updated"); },
    onError: (e) => toast.error(e.message),
  });

  const uploadLogo = async (file: File) => {
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/upload/associate-landing-media", { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Upload failed");
      setForm(f => ({ ...f, logoUrl: data.url }));
      toast.success("Logo uploaded — click Save to publish it");
    } catch (err: any) {
      toast.error(err.message ?? "Upload failed");
    }
    setLogoUploading(false);
  };

  const updatePerk = (i: number, field: "title" | "description", value: string) => {
    setForm(f => ({ ...f, perks: f.perks.map((p, idx) => (idx === i ? { ...p, [field]: value } : p)) }));
  };

  const handleSave = () => {
    updateMutation.mutate({
      logoUrl: form.logoUrl.trim() || null,
      heroHeadline: form.heroHeadline.trim() || null,
      heroSubtext: form.heroSubtext.trim() || null,
      perks: form.perks,
    });
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }

  const previewLogo = form.logoUrl.trim() || ASSOCIATE_LANDING_DEFAULT_LOGO_URL;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-1">Associates Landing Page</h2>
        <p className="text-sm text-gray-500">
          Edit the logo, hero copy, and perk text shown on <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">/associates</code> — the shared associate invitation link. Layout and design stay fixed; leave any field blank to use the default shown as its placeholder.
        </p>
      </div>

      {/* Logo */}
      <div className="bg-white border border-[#EBE2D4] rounded-2xl p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <ImageIcon className="w-4 h-4 text-[#7B4FD4]" />
          <span className="font-semibold text-gray-800">Logo</span>
        </div>
        <div className="flex items-center gap-5">
          <div className="w-40 h-16 bg-[#0c1f3a] rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
            <img src={previewLogo} alt="Logo preview" className="max-w-[85%] max-h-[70%] object-contain" />
          </div>
          <div className="flex-1 space-y-2">
            <label className={`inline-flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-lg border cursor-pointer transition-colors ${logoUploading ? "opacity-50 pointer-events-none" : "hover:bg-gray-50"} border-gray-200 text-gray-700`}>
              {logoUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              Upload new logo
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ""; }} />
            </label>
            {form.logoUrl && (
              <button onClick={() => setForm(f => ({ ...f, logoUrl: "" }))} className="ml-3 text-xs text-gray-400 hover:text-red-500">Reset to default</button>
            )}
          </div>
        </div>
      </div>

      {/* Hero copy */}
      <div className="bg-white border border-[#EBE2D4] rounded-2xl p-5 shadow-sm space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <LayoutTemplate className="w-4 h-4 text-[#7B4FD4]" />
          <span className="font-semibold text-gray-800">Hero Section</span>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Headline</label>
          <Input value={form.heroHeadline} onChange={e => setForm(f => ({ ...f, heroHeadline: e.target.value }))} placeholder={ASSOCIATE_LANDING_DEFAULT_HEADLINE} className="text-sm" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Subtext</label>
          <textarea
            value={form.heroSubtext}
            onChange={e => setForm(f => ({ ...f, heroSubtext: e.target.value }))}
            placeholder={ASSOCIATE_LANDING_DEFAULT_SUBTEXT}
            rows={3}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B4FD4]/40 focus:border-[#7B4FD4] resize-none"
          />
        </div>
      </div>

      {/* Perks */}
      <div className="bg-white border border-[#EBE2D4] rounded-2xl p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Star className="w-4 h-4 text-[#7B4FD4]" />
          <span className="font-semibold text-gray-800">"What You Get" Perks (6)</span>
        </div>
        <div className="space-y-4">
          {form.perks.map((perk, i) => (
            <div key={i} className="border border-gray-100 rounded-xl p-3 space-y-2">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">Perk {i + 1} Title</label>
              <Input value={perk.title} onChange={e => updatePerk(i, "title", e.target.value)} placeholder={ASSOCIATE_LANDING_DEFAULT_PERKS[i].title} className="text-sm" />
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">Perk {i + 1} Description</label>
              <textarea
                value={perk.description}
                onChange={e => updatePerk(i, "description", e.target.value)}
                placeholder={ASSOCIATE_LANDING_DEFAULT_PERKS[i].description}
                rows={2}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B4FD4]/40 focus:border-[#7B4FD4] resize-none"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={handleSave} disabled={updateMutation.isPending} className="bg-[#7B4FD4] hover:bg-[#6a3fc0] text-white gap-2">
          {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Changes
        </Button>
        <a href="/associates" target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-[#7B4FD4] hover:underline flex items-center gap-1">
          Preview page <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
}

// ─── Admin Vertical Referral Links Tab ─────────────────────────────────────────────────────────────────────────────────────
const VERTICAL_OPTIONS = ["Scale360", "Fundable360", "Share360 Business", "Share360 Nonprofit", "Legacy360", "Media360"];

const VERTICAL_COLORS_MAP: Record<string, string> = {
  "Scale360": "#E8620A", "Fundable360": "#84ab4a", "Share360 Business": "#1A6FD4",
  "Share360 Nonprofit": "#0FA8A8", "Legacy360": "#C8920A", "Media360": "#7B4FD4",
};

// Default fallback domains matching VERTICAL_CONFIG in RepDashboard
const VERTICAL_DEFAULT_DOMAINS: Record<string, string> = {
  "Scale360": "https://scale360.wibiz.ai/",
  "Fundable360": "https://fundable360.wibiz.ai/",
  "Share360 Business": "https://share360.wibiz.ai/",
  "Share360 Nonprofit": "https://share360.wibiz.ai/",
  "Legacy360": "https://legacy360.wibiz.ai/",
  "Media360": "https://media360.wibiz.ai/",
};

function MainReferralUrlsSection() {
  const utils = trpc.useUtils();
  const { data: savedUrls = {}, isLoading } = trpc.verticalLinks.getAllMainUrls.useQuery();
  const setMainUrl = trpc.verticalLinks.setMainUrl.useMutation({
    onSuccess: () => { toast.success("Main referral URL saved!"); utils.verticalLinks.getAllMainUrls.invalidate(); },
    onError: (e) => toast.error(e.message),
  });

  const [drafts, setDrafts] = useState<Record<string, string>>({});

  // Initialise drafts from saved values (or fallback defaults) once loaded
  const getUrl = (v: string) => drafts[v] ?? savedUrls[v] ?? VERTICAL_DEFAULT_DOMAINS[v] ?? "";

  const handleSave = (vertical: string) => {
    setMainUrl.mutate({ vertical, url: getUrl(vertical) });
  };

  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden mb-8">
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="text-sm font-bold text-gray-800">Main Referral Links</h3>
        <p className="text-xs text-gray-400 mt-0.5">
          The primary landing page URL for each vertical. Your rep code is appended automatically (<span className="font-mono text-[#84ab4a]">?ref=[repCode]</span>). Changes reflect immediately for all agents.
        </p>
      </div>
      {isLoading ? (
        <div className="px-5 py-6 text-center text-gray-400"><Loader2 className="w-5 h-5 mx-auto animate-spin opacity-40" /></div>
      ) : (
        <div className="divide-y divide-gray-50">
          {VERTICAL_OPTIONS.map(v => {
            const color = VERTICAL_COLORS_MAP[v] ?? "#84ab4a";
            const url = getUrl(v);
            const isSaved = savedUrls[v] === url || (!savedUrls[v] && url === (VERTICAL_DEFAULT_DOMAINS[v] ?? ""));
            return (
              <div key={v} className="px-5 py-3.5 flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
                <span className="text-sm font-semibold text-gray-700 w-36 flex-shrink-0">{v}</span>
                <input
                  type="url"
                  value={url}
                  onChange={e => setDrafts(d => ({ ...d, [v]: e.target.value }))}
                  placeholder={VERTICAL_DEFAULT_DOMAINS[v] ?? "https://..."}
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/30"
                />
                <button
                  onClick={() => handleSave(v)}
                  disabled={setMainUrl.isPending}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all"
                  style={isSaved && savedUrls[v] ? { background: "#f0f9eb", color: "#84ab4a", borderColor: "#84ab4a40" } : { background: "#84ab4a", color: "white", borderColor: "#84ab4a" }}
                >
                  {setMainUrl.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  {isSaved && savedUrls[v] ? "Saved" : "Save"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Admin controls for the Legacy360 125 Plan certification gate (video URLs +
// attempt history). Separate from the main Legacy360/Living Trust cert flow —
// nothing here affects Living Trust. Tag a specific product link above with
// "Legacy360 125 Plan Certification" to have it enforce this gate.
function Legacy360Plan125AdminPanel() {
  const utils = trpc.useUtils();
  const { data: videoUrls, isLoading } = trpc.legacy360Plan125.getVideoUrls.useQuery();
  const { data: attempts = [] } = trpc.legacy360Plan125.adminList.useQuery();
  const setVideoUrls = trpc.legacy360Plan125.adminSetVideoUrls.useMutation({
    onSuccess: () => { toast.success("125 Plan video URLs saved!"); utils.legacy360Plan125.getVideoUrls.invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const [drafts, setDrafts] = useState<{ video1Url: string; video2Url: string } | null>(null);
  const v1 = drafts?.video1Url ?? videoUrls?.video1Url ?? "";
  const v2 = drafts?.video2Url ?? videoUrls?.video2Url ?? "";
  const passedCount = attempts.filter((a: any) => a.passed).length;

  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden mb-8">
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="text-sm font-bold text-gray-800">Legacy360 125 Plan Certification</h3>
        <p className="text-xs text-gray-400 mt-0.5">
          Video 1 must finish before Video 2 unlocks; Video 2 must finish before the quiz unlocks. Passing (8/10) unlocks any referral link above tagged with this gate. {passedCount} associate{passedCount === 1 ? "" : "s"} certified.
        </p>
      </div>
      {isLoading ? (
        <div className="px-5 py-6 text-center text-gray-400"><Loader2 className="w-5 h-5 mx-auto animate-spin opacity-40" /></div>
      ) : (
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Video 1 URL</label>
            <input
              type="url"
              value={v1}
              onChange={e => setDrafts({ video1Url: e.target.value, video2Url: v2 })}
              placeholder="https://..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/30"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Video 2 URL</label>
            <input
              type="url"
              value={v2}
              onChange={e => setDrafts({ video1Url: v1, video2Url: e.target.value })}
              placeholder="https://..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/30"
            />
          </div>
          <Button
            onClick={() => setVideoUrls.mutate({ video1Url: v1 || null, video2Url: v2 || null })}
            disabled={setVideoUrls.isPending}
            className="bg-[#84ab4a] hover:bg-[#6d9340] text-white text-xs"
          >
            {setVideoUrls.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Save Video URLs
          </Button>
        </div>
      )}
    </div>
  );
}

function AdminVerticalLinksTab() {
  const utils = trpc.useUtils();
  const { data: links = [], isLoading } = trpc.verticalLinks.listAll.useQuery({});
  const createLink = trpc.verticalLinks.create.useMutation({
    onSuccess: () => { toast.success("Referral link added!"); utils.verticalLinks.listAll.invalidate(); setShowForm(false); resetForm(); },
    onError: (e) => toast.error(e.message),
  });
  const updateLink = trpc.verticalLinks.update.useMutation({
    onSuccess: () => { toast.success("Referral link updated!"); utils.verticalLinks.listAll.invalidate(); setEditingLink(null); },
    onError: (e) => toast.error(e.message),
  });
  const deleteLink = trpc.verticalLinks.delete.useMutation({
    onSuccess: () => { toast.success("Referral link deleted"); utils.verticalLinks.listAll.invalidate(); },
    onError: (e) => toast.error(e.message),
  });

  const [showForm, setShowForm] = useState(false);
  const [editingLink, setEditingLink] = useState<any | null>(null);
  const [form, setForm] = useState({ vertical: "Scale360", productName: "", baseUrl: "", description: "", sortOrder: 0, requiresCertKey: "" as string });

  const resetForm = () => setForm({ vertical: "Scale360", productName: "", baseUrl: "", description: "", sortOrder: 0, requiresCertKey: "" });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Editing writes into `editingLink` (see the input bindings below), not
    // `form` — `form` stays whatever it was before editing started (empty,
    // right after mount), so validating against it here rejected every edit.
    const activeProductName = editingLink ? editingLink.productName : form.productName;
    const activeBaseUrl = editingLink ? editingLink.baseUrl : form.baseUrl;
    if (!activeProductName?.trim() || !activeBaseUrl?.trim()) { toast.error("Product name and base URL are required"); return; }
    if (editingLink) {
      updateLink.mutate({ id: editingLink.id, ...editingLink, requiresCertKey: editingLink.requiresCertKey || null });
    } else {
      createLink.mutate({ ...form, requiresCertKey: form.requiresCertKey || null });
    }
  };

  // Group by vertical
  const grouped = VERTICAL_OPTIONS.reduce((acc, v) => {
    acc[v] = links.filter((l: any) => l.vertical === v);
    return acc;
  }, {} as Record<string, any[]>);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Referral Links</h2>
          <p className="text-sm text-gray-500 mt-0.5">Manage main and product-specific referral links per vertical. All changes reflect immediately for agents.</p>
        </div>
        <Button onClick={() => { setShowForm(true); setEditingLink(null); resetForm(); }} className="bg-[#84ab4a] hover:bg-[#6d9340] text-white">
          <Plus className="w-4 h-4 mr-1.5" /> Add Product Link
        </Button>
      </div>

      <MainReferralUrlsSection />

      <Legacy360Plan125AdminPanel />

      <h3 className="text-sm font-bold text-gray-700 mb-4">Product-Specific Links</h3>

      {(showForm || editingLink) && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
          <h3 className="text-sm font-bold text-gray-800 mb-4">{editingLink ? "Edit Referral Link" : "Add New Referral Link"}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Vertical</label>
                <select
                  value={editingLink ? editingLink.vertical : form.vertical}
                  onChange={e => editingLink ? setEditingLink({ ...editingLink, vertical: e.target.value }) : setForm(f => ({ ...f, vertical: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/30"
                >
                  {VERTICAL_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Product Name</label>
                <input
                  type="text"
                  placeholder="e.g. Business Credit Builder"
                  value={editingLink ? editingLink.productName : form.productName}
                  onChange={e => editingLink ? setEditingLink({ ...editingLink, productName: e.target.value }) : setForm(f => ({ ...f, productName: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/30"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Base URL <span className="text-gray-400 font-normal">(without ?ref= — that's appended automatically)</span></label>
              <input
                type="url"
                placeholder="https://fundable360.wibiz.ai/credit-builder"
                value={editingLink ? editingLink.baseUrl : form.baseUrl}
                onChange={e => editingLink ? setEditingLink({ ...editingLink, baseUrl: e.target.value }) : setForm(f => ({ ...f, baseUrl: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/30"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Description <span className="text-gray-400 font-normal">(optional)</span></label>
              <input
                type="text"
                placeholder="Brief description of this product"
                value={editingLink ? editingLink.description ?? "" : form.description}
                onChange={e => editingLink ? setEditingLink({ ...editingLink, description: e.target.value }) : setForm(f => ({ ...f, description: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/30"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Requires Certification <span className="text-gray-400 font-normal">(optional gate — leave blank to show to every certified rep as before)</span></label>
              <select
                value={editingLink ? editingLink.requiresCertKey ?? "" : form.requiresCertKey}
                onChange={e => editingLink ? setEditingLink({ ...editingLink, requiresCertKey: e.target.value }) : setForm(f => ({ ...f, requiresCertKey: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/30"
              >
                <option value="">None</option>
                <option value="legacy360_125plan">Legacy360 125 Plan Certification</option>
              </select>
            </div>
            <div className="flex gap-3 pt-1">
              <Button type="submit" className="bg-[#84ab4a] hover:bg-[#6d9340] text-white" disabled={createLink.isPending || updateLink.isPending}>
                {(createLink.isPending || updateLink.isPending) ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingLink ? "Save Changes" : "Add Link")}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingLink(null); }}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {isLoading ? (
        <div className="py-16 text-center text-gray-400"><Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin opacity-40" /></div>
      ) : (
        <div className="space-y-6">
          {VERTICAL_OPTIONS.map(vertical => {
            const vLinks = grouped[vertical] ?? [];
            const color = VERTICAL_COLORS_MAP[vertical] ?? "#84ab4a";
            return (
              <div key={vertical} className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
                  <span className="text-sm font-bold text-gray-800">{vertical}</span>
                  <span className="ml-auto text-xs text-gray-400">{vLinks.length} product link{vLinks.length !== 1 ? "s" : ""}</span>
                </div>
                {vLinks.length === 0 ? (
                  <div className="px-5 py-6 text-center text-gray-400">
                    <Link2 className="w-6 h-6 mx-auto mb-1.5 opacity-30" />
                    <p className="text-xs">No product links for {vertical} yet.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-50">
                    {vLinks.map((link: any) => (
                      <div key={link.id} className="px-5 py-3.5 flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                            {link.productName}
                            {link.requiresCertKey && (
                              <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Gated: {link.requiresCertKey}</span>
                            )}
                          </div>
                          {link.description && <div className="text-xs text-gray-400 mt-0.5">{link.description}</div>}
                          <div className="font-mono text-xs text-gray-500 mt-1 truncate">{link.baseUrl}<span className="text-[#84ab4a]">?ref=[repCode]</span></div>
                        </div>
                        <button
                          onClick={() => { setEditingLink({ ...link }); setShowForm(false); }}
                          className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100 transition-all"
                        >
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                        <button
                          onClick={() => { if (confirm(`Delete "${link.productName}"?`)) deleteLink.mutate({ id: link.id }); }}
                          className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-red-50 text-red-600 border border-red-100 hover:bg-red-100 transition-all"
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Admin Quiz Questions Tab ──────────────────────────────────────────────────
const QUIZ_VERTICALS = ["Fundable360", "Share360", "Legacy360", "Scale360"] as const;
type QuizVertical = typeof QUIZ_VERTICALS[number];

function AdminQuizQuestionsTab() {
  const utils = trpc.useUtils();
  const [selectedVertical, setSelectedVertical] = useState<QuizVertical>("Fundable360");
  const { data: questions = [], isLoading } = trpc.certification.adminListQuestions.useQuery({ vertical: selectedVertical });

  const createQ = trpc.certification.adminCreateQuestion.useMutation({
    onSuccess: () => { toast.success("Question added!"); utils.certification.adminListQuestions.invalidate(); setShowForm(false); resetForm(); },
    onError: (e) => toast.error(e.message),
  });
  const updateQ = trpc.certification.adminUpdateQuestion.useMutation({
    onSuccess: () => { toast.success("Question updated!"); utils.certification.adminListQuestions.invalidate(); setEditingQ(null); },
    onError: (e) => toast.error(e.message),
  });
  const deleteQ = trpc.certification.adminDeleteQuestion.useMutation({
    onSuccess: () => { toast.success("Question deleted"); utils.certification.adminListQuestions.invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const seedDefaults = trpc.certification.adminSeedDefaults.useMutation({
    onSuccess: (data) => { toast.success(`${data.seeded} built-in questions loaded!`); utils.certification.adminListQuestions.invalidate(); },
    onError: (e) => toast.error(e.message),
  });

  const blankForm = { questionId: "", question: "", options: ["", "", "", ""], correctAnswer: "", sortOrder: 0 };
  const [showForm, setShowForm] = useState(false);
  const [editingQ, setEditingQ] = useState<any | null>(null);
  const [form, setForm] = useState({ ...blankForm });

  const resetForm = () => setForm({ ...blankForm });

  const activeForm = editingQ ?? form;
  const setActiveForm = (patch: any) => editingQ ? setEditingQ((p: any) => ({ ...p, ...patch })) : setForm(p => ({ ...p, ...patch }));

  const getOptions = () => {
    if (editingQ) return Array.isArray(editingQ.optionsJson) ? editingQ.optionsJson : JSON.parse(editingQ.optionsJson ?? "[]");
    return form.options;
  };
  const setOption = (idx: number, val: string) => {
    const opts = [...getOptions()];
    opts[idx] = val;
    setActiveForm({ options: opts, ...(editingQ ? { optionsJson: JSON.stringify(opts) } : {}) });
  };
  const addOption = () => {
    const opts = [...getOptions(), ""];
    setActiveForm({ options: opts, ...(editingQ ? { optionsJson: JSON.stringify(opts) } : {}) });
  };
  const removeOption = (idx: number) => {
    const opts = getOptions().filter((_: string, i: number) => i !== idx);
    setActiveForm({ options: opts, ...(editingQ ? { optionsJson: JSON.stringify(opts) } : {}) });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const opts = getOptions().filter((o: string) => o.trim());
    const qText = editingQ ? editingQ.question : form.question;
    const correct = editingQ ? editingQ.correctAnswer : form.correctAnswer;
    if (!qText.trim()) { toast.error("Question text is required"); return; }
    if (opts.length < 2) { toast.error("At least 2 options required"); return; }
    if (!correct.trim() || !opts.includes(correct)) { toast.error("Correct answer must match one of the options exactly"); return; }
    if (editingQ) {
      updateQ.mutate({ id: editingQ.id, question: qText, options: opts, correctAnswer: correct, sortOrder: editingQ.sortOrder ?? 0 });
    } else {
      const qid = form.questionId.trim() || `${selectedVertical.toLowerCase().replace("360", "")}_q${Date.now()}`;
      createQ.mutate({ vertical: selectedVertical, questionId: qid, question: qText, options: opts, correctAnswer: correct, sortOrder: form.sortOrder });
    }
  };

  const VERTICAL_COLORS: Record<string, string> = { Fundable360: "#84ab4a", Share360: "#1A6FD4", Legacy360: "#C8920A", Scale360: "#E8620A" };
  const color = VERTICAL_COLORS[selectedVertical] ?? "#84ab4a";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Quiz Questions</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage certification quiz questions per vertical. When DB questions exist for a vertical they override the built-in defaults.
          </p>
        </div>
        <Button onClick={() => { setShowForm(true); setEditingQ(null); resetForm(); }} className="bg-[#84ab4a] hover:bg-[#6d9340] text-white">
          <Plus className="w-4 h-4 mr-1.5" /> Add Question
        </Button>
      </div>

      {/* Vertical selector */}
      <div className="flex gap-2 mb-6">
        {QUIZ_VERTICALS.map(v => (
          <button
            key={v}
            onClick={() => setSelectedVertical(v)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-all ${selectedVertical === v ? "text-white border-transparent" : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"}`}
            style={selectedVertical === v ? { background: VERTICAL_COLORS[v], borderColor: VERTICAL_COLORS[v] } : {}}
          >
            {v}
          </button>
        ))}
      </div>

      {/* Add / edit form */}
      {(showForm || editingQ) && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
          <h3 className="text-sm font-bold text-gray-800 mb-4">{editingQ ? "Edit Question" : "Add New Question"}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!editingQ && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Question ID <span className="text-gray-400 font-normal">(auto-generated if blank)</span></label>
                  <input
                    type="text"
                    placeholder="e.g. f360_q1"
                    value={form.questionId}
                    onChange={e => setForm(p => ({ ...p, questionId: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/30"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Sort Order</label>
                  <input
                    type="number"
                    min={0}
                    value={form.sortOrder}
                    onChange={e => setForm(p => ({ ...p, sortOrder: parseInt(e.target.value) || 0 }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/30"
                  />
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Question Text</label>
              <textarea
                rows={2}
                placeholder="Enter the question..."
                value={editingQ ? editingQ.question : form.question}
                onChange={e => setActiveForm({ question: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/30 resize-none"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-semibold text-gray-600">Answer Options</label>
                <button type="button" onClick={addOption} className="text-xs text-[#84ab4a] hover:underline">+ Add option</button>
              </div>
              <div className="space-y-2">
                {getOptions().map((opt: string, idx: number) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="correctAnswer"
                      checked={(editingQ ? editingQ.correctAnswer : form.correctAnswer) === opt && opt.trim() !== ""}
                      onChange={() => setActiveForm({ correctAnswer: opt })}
                      title="Mark as correct answer"
                      className="shrink-0"
                    />
                    <input
                      type="text"
                      placeholder={`Option ${idx + 1}`}
                      value={opt}
                      onChange={e => setOption(idx, e.target.value)}
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/30"
                    />
                    {getOptions().length > 2 && (
                      <button type="button" onClick={() => removeOption(idx)} className="text-red-400 hover:text-red-600 shrink-0">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-1.5">Click the radio button next to the correct answer.</p>
            </div>
            <div className="flex gap-3 pt-1">
              <Button type="submit" className="bg-[#84ab4a] hover:bg-[#6d9340] text-white" disabled={createQ.isPending || updateQ.isPending}>
                {(createQ.isPending || updateQ.isPending) ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingQ ? "Save Changes" : "Add Question")}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingQ(null); }}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {isLoading ? (
        <div className="py-16 text-center text-gray-400"><Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin opacity-40" /></div>
      ) : questions.length === 0 ? (
        <div className="bg-gray-50 border border-dashed border-gray-200 rounded-2xl p-12 text-center">
          <BookOpen className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="font-semibold text-gray-400 mb-1">No questions for {selectedVertical} yet</p>
          <p className="text-sm text-gray-400 mb-5">The built-in quiz is active. Load the defaults to start tweaking.</p>
          <Button
            onClick={() => seedDefaults.mutate({ vertical: selectedVertical })}
            disabled={seedDefaults.isPending}
            className="bg-[#84ab4a] hover:bg-[#6d9340] text-white"
          >
            {seedDefaults.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <BookOpen className="w-4 h-4 mr-1.5" />}
            Load Built-in Questions
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-gray-400">{questions.length} question{questions.length !== 1 ? "s" : ""} — these override the built-in quiz for {selectedVertical}</p>
          {questions.map((q: any, idx: number) => (
            <div key={q.id} className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ background: color }}>
                  {idx + 1}
                </div>
                <span className="flex-1 text-sm font-medium text-gray-800 truncate">{q.question}</span>
                <span className="text-[10px] text-gray-400 font-mono ml-auto mr-3">{q.questionId}</span>
                <button
                  onClick={() => { setEditingQ({ ...q, optionsJson: q.optionsJson, correctAnswer: q.correctAnswer }); setShowForm(false); }}
                  className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100 transition-all"
                >
                  <Pencil className="w-3 h-3" /> Edit
                </button>
                <button
                  onClick={() => { if (confirm("Delete this question?")) deleteQ.mutate({ id: q.id }); }}
                  className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-red-50 text-red-600 border border-red-100 hover:bg-red-100 transition-all"
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
              <div className="px-5 py-3">
                {(Array.isArray(q.optionsJson) ? q.optionsJson : JSON.parse(q.optionsJson ?? "[]")).map((opt: string) => (
                  <div key={opt} className={`flex items-center gap-2 py-0.5 text-sm ${opt === q.correctAnswer ? "text-green-700 font-medium" : "text-gray-500"}`}>
                    <span className={`w-3.5 h-3.5 rounded-full border flex-shrink-0 ${opt === q.correctAnswer ? "bg-green-500 border-green-500" : "border-gray-300"}`} />
                    {opt}
                    {opt === q.correctAnswer && <span className="text-[10px] text-green-500 font-bold ml-1">✓ CORRECT</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Consultation Eligibility Gate card ──────────────────────────────────────
// One card per gated vertical (Share360, Legacy360 Living Trust), each with
// its own independent app_settings enforcement flag - see server/routers.ts
// consultationRouter. Unset/"false" = shadow mode (real behavior unchanged,
// would-block results only logged to complianceLogs); "true" = enforced.
// Agency Partner approval has been removed entirely - the client decided
// only the automated prerequisite checks below should ever gate scheduling.
// There is no Agency Partner review/approval control anywhere in the app.
function ConsultationGateCard({
  title,
  icon,
  description,
  settingKey,
  confirmMessage,
  emptyMessage,
  gateSummary,
  recentActivity,
  showProposalAction,
  onRefetchActivity,
}: {
  title: string;
  icon: ReactNode;
  description: string;
  settingKey: string;
  confirmMessage: string;
  emptyMessage: string;
  gateSummary?: { totalLeadsChecked: number; eligibleCount: number; blockedCount: number; blockedLeads: any[] };
  recentActivity: any[];
  showProposalAction?: boolean;
  onRefetchActivity: () => void;
}) {
  const gateSettingQuery = (trpc as any).appSettings.get.useQuery({ key: settingKey });
  const setGateSetting = (trpc as any).appSettings.set.useMutation({
    onSuccess: () => { toast.success("Consultation gate setting saved"); gateSettingQuery.refetch(); },
    onError: () => toast.error("Failed to save consultation gate setting"),
  });
  const gateEnforced = gateSettingQuery.data === "true";
  const [showActivityFeed, setShowActivityFeed] = useState(false);

  const markProposalGenerated = (trpc as any).consultation.markProposalGenerated.useMutation({
    onSuccess: () => { toast.success("Marked — Merchant Proposal Generated."); onRefetchActivity(); },
    onError: (e: any) => toast.error(e.message || "Failed to update."),
  });
  const adminOverride = (trpc as any).consultation.adminOverride.useMutation({
    onSuccess: () => { toast.success("Override applied — this lead can now schedule."); onRefetchActivity(); },
    onError: (e: any) => toast.error(e.message || "Failed to override."),
  });

  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden mb-6">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
        {icon}
        <span className="font-semibold text-gray-900">{title}</span>
      </div>
      <div className="px-5 py-4">
        <p className="text-sm text-gray-500 mb-4">{description}</p>
        <div className="flex items-center justify-between gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100">
          <div>
            <div className="text-sm font-semibold text-gray-800 mb-0.5">
              {gateEnforced ? "Enforced — button is disabled until requirements are met" : "Shadow mode — logging only, button behaves as before"}
            </div>
            <div className="text-xs text-gray-400">
              {gateSettingQuery.isLoading
                ? "Loading current status..."
                : gateEnforced
                  ? "Every check is logged to Compliance Logs."
                  : "Flip this on once you've reviewed shadow-mode Compliance Logs and are ready to actually block ineligible leads."}
            </div>
          </div>
          <Switch
            checked={gateEnforced}
            disabled={gateSettingQuery.isLoading || setGateSetting.isPending}
            onCheckedChange={(checked: boolean) => {
              if (checked && !window.confirm(confirmMessage)) return;
              setGateSetting.mutate({ key: settingKey, value: checked ? "true" : "false" });
            }}
          />
        </div>

        {/* Shadow-mode results — review before flipping the switch above */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          {!gateSummary || gateSummary.totalLeadsChecked === 0 ? (
            <div className="text-xs text-gray-400">{emptyMessage}</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-100">
                  <div className="text-xl font-bold font-mono text-gray-700">{gateSummary.totalLeadsChecked}</div>
                  <div className="text-[10px] text-gray-400 uppercase font-semibold">Leads Checked</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                  <div className="text-xl font-bold font-mono text-emerald-600">{gateSummary.eligibleCount}</div>
                  <div className="text-[10px] text-emerald-500 uppercase font-semibold">Would Pass Today</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-amber-50 border border-amber-100">
                  <div className="text-xl font-bold font-mono text-amber-600">{gateSummary.blockedCount}</div>
                  <div className="text-[10px] text-amber-500 uppercase font-semibold">Would Be Blocked</div>
                </div>
              </div>

              {gateSummary.blockedLeads.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs font-semibold text-gray-600 mb-2">Currently would be blocked:</div>
                  <div className="space-y-1.5 max-h-64 overflow-y-auto">
                    {gateSummary.blockedLeads.map((l: any) => {
                      const missingProposalOnly = showProposalAction && l.missingDocs.length === 1 && l.missingDocs[0]?.startsWith("Merchant Proposal");
                      return (
                        <div key={l.leadId} className="flex items-start justify-between gap-3 p-2.5 rounded-lg bg-amber-50/50 border border-amber-100">
                          <div className="min-w-0">
                            <div className="text-xs font-semibold text-gray-800 truncate">{l.leadName}{l.businessName ? ` · ${l.businessName}` : ""}</div>
                            <div className="text-[10px] text-gray-400">{l.verticalName}</div>
                            <div className="text-[10px] text-amber-600 mt-0.5">
                              Missing: {l.missingDocs.join(", ") || "requirements"}
                            </div>
                            <div className="flex items-center gap-2 mt-1.5">
                              {showProposalAction && (
                                <button
                                  onClick={() => markProposalGenerated.mutate({ leadId: l.leadId })}
                                  disabled={markProposalGenerated.isPending}
                                  className={`text-[10px] font-semibold px-2 py-1 rounded-md border transition-colors disabled:opacity-50 ${
                                    missingProposalOnly
                                      ? "bg-[#84ab4a] text-white border-[#84ab4a] hover:bg-[#6e9038]"
                                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                                  }`}
                                >
                                  Mark Proposal Generated
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  const reason = window.prompt("Reason for overriding this lead's consultation gate (required):");
                                  if (reason && reason.trim()) adminOverride.mutate({ leadId: l.leadId, reason: reason.trim() });
                                }}
                                disabled={adminOverride.isPending}
                                className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                              >
                                <Unlock className="w-3 h-3" /> Override
                              </button>
                            </div>
                          </div>
                          <div className="text-[10px] text-gray-300 flex-shrink-0 whitespace-nowrap">
                            {new Date(l.lastCheckedAt).toLocaleDateString()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <button
                onClick={() => setShowActivityFeed(v => !v)}
                className="text-xs text-[#84ab4a] hover:text-[#6e9038] font-semibold flex items-center gap-1"
              >
                {showActivityFeed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                Recent activity ({recentActivity.length})
              </button>
              {showActivityFeed && (
                <div className="mt-2 space-y-1.5 max-h-56 overflow-y-auto">
                  {recentActivity.length === 0 ? (
                    <div className="text-xs text-gray-400">No activity recorded yet.</div>
                  ) : recentActivity.map((log: any) => (
                    <div key={log.id} className="text-[11px] p-2 rounded-lg bg-gray-50 border border-gray-100">
                      <span className="font-semibold text-gray-700">{log.action.replace(/_/g, " ")}</span>
                      <span className="text-gray-400"> · lead #{log.entityId} · {log.actorId} · {new Date(log.createdAt).toLocaleString()}</span>
                      {log.details?.note && <div className="text-gray-500 mt-0.5">Note: {log.details.note}</div>}
                      {log.details?.reason && <div className="text-gray-500 mt-0.5">Reason: {log.details.reason}</div>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AdminSettingsTab() {
  const allSettings = trpc.settings.getAll.useQuery();
  const updateSetting = trpc.settings.update.useMutation({
    onSuccess: () => {
      toast.success("Setting saved");
      allSettings.refetch();
    },
    onError: () => toast.error("Failed to save setting"),
  });
  const [editValues, setEditValues] = useState<Record<string, string>>({});

  // Hero message (daily motivational line in the agent dashboard)
  const heroMessageQuery = (trpc as any).appSettings.get.useQuery({ key: "hero_message" });
  const setHeroMessage   = (trpc as any).appSettings.set.useMutation({
    onSuccess: () => { toast.success("Hero message saved"); heroMessageQuery.refetch(); },
    onError:   () => toast.error("Failed to save hero message"),
  });
  const [heroMessageDraft, setHeroMessageDraft] = useState<string | null>(null);
  const heroMessageCurrent: string = heroMessageDraft ?? heroMessageQuery.data ?? "";

  // Consultation eligibility gate activity - shared across both per-vertical
  // gate cards below (Share360, Legacy360 Living Trust) - see
  // server/routers.ts consultationRouter.getGateActivity.
  const gateActivityQuery = (trpc as any).consultation.getGateActivity.useQuery(undefined, { refetchInterval: 60_000 });
  const share360RecentActivity = (gateActivityQuery.data?.recentActivity ?? []).filter((log: any) =>
    ((log.details?.verticalName ?? "") as string).startsWith("Share360") || log.action.startsWith("share360_") || log.action === "agency_partner_consultation_review"
  );
  const legacy360RecentActivity = (gateActivityQuery.data?.recentActivity ?? []).filter((log: any) =>
    ((log.details?.verticalName ?? "") as string) === "Legacy360" || log.action.startsWith("legacy360_")
  );

  const getValue = (key: string) => {
    if (editValues[key] !== undefined) return editValues[key];
    return allSettings.data?.find((s: any) => s.key === key)?.value ?? "";
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-bold text-gray-900 mb-1">System Settings</h2>
        <p className="text-sm text-gray-400">Configure Calendar IDs and other system-wide settings. Changes take effect immediately.</p>
      </div>

      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden mb-6">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#84ab4a]" />
          <span className="font-semibold text-gray-900">Calendar IDs</span>
        </div>
        <div className="divide-y divide-gray-50">
          {CALENDAR_SETTINGS.map(({ key, label, description }) => (
            <div key={key} className="px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="font-medium text-sm text-gray-900 mb-0.5">{label}</div>
                  <div className="text-xs text-gray-400 mb-2">{description}</div>
                  <div className="text-xs text-gray-300 font-mono">Embed URL: https://api.wibiz.ai/widget/booking/<span className="text-[#84ab4a]">{getValue(key) || "<calendar-id>"}</span></div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <input
                    className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono w-52 focus:outline-none focus:border-[#84ab4a]/60 focus:ring-1 focus:ring-[#84ab4a]/20"
                    placeholder="Enter Calendar ID"
                    value={getValue(key)}
                    onChange={(e) => setEditValues(prev => ({ ...prev, [key]: e.target.value }))}
                  />
                  <Button
                    size="sm"
                    className="bg-[#84ab4a] hover:bg-[#6e9038] text-white h-9"
                    onClick={() => updateSetting.mutate({ key, value: getValue(key) || null })}
                    disabled={updateSetting.isPending}
                  >
                    <Save className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Hero Message ── */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden mb-6">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[#E2701F]" />
          <span className="font-semibold text-gray-900">Daily Agent Message</span>
        </div>
        <div className="px-5 py-4">
          <div className="text-sm text-gray-500 mb-3">
            This message appears in the hero banner on the agent dashboard. Leave blank to show the default: <em className="text-gray-700">"Let's make today count."</em>
          </div>
          <div className="flex items-center gap-2">
            <input
              className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E2701F]/60 focus:ring-1 focus:ring-[#E2701F]/20"
              placeholder="e.g. Let's make today count."
              value={heroMessageCurrent}
              onChange={(e) => setHeroMessageDraft(e.target.value)}
            />
            <Button
              size="sm"
              className="bg-[#E2701F] hover:bg-[#C75E12] text-white h-9"
              onClick={() => setHeroMessage.mutate({ key: "hero_message", value: heroMessageCurrent.trim() || null })}
              disabled={setHeroMessage.isPending}
            >
              <Save className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* ── Consultation Eligibility Gates ── */}
      <ConsultationGateCard
        title="Share360 Consultation Eligibility Gate"
        icon={<Shield className="w-4 h-4 text-[#84ab4a]" />}
        description={'Requires Share360 Business Steps 1&2 (Processing Questionnaire + 3 Processing Statements) plus the Associate Next Steps checklist, or full Nonprofit enrollment plus its Associate Next Steps checklist, before an associate can reach the live Wibiz booking calendar for that lead. Merchant Proposal generation is an internal Concierge-team step and does NOT block scheduling (the associate instead acknowledges a proposal will be generated). Legacy360 125 Plan/New Agent/Advantage, Fundable360, Media360, and Scale360 are not affected by this gate.'}
        settingKey="consultation_gate_enforced_share360"
        confirmMessage="This will start blocking Share360 associates from scheduling a consultation until Steps 1&2 (or, for Nonprofit, full enrollment) and the Associate Next Steps checklist are complete. Merchant Proposal generation is NOT part of this gate. Continue?"
        emptyMessage='No Share360 leads have opened the "Schedule a Consultation" panel yet — nothing to review.'
        gateSummary={gateActivityQuery.data?.share360}
        recentActivity={share360RecentActivity}
        showProposalAction
        onRefetchActivity={() => gateActivityQuery.refetch()}
      />
      <ConsultationGateCard
        title="Legacy360 Living Trust Consultation Eligibility Gate"
        icon={<FileText className="w-4 h-4 text-[#84ab4a]" />}
        description="Requires the associate's Pre-Appointment Checklist complete (client educated, fees discussed, client confirmed decision, payment/onboarding disclosure acknowledged) before an associate can reach the live Wibiz booking calendar for a Legacy360 Living Trust lead. 125 Plan, New Agent, and Advantage/Expansion sub-types are not affected by this gate."
        settingKey="consultation_gate_enforced_legacy360_living_trust"
        confirmMessage="This will start blocking Legacy360 Living Trust associates from scheduling a Concierge appointment until their pre-appointment checklist is complete. Continue?"
        emptyMessage='No Legacy360 Living Trust leads have opened the "Schedule Consultation" panel yet — nothing to review.'
        gateSummary={gateActivityQuery.data?.legacy360LivingTrust}
        recentActivity={legacy360RecentActivity}
        onRefetchActivity={() => gateActivityQuery.refetch()}
      />

      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
        <div className="flex items-start gap-3">
          <Settings className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-blue-900 mb-1">How to find your Calendar ID</div>
            <p className="text-sm text-blue-700">In your Wibiz Dashboard, go to <strong>Calendars</strong> → select a calendar → click <strong>Edit</strong>. The Calendar ID appears in the URL or in the calendar settings panel. It looks like a random string of letters and numbers (e.g., <code className="bg-blue-100 px-1 rounded">O6uW585cnEvZAB2fNRmE</code>).</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Scale360 Setup Complete Panel ───────────────────────────────────────────
function Scale360SetupCompletePanel() {
  const utils = trpc.useUtils();
  const { data: submissions = [], isLoading, refetch } = trpc.scale360.listSubmissions.useQuery(undefined, {
    refetchInterval: 60_000,
  });

  // Per-row portal URL state: keyed by submission id
  const [portalUrls, setPortalUrls] = useState<Record<number, string>>({});
  const [rowStatus, setRowStatus] = useState<Record<number, "idle" | "loading" | "ok" | "error">>({});
  const [rowMsg, setRowMsg]     = useState<Record<number, string>>({});

  // Manual fallback state (for customers not yet in DB)
  const [manualOpen, setManualOpen] = useState(false);
  const [manualEmail, setManualEmail] = useState("");
  const [manualUrl, setManualUrl] = useState("");
  const [manualStatus, setManualStatus] = useState<"idle" | "loading" | "ok" | "error">("idle");
  const [manualMsg, setManualMsg] = useState("");

  const markComplete = trpc.scale360.markSetupComplete.useMutation({
    onSuccess: (_data, variables) => {
      const sub = submissions.find(s => s.email === variables.email);
      if (sub) {
        setRowStatus(p => ({ ...p, [sub.id]: "ok" }));
        setRowMsg(p => ({ ...p, [sub.id]: "Setup complete — tag fired, URL written." }));
      } else {
        setManualStatus("ok");
        setManualMsg("Done — tag fired and portal URL written.");
        setManualEmail(""); setManualUrl("");
      }
      utils.scale360.listSubmissions.invalidate();
    },
    onError: (e, variables) => {
      const sub = submissions.find(s => s.email === variables.email);
      if (sub) {
        setRowStatus(p => ({ ...p, [sub.id]: "error" }));
        setRowMsg(p => ({ ...p, [sub.id]: e.message }));
      } else {
        setManualStatus("error");
        setManualMsg(e.message);
      }
    },
  });

  const fire = (email: string, url: string, id?: number) => {
    if (id !== undefined) setRowStatus(p => ({ ...p, [id]: "loading" }));
    else setManualStatus("loading");
    markComplete.mutate({ email, ...(url ? { commandCenterPortalUrl: url } : {}) });
  };

  const planBadge = (plan: string | null) => {
    const color = plan === "PRO" ? "#7C3AED" : "#1A6FD4";
    return (
      <span style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}
        className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full">
        {plan ?? "—"}
      </span>
    );
  };

  const pending   = submissions.filter(s => !s.setupCompletedAt);
  const completed = submissions.filter(s =>  s.setupCompletedAt);

  return (
    <div className="bg-[#1a1a2e] border border-[#E8620A]/30 rounded-2xl p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#E8620A]" />
          <span className="text-xs font-bold text-[#E8620A]/80 uppercase tracking-wider">Scale360 — Setup Complete</span>
        </div>
        <button onClick={() => refetch()} className="text-[10px] text-white/30 hover:text-white/60 transition-colors">↻ Refresh</button>
      </div>
      <p className="text-xs text-white/40 mb-4">
        Standard &amp; Pro customers only. Enter the Command Center URL and click "Mark Setup Complete" to write the custom field and fire Workflow 2.
      </p>

      {isLoading ? (
        <p className="text-xs text-white/30 py-4 text-center">Loading submissions…</p>
      ) : pending.length === 0 && completed.length === 0 ? (
        <p className="text-xs text-white/30 py-4 text-center">No Standard or Pro submissions yet.</p>
      ) : (
        <div className="space-y-3">
          {/* ── Pending rows ── */}
          {pending.map(sub => (
            <div key={sub.id} className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    {planBadge(sub.planSelected)}
                    {sub.provisioningStatus === "completed"
                      ? <span className="text-[10px] text-green-400 font-semibold">✓ Sub-account provisioned</span>
                      : <span className="text-[10px] text-yellow-400 font-semibold">⚠ Not provisioned yet</span>}
                  </div>
                  <div className="text-sm font-semibold text-white truncate">{sub.accountFullName ?? sub.businessName ?? sub.email}</div>
                  <div className="text-xs text-white/40 truncate">{sub.email}</div>
                  {sub.businessName && sub.accountFullName && (
                    <div className="text-xs text-white/30 truncate">{sub.businessName}</div>
                  )}
                </div>
                <div className="text-[10px] text-white/20 text-right flex-shrink-0">
                  {sub.submittedAt ? new Date(sub.submittedAt).toLocaleDateString() : "—"}
                </div>
              </div>
              <div className="flex gap-2">
                <input
                  type="url"
                  placeholder="Command Center URL (required for Standard/Pro)"
                  value={portalUrls[sub.id] ?? sub.commandCenterPortalUrl ?? ""}
                  onChange={e => setPortalUrls(p => ({ ...p, [sub.id]: e.target.value }))}
                  className="flex-1 bg-white/6 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder-white/25 focus:outline-none focus:border-[#E8620A]/60"
                />
                <button
                  disabled={rowStatus[sub.id] === "loading"}
                  onClick={() => fire(sub.email, portalUrls[sub.id] ?? sub.commandCenterPortalUrl ?? "", sub.id)}
                  className="bg-[#E8620A] hover:bg-[#c9530a] text-white px-4 py-2 rounded-lg text-xs font-bold disabled:opacity-40 transition-colors whitespace-nowrap"
                >
                  {rowStatus[sub.id] === "loading" ? "…" : "Mark Setup Complete"}
                </button>
              </div>
              {rowMsg[sub.id] && (
                <p className={`text-xs ${rowStatus[sub.id] === "ok" ? "text-green-400" : "text-red-400"}`}>{rowMsg[sub.id]}</p>
              )}
            </div>
          ))}

          {/* ── Completed rows (collapsed by default) ── */}
          {completed.length > 0 && (
            <details className="group">
              <summary className="text-xs text-white/30 hover:text-white/50 cursor-pointer select-none py-1">
                {completed.length} completed setup{completed.length > 1 ? "s" : ""} ▾
              </summary>
              <div className="space-y-2 mt-2">
                {completed.map(sub => (
                  <div key={sub.id} className="bg-white/3 border border-green-900/40 rounded-xl px-4 py-3 flex items-center gap-3">
                    <span className="text-green-500 text-base flex-shrink-0">✓</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">{planBadge(sub.planSelected)}</div>
                      <div className="text-sm font-medium text-white/70 truncate">{sub.accountFullName ?? sub.businessName ?? sub.email}</div>
                      <div className="text-xs text-white/30 truncate">{sub.email}</div>
                      {sub.commandCenterPortalUrl && (
                        <div className="text-[10px] text-white/25 font-mono truncate mt-0.5">{sub.commandCenterPortalUrl}</div>
                      )}
                    </div>
                    <div className="text-[10px] text-white/20 text-right flex-shrink-0">
                      Completed<br />{sub.setupCompletedAt ? new Date(sub.setupCompletedAt).toLocaleDateString() : "—"}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {/* ── Manual fallback (for emails not in DB) ── */}
      <div className="mt-4 pt-4 border-t border-white/8">
        <button onClick={() => setManualOpen(o => !o)}
          className="text-xs text-white/30 hover:text-white/50 transition-colors">
          {manualOpen ? "▾" : "▸"} Manual entry (email not in list above)
        </button>
        {manualOpen && (
          <div className="mt-3 flex flex-col gap-2">
            <input type="email" placeholder="Customer email"
              value={manualEmail} onChange={e => setManualEmail(e.target.value)}
              className="bg-white/6 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder-white/25 focus:outline-none focus:border-[#E8620A]/60"
            />
            <input type="url" placeholder="Command Center URL (optional)"
              value={manualUrl} onChange={e => setManualUrl(e.target.value)}
              className="bg-white/6 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder-white/25 focus:outline-none focus:border-[#E8620A]/60"
            />
            <button disabled={!manualEmail || manualStatus === "loading"}
              onClick={() => fire(manualEmail, manualUrl)}
              className="bg-[#E8620A] hover:bg-[#c9530a] text-white px-4 py-2 rounded-lg text-xs font-bold disabled:opacity-40 transition-colors">
              {manualStatus === "loading" ? "Sending…" : "Mark Setup Complete"}
            </button>
            {manualMsg && (
              <p className={`text-xs ${manualStatus === "ok" ? "text-green-400" : "text-red-400"}`}>{manualMsg}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Admin Dashboard ─────────────────────────────────────────────────
export default function AdminDashboard() {
  return (
    <AdminLock>
      <AdminDashboardInner />
    </AdminLock>
  );
}

// ─── Role Upgrade Requests Tab ────────────────────────────────────────────────
function RoleUpgradesTab() {
  const [adminNotes, setAdminNotes] = useState<Record<number, string>>({});
  const utils = trpc.useUtils();

  const { data: requests = [], isLoading, refetch } = trpc.repUpgrade.listAll.useQuery(undefined, {
    refetchInterval: 60_000,
  });

  const approveMutation = trpc.repUpgrade.approve.useMutation({
    onSuccess: (r) => {
      toast.success(`${r.repCode} promoted to ${r.newLevel}`);
      utils.repUpgrade.listAll.invalidate();
      utils.repUpgrade.listPending.invalidate();
    },
    onError: (e: any) => toast.error(e.message),
  });

  const rejectMutation = trpc.repUpgrade.reject.useMutation({
    onSuccess: () => {
      toast.success("Request rejected.");
      utils.repUpgrade.listAll.invalidate();
      utils.repUpgrade.listPending.invalidate();
    },
    onError: (e: any) => toast.error(e.message),
  });

  const pending = (requests as any[]).filter(r => r.status === "pending");
  const reviewed = (requests as any[]).filter(r => r.status !== "pending");

  function fmtDate(d: string | Date) {
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  const StatusBadge = ({ status }: { status: string }) => {
    const map: Record<string, string> = {
      pending: "bg-amber-100 text-amber-700",
      approved: "bg-green-100 text-green-700",
      rejected: "bg-red-100 text-red-700",
    };
    return (
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full capitalize ${map[status] ?? "bg-gray-100 text-gray-600"}`}>
        {status}
      </span>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Senior Associate Upgrade Requests</h2>
          <p className="text-sm text-gray-400">
            Associates requesting promotion to Senior Associate. Only you (Admin) can approve.
          </p>
        </div>
        <button onClick={() => refetch()} className="text-xs text-gray-400 hover:text-gray-600 border border-gray-200 rounded-lg px-3 py-1.5 transition-colors">
          Refresh
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <>
          {/* Pending requests */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">Pending Review</div>
              {pending.length > 0 && (
                <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                  {pending.length} awaiting
                </span>
              )}
            </div>
            {pending.length === 0 ? (
              <div className="bg-white border border-gray-100 rounded-2xl p-10 text-center text-gray-400">
                <ArrowUpCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <div className="text-sm">No pending upgrade requests</div>
              </div>
            ) : (
              <div className="space-y-4">
                {pending.map((req: any) => (
                  <div key={req.id} className="bg-white border border-amber-100 rounded-2xl shadow-sm p-5">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-bold text-gray-900">{req.repName}</span>
                          <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{req.repCode}</span>
                          <StatusBadge status={req.status} />
                        </div>
                        <div className="text-xs text-gray-400">{req.repEmail} · Requested {fmtDate(req.createdAt)}</div>
                        <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                          <ArrowUpCircle className="w-3.5 h-3.5 text-[#84ab4a]" />
                          <span className="font-semibold">{req.fromLevel}</span>
                          <span className="text-gray-300">→</span>
                          <span className="font-semibold text-[#84ab4a]">{req.toLevel}</span>
                        </div>
                        <div className="flex flex-wrap gap-3 mt-1.5">
                          {req.originalUplineRepCode && (
                            <span className="text-[10px] text-gray-400 flex items-center gap-1">
                              <Users className="w-3 h-3" /> Recruited by: <span className="font-mono font-semibold text-gray-600">{req.originalUplineRepCode}</span>
                            </span>
                          )}
                          {req.nominatedByRepCode && (
                            <span className="text-[10px] text-blue-500 flex items-center gap-1">
                              <ArrowUpCircle className="w-3 h-3" /> Nominated by: <span className="font-mono font-semibold">{req.nominatedByRepCode}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {req.requestNotes && (
                      <div className="bg-gray-50 border border-gray-100 rounded-xl px-3.5 py-2.5 text-sm text-gray-600 mb-4">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 mb-1">
                          <MessageSquare className="w-3 h-3" /> Rep's note
                        </div>
                        {req.requestNotes}
                      </div>
                    )}
                    <div className="flex items-end gap-3">
                      <div className="flex-1">
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Admin notes <span className="font-normal text-gray-400">(shown to rep on rejection)</span></label>
                        <input
                          type="text"
                          value={adminNotes[req.id] ?? ""}
                          onChange={e => setAdminNotes(n => ({ ...n, [req.id]: e.target.value }))}
                          placeholder="Optional feedback…"
                          className="w-full text-sm border border-gray-200 rounded-xl px-3.5 py-2 focus:outline-none focus:border-[#84ab4a]/50"
                        />
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          onClick={() => rejectMutation.mutate({ id: req.id, adminNotes: adminNotes[req.id] })}
                          disabled={rejectMutation.isPending || approveMutation.isPending}
                          className="px-4 py-2 text-xs font-semibold rounded-xl border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => approveMutation.mutate({ id: req.id, adminNotes: adminNotes[req.id] })}
                          disabled={approveMutation.isPending || rejectMutation.isPending}
                          className="px-4 py-2 text-xs font-semibold rounded-xl bg-[#84ab4a] hover:bg-[#6e9038] text-white disabled:opacity-50 transition-colors flex items-center gap-1.5"
                        >
                          {approveMutation.isPending && <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                          Approve & Promote
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Reviewed history */}
          {reviewed.length > 0 && (
            <div>
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Review History</div>
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Rep</th>
                      <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Journey</th>
                      <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Requested</th>
                      <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Status</th>
                      <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Reviewed</th>
                      <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewed.map((req: any) => (
                      <tr key={req.id} className="border-t border-gray-50 hover:bg-gray-50/50">
                        <td className="px-5 py-3">
                          <div className="font-semibold text-gray-900">{req.repName}</div>
                          <div className="text-xs text-gray-400 font-mono">{req.repCode}</div>
                        </td>
                        <td className="px-5 py-3">
                          <div className="space-y-0.5">
                            {req.originalUplineRepCode && (
                              <div className="text-[10px] text-gray-500">Recruited by: <span className="font-mono font-semibold text-gray-700">{req.originalUplineRepCode}</span></div>
                            )}
                            {req.nominatedByRepCode && (
                              <div className="text-[10px] text-blue-500">Nominated by: <span className="font-mono font-semibold">{req.nominatedByRepCode}</span></div>
                            )}
                            {!req.originalUplineRepCode && !req.nominatedByRepCode && (
                              <span className="text-[10px] text-gray-300">Self-request</span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-3 text-xs text-gray-500">{fmtDate(req.createdAt)}</td>
                        <td className="px-5 py-3"><StatusBadge status={req.status} /></td>
                        <td className="px-5 py-3 text-xs text-gray-500">{req.reviewedAt ? fmtDate(req.reviewedAt) : "—"}</td>
                        <td className="px-5 py-3 text-xs text-gray-500 max-w-xs truncate">{req.adminNotes || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Fundable360 Financing Tab ────────────────────────────────────────────────
function fmt2(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(n);
}

// Shared pager for Financing Plans + Recent Checkout Sessions - both tables
// paginate client-side over their already-fetched array, so this just slices
// what the caller already filtered. If either table's row count grows large
// enough that fetching the full set becomes expensive, this is the spot to
// swap in real server-side pagination.
function Fundable360TablePager({ page, totalPages, totalItems, pageSize, onChange }: {
  page: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onChange: (page: number) => void;
}) {
  if (totalItems === 0) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);
  return (
    <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50/40">
      <span className="text-xs text-gray-400">Showing {start}-{end} of {totalItems}</span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 text-gray-500 disabled:opacity-30 hover:bg-white transition-colors"
        >
          Prev
        </button>
        <span className="text-xs text-gray-400 px-2">Page {page} of {totalPages}</span>
        <button
          onClick={() => onChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className="px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 text-gray-500 disabled:opacity-30 hover:bg-white transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function fundable360PlanStatusCategory(row: any): "failed" | "completed" | "active" {
  if (row.hasFailedOrOverdue) return "failed";
  if (row.plan.status === "completed") return "completed";
  return "active";
}

const FUNDABLE360_PAGE_SIZE = 10;
const FUNDABLE360_FILTER_INPUT_CLASS = "px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-[#156082]/60 focus:ring-2 focus:ring-[#156082]/20";

function Fundable360FinancingTab() {
  // "tracker" is the default landing view (stat cards + the two data tables).
  // "stripe_settings" is a self-contained sub-page reached via the button
  // below - not a separate TabKey/route, since this app's nav is local
  // React state rather than URL-routed tabs; this keeps the same pattern.
  const [view, setView] = useState<"tracker" | "stripe_settings">("tracker");
  const [linkRepCode, setLinkRepCode] = useState("");
  const [showLinksFor, setShowLinksFor] = useState<string | null>(null);
  const [expandedPlanId, setExpandedPlanId] = useState<number | null>(null);
  const utils = trpc.useUtils();

  const { data: tracker = [], isLoading, refetch } = trpc.fundable360.tracker.useQuery(undefined, {
    refetchInterval: 60_000,
  });
  const { data: financingLinks } = trpc.fundable360.getFinancingLinks.useQuery(
    { repCode: showLinksFor ?? "" },
    { enabled: !!showLinksFor },
  );

  const reconcileMutation = trpc.fundable360.reconcileInstallment.useMutation({
    onSuccess: () => {
      toast.success("Installment updated.");
      utils.fundable360.tracker.invalidate();
      utils.fundable360.auditLog.invalidate();
    },
    onError: (e: any) => toast.error(e.message),
  });

  // ── Create External (non-Stripe) Financing Plan ──────────────────────────
  // For a financed enrollment whose payments are collected entirely outside
  // BizConnect360's own Stripe integration (e.g. a channel partner billing
  // the client directly) - see fundable360.createManualPlan on the server.
  const [showManualPlanModal, setShowManualPlanModal] = useState(false);
  const [manualPlanForm, setManualPlanForm] = useState({
    leadId: "", program: "Growth" as "Growth" | "Elite", repCode: "", monthlyClientAmount: "", notes: "",
  });
  const manualPlanLeadId = parseInt(manualPlanForm.leadId, 10);
  const manualPlanLeadQuery = trpc.lead.getById.useQuery(
    { id: manualPlanLeadId },
    { enabled: showManualPlanModal && Number.isFinite(manualPlanLeadId) && manualPlanLeadId > 0 },
  );
  const createManualPlanMutation = trpc.fundable360.createManualPlan.useMutation({
    onSuccess: (data: any) => {
      toast.success(data.message ?? "Financing plan created.");
      utils.fundable360.tracker.invalidate();
      utils.fundable360.auditLog.invalidate();
      setShowManualPlanModal(false);
      setManualPlanForm({ leadId: "", program: "Growth", repCode: "", monthlyClientAmount: "", notes: "" });
      if (data.planId) setExpandedPlanId(data.planId);
    },
    onError: (e: any) => toast.error(e.message),
  });
  const [showFoundationPaymentModal, setShowFoundationPaymentModal] = useState(false);
  const [foundationPayment, setFoundationPayment] = useState({ leadId: "", type: "monthly" as "monthly" | "setup", periodMonth: "", notes: "" });
  const foundationPaymentLeadId = Number(foundationPayment.leadId);
  const foundationPaymentLead = trpc.lead.getById.useQuery(
    { id: foundationPaymentLeadId },
    { enabled: showFoundationPaymentModal && Number.isInteger(foundationPaymentLeadId) && foundationPaymentLeadId > 0 },
  );
  const recordFoundationPayment = trpc.fundable360.recordExternalFoundationPayment.useMutation({
    onSuccess: result => {
      toast.success(result.outcome === "duplicate" ? "This commission is already recorded." : `Foundation commission of $${result.amount.toFixed(2)} recorded.`);
      utils.fundable360.auditLog.invalidate();
      setShowFoundationPaymentModal(false);
      setFoundationPayment({ leadId: "", type: "monthly", periodMonth: "", notes: "" });
    },
    onError: (error: any) => toast.error(error.message),
  });

  // Read-only view of the BizConnect360 Stripe env vars. Unlike Scale360's
  // Billing panel there is no save path: these credentials live only in Railway,
  // never in the database, so this panel reports status and never edits.
  // connMode only filters which set of keys is displayed. It does NOT change
  // how checkout behaves; that is decided per request by the ?test=1 URL param.
  // Only fetched once the Stripe Settings sub-page is open - the tracker view
  // never needed these values and there is no reason to pay for the request.
  const [connMode, setConnMode] = useState<"live" | "test">("live");
  const stripeConnQuery = trpc.fundable360.stripeConnection.useQuery(undefined, { enabled: view === "stripe_settings" });
  const conn = stripeConnQuery.data;
  const connIsTest = connMode === "test";
  const connPublishable = (connIsTest ? conn?.testPublishableKey : conn?.publishableKey) ?? "";
  const connHasSecret = connIsTest ? conn?.hasTestSecretKey : conn?.hasSecretKey;
  const connHasWebhook = connIsTest ? conn?.hasTestWebhookSecret : conn?.hasWebhookSecret;
  const connExpectedMode = connIsTest ? "test" : "live";
  const connPublishableMode = connIsTest ? conn?.testPublishableKeyMode : conn?.publishableKeyMode;
  const connSecretMode = connIsTest ? conn?.testSecretKeyMode : conn?.secretKeyMode;
  const connPublishableValid = connPublishableMode === connExpectedMode;
  const connSecretValid = connSecretMode === connExpectedMode;
  const connComplete = connPublishableValid && connSecretValid && connHasWebhook;
  const connHasModeMismatch =
    (!!connPublishable && !connPublishableValid) || (!!connHasSecret && !connSecretValid);

  // Fundable360 Stripe Price IDs. Brand is hardcoded here (unlike the Scale360
  // Billing tab's brand dropdown) so this panel can never write a plan row under
  // the wrong brand. Foundation/Growth/Elite are seeded by migration 0085.
  // Same lazy-fetch reasoning as stripeConnQuery above.
  const fundablePlansQuery = trpc.billing.plans.list.useQuery({ brand: "fundable360" }, { enabled: view === "stripe_settings" });

  // Fundable360's own procedure, joined server-side against a Fundable360-
  // exclusive side table for method/amount - not the shared billing.sessions
  // procedure Scale360 also uses.
  const fundableSessionsQuery = trpc.fundable360.checkoutSessions.useQuery();
  const [editingPriceIds, setEditingPriceIds] = useState<any | null>(null);
  const [priceIdForm, setPriceIdForm] = useState({
    setupFeePriceId: "", testSetupFeePriceId: "", recurringPriceId: "", testRecurringPriceId: "",
  });
  const savePricesMut = trpc.billing.plans.save.useMutation({
    onSuccess: () => {
      toast.success("Price IDs saved.");
      utils.billing.plans.list.invalidate();
      setEditingPriceIds(null);
    },
    onError: (e: any) => toast.error(e.message),
  });

  function openPriceIdEditor(plan: any) {
    setPriceIdForm({
      setupFeePriceId: plan.setupFeePriceId ?? "",
      testSetupFeePriceId: plan.testSetupFeePriceId ?? "",
      recurringPriceId: plan.recurringPriceId ?? "",
      testRecurringPriceId: plan.testRecurringPriceId ?? "",
    });
    setEditingPriceIds(plan);
  }

  // Warn on a Price ID pasted from the wrong Stripe account. Fundable360 bills
  // through BizConnect360's own account; Scale360's Wibiz account IDs carry the
  // "I3d16cIOnj" fragment. Advisory only, never blocks saving.
  function wrongAccountWarning(v: string) {
    return v.trim() && v.includes("I3d16cIOnj")
      ? "Looks like a Scale360/Wibiz Price ID, not a BizConnect360 one"
      : null;
  }
  function priceIdShapeWarning(v: string) {
    const t = v.trim();
    if (!t) return null;
    if (t.startsWith("prod_")) return "That is a Product ID. You need the Price ID (price_...)";
    if (!t.startsWith("price_")) return "Stripe Price IDs start with price_";
    return null;
  }

  const totals = (tracker as any[]).reduce(
    (acc, r) => {
      acc.commissionPaid += r.commissionPaid;
      acc.commissionRemaining += r.commissionRemaining;
      if (r.hasFailedOrOverdue) acc.failedOrOverdue += 1;
      return acc;
    },
    { commissionPaid: 0, commissionRemaining: 0, failedOrOverdue: 0 },
  );

  function copyLink(url: string) {
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied."));
  }

  // ── Financing Plans: search, filter, pagination ──────────────────────────
  const [plansSearch, setPlansSearch] = useState("");
  const [plansProgramFilter, setPlansProgramFilter] = useState("all");
  const [plansStatusFilter, setPlansStatusFilter] = useState<"all" | "active" | "completed" | "failed">("all");
  const [plansPage, setPlansPage] = useState(1);

  const plansProgramOptions = useMemo(
    () => Array.from(new Set((tracker as any[]).map(r => r.plan.program).filter(Boolean))).sort(),
    [tracker],
  );

  const filteredPlans = useMemo(() => {
    const q = plansSearch.trim().toLowerCase();
    return (tracker as any[]).filter(row => {
      if (plansProgramFilter !== "all" && row.plan.program !== plansProgramFilter) return false;
      if (plansStatusFilter !== "all" && fundable360PlanStatusCategory(row) !== plansStatusFilter) return false;
      if (q) {
        const haystack = [
          row.lead?.businessName, row.lead?.firstName, row.lead?.lastName, row.lead?.email,
          row.repName, row.plan.repCode, row.plan.rankAtEnrollment,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [tracker, plansSearch, plansProgramFilter, plansStatusFilter]);

  useEffect(() => { setPlansPage(1); }, [plansSearch, plansProgramFilter, plansStatusFilter]);

  const plansTotalPages = Math.max(1, Math.ceil(filteredPlans.length / FUNDABLE360_PAGE_SIZE));
  const plansPageSafe = Math.min(plansPage, plansTotalPages);
  const paginatedPlans = filteredPlans.slice(
    (plansPageSafe - 1) * FUNDABLE360_PAGE_SIZE,
    plansPageSafe * FUNDABLE360_PAGE_SIZE,
  );
  const plansFiltersActive = !!plansSearch || plansProgramFilter !== "all" || plansStatusFilter !== "all";

  // ── Recent Checkout Sessions: search, filter, pagination ─────────────────
  const [sessionsSearch, setSessionsSearch] = useState("");
  const [sessionsProgramFilter, setSessionsProgramFilter] = useState("all");
  const [sessionsStatusFilter, setSessionsStatusFilter] = useState("all");
  const [sessionsPage, setSessionsPage] = useState(1);

  const sessionsProgramOptions = useMemo(
    () => Array.from(new Set((fundableSessionsQuery.data ?? []).map((s: any) => s.planKey).filter(Boolean))).sort(),
    [fundableSessionsQuery.data],
  );
  const sessionsStatusOptions = useMemo(
    () => Array.from(new Set((fundableSessionsQuery.data ?? []).map((s: any) => s.status ?? "created").filter(Boolean))).sort(),
    [fundableSessionsQuery.data],
  );

  const filteredSessions = useMemo(() => {
    const q = sessionsSearch.trim().toLowerCase();
    return (fundableSessionsQuery.data ?? []).filter((s: any) => {
      if (sessionsProgramFilter !== "all" && s.planKey !== sessionsProgramFilter) return false;
      if (sessionsStatusFilter !== "all" && (s.status ?? "created") !== sessionsStatusFilter) return false;
      if (q) {
        const haystack = [s.customerName, s.customerEmail, s.repName, s.repCode, s.stripeSessionId]
          .filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [fundableSessionsQuery.data, sessionsSearch, sessionsProgramFilter, sessionsStatusFilter]);

  useEffect(() => { setSessionsPage(1); }, [sessionsSearch, sessionsProgramFilter, sessionsStatusFilter]);

  const sessionsTotalPages = Math.max(1, Math.ceil(filteredSessions.length / FUNDABLE360_PAGE_SIZE));
  const sessionsPageSafe = Math.min(sessionsPage, sessionsTotalPages);
  const paginatedSessions = filteredSessions.slice(
    (sessionsPageSafe - 1) * FUNDABLE360_PAGE_SIZE,
    sessionsPageSafe * FUNDABLE360_PAGE_SIZE,
  );
  const sessionsFiltersActive = !!sessionsSearch || sessionsProgramFilter !== "all" || sessionsStatusFilter !== "all";

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Active Plans", value: (tracker as any[]).filter(r => r.plan.status === "active").length.toLocaleString("en-US"), color: "#156082" },
          { label: "Commission Paid", value: fmt2(totals.commissionPaid), color: "#84ab4a" },
          { label: "Commission Remaining", value: fmt2(totals.commissionRemaining), color: "#C8920A" },
          { label: "Failed / Overdue", value: totals.failedOrOverdue.toLocaleString("en-US"), color: "#DC2626" },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: color }} />
            <div className="text-2xl font-bold font-mono text-gray-900">{value}</div>
            <div className="text-xs text-gray-400 mt-1 font-medium">{label}</div>
          </div>
        ))}
      </div>

      {view === "stripe_settings" ? (
        <div className="space-y-6">
          {/* Settings sub-page header - breadcrumb back to the tracker view */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setView("tracker")}
              className="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-700"
            >
              <ChevronRight className="w-3.5 h-3.5 rotate-180" /> Financing Tracker
            </button>
            <span className="text-gray-300">/</span>
            <span className="text-sm font-bold text-gray-800">Stripe Settings</span>
          </div>

      {/* Security notice */}
      <div className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 flex items-start gap-3">
        <AlertCircle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-orange-700">
          <span className="font-semibold">Security notice:</span> Fundable360 Stripe credentials are read only here.
          They are stored solely as <code className="text-xs font-mono bg-orange-100 px-1 rounded">BIZCONNECT360_STRIPE_*</code> environment
          variables on the server, never in the database and never editable from this UI. Secret keys and signing
          secrets are never displayed, only reported as saved or not set. Never share or log Stripe secret keys.
        </div>
      </div>

      {/* Stripe Connection (read only) */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <span className="text-sm font-semibold text-gray-700">Stripe Connection</span>
            <span className="ml-2 text-xs text-gray-400">BizConnect360 Stripe account credentials for Fundable360 billing</span>
          </div>
          <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${
            connComplete
              ? "bg-green-50 text-green-600"
              : "bg-orange-50 text-orange-500"
          }`}>
            {connComplete
              ? `${connIsTest ? "Test" : "Live"} key types valid`
              : connHasModeMismatch
                ? `${connIsTest ? "Test" : "Live"} key mismatch`
                : `${connIsTest ? "Test" : "Live"} incomplete`}
          </span>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Stripe Mode</label>
              <select
                value={connMode}
                onChange={e => setConnMode(e.target.value as "live" | "test")}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#156082]"
              >
                <option value="live">Live Mode</option>
                <option value="test">Test Mode</option>
              </select>
              <p className="text-[10px] text-gray-400 mt-1">
                View filter only. Checkout mode is set per link by <code className="font-mono bg-gray-100 px-1 rounded">?test=1</code>, not here.
              </p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Default Currency</label>
              <select
                value={conn?.currency ?? "usd"}
                disabled
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
              >
                <option value="usd">USD</option>
              </select>
              <p className="text-[10px] text-gray-400 mt-1">
                Fixed by the Stripe Price objects themselves, not an app setting.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              {connIsTest ? "Test Publishable Key" : "Stripe Publishable Key"}
            </label>
            <input
              type="text"
              value={stripeConnQuery.isLoading ? "Loading..." : (connPublishable || "")}
              readOnly
              placeholder="Not set. Add the publishable key to the server configuration."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono bg-gray-50 text-gray-600"
            />
            {!stripeConnQuery.isLoading && connPublishable && !connPublishableValid && (
              <p className="text-[10px] text-orange-600 mt-1">
                Wrong key type for {connExpectedMode} mode. Expected a <code className="font-mono">pk_{connExpectedMode}_...</code> key.
              </p>
            )}
          </div>

          {([
            { label: connIsTest ? "Test Secret Key" : "Stripe Secret Key", ok: connSecretValid, present: connHasSecret, modeCheckable: true, env: `BIZCONNECT360_STRIPE_${connIsTest ? "TEST_" : ""}SECRET_KEY` },
            { label: connIsTest ? "Test Webhook Signing Secret" : "Stripe Webhook Signing Secret", ok: connHasWebhook, present: connHasWebhook, modeCheckable: false, env: `BIZCONNECT360_STRIPE_${connIsTest ? "TEST_" : ""}WEBHOOK_SECRET` },
          ] as const).map(({ label, ok, present, modeCheckable, env }) => (
            <div key={env}>
              <div className="flex items-center gap-2 mb-1">
                <label className="block text-xs font-semibold text-gray-500">{label}</label>
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${ok ? "bg-green-50 text-green-600" : "bg-orange-50 text-orange-500"}`}>
                  {stripeConnQuery.isLoading ? "..." : ok ? (modeCheckable ? "Correct mode" : "Present") : present ? "Wrong mode" : "Not set"}
                </span>
              </div>
              <div className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-400 font-mono">
                {present ? "•".repeat(24) + "  (never displayed)" : "Not configured on the server"}
              </div>
              {!stripeConnQuery.isLoading && present && modeCheckable && !ok && (
                <p className="text-[10px] text-orange-600 mt-1">
                  Wrong key type for {connExpectedMode} mode. Expected an <code className="font-mono">sk_{connExpectedMode}_...</code> key.
                </p>
              )}
              {!stripeConnQuery.isLoading && present && !modeCheckable && (
                <p className="text-[10px] text-gray-400 mt-1">
                  Webhook signing secrets use the same prefix in test and live mode, so this confirms presence only. Verify mode with a real webhook event.
                </p>
              )}
            </div>
          ))}

          <div className="border-t border-gray-100 pt-4">
            <label className="block text-xs font-semibold text-gray-500 mb-1">Webhook Endpoint URL</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-[11px] font-mono bg-gray-50 border border-gray-100 rounded px-2 py-2 truncate text-gray-600">
                {(conn?.appBaseUrl ?? "https://your-domain") + (conn?.webhookPath ?? "")}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText((conn?.appBaseUrl ?? window.location.origin) + (conn?.webhookPath ?? ""));
                  toast.success("Webhook URL copied.");
                }}
                className="text-[11px] px-2.5 py-2 rounded bg-gray-100 hover:bg-gray-200 text-gray-500 font-semibold flex-shrink-0"
              >Copy</button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1">
              Register this in BizConnect360's Stripe account for both Test and Live, sending checkout.session.completed,
              invoice.payment_succeeded and invoice.payment_failed.
              {!conn?.appBaseUrl && <span className="text-orange-500"> The app base URL is not configured on the server.</span>}
            </p>
          </div>
        </div>
      </div>

      {/* Stripe Price IDs (Fundable360 only) */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100">
          <span className="text-sm font-semibold text-gray-700">Stripe Price IDs</span>
          <span className="ml-2 text-xs text-gray-400">
            Foundation, Growth and Elite. One-time price for paid in full, recurring price for 6 month financing. Created in BizConnect360's own Stripe account.
          </span>
        </div>
        <div className="divide-y divide-gray-50">
          {fundablePlansQuery.isLoading && (
            <div className="py-10 text-center text-gray-400 text-sm">Loading plans...</div>
          )}
          {!fundablePlansQuery.isLoading && (fundablePlansQuery.data ?? []).length === 0 && (
            <div className="py-10 text-center text-gray-400 text-sm">
              No Fundable360 plans found. They are seeded on deploy by migration 0085.
            </div>
          )}
          {(fundablePlansQuery.data ?? []).map((plan: any) => {
            const liveReady = !!plan.setupFeePriceId && !!plan.recurringPriceId;
            const testReady = !!plan.testSetupFeePriceId && !!plan.testRecurringPriceId;
            return (
              <div key={plan.id} className="px-5 py-4 flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-gray-800">{plan.name}</span>
                    <span className="text-[10px] font-mono text-gray-400 uppercase bg-gray-100 px-1.5 py-0.5 rounded">{plan.planKey}</span>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${liveReady ? "bg-green-50 text-green-600" : "bg-orange-50 text-orange-500"}`}>
                      {liveReady ? "Live ready" : "Live incomplete"}
                    </span>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${testReady ? "bg-blue-50 text-blue-500" : "bg-gray-100 text-gray-400"}`}>
                      {testReady ? "Test ready" : "Test incomplete"}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
                    <span>${((plan.setupAmountCents ?? 0) / 100).toFixed(0)} paid in full</span>
                    <span>${((plan.monthlyAmountCents ?? 0) / 100).toFixed(0)}/mo x 6 financed</span>
                  </div>
                  <div className="mt-1.5 grid gap-0.5 text-[10px] font-mono">
                    {plan.setupFeePriceId
                      ? <span className="text-green-600 truncate">Paid in full: {plan.setupFeePriceId}</span>
                      : <span className="text-orange-500">Paid in full: not set</span>}
                    {plan.recurringPriceId
                      ? <span className="text-green-600 truncate">Financing: {plan.recurringPriceId}</span>
                      : <span className="text-orange-500">Financing: not set</span>}
                    {plan.testSetupFeePriceId && <span className="text-blue-400 truncate">TEST paid in full: {plan.testSetupFeePriceId}</span>}
                    {plan.testRecurringPriceId && <span className="text-blue-400 truncate">TEST financing: {plan.testRecurringPriceId}</span>}
                  </div>
                </div>
                <button
                  onClick={() => openPriceIdEditor(plan)}
                  className="text-xs font-semibold hover:underline flex-shrink-0"
                  style={{ color: "#156082" }}
                >Edit</button>
              </div>
            );
          })}
        </div>
      </div>
        </div>
      ) : (
        <>
          {/* Stripe Settings entry point */}
          <div className="flex items-center justify-end">
            <button
              onClick={() => setView("stripe_settings")}
              className="flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors"
            >
              <Settings className="w-3.5 h-3.5" /> Stripe Settings
            </button>
          </div>

      {/* Financing Plans - moved to the top of the page; search, filter, pagination */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-sm font-semibold text-gray-700">Financing Plans</span>
          <div className="flex gap-2">
            <button onClick={() => setShowFoundationPaymentModal(true)} className="text-xs font-semibold text-[#156082] border border-[#156082] px-3 py-1.5 rounded-lg">
              + Record External Foundation Payment
            </button>
            <button
              onClick={() => setShowManualPlanModal(true)}
              className="text-xs font-semibold text-white bg-[#156082] hover:bg-[#0f4a63] px-3 py-1.5 rounded-lg transition-colors"
            >
              + Create External Plan
            </button>
          </div>
        </div>
        <div className="px-5 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px] max-w-xs">
            <Search className="w-3.5 h-3.5 text-gray-300 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={plansSearch}
              onChange={e => setPlansSearch(e.target.value)}
              placeholder="Search client, email, or rep code..."
              className={`${FUNDABLE360_FILTER_INPUT_CLASS} pl-8 w-full`}
            />
          </div>
          <select value={plansProgramFilter} onChange={e => setPlansProgramFilter(e.target.value)} className={FUNDABLE360_FILTER_INPUT_CLASS}>
            <option value="all">All Programs</option>
            {plansProgramOptions.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={plansStatusFilter} onChange={e => setPlansStatusFilter(e.target.value as any)} className={FUNDABLE360_FILTER_INPUT_CLASS}>
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed / Overdue</option>
          </select>
          {plansFiltersActive && (
            <button
              onClick={() => { setPlansSearch(""); setPlansProgramFilter("all"); setPlansStatusFilter("all"); }}
              className="text-xs font-semibold text-gray-400 hover:text-gray-600 px-2 flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
                <th className="px-4 py-2.5">Client</th>
                <th className="px-4 py-2.5">Program</th>
                <th className="px-4 py-2.5">Associate / Rank</th>
                <th className="px-4 py-2.5">Estimated Commission</th>
                <th className="px-4 py-2.5">Installments</th>
                <th className="px-4 py-2.5">Commission Paid</th>
                <th className="px-4 py-2.5">Remaining</th>
                <th className="px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr>
              )}
              {!isLoading && (tracker as any[]).length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-400">No financed Fundable360 plans yet.</td></tr>
              )}
              {!isLoading && (tracker as any[]).length > 0 && filteredPlans.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-400">No plans match your search/filters.</td></tr>
              )}
              {paginatedPlans.map((row) => (
                <>
                  <tr
                    key={row.plan.id}
                    className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setExpandedPlanId(expandedPlanId === row.plan.id ? null : row.plan.id)}
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {row.lead?.businessName || `${row.lead?.firstName ?? ""} ${row.lead?.lastName ?? ""}`.trim() || row.lead?.email || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      Fundable360 {row.plan.program}
                      {!row.plan.stripeSubscriptionId && (
                        <span
                          className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 align-middle"
                          title="Manually created — payments collected outside BizConnect360's Stripe"
                        >
                          External
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <div className="font-medium text-gray-900">{row.repName ?? row.plan.repCode ?? "-"}</div>
                      <div className="text-xs text-gray-400">
                        {row.repName && row.plan.repCode ? <span className="font-mono">{row.plan.repCode} · </span> : null}
                        {row.plan.rankAtEnrollment}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-gray-900">{fmt2(row.currentCommissionEstimate)}</td>
                    <td className="px-4 py-3 text-gray-600">{row.installmentsPaid}/6</td>
                    <td className="px-4 py-3 font-mono text-green-700">{fmt2(row.commissionPaid)}</td>
                    <td className="px-4 py-3 font-mono text-gray-500">{fmt2(row.commissionRemaining)}</td>
                    <td className="px-4 py-3">
                      {row.hasFailedOrOverdue ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Failed / Overdue</span>
                      ) : row.plan.status === "completed" ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Completed</span>
                      ) : (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                          {row.nextInstallmentNumber ? `Next: #${row.nextInstallmentNumber}` : "Active"}
                        </span>
                      )}
                    </td>
                  </tr>
                  {expandedPlanId === row.plan.id && (
                    <tr key={`${row.plan.id}-detail`} className="bg-gray-50/60">
                      <td colSpan={8} className="px-4 py-4">
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                          {row.installments.map((inst: any) => (
                            <div key={inst.id} className="border border-gray-200 rounded-lg p-2.5 bg-white">
                              <div className="text-[10px] text-gray-400 font-semibold">#{inst.installmentNumber}</div>
                              <div className={`text-xs font-bold mt-0.5 ${
                                inst.status === "paid" ? "text-green-700" : inst.status === "failed" ? "text-red-600" : "text-gray-400"
                              }`}>
                                {inst.status}
                              </div>
                              <div className="text-xs font-mono text-gray-600 mt-1">{fmt2(Number(inst.commissionAmount))}</div>
                              {inst.status !== "paid" && (
                                <div className="flex gap-1 mt-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const notes = window.prompt("Enter the payment confirmation source and date before releasing this commission:");
                                      if (!notes?.trim()) return;
                                      const stripeInvoiceId = row.plan.stripeSubscriptionId
                                        ? window.prompt("Enter the paid Stripe invoice ID (in_...) so a later webhook cannot release it twice:")
                                        : null;
                                      if (row.plan.stripeSubscriptionId && !stripeInvoiceId?.trim()) return;
                                      reconcileMutation.mutate({ installmentId: inst.id, action: "manual_release", notes: notes.trim(), stripeInvoiceId: stripeInvoiceId?.trim() || undefined });
                                    }}
                                    className="text-[10px] font-semibold text-[#156082]"
                                  >
                                    Release
                                  </button>
                                  {inst.status !== "failed" && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); reconcileMutation.mutate({ installmentId: inst.id, action: "manual_mark_failed" }); }}
                                      className="text-[10px] font-semibold text-red-600"
                                    >
                                      Mark Failed
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
        <Fundable360TablePager
          page={plansPageSafe}
          totalPages={plansTotalPages}
          totalItems={filteredPlans.length}
          pageSize={FUNDABLE360_PAGE_SIZE}
          onChange={setPlansPage}
        />
      </div>

      {/* Recent Checkout Sessions - moved to the top of the page; search, filter, pagination */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <span className="text-sm font-semibold text-gray-700">Recent Checkout Sessions</span>
            <span className="ml-2 text-xs text-gray-400">
              Stripe Checkout sessions started from the Fundable360 pricing page. A session is created when someone opens
              checkout, so a row here does not mean a payment was taken.
            </span>
          </div>
          <button onClick={() => fundableSessionsQuery.refetch()} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
        <div className="px-5 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px] max-w-xs">
            <Search className="w-3.5 h-3.5 text-gray-300 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={sessionsSearch}
              onChange={e => setSessionsSearch(e.target.value)}
              placeholder="Search customer, email, ref, or session ID..."
              className={`${FUNDABLE360_FILTER_INPUT_CLASS} pl-8 w-full`}
            />
          </div>
          <select value={sessionsProgramFilter} onChange={e => setSessionsProgramFilter(e.target.value)} className={FUNDABLE360_FILTER_INPUT_CLASS}>
            <option value="all">All Programs</option>
            {sessionsProgramOptions.map((p: string) => <option key={p} value={p}>{p.toUpperCase()}</option>)}
          </select>
          <select value={sessionsStatusFilter} onChange={e => setSessionsStatusFilter(e.target.value)} className={FUNDABLE360_FILTER_INPUT_CLASS}>
            <option value="all">All Statuses</option>
            {sessionsStatusOptions.map((s: string) => <option key={s} value={s}>{s}</option>)}
          </select>
          {sessionsFiltersActive && (
            <button
              onClick={() => { setSessionsSearch(""); setSessionsProgramFilter("all"); setSessionsStatusFilter("all"); }}
              className="text-xs font-semibold text-gray-400 hover:text-gray-600 px-2 flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
        {fundableSessionsQuery.isLoading ? (
          <div className="py-10 flex items-center justify-center text-gray-400 gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading...</div>
        ) : (fundableSessionsQuery.data ?? []).length === 0 ? (
          <div className="py-10 text-center text-gray-400 text-sm">No Fundable360 checkout sessions yet.</div>
        ) : filteredSessions.length === 0 ? (
          <div className="py-10 text-center text-gray-400 text-sm">No sessions match your search/filters.</div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  {["Customer", "Program", "Method", "Price", "Associate", "Status", "Date", "Session ID"].map(h => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedSessions.map((s: any) => (
                  <tr key={s.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="px-5 py-3">
                      <div className="font-medium text-gray-800 text-xs">{s.customerName ?? "-"}</div>
                      <div className="text-[10px] text-gray-400">{s.customerEmail ?? ""}</div>
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-600 font-mono uppercase">{s.planKey ?? "-"}</td>
                    <td className="px-5 py-3">
                      {s.method === "paid_in_full"
                        ? <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Paid in Full</span>
                        : s.method === "financed"
                        ? <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700">Financed</span>
                        : <span className="text-[10px] text-gray-300">-</span>}
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-700 font-mono">
                      {s.amountCents != null
                        ? `$${(s.amountCents / 100).toFixed(2)}${s.method === "financed" ? "/mo" : ""}`
                        : "-"}
                    </td>
                    <td className="px-5 py-3 text-xs">
                      <div className="text-gray-800">{s.repName ?? s.repCode ?? "-"}</div>
                      {s.repName && s.repCode && (
                        <div className="text-gray-400 font-mono">{s.repCode}</div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {/* Mode column removed - Fundable360 is live-only now. This badge
                          stays hidden unless a test-mode session somehow appears, so a
                          test row can never be mistaken for a real one. */}
                      {s.isTest && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-600 mr-1.5">TEST</span>
                      )}
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        s.status === "completed" ? "bg-green-100 text-green-700" :
                        s.status === "created" ? "bg-blue-100 text-blue-600" :
                        "bg-gray-100 text-gray-500"
                      }`}>{s.status ?? "created"}</span>
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-500">{s.createdAt ? new Date(s.createdAt).toLocaleDateString() : "-"}</td>
                    <td className="px-5 py-3 text-[10px] text-gray-300 font-mono truncate max-w-[220px]">{s.stripeSessionId ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Fundable360TablePager
            page={sessionsPageSafe}
            totalPages={sessionsTotalPages}
            totalItems={filteredSessions.length}
            pageSize={FUNDABLE360_PAGE_SIZE}
            onChange={setSessionsPage}
          />
          </>
        )}
      </div>

      {/* Financing links generator - moved below the two tables now that they lead the page */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5">
        <span className="text-sm font-semibold text-gray-700">Send a Financing Checkout Link</span>
        <p className="text-xs text-gray-400 mt-1 mb-3">
          Generates the 3 shareable Fundable360 financing checkout links (Foundation/Growth/Elite), pre-filled with a rep's referral code for attribution.
        </p>
        <div className="flex items-center gap-2">
          <input
            value={linkRepCode}
            onChange={e => setLinkRepCode(e.target.value.toUpperCase())}
            placeholder="Rep code (e.g. BC360-001)"
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-[#156082]/60 focus:ring-2 focus:ring-[#156082]/20 flex-1 max-w-xs"
          />
          <button
            onClick={() => setShowLinksFor(linkRepCode.trim())}
            disabled={!linkRepCode.trim()}
            className="px-4 py-2 text-sm font-semibold rounded-lg text-white disabled:opacity-40"
            style={{ background: "#156082" }}
          >
            Generate Links
          </button>
        </div>
        {showLinksFor && financingLinks && (
          <div className="mt-4 space-y-2">
            {financingLinks.map((l: any) => (
              <div key={l.program} className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 rounded-lg text-xs">
                <span className="font-semibold text-gray-700 w-20 shrink-0">{l.program}</span>
                <span className="font-mono text-gray-500 truncate flex-1">{l.financedUrl}</span>
                <button onClick={() => copyLink(l.financedUrl)} className="text-[#156082] font-semibold shrink-0">Copy</button>
              </div>
            ))}
          </div>
        )}
      </div>
        </>
      )}

      {showFoundationPaymentModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowFoundationPaymentModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4" onClick={event => event.stopPropagation()}>
            <h3 className="font-bold text-gray-800">Record External Foundation Payment</h3>
            <p className="text-xs text-gray-500">Record a confirmed setup or monthly payment collected outside integrated billing. Each setup fee and each monthly period can generate only one commission.</p>
            <label className="block text-xs font-semibold text-gray-600">Lead ID
              <input type="number" min="1" value={foundationPayment.leadId} onChange={event => setFoundationPayment(form => ({ ...form, leadId: event.target.value }))} className="block w-full border border-gray-200 rounded-lg px-3 py-2 mt-1" />
            </label>
            {foundationPaymentLead.data && (
              <p className="text-xs text-gray-600">{(foundationPaymentLead.data as any).businessName || `${(foundationPaymentLead.data as any).firstName ?? ""} ${(foundationPaymentLead.data as any).lastName ?? ""}`.trim() || (foundationPaymentLead.data as any).email}<br />
                Stage: {(foundationPaymentLead.data as any).ghlStageName || "not recorded"} | Assigned rep: {(foundationPaymentLead.data as any).repCode || "none"}</p>
            )}
            <label className="block text-xs font-semibold text-gray-600">Payment type
              <select value={foundationPayment.type} onChange={event => setFoundationPayment(form => ({ ...form, type: event.target.value as "monthly" | "setup" }))} className="block w-full border border-gray-200 rounded-lg px-3 py-2 mt-1">
                <option value="monthly">Monthly subscription</option><option value="setup">One-time setup fee</option>
              </select>
            </label>
            <label className="block text-xs font-semibold text-gray-600">Payment month
              <input type="month" value={foundationPayment.periodMonth} onChange={event => setFoundationPayment(form => ({ ...form, periodMonth: event.target.value }))} className="block w-full border border-gray-200 rounded-lg px-3 py-2 mt-1" />
            </label>
            <label className="block text-xs font-semibold text-gray-600">Payment confirmation source and date
              <textarea value={foundationPayment.notes} onChange={event => setFoundationPayment(form => ({ ...form, notes: event.target.value }))} rows={3} className="block w-full border border-gray-200 rounded-lg px-3 py-2 mt-1" />
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowFoundationPaymentModal(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg">Cancel</button>
              <button disabled={recordFoundationPayment.isPending || !foundationPaymentLead.data || !foundationPayment.periodMonth || foundationPayment.notes.trim().length < 5}
                onClick={() => recordFoundationPayment.mutate({ leadId: Number(foundationPayment.leadId), type: foundationPayment.type, periodMonth: foundationPayment.periodMonth, notes: foundationPayment.notes.trim() })}
                className="px-4 py-2 text-sm bg-[#156082] text-white rounded-lg disabled:opacity-40">
                {recordFoundationPayment.isPending ? "Recording..." : "Record Payment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create External Financing Plan Modal */}
      {showManualPlanModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowManualPlanModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800">Create External Financing Plan</h3>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  For a financed client whose payments are collected outside BizConnect360's own Stripe
                  (e.g. a channel partner). Creates the 6-installment schedule; release each installment
                  below as its payment is confirmed.
                </p>
              </div>
              <button onClick={() => setShowManualPlanModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Lead ID</label>
              <input
                type="text"
                value={manualPlanForm.leadId}
                onChange={e => setManualPlanForm(f => ({ ...f, leadId: e.target.value.replace(/[^0-9]/g, "") }))}
                placeholder="e.g. 1932"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#156082]"
              />
              {manualPlanLeadQuery.data && (
                <div className="text-[11px] mt-1 text-gray-500">
                  {(manualPlanLeadQuery.data as any).businessName || `${(manualPlanLeadQuery.data as any).firstName ?? ""} ${(manualPlanLeadQuery.data as any).lastName ?? ""}`.trim() || (manualPlanLeadQuery.data as any).email}
                  {" - "}
                  <span className={(manualPlanLeadQuery.data as any).verticalName === "Fundable360" ? "text-green-600" : "text-red-500 font-semibold"}>
                    {(manualPlanLeadQuery.data as any).verticalName ?? "no vertical"}
                  </span>
                  <div>Recorded stage: {(manualPlanLeadQuery.data as any).ghlStageName || (manualPlanLeadQuery.data as any).stage || "not recorded"}</div>
                  <div>Assigned rep: {(manualPlanLeadQuery.data as any).repCode || "none"}</div>
                </div>
              )}
              {manualPlanForm.leadId && !manualPlanLeadQuery.isLoading && !manualPlanLeadQuery.data && (
                <p className="text-[11px] mt-1 text-red-500">Lead not found.</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Program</label>
              <select
                value={manualPlanForm.program}
                onChange={e => setManualPlanForm(f => ({ ...f, program: e.target.value as any }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#156082]"
              >
                <option value="Growth">Growth</option>
                <option value="Elite">Elite</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Associate Rep Code</label>
              <input
                type="text"
                value={manualPlanForm.repCode}
                onChange={e => setManualPlanForm(f => ({ ...f, repCode: e.target.value.toUpperCase() }))}
                placeholder="e.g. BC-642"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#156082]"
              />
              <p className="text-[10px] text-gray-400 mt-1">Must already exist in Reps. This is who gets paid.</p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">
                Monthly Client Amount <span className="font-normal text-gray-400">(optional, defaults to the standard financed rate for this program)</span>
              </label>
              <input
                type="text"
                value={manualPlanForm.monthlyClientAmount}
                onChange={e => setManualPlanForm(f => ({ ...f, monthlyClientAmount: e.target.value.replace(/[^0-9.]/g, "") }))}
                placeholder="e.g. 400"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#156082]"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Notes <span className="font-normal text-gray-400">(required, where and how this is being paid)</span></label>
              <textarea
                value={manualPlanForm.notes}
                onChange={e => setManualPlanForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="e.g. Channel partner billing directly, first payment confirmed 2026-09-27"
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#156082]"
              />
            </div>

            <div className="flex gap-2 justify-end pt-2 border-t border-gray-100">
              <button onClick={() => setShowManualPlanModal(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">Cancel</button>
              <button
                disabled={
                  createManualPlanMutation.isPending ||
                  !manualPlanForm.leadId || !manualPlanForm.repCode.trim() || !manualPlanForm.notes.trim()
                }
                onClick={() => createManualPlanMutation.mutate({
                  leadId: manualPlanLeadId,
                  program: manualPlanForm.program,
                  repCode: manualPlanForm.repCode.trim(),
                  monthlyClientAmount: manualPlanForm.monthlyClientAmount ? parseFloat(manualPlanForm.monthlyClientAmount) : undefined,
                  notes: manualPlanForm.notes.trim(),
                })}
                className="px-4 py-2 text-sm bg-[#156082] text-white rounded-lg hover:bg-[#0f4a63] transition-colors disabled:opacity-40"
              >
                {createManualPlanMutation.isPending ? "Creating…" : "Create Plan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Price ID Editor Modal */}
      {editingPriceIds !== null && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditingPriceIds(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800">{editingPriceIds.name}</h3>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  planKey <code className="font-mono bg-gray-100 px-1 rounded">{editingPriceIds.planKey}</code> is fixed. Checkout URLs depend on it.
                </p>
              </div>
              <button onClick={() => setEditingPriceIds(null)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>

            <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-[11px] text-gray-500">
              Charge amounts (${((editingPriceIds.setupAmountCents ?? 0) / 100).toFixed(0)} paid in full,
              ${((editingPriceIds.monthlyAmountCents ?? 0) / 100).toFixed(0)}/mo financed) are fixed server side and are not editable here.
            </div>

            {([
              { key: "setupFeePriceId", label: "Paid in Full Price ID", hint: "the one-time price", test: false },
              { key: "recurringPriceId", label: "Financing Price ID", hint: "the recurring monthly price", test: false },
              { key: "testSetupFeePriceId", label: "TEST Paid in Full Price ID", hint: "test mode one-time price", test: true },
              { key: "testRecurringPriceId", label: "TEST Financing Price ID", hint: "test mode recurring price", test: true },
            ] as const).map(({ key, label, hint, test }) => {
              const val = (priceIdForm as any)[key] as string;
              const warn = priceIdShapeWarning(val) ?? wrongAccountWarning(val);
              return (
                <div key={key}>
                  <label className={`block text-xs font-semibold mb-1 ${test ? "text-blue-500" : "text-gray-500"}`}>
                    {label} <span className="font-normal text-gray-400">- {hint}</span>
                  </label>
                  <input
                    type="text"
                    value={val}
                    onChange={e => setPriceIdForm(f => ({ ...f, [key]: e.target.value }))}
                    placeholder="price_..."
                    className={`w-full border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none ${
                      warn ? "border-orange-300 focus:border-orange-400" : test ? "border-blue-100 focus:border-blue-300" : "border-gray-200 focus:border-[#156082]"
                    }`}
                  />
                  {warn && <p className="text-[10px] text-orange-500 mt-1">{warn}</p>}
                </div>
              );
            })}

            <div className="flex gap-2 justify-end pt-2 border-t border-gray-100">
              <button onClick={() => setEditingPriceIds(null)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">Cancel</button>
              <button
                onClick={() => savePricesMut.mutate({
                  id: editingPriceIds.id,
                  brand: "fundable360",
                  planKey: editingPriceIds.planKey,
                  name: editingPriceIds.name,
                  monthlyAmountCents: editingPriceIds.monthlyAmountCents ?? 0,
                  setupAmountCents: editingPriceIds.setupAmountCents ?? 0,
                  yearlyAmountCents: editingPriceIds.yearlyAmountCents ?? 0,
                  setupFeePriceId: priceIdForm.setupFeePriceId.trim() || null,
                  recurringPriceId: priceIdForm.recurringPriceId.trim() || null,
                  testSetupFeePriceId: priceIdForm.testSetupFeePriceId.trim() || null,
                  testRecurringPriceId: priceIdForm.testRecurringPriceId.trim() || null,
                  yearlyRecurringPriceId: editingPriceIds.yearlyRecurringPriceId ?? null,
                  testYearlyRecurringPriceId: editingPriceIds.testYearlyRecurringPriceId ?? null,
                  successUrl: editingPriceIds.successUrl ?? null,
                  cancelUrl: editingPriceIds.cancelUrl ?? null,
                  description: editingPriceIds.description ?? null,
                  isActive: editingPriceIds.isActive ?? true,
                })}
                disabled={savePricesMut.isPending}
                className="px-4 py-2 text-sm text-white rounded-lg font-semibold disabled:opacity-50 transition-colors flex items-center gap-1.5"
                style={{ background: "#156082" }}
              >
                {savePricesMut.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Save Price IDs
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GracePhoneAdminTab() {
  const [toNumber, setToNumber] = useState("");
  const [repCode, setRepCode] = useState("");
  const [outboundAgent, setOutboundAgent] = useState<"grace" | "dawn">("grace");
  const [lastResult, setLastResult] = useState<any>(null);
  const [lastError, setLastError] = useState<{ message: string; detail?: any } | null>(null);
  const [transferEdits, setTransferEdits] = useState<Record<string, string>>({});
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);
  const [callFilter, setCallFilter] = useState<"all" | "grace" | "dawn">("all");
  // Wibiz Voice persistent history state
  const [elFilter, setElFilter] = useState<"all" | "grace" | "dawn">("all");
  const [expandedElId, setExpandedElId] = useState<string | null>(null);
  const [elAudioUrl, setElAudioUrl] = useState<Record<string, string>>({});
  const [elAudioLoading, setElAudioLoading] = useState<Set<string>>(new Set());
  const [showDebugLog, setShowDebugLog] = useState(false);
  const statusQuery = trpc.grace.phoneStatus.useQuery(undefined, { refetchInterval: 15000 });
  const logsQuery = trpc.grace.phoneLogs.useQuery({ limit: 60 }, { refetchInterval: 5000 });
  const callLogsQuery = trpc.grace.callLogs.useQuery({ limit: 100 }, { refetchInterval: 15000 });
  const elConvsQuery = trpc.grace.elevenLabsConversations.useQuery(
    { agentFilter: elFilter, limit: 100 },
    { refetchInterval: 30000 }
  );
  const elDetailQuery = trpc.grace.elevenLabsConversationDetail.useQuery(
    { conversationId: expandedElId ?? "" },
    { enabled: !!expandedElId }
  );
  const allSettings = trpc.settings.getAll.useQuery();
  const updateSetting = trpc.settings.update.useMutation({
    onSuccess: () => {
      toast.success("Transfer number saved.");
      allSettings.refetch();
      statusQuery.refetch();
    },
    onError: () => toast.error("Failed to save transfer number."),
  });
  const outboundTestMutation = trpc.grace.outboundTestCall.useMutation({
    onSuccess: (result) => {
      setLastResult(result);
      setLastError(null);
      logsQuery.refetch();
      toast.success(`${(result as any).agent === "dawn" ? "Dawn" : "Grace"} outbound test call started.`);
    },
    onError: (error: any) => {
      setLastError({
        message: error.message || "Failed to start outbound test call.",
        detail: error.data ?? error.shape ?? null,
      });
      logsQuery.refetch();
      toast.error(error.message || "Failed to start outbound test call.");
    },
  });

  const getSettingValue = (key: string, fallback?: string | null) => {
    if (transferEdits[key] !== undefined) return transferEdits[key];
    return allSettings.data?.find((s: any) => s.key === key)?.value ?? fallback ?? "";
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Voice Phone Testing</h2>
            <p className="text-sm text-gray-500">
              Internal tool for outbound phone-call testing and Railway-side debug visibility.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => { statusQuery.refetch(); logsQuery.refetch(); }}>
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { label: "API Key", ok: !!statusQuery.data?.apiKeyPartial, hint: statusQuery.data?.apiKeyPartial },
            { label: "Grace Agent ID", ok: statusQuery.data?.graceAgentConfigured, hint: statusQuery.data?.graceAgentIdPartial },
            { label: "Grace Phone Num ID", ok: statusQuery.data?.outboundConfigured, hint: statusQuery.data?.gracePhoneNumIdPartial },
            { label: "Dawn Agent ID", ok: statusQuery.data?.dawnAgentConfigured, hint: statusQuery.data?.dawnAgentIdPartial },
            { label: "Dawn Phone Num ID", ok: !!statusQuery.data?.dawnPhoneNumIdPartial, hint: statusQuery.data?.dawnPhoneNumIdPartial },
            { label: "Personalization Secret", ok: statusQuery.data?.personalizationSecretConfigured, hint: null },
            { label: "Support Transfer", ok: statusQuery.data?.transferSupportConfigured, hint: null },
            { label: "Booking Transfer", ok: statusQuery.data?.transferBookingConfigured, hint: null },
            { label: "Specialist Transfer", ok: statusQuery.data?.transferSpecialistConfigured, hint: null },
          ].map((item) => (
            <div key={item.label} className="rounded-xl border border-gray-100 bg-[#F8FAFC] px-4 py-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">{item.label}</div>
              <div className={`mt-1 text-sm font-semibold ${item.ok ? "text-emerald-600" : "text-rose-600"}`}>
                {item.ok ? "Configured" : "Missing"}
              </div>
              {item.hint && (
                <div className="mt-0.5 text-[11px] font-mono text-gray-400 truncate">{item.hint}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <div className="mb-4">
          <h3 className="text-base font-bold text-gray-900">Transfer Destinations</h3>
          <p className="text-sm text-gray-500">
            Update the phone numbers the voice agents should use for support, booking, and specialist transfers. These override Railway env vars immediately.
          </p>
        </div>

        <div className="space-y-4">
          {[
            {
              key: "grace_transfer_support_number",
              label: "Support Transfer Number",
              fallback: statusQuery.data?.transferSupportNumber,
            },
            {
              key: "grace_transfer_booking_number",
              label: "Booking Transfer Number",
              fallback: statusQuery.data?.transferBookingNumber,
            },
            {
              key: "grace_transfer_specialist_number",
              label: "Specialist Transfer Number",
              fallback: statusQuery.data?.transferSpecialistNumber,
            },
          ].map((item) => (
            <div key={item.key} className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                  {item.label}
                </label>
                <Input
                  value={getSettingValue(item.key, item.fallback)}
                  onChange={(e) => setTransferEdits((prev) => ({ ...prev, [item.key]: e.target.value }))}
                  placeholder="+15551234567"
                />
              </div>
              <Button
                className="bg-[#84ab4a] hover:bg-[#6e9038] text-white"
                disabled={updateSetting.isPending}
                onClick={() => updateSetting.mutate({
                  key: item.key,
                  value: getSettingValue(item.key, item.fallback).trim() || null,
                })}
              >
                <Save className="w-4 h-4 mr-1.5" />
                Save
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <div className="mb-4">
          <h3 className="text-base font-bold text-gray-900">Start Outbound Test Call</h3>
          <p className="text-sm text-gray-500">
            Select which voice agent to call from. Enter an internal team phone number and an optional rep code for associate-context personalization.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[auto_1.4fr_1fr_auto] gap-3 items-end">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
              Agent
            </label>
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              {(["grace", "dawn"] as const).map(a => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setOutboundAgent(a)}
                  className={`px-4 py-2 text-xs font-bold capitalize transition-colors ${outboundAgent === a ? "bg-[#84ab4a] text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
                >
                  {a === "grace" ? "Grace (+1 979 315 9559)" : "Dawn (+1 979 978 7337)"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
              Phone Number
            </label>
            <Input
              value={toNumber}
              onChange={(e) => setToNumber(e.target.value)}
              placeholder="+15551234567"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
              Rep Code{" "}
              {outboundAgent === "dawn"
                ? <span className="text-red-400 normal-case font-normal">(Required for Dawn)</span>
                : <span className="normal-case font-normal">(Optional for Grace)</span>}
            </label>
            <Input
              value={repCode}
              onChange={(e) => setRepCode(e.target.value.toUpperCase())}
              placeholder={outboundAgent === "dawn" ? "BC-362" : "BC-360 (optional)"}
            />
          </div>
          <Button
            className="bg-[#84ab4a] hover:bg-[#6e9038] text-white"
            disabled={!toNumber.trim() || (outboundAgent === "dawn" && !repCode.trim()) || outboundTestMutation.isPending}
            onClick={() => outboundTestMutation.mutate({
              toNumber: toNumber.trim(),
              agent: outboundAgent,
              ...(repCode.trim() ? { repCode: repCode.trim() } : {}),
            })}
          >
            {outboundTestMutation.isPending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Phone className="w-4 h-4 mr-1.5" />}
            Call Now
          </Button>
        </div>

        {lastResult && !lastError && (
          <div className="mt-4 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">
            <div className="text-sm font-semibold text-emerald-700">
              Call accepted — {(lastResult as any).agent === "dawn" ? "Dawn" : "Grace"}
            </div>
            <pre className="mt-2 text-[11px] font-mono text-emerald-800 whitespace-pre-wrap break-all bg-white rounded-lg border border-emerald-100 p-2">
              {JSON.stringify({ agent: lastResult.agent, mode: lastResult.mode, repCode: lastResult.repCode ?? null, ...lastResult.response }, null, 2)}
            </pre>
          </div>
        )}
        {lastError && (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-rose-700">
              <span>Call failed</span>
            </div>
            <div className="mt-1 text-xs text-rose-800">{lastError.message}</div>
            {lastError.detail && (
              <pre className="mt-2 text-[11px] font-mono text-rose-700 whitespace-pre-wrap break-all bg-white rounded-lg border border-rose-100 p-2 overflow-x-auto">
                {JSON.stringify(lastError.detail, null, 2)}
              </pre>
            )}
            <p className="mt-2 text-[11px] text-rose-600">Check the event log below for the full Wibiz Voice response body and HTTP status.</p>
          </div>
        )}
      </div>

      {/* ── AI Call Command Center ── */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-base font-bold text-gray-900">AI Call Command Center</h3>
            <p className="text-sm text-gray-500">
              Completed call records from all AI voice workflows — inbound, outbound, and transferred calls.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-semibold">
              {(["all", "grace", "dawn"] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setCallFilter(f)}
                  className={`px-3 py-1.5 capitalize transition-colors ${callFilter === f ? "bg-[#84ab4a] text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
                >
                  {f === "all" ? "All Agents" : f === "grace" ? "Grace" : "Dawn"}
                </button>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={() => callLogsQuery.refetch()}>
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Stats row */}
        {(() => {
          const calls = callLogsQuery.data ?? [];
          const filtered = callFilter === "all" ? calls : calls.filter((c: any) => c.agentLabel?.toLowerCase() === callFilter);
          const successCount = filtered.filter((c: any) => c.outcome === "true" || c.outcome === "success").length;
          const avgDur = filtered.length ? Math.round(filtered.reduce((s: number, c: any) => s + (c.durationSecs ?? 0), 0) / filtered.length) : 0;
          return (
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { label: "Total Calls", value: filtered.length },
                { label: "Success Rate", value: filtered.length ? `${Math.round((successCount / filtered.length) * 100)}%` : "—" },
                { label: "Avg Duration", value: avgDur ? `${Math.floor(avgDur / 60)}m ${avgDur % 60}s` : "—" },
              ].map(stat => (
                <div key={stat.label} className="rounded-xl border border-gray-100 bg-[#F8FAFC] px-4 py-3 text-center">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">{stat.label}</div>
                  <div className="mt-1 text-xl font-bold text-gray-900">{stat.value}</div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Call records table */}
        {(() => {
          const calls = callLogsQuery.data ?? [];
          const filtered = callFilter === "all" ? calls : calls.filter((c: any) => c.agentLabel?.toLowerCase() === callFilter);
          if (!filtered.length) {
            return (
              <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-6 py-10 text-center">
                <Phone className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No completed calls recorded yet.</p>
                <p className="text-xs text-gray-400 mt-1">Records appear here after Wibiz Voice fires the post-call webhook.</p>
              </div>
            );
          }
          return (
            <div className="space-y-2">
              {/* Table header */}
              <div className="grid grid-cols-[80px_1fr_1fr_80px_60px_90px_90px] gap-2 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                <div>Agent</div>
                <div>Date &amp; Time</div>
                <div>Caller / Callee</div>
                <div>Duration</div>
                <div>Msgs</div>
                <div>Outcome</div>
                <div></div>
              </div>

              {filtered.map((call: any) => {
                const isExpanded = expandedCallId === call.id;
                const isSuccess = call.outcome === "true" || call.outcome === "success";
                const dur = call.durationSecs != null
                  ? `${Math.floor(call.durationSecs / 60)}m ${call.durationSecs % 60}s`
                  : "—";
                const caller = call.callerNumber ? `****${String(call.callerNumber).slice(-4)}` : "—";
                const callee = call.calleeNumber ? `****${String(call.calleeNumber).slice(-4)}` : "—";
                const ts = new Date(call.timestamp);
                const dateStr = ts.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                const timeStr = ts.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
                return (
                  <div key={call.id} className="rounded-xl border border-gray-100 overflow-hidden">
                    <div className="grid grid-cols-[80px_1fr_1fr_80px_60px_90px_90px] gap-2 items-center px-3 py-2.5 bg-[#F8FAFC] hover:bg-gray-100 transition-colors">
                      <div className="flex flex-col gap-1">
                        <Badge variant="outline" className={`text-[10px] font-bold uppercase ${call.agentLabel === "Dawn" ? "border-violet-300 text-violet-700 bg-violet-50" : "border-emerald-300 text-emerald-700 bg-emerald-50"}`}>
                          {call.agentLabel}
                        </Badge>
                        {call.direction && call.direction !== "unknown" && (
                          <span className={`text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded w-fit ${call.direction === "inbound" ? "bg-blue-50 text-blue-600" : "bg-orange-50 text-orange-600"}`}>
                            {call.direction}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-700">
                        <span className="font-medium">{dateStr}</span>
                        <span className="text-gray-400 ml-1">{timeStr}</span>
                      </div>
                      <div className="text-xs text-gray-500 font-mono">
                        {caller !== "—" ? <span>Caller: {caller}</span> : null}
                        {callee !== "—" && caller !== callee ? <span className="ml-2 text-gray-400">→ {callee}</span> : null}
                        {caller === "—" && callee === "—" ? "—" : null}
                      </div>
                      <div className="text-xs text-gray-700 font-medium">{dur}</div>
                      <div className="text-xs text-gray-500">{call.messageCount ?? "—"}</div>
                      <div>
                        {call.outcome ? (
                          <Badge variant="outline" className={`text-[10px] ${isSuccess ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-rose-300 text-rose-700 bg-rose-50"}`}>
                            {isSuccess ? "Success" : call.outcome}
                          </Badge>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </div>
                      <div className="flex justify-end">
                        <button
                          onClick={() => setExpandedCallId(isExpanded ? null : call.id)}
                          className="text-xs text-[#84ab4a] font-semibold hover:underline flex items-center gap-1"
                        >
                          <MessageSquare className="w-3 h-3" />
                          {isExpanded ? "Hide" : "Transcript"}
                        </button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="border-t border-gray-100 bg-white px-4 py-3 space-y-3">
                        {/* Summary */}
                        {call.summary && (
                          <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2">
                            <div className="text-[10px] font-bold uppercase tracking-wide text-blue-500 mb-1">AI Summary</div>
                            <p className="text-xs text-blue-900 leading-relaxed">{call.summary}</p>
                          </div>
                        )}

                        {/* Full transcript */}
                        {call.transcript && call.transcript.length > 0 ? (
                          <div className="space-y-2">
                            <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Full Conversation</div>
                            {call.transcript.map((turn: any, i: number) => {
                              const isAgent = turn.role === "agent" || turn.role === "assistant";
                              return (
                                <div key={i} className={`flex gap-2 ${isAgent ? "" : "flex-row-reverse"}`}>
                                  <div className={`shrink-0 rounded-full w-6 h-6 flex items-center justify-center text-[9px] font-bold ${isAgent ? "bg-[#84ab4a] text-white" : "bg-gray-200 text-gray-600"}`}>
                                    {isAgent ? call.agentLabel?.[0] ?? "A" : "C"}
                                  </div>
                                  <div className={`max-w-[80%] rounded-xl px-3 py-2 text-xs leading-relaxed ${isAgent ? "bg-gray-100 text-gray-800 rounded-tl-none" : "bg-[#84ab4a]/10 text-gray-800 rounded-tr-none"}`}>
                                    <div className={`text-[9px] font-bold uppercase tracking-wide mb-0.5 ${isAgent ? "text-[#84ab4a]" : "text-gray-500"}`}>
                                      {isAgent ? (call.agentLabel ?? "Agent") : "Caller"}
                                      {turn.timeInCallSecs != null ? <span className="ml-1 font-normal normal-case">@ {turn.timeInCallSecs}s</span> : null}
                                    </div>
                                    {turn.message}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="rounded-lg bg-gray-50 border border-dashed border-gray-200 px-3 py-4 text-center">
                            <p className="text-xs text-gray-400">
                              {call.summary ? "Full transcript not captured for this call." : "No transcript or summary available for this call."}
                            </p>
                            <p className="text-[10px] text-gray-400 mt-1">Wibiz Voice must include <code>transcript</code> in the post-call webhook payload.</p>
                          </div>
                        )}

                        {/* Meta */}
                        <div className="flex gap-4 pt-1 text-[10px] text-gray-400 font-mono">
                          {call.conversationId && <span>ID: {call.conversationId}</span>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* ── Wibiz Voice Call History (persistent, survives deploys) ── */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-base font-bold text-gray-900">Wibiz Voice Call History</h3>
            <p className="text-sm text-gray-500">
              All conversations stored in Wibiz Voice — persistent across deploys. Includes transcripts and audio replay.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-semibold">
              {(["all", "grace", "dawn"] as const).map(f => (
                <button
                  key={f}
                  onClick={() => { setElFilter(f); setExpandedElId(null); }}
                  className={`px-3 py-1.5 capitalize transition-colors ${elFilter === f ? "bg-[#84ab4a] text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
                >
                  {f === "all" ? "All Agents" : f === "grace" ? "Grace" : "Dawn"}
                </button>
              ))}
            </div>
            <button
              onClick={() => elConvsQuery.refetch()}
              className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 text-gray-500 ${elConvsQuery.isFetching ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {elConvsQuery.isLoading ? (
          <div className="flex items-center justify-center py-10 gap-2 text-gray-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading conversations…
          </div>
        ) : elConvsQuery.isError ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700">
            Could not load Wibiz Voice history. Check that the voice API key is configured.
          </div>
        ) : !elConvsQuery.data?.length ? (
          <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-6 py-10 text-center">
            <Phone className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No conversations found in Wibiz Voice yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Table header */}
            <div className="grid grid-cols-[80px_1fr_1fr_80px_60px_100px_120px] gap-2 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              <div>Agent</div>
              <div>Date &amp; Time</div>
              <div>Caller / Callee</div>
              <div>Duration</div>
              <div>Msgs</div>
              <div>Outcome</div>
              <div></div>
            </div>

            {(elConvsQuery.data ?? []).map((conv: any) => {
              const isExpanded = expandedElId === conv.conversationId;
              const isSuccess = conv.callSuccessful === "true" || conv.callSuccessful === "success";
              const dur = conv.durationSecs != null
                ? `${Math.floor(conv.durationSecs / 60)}m ${conv.durationSecs % 60}s`
                : "—";
              const caller = conv.callerNumber ? `****${String(conv.callerNumber).slice(-4)}` : "—";
              const callee = conv.calleeNumber ? `****${String(conv.calleeNumber).slice(-4)}` : "—";
              const ts = conv.startTimeUnixSecs ? new Date(conv.startTimeUnixSecs * 1000) : null;
              const dateStr = ts ? ts.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
              const timeStr = ts ? ts.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }) : "";

              const detail = isExpanded ? elDetailQuery.data : null;
              const detailLoading = isExpanded && elDetailQuery.isFetching;
              const audioUrl = elAudioUrl[conv.conversationId];
              const audioLoading = elAudioLoading.has(conv.conversationId);

              const loadAudio = async () => {
                if (audioUrl || audioLoading) return;
                setElAudioLoading(prev => new Set(prev).add(conv.conversationId));
                try {
                  const res = await fetch(`/api/grace-phone/el-conversation/${encodeURIComponent(conv.conversationId)}/audio`);
                  if (res.ok) {
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    setElAudioUrl(prev => ({ ...prev, [conv.conversationId]: url }));
                  }
                } catch { /* ignore */ } finally {
                  setElAudioLoading(prev => { const s = new Set(prev); s.delete(conv.conversationId); return s; });
                }
              };

              // Infer direction: if the callee is one of the known Grace/Dawn numbers → inbound
              const calleeStr = conv.calleeNumber ?? "";
              const callerStr = conv.callerNumber ?? "";
              const calleeIsAgent = calleeStr.endsWith("9559") || calleeStr.endsWith("7337");
              const callerIsAgent = callerStr.endsWith("9559") || callerStr.endsWith("7337");
              const elDirection = calleeIsAgent ? "inbound" : callerIsAgent ? "outbound" : null;

              return (
                <div key={conv.conversationId} className="rounded-xl border border-gray-100 overflow-hidden">
                  <div className="grid grid-cols-[80px_1fr_1fr_80px_60px_100px_120px] gap-2 items-center px-3 py-2.5 bg-[#F8FAFC] hover:bg-gray-100 transition-colors">
                    <div className="flex flex-col gap-1">
                      <Badge variant="outline" className={`text-[10px] font-bold uppercase ${conv.agentLabel === "Dawn" ? "border-violet-300 text-violet-700 bg-violet-50" : "border-emerald-300 text-emerald-700 bg-emerald-50"}`}>
                        {conv.agentLabel}
                      </Badge>
                      {elDirection && (
                        <span className={`text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded w-fit ${elDirection === "inbound" ? "bg-blue-50 text-blue-600" : "bg-orange-50 text-orange-600"}`}>
                          {elDirection}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-700">
                      <span className="font-medium">{dateStr}</span>
                      {timeStr && <span className="text-gray-400 ml-1">{timeStr}</span>}
                    </div>
                    <div className="text-xs text-gray-500 font-mono">
                      {caller !== "—" ? <span>Caller: {caller}</span> : null}
                      {callee !== "—" && caller !== callee ? <span className="ml-2 text-gray-400">→ {callee}</span> : null}
                      {caller === "—" && callee === "—" ? "—" : null}
                    </div>
                    <div className="text-xs text-gray-700 font-medium">{dur}</div>
                    <div className="text-xs text-gray-500">{conv.messageCount ?? "—"}</div>
                    <div>
                      {conv.callSuccessful ? (
                        <Badge variant="outline" className={`text-[10px] ${isSuccess ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-rose-300 text-rose-700 bg-rose-50"}`}>
                          {isSuccess ? "Success" : conv.callSuccessful}
                        </Badge>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setExpandedElId(isExpanded ? null : conv.conversationId)}
                        className="text-xs text-[#84ab4a] font-semibold hover:underline flex items-center gap-1"
                      >
                        <MessageSquare className="w-3 h-3" />
                        {isExpanded ? "Hide" : "Transcript"}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-white px-4 py-3 space-y-3">
                      {detailLoading && (
                        <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading transcript…
                        </div>
                      )}

                      {/* Audio player */}
                      {!audioUrl ? (
                        <button
                          onClick={loadAudio}
                          disabled={audioLoading}
                          className="flex items-center gap-1.5 text-xs text-violet-600 font-semibold hover:underline disabled:opacity-50"
                        >
                          {audioLoading
                            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading audio…</>
                            : <><span>▶</span> Play Recording</>
                          }
                        </button>
                      ) : (
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">Call Recording</div>
                          <audio controls src={audioUrl} className="w-full h-8" style={{ height: 32 }} />
                        </div>
                      )}

                      {/* Summary */}
                      {detail?.transcriptSummary && (
                        <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2">
                          <div className="text-[10px] font-bold uppercase tracking-wide text-blue-500 mb-1">AI Summary</div>
                          <p className="text-xs text-blue-900 leading-relaxed">{detail.transcriptSummary}</p>
                        </div>
                      )}

                      {/* Full transcript */}
                      {detail?.transcript && detail.transcript.length > 0 ? (
                        <div className="space-y-2">
                          <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Full Conversation</div>
                          {detail.transcript.map((turn: any, i: number) => {
                            const isAgent = turn.role === "agent" || turn.role === "assistant";
                            return (
                              <div key={i} className={`flex gap-2 ${isAgent ? "" : "flex-row-reverse"}`}>
                                <div className={`shrink-0 rounded-full w-6 h-6 flex items-center justify-center text-[9px] font-bold ${isAgent ? (conv.agentLabel === "Dawn" ? "bg-violet-500 text-white" : "bg-[#84ab4a] text-white") : "bg-gray-200 text-gray-600"}`}>
                                  {isAgent ? conv.agentLabel?.[0] ?? "A" : "C"}
                                </div>
                                <div className={`max-w-[80%] rounded-xl px-3 py-2 text-xs leading-relaxed ${isAgent ? "bg-gray-100 text-gray-800 rounded-tl-none" : "bg-[#84ab4a]/10 text-gray-800 rounded-tr-none"}`}>
                                  <div className={`text-[9px] font-bold uppercase tracking-wide mb-0.5 ${isAgent ? (conv.agentLabel === "Dawn" ? "text-violet-600" : "text-[#84ab4a]") : "text-gray-500"}`}>
                                    {isAgent ? (conv.agentLabel ?? "Agent") : "Caller"}
                                    {turn.timeInCallSecs != null ? <span className="ml-1 font-normal normal-case">@ {turn.timeInCallSecs}s</span> : null}
                                  </div>
                                  {turn.message}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : !detailLoading && (
                        <div className="rounded-lg bg-gray-50 border border-dashed border-gray-200 px-3 py-4 text-center">
                          <p className="text-xs text-gray-400">Transcript not available for this conversation.</p>
                        </div>
                      )}

                      {/* Meta */}
                      <div className="flex gap-4 pt-1 text-[10px] text-gray-400 font-mono">
                        <span>ID: {conv.conversationId}</span>
                        {detail?.status && <span>Status: {detail.status}</span>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Recent Voice Phone Events (debug log) ── */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              Recent Voice Phone Events
              <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">Developer</span>
            </h3>
            <p className="text-sm text-gray-500">
              Latest backend events captured in memory on the live server.
            </p>
          </div>
          <button
            onClick={() => setShowDebugLog(v => !v)}
            className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${showDebugLog ? "rotate-180" : ""}`} />
            {showDebugLog ? "Hide" : "Show"}
          </button>
        </div>

        {showDebugLog && <div className="space-y-3 max-h-[620px] overflow-y-auto">
          {(logsQuery.data ?? []).map((event: any) => {
            const isError = event.type === "outbound_error" || event.type === "personalization_error" || event.type === "voice_http_error";
            const isSuccess = event.type === "outbound_response";
            const isRequest = event.type === "outbound_request";
            const rowCls = isError
              ? "rounded-xl border border-rose-200 bg-rose-50 px-4 py-3"
              : isSuccess
              ? "rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3"
              : isRequest
              ? "rounded-xl border border-blue-100 bg-blue-50 px-4 py-3"
              : "rounded-xl border border-gray-100 bg-[#F8FAFC] px-4 py-3";
            const badgeCls = isError
              ? "text-[11px] uppercase tracking-wide border-rose-300 text-rose-700 bg-rose-100"
              : isSuccess
              ? "text-[11px] uppercase tracking-wide border-emerald-300 text-emerald-700 bg-emerald-100"
              : isRequest
              ? "text-[11px] uppercase tracking-wide border-blue-300 text-blue-700 bg-blue-100"
              : "text-[11px] uppercase tracking-wide";
            const msgCls = isError ? "text-sm font-semibold text-rose-900" : isSuccess ? "text-sm font-semibold text-emerald-900" : "text-sm font-semibold text-gray-900";
            const preCls = isError
              ? "mt-3 text-xs whitespace-pre-wrap break-words bg-white rounded-lg border border-rose-100 p-3 overflow-x-auto text-rose-800 font-mono"
              : isSuccess
              ? "mt-3 text-xs whitespace-pre-wrap break-words bg-white rounded-lg border border-emerald-100 p-3 overflow-x-auto text-emerald-800 font-mono"
              : "mt-3 text-xs text-gray-600 whitespace-pre-wrap break-words bg-white rounded-lg border border-gray-100 p-3 overflow-x-auto font-mono";
            return (
              <div key={event.id} className={rowCls}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant="outline" className={badgeCls}>{event.type}</Badge>
                    <span className={`${msgCls} truncate`}>{event.message}</span>
                  </div>
                  <span className="text-xs text-gray-400 shrink-0">{new Date(event.timestamp).toLocaleTimeString()}</span>
                </div>
                {event.metadata && (
                  <pre className={preCls}>
                    {JSON.stringify(event.metadata, null, 2)}
                  </pre>
                )}
              </div>
            );
          })}
          {!logsQuery.data?.length && (
            <div className="text-sm text-gray-400">No phone events captured yet.</div>
          )}
        </div>}
      </div>
    </div>
  );
}

// A "ghost lead" (hidden by default, eligible for bulk cleanup) is a
// self-registration/pre-pipeline artifact with no real GHL opportunity AND no
// other legitimate tracking mechanism. Some verticals intentionally never get
// a ghlOpportunityId at all - Scale360's Comprehensive Analysis funnel
// (tracked instead via s360AnalysisSessionId, see getAllLeads in server/db.ts)
// and Fundable360's Stripe-checkout-originated enrollments (source
// "fundable360-checkout") - so those must never be classified as ghosts, or
// they silently disappear from Admin's default view/search (reported
// 2026-08-17: visible on the associate dashboard, "0 matching" in Admin) and,
// far worse, become eligible for the "Delete N no-opp-ID leads" bulk-delete
// button, which would permanently destroy real customer leads. Mirrors the
// exact carve-out server/db.ts's getAllLeads already applies.
export function isGhostLead(l: { ghlOpportunityId?: string | null; s360AnalysisSessionId?: string | null; source?: string | null }): boolean {
  return !l.ghlOpportunityId && !l.s360AnalysisSessionId && l.source !== "fundable360-checkout";
}

// ─── All Leads Table ──────────────────────────────────────────────────────────
function AllLeadsTable({
  leads,
  onSelectLead,
  onDeleteLead,
  onBulkDeleteNoOppId,
  isBulkDeleting,
}: {
  leads: any[];
  onSelectLead: (id: number) => void;
  onDeleteLead: (id: number) => void;
  onBulkDeleteNoOppId: (repCode?: string) => void;
  isBulkDeleting: boolean;
}) {
  const [search, setSearch] = useState("");
  const [verticalFilter, setVerticalFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [page, setPage] = useState(0);
  const [sortCol, setSortCol] = useState<"createdAt" | "firstName" | "verticalName" | "stage" | "repCode">("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [showGhosts, setShowGhosts] = useState(false);

  const PAGE_SIZE = 50;

  const STAGE_LABELS: Record<string, string> = {
    lead_captured: "New Lead",
    audit_submitted: "Appt. Booked",
    follow_up_active: "In Outreach",
    payment_received: "Enrolled",
    onboarding_submitted: "Docs In Review",
    active_client: "Active Client",
    lost_cold: "Inactive / Lost",
  };

  const STAGE_COLORS: Record<string, string> = {
    lead_captured: "bg-blue-50 text-blue-700",
    audit_submitted: "bg-purple-50 text-purple-700",
    follow_up_active: "bg-yellow-50 text-yellow-700",
    payment_received: "bg-green-50 text-green-700",
    onboarding_submitted: "bg-teal-50 text-teal-700",
    active_client: "bg-emerald-100 text-emerald-800",
    lost_cold: "bg-gray-100 text-gray-500",
  };

  const ALL_VERTICALS_LIST = ["Fundable360", "Scale360", "Share360", "Legacy360", "Media360"];

  const ghostCount = leads.filter(isGhostLead).length;

  const filtered = leads.filter(l => {
    if (!showGhosts && isGhostLead(l)) return false;
    if (verticalFilter === "__none__") { if (l.verticalName) return false; }
    else if (verticalFilter && l.verticalName !== verticalFilter && !l.verticalName?.startsWith(verticalFilter)) return false;
    if (stageFilter && l.stage !== stageFilter) return false;
    if (agentFilter && !(l.repCode ?? "").toUpperCase().includes(agentFilter)) return false;
    if (search) {
      const q = search.toLowerCase();
      const name = `${l.firstName ?? ""} ${l.lastName ?? ""}`.toLowerCase();
      if (!name.includes(q) && !(l.businessName ?? "").toLowerCase().includes(q) &&
          !(l.email ?? "").toLowerCase().includes(q) && !(l.phone ?? "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    let va: any = a[sortCol];
    let vb: any = b[sortCol];
    if (sortCol === "createdAt") { va = new Date(va).getTime(); vb = new Date(vb).getTime(); }
    else { va = (va ?? "").toString().toLowerCase(); vb = (vb ?? "").toString().toLowerCase(); }
    if (va < vb) return sortDir === "asc" ? -1 : 1;
    if (va > vb) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const pageLeads = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  function toggleSort(col: typeof sortCol) {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  }

  useEffect(() => { setPage(0); }, [search, agentFilter, verticalFilter, stageFilter, showGhosts]);

  function exportCsv() {
    const headers = ["ID", "First Name", "Last Name", "Email", "Phone", "Business", "Vertical", "Stage", "Agent Code", "GHL Contact ID", "GHL Opp ID", "Created"];
    const rows = filtered.map(l => [
      l.id, l.firstName ?? "", l.lastName ?? "", l.email ?? "", l.phone ?? "",
      l.businessName ?? "", l.verticalName ?? "", l.ghlStageName || l.stage, l.repCode ?? "",
      l.ghlContactId ?? "", l.ghlOpportunityId ?? "", new Date(l.createdAt).toLocaleDateString(),
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `all_leads_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  function SortBtn({ col, label }: { col: typeof sortCol; label: string }) {
    return (
      <button onClick={() => toggleSort(col)} className="flex items-center gap-1 hover:text-gray-700 whitespace-nowrap">
        {label} {sortCol === col && <span className="text-[#84ab4a]">{sortDir === "asc" ? "↑" : "↓"}</span>}
      </button>
    );
  }

  const hasFilters = !!(search || agentFilter || verticalFilter || stageFilter);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const noOppIdLeads = leads.filter(isGhostLead);
  const noOppIdInFilter = filtered.filter(isGhostLead);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">All Leads</h2>
          <p className="text-sm text-gray-400">
            {hasFilters ? `${filtered.length} matching` : `${leads.length - (showGhosts ? 0 : ghostCount)} pipeline`} lead{leads.length !== 1 ? "s" : ""} across all verticals
            {ghostCount > 0 && !showGhosts && (
              <span className="ml-2 text-amber-500 font-medium">· {ghostCount} ghost lead{ghostCount !== 1 ? "s" : ""} hidden</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {noOppIdInFilter.length > 0 && (
            !confirmBulkDelete ? (
              <button
                onClick={() => setConfirmBulkDelete(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-50 border border-amber-200 text-sm font-semibold text-amber-700 hover:bg-amber-100 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete {noOppIdInFilter.length} no-opp-ID lead{noOppIdInFilter.length !== 1 ? "s" : ""}
                {agentFilter ? ` (${agentFilter})` : ""}
              </button>
            ) : (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-2">
                <span className="text-sm text-red-700 font-medium">
                  Permanently delete {noOppIdInFilter.length} leads?
                </span>
                <button
                  onClick={() => { onBulkDeleteNoOppId(agentFilter || undefined); setConfirmBulkDelete(false); }}
                  disabled={isBulkDeleting}
                  className="px-3 py-1 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {isBulkDeleting ? "Deleting…" : "Confirm Delete"}
                </button>
                <button
                  onClick={() => setConfirmBulkDelete(false)}
                  className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg bg-white"
                >
                  Cancel
                </button>
              </div>
            )
          )}
          <button
            onClick={exportCsv}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 text-sm font-semibold text-gray-600 hover:border-[#84ab4a]/50 hover:text-[#84ab4a] shadow-sm transition-colors"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search name, email, phone, business…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-[#84ab4a]/60 focus:ring-2 focus:ring-[#84ab4a]/20"
          />
        </div>
        <input
          type="text"
          placeholder="Agent code (e.g. BC-457)"
          value={agentFilter}
          onChange={e => setAgentFilter(e.target.value.toUpperCase())}
          className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-[#84ab4a]/60 focus:ring-2 focus:ring-[#84ab4a]/20 w-48"
        />
        <select
          value={verticalFilter}
          onChange={e => setVerticalFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-[#84ab4a]/60 focus:ring-2 focus:ring-[#84ab4a]/20"
        >
          <option value="">All Verticals</option>
          {ALL_VERTICALS_LIST.map(v => <option key={v} value={v}>{v}</option>)}
          <option value="__none__">— No Vertical —</option>
        </select>
        <select
          value={stageFilter}
          onChange={e => setStageFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-[#84ab4a]/60 focus:ring-2 focus:ring-[#84ab4a]/20"
        >
          <option value="">All Stages</option>
          {Object.entries(STAGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {ghostCount > 0 && (
          <button
            onClick={() => setShowGhosts(g => !g)}
            className={`px-3 py-2 text-sm border rounded-xl flex items-center gap-1.5 transition-colors ${
              showGhosts
                ? "bg-amber-50 border-amber-300 text-amber-700 font-medium"
                : "bg-white border-gray-200 text-gray-400 hover:text-amber-600 hover:border-amber-200"
            }`}
          >
            {showGhosts ? "Hide" : "Show"} {ghostCount} ghost lead{ghostCount !== 1 ? "s" : ""}
          </button>
        )}
        {hasFilters && (
          <button
            onClick={() => { setSearch(""); setAgentFilter(""); setVerticalFilter(""); setStageFilter(""); }}
            className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-xl bg-white flex items-center gap-1.5"
          >
            <X className="w-3.5 h-3.5" /> Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/80">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide"><SortBtn col="firstName" label="Name" /></th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Business</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide"><SortBtn col="verticalName" label="Vertical" /></th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide"><SortBtn col="stage" label="Stage" /></th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide"><SortBtn col="repCode" label="Agent" /></th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide"><SortBtn col="createdAt" label="Created" /></th>
                <th className="w-10 px-2 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {pageLeads.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-16 text-gray-400 text-sm">
                    {leads.length === 0 ? "No leads yet." : "No leads match the current filters."}
                  </td>
                </tr>
              )}
              {pageLeads.map(l => (
                <tr key={l.id} className="hover:bg-[#84ab4a]/4 transition-colors group">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onSelectLead(l.id)}
                      className="font-semibold text-gray-900 hover:text-[#84ab4a] text-left leading-tight"
                    >
                      {[l.firstName, l.lastName].filter(Boolean).join(" ") || <span className="text-gray-300 italic font-normal">No name</span>}
                    </button>
                    {l.ghlOpportunityId
                      ? <div className="text-[10px] text-gray-300 mt-0.5 font-mono">…{l.ghlOpportunityId.slice(-8)}</div>
                      : <div className="text-[10px] text-amber-400 mt-0.5 font-medium">no opp ID</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {l.email && <div className="truncate max-w-[160px]">{l.email}</div>}
                    {l.phone && <div className="text-gray-400">{l.phone}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs max-w-[140px] truncate">
                    {l.businessName || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {l.verticalName
                      ? <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">{l.verticalName}</span>
                      : <span className="text-xs text-amber-500 font-medium bg-amber-50 px-2 py-0.5 rounded-full">No vertical</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STAGE_COLORS[l.stage] ?? "bg-gray-100 text-gray-500"}`}>
                      {l.ghlStageName || STAGE_LABELS[l.stage] || l.stage}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">
                    {l.repCode || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                    {new Date(l.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-2 py-3">
                    <button
                      onClick={() => onDeleteLead(l.id)}
                      className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      title="Delete lead"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
            <span className="text-xs text-gray-400">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, sorted.length)} of {sorted.length}
            </span>
            <div className="flex gap-1.5">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                className="px-3 py-1 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-white transition-colors">← Prev</button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pg = Math.max(0, Math.min(page - 2, totalPages - 5)) + i;
                return (
                  <button key={pg} onClick={() => setPage(pg)}
                    className={`px-2.5 py-1 text-xs border rounded-lg transition-colors ${page === pg ? "bg-[#84ab4a] text-white border-[#84ab4a]" : "border-gray-200 hover:bg-white"}`}>
                    {pg + 1}
                  </button>
                );
              })}
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                className="px-3 py-1 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-white transition-colors">Next →</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Admin Billing Panel ──────────────────────────────────────────────────────
function AdminBillingPanel() {
  const utils = trpc.useUtils();

  // Settings
  const settingsQuery = trpc.billing.settings.get.useQuery();
  const saveSettingsMut = trpc.billing.settings.save.useMutation({
    onSuccess: () => { toast.success("Billing settings saved."); utils.billing.settings.get.invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const testConnectionMut = trpc.billing.testConnection.useMutation({
    onSuccess: (r) => r.ok ? toast.success(`Stripe connected (${r.mode} mode)`) : toast.error(r.error ?? "Connection failed"),
    onError: (e) => toast.error(e.message),
  });

  const [mode, setMode] = useState<"test" | "live">("test");
  const [publishableKey, setPublishableKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [currency, setCurrency] = useState("usd");
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [testPublishableKey, setTestPublishableKey] = useState("");
  const [testSecretKey, setTestSecretKey] = useState("");
  const [showTestSecretKey, setShowTestSecretKey] = useState(false);

  useEffect(() => {
    if (settingsQuery.data) {
      setMode(settingsQuery.data.mode as "test" | "live");
      setPublishableKey(settingsQuery.data.publishableKey ?? "");
      setCurrency(settingsQuery.data.defaultCurrency ?? "usd");
      setTestPublishableKey((settingsQuery.data as any).testPublishableKey ?? "");
    }
  }, [settingsQuery.data]);

  // Plans
  // Scale360-only by design. Fundable360 plans are edited from Admin ›
  // Fundable360 Financing › Stripe Price IDs, which hardcodes its own brand.
  // Do NOT reintroduce a brand switcher here: two entry points onto the same
  // billing_plans rows means a mis-set dropdown can write a plan row under the
  // wrong vertical, and upsertBillingPlan matches on id alone with no brand guard.
  const plansQuery = trpc.billing.plans.list.useQuery({ brand: "scale360" });
  const savePlanMut = trpc.billing.plans.save.useMutation({
    onSuccess: () => { toast.success("Plan saved."); utils.billing.plans.list.invalidate(); setEditingPlan(null); },
    onError: (e) => toast.error(e.message),
  });

  const [editingPlan, setEditingPlan] = useState<any | null>(null);
  const blankPlan = { brand: "scale360", planKey: "lite", name: "", monthlyAmountCents: 0, setupAmountCents: 0, yearlyAmountCents: 0, recurringPriceId: "", setupFeePriceId: "", yearlyRecurringPriceId: "", testRecurringPriceId: "", testSetupFeePriceId: "", testYearlyRecurringPriceId: "", successUrl: "", cancelUrl: "", description: "", isActive: true };
  const [planForm, setPlanForm] = useState(blankPlan);

  function openPlanEditor(plan?: any) {
    if (plan) {
      setPlanForm({ ...blankPlan, ...plan, recurringPriceId: plan.recurringPriceId ?? "", setupFeePriceId: plan.setupFeePriceId ?? "", yearlyRecurringPriceId: plan.yearlyRecurringPriceId ?? "", testRecurringPriceId: plan.testRecurringPriceId ?? "", testSetupFeePriceId: plan.testSetupFeePriceId ?? "", testYearlyRecurringPriceId: plan.testYearlyRecurringPriceId ?? "", successUrl: plan.successUrl ?? "", cancelUrl: plan.cancelUrl ?? "", description: plan.description ?? "" });
      setEditingPlan(plan);
    } else {
      setPlanForm(blankPlan);
      setEditingPlan({});
    }
  }

  // Sessions
  // Scale360-scoped. checkout_sessions is shared across verticals, so without
  // this brand filter Fundable360 sessions render in the Scale360 table.
  const sessionsQuery = trpc.billing.sessions.list.useQuery({ brand: "scale360" });

  return (
    <div className="space-y-6">
      {/* Warning banner */}
      <div className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 flex items-start gap-3">
        <AlertCircle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-orange-700">
          <span className="font-semibold">Security notice:</span> Stripe secret keys are encrypted before storage.
          For production, always set <code className="text-xs font-mono bg-orange-100 px-1 rounded">STRIPE_SECRET_KEY</code> and <code className="text-xs font-mono bg-orange-100 px-1 rounded">BILLING_ENCRYPTION_KEY</code> as environment variables in Railway rather than saving them via this UI.
          Never share or log Stripe secret keys.
        </div>
      </div>

      {/* Stripe Connection */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <span className="text-sm font-semibold text-gray-700">Stripe Connection</span>
            <span className="ml-2 text-xs text-gray-400">Wibiz Stripe account credentials for Scale360 billing</span>
          </div>
          <button
            onClick={() => testConnectionMut.mutate()}
            disabled={testConnectionMut.isPending}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#E8620A]/10 text-[#E8620A] font-semibold hover:bg-[#E8620A]/20 disabled:opacity-50 transition-colors"
          >
            {testConnectionMut.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
            Test Connection
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Stripe Mode</label>
              <select value={mode} onChange={e => setMode(e.target.value as any)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8620A]">
                <option value="test">Test Mode</option>
                <option value="live">Live Mode</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Default Currency</label>
              <select value={currency} onChange={e => setCurrency(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8620A]">
                <option value="usd">USD</option>
                <option value="cad">CAD</option>
                <option value="gbp">GBP</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Stripe Publishable Key</label>
            <input type="text" value={publishableKey} onChange={e => setPublishableKey(e.target.value)} placeholder="pk_test_... or pk_live_..." className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#E8620A]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              Stripe Secret Key
              {settingsQuery.data?.hasSecretKey && <span className="ml-2 text-green-600 text-[10px] font-semibold bg-green-50 px-1.5 py-0.5 rounded">Saved</span>}
            </label>
            <div className="relative">
              <input
                type={showSecretKey ? "text" : "password"}
                value={secretKey}
                onChange={e => setSecretKey(e.target.value)}
                placeholder={settingsQuery.data?.hasSecretKey ? "Leave blank to keep existing key" : "sk_test_... or sk_live_..."}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 pr-10 text-sm font-mono focus:outline-none focus:border-[#E8620A]"
              />
              <button type="button" onClick={() => setShowSecretKey(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <Eye className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              Stripe Webhook Signing Secret
              {settingsQuery.data?.hasWebhookSecret && <span className="ml-2 text-green-600 text-[10px] font-semibold bg-green-50 px-1.5 py-0.5 rounded">Saved</span>}
            </label>
            <div className="relative">
              <input
                type={showWebhookSecret ? "text" : "password"}
                value={webhookSecret}
                onChange={e => setWebhookSecret(e.target.value)}
                placeholder={settingsQuery.data?.hasWebhookSecret ? "Leave blank to keep existing secret" : "whsec_..."}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 pr-10 text-sm font-mono focus:outline-none focus:border-[#E8620A]"
              />
              <button type="button" onClick={() => setShowWebhookSecret(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <Eye className="w-4 h-4" />
              </button>
            </div>
          </div>
          {/* Test Mode Keys */}
          <div className="border-t border-dashed border-blue-100 pt-4">
            <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-wide mb-3">Test Mode Keys — for safe testing without real charges</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">Test Publishable Key</label>
                <input type="text" value={testPublishableKey} onChange={e => setTestPublishableKey(e.target.value)} placeholder="pk_test_..." className="w-full border border-blue-100 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-blue-300" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                  Test Secret Key
                  {(settingsQuery.data as any)?.hasTestSecretKey && <span className="ml-2 text-blue-600 text-[10px] font-semibold bg-blue-50 px-1.5 py-0.5 rounded">Saved</span>}
                </label>
                <div className="relative">
                  <input
                    type={showTestSecretKey ? "text" : "password"}
                    value={testSecretKey}
                    onChange={e => setTestSecretKey(e.target.value)}
                    placeholder={(settingsQuery.data as any)?.hasTestSecretKey ? "Leave blank to keep existing key" : "sk_test_..."}
                    className="w-full border border-blue-100 rounded-lg px-3 py-2 pr-10 text-sm font-mono focus:outline-none focus:border-blue-300"
                  />
                  <button type="button" onClick={() => setShowTestSecretKey(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-[10px] text-gray-400 mt-1">Or set <code className="font-mono bg-gray-100 px-1 rounded">STRIPE_TEST_SECRET_KEY</code> in Railway env vars.</p>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={() => saveSettingsMut.mutate({ mode, publishableKey, secretKey: secretKey || undefined, webhookSecret: webhookSecret || undefined, defaultCurrency: currency, testPublishableKey: testPublishableKey || undefined, testSecretKey: testSecretKey || undefined })}
              disabled={saveSettingsMut.isPending}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#E8620A] text-white text-sm font-semibold rounded-lg hover:bg-[#c94e08] disabled:opacity-50 transition-colors"
            >
              {saveSettingsMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Save Settings
            </button>
          </div>
        </div>
      </div>

      {/* Plans (Scale360 only) */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <span className="text-sm font-semibold text-gray-700">Scale360 Plans</span>
            <span className="ml-2 text-xs text-gray-400">
              Configure Lite, Standard, and Pro plan pricing and Stripe Price IDs
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => openPlanEditor()}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#E8620A]/10 text-[#E8620A] font-semibold hover:bg-[#E8620A]/20 transition-colors"
            >
              + Add Plan
            </button>
          </div>
        </div>
        <div className="divide-y divide-gray-50">
          {(plansQuery.data ?? []).length === 0 && (
            <div className="py-10 text-center text-gray-400 text-sm">
              No plans configured yet. Add Lite, Standard, and Pro.
            </div>
          )}
          {(plansQuery.data ?? []).map((plan: any) => (
            <div key={plan.id} className="px-5 py-4 flex items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-gray-800">{plan.name}</span>
                  <span className="text-[10px] font-mono text-gray-400 uppercase bg-gray-100 px-1.5 py-0.5 rounded">{plan.planKey}</span>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${plan.isActive ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-400"}`}>
                    {plan.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
                  <span>${((plan.monthlyAmountCents ?? 0) / 100).toFixed(0)}/mo</span>
                  {(plan.setupAmountCents ?? 0) > 0 && <span>+${((plan.setupAmountCents ?? 0) / 100).toFixed(0)} setup</span>}
                  {(plan.yearlyAmountCents ?? 0) > 0 && <span className="text-blue-600">${((plan.yearlyAmountCents ?? 0) / 100).toFixed(0)}/yr</span>}
                </div>
                <div className="mt-1 flex flex-wrap gap-3 text-[10px] text-gray-400 font-mono">
                  {plan.recurringPriceId ? <span className="text-green-600">Monthly: {plan.recurringPriceId}</span> : <span className="text-orange-500">No monthly price ID</span>}
                  {plan.setupFeePriceId && <span className="text-green-600">Setup: {plan.setupFeePriceId}</span>}
                  {plan.yearlyRecurringPriceId ? <span className="text-blue-600">Yearly: {plan.yearlyRecurringPriceId}</span> : <span className="text-gray-300">No yearly price ID</span>}
                </div>
                {(plan.testRecurringPriceId || plan.testYearlyRecurringPriceId) && (
                  <div className="mt-1 flex flex-wrap gap-3 text-[10px] font-mono">
                    {plan.testRecurringPriceId && <span className="text-blue-400">TEST Monthly: {plan.testRecurringPriceId}</span>}
                    {plan.testSetupFeePriceId && <span className="text-blue-400">TEST Setup: {plan.testSetupFeePriceId}</span>}
                    {plan.testYearlyRecurringPriceId && <span className="text-blue-400">TEST Yearly: {plan.testYearlyRecurringPriceId}</span>}
                  </div>
                )}
              </div>
              <button onClick={() => openPlanEditor(plan)} className="text-xs text-[#E8620A] font-semibold hover:underline flex-shrink-0">Edit</button>
            </div>
          ))}
        </div>
      </div>

      {/* WordPress Checkout URLs */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100">
          <span className="text-sm font-semibold text-gray-700">WordPress Checkout Links</span>
          <span className="ml-2 text-xs text-gray-400">Paste these as href values in your WordPress anchor tags to replace old payment links</span>
        </div>
        <div className="p-5 space-y-4">
          {["lite", "standard", "pro"].map(key => (
            <div key={key}>
              <p className="text-xs font-semibold text-gray-500 capitalize mb-1.5">{key.charAt(0).toUpperCase() + key.slice(1)} Plan</p>
              <div className="space-y-1.5">
                {[
                  { label: "Monthly (live)", url: `/api/billing/scale360/checkout/redirect?plan=${key}&period=monthly` },
                  { label: "Yearly (live)", url: `/api/billing/scale360/checkout/redirect?plan=${key}&period=yearly` },
                  { label: "Monthly (test)", url: `/api/billing/scale360/checkout/redirect?plan=${key}&period=monthly&test=1` },
                  { label: "Yearly (test)", url: `/api/billing/scale360/checkout/redirect?plan=${key}&period=yearly&test=1` },
                ].map(({ label, url }) => (
                  <div key={label} className="flex items-center gap-2">
                    <span className={`text-[10px] font-semibold w-28 flex-shrink-0 ${label.includes("test") ? "text-blue-500" : "text-green-600"}`}>{label}</span>
                    <code className="flex-1 text-[10px] font-mono bg-gray-50 border border-gray-100 rounded px-2 py-1 truncate text-gray-600">{url}</code>
                    <button
                      onClick={() => { navigator.clipboard.writeText(window.location.origin + url); }}
                      className="text-[10px] px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-500 font-semibold flex-shrink-0"
                    >Copy</button>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <p className="text-[10px] text-gray-400 pt-2 border-t border-gray-50">Add <code className="font-mono bg-gray-100 px-1 rounded">&ref=REPCODE</code> to any URL to attribute the sale to a rep. Example: <code className="font-mono bg-gray-100 px-1 rounded">...&period=monthly&ref=john123</code></p>
        </div>
      </div>

      {/* Plan Editor Modal */}
      {editingPlan !== null && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditingPlan(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-800">{editingPlan?.id ? "Edit Plan" : "Add Plan"}</h3>
              <button onClick={() => setEditingPlan(null)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Plan Key</label>
                <select value={planForm.planKey} onChange={e => setPlanForm(f => ({ ...f, planKey: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8620A]">
                  <option value="lite">Lite</option>
                  <option value="standard">Standard</option>
                  <option value="pro">Pro</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Plan Name</label>
                <input type="text" value={planForm.name} onChange={e => setPlanForm(f => ({ ...f, name: e.target.value }))} placeholder="Scale360 Lite" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8620A]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Monthly Price (cents)</label>
                <input type="number" min={0} value={planForm.monthlyAmountCents} onChange={e => setPlanForm(f => ({ ...f, monthlyAmountCents: parseInt(e.target.value) || 0 }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8620A]" />
                <span className="text-[10px] text-gray-400">${(planForm.monthlyAmountCents / 100).toFixed(0)}/mo</span>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Setup Fee (cents)</label>
                <input type="number" min={0} value={planForm.setupAmountCents} onChange={e => setPlanForm(f => ({ ...f, setupAmountCents: parseInt(e.target.value) || 0 }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8620A]" />
                <span className="text-[10px] text-gray-400">${(planForm.setupAmountCents / 100).toFixed(0)} one-time</span>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Yearly Price (cents)</label>
                <input type="number" min={0} value={(planForm as any).yearlyAmountCents ?? 0} onChange={e => setPlanForm(f => ({ ...f, yearlyAmountCents: parseInt(e.target.value) || 0 }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8620A]" />
                <span className="text-[10px] text-gray-400">${(((planForm as any).yearlyAmountCents ?? 0) / 100).toFixed(0)}/yr</span>
              </div>
              <div />
            </div>
            <div className="border-t border-gray-100 pt-3">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-3">Monthly Billing Price IDs</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Monthly Recurring Price ID <span className="text-orange-500">*required</span></label>
              <input type="text" value={planForm.recurringPriceId} onChange={e => setPlanForm(f => ({ ...f, recurringPriceId: e.target.value }))} placeholder="price_..." className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#E8620A]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Setup Fee Price ID (one-time, charged with monthly)</label>
              <input type="text" value={planForm.setupFeePriceId} onChange={e => setPlanForm(f => ({ ...f, setupFeePriceId: e.target.value }))} placeholder="price_..." className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#E8620A]" />
            </div>
            <div className="border-t border-gray-100 pt-3">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-3">Yearly Billing Price ID</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Yearly Recurring Price ID</label>
              <input type="text" value={(planForm as any).yearlyRecurringPriceId ?? ""} onChange={e => setPlanForm(f => ({ ...f, yearlyRecurringPriceId: e.target.value }))} placeholder="price_..." className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#E8620A]" />
            </div>
            {/* Test Price IDs */}
            <div className="border-t border-dashed border-blue-100 pt-3">
              <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-wide mb-3">Test Mode Price IDs — from your Stripe test dashboard</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Test Monthly Recurring Price ID</label>
              <input type="text" value={(planForm as any).testRecurringPriceId ?? ""} onChange={e => setPlanForm(f => ({ ...f, testRecurringPriceId: e.target.value }))} placeholder="price_..." className="w-full border border-blue-100 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-blue-300" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Test Setup Fee Price ID</label>
              <input type="text" value={(planForm as any).testSetupFeePriceId ?? ""} onChange={e => setPlanForm(f => ({ ...f, testSetupFeePriceId: e.target.value }))} placeholder="price_..." className="w-full border border-blue-100 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-blue-300" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Test Yearly Recurring Price ID</label>
              <input type="text" value={(planForm as any).testYearlyRecurringPriceId ?? ""} onChange={e => setPlanForm(f => ({ ...f, testYearlyRecurringPriceId: e.target.value }))} placeholder="price_..." className="w-full border border-blue-100 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-blue-300" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Checkout Success URL</label>
              <input type="text" value={planForm.successUrl} onChange={e => setPlanForm(f => ({ ...f, successUrl: e.target.value }))} placeholder="https://scale360.wibiz.ai/success" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8620A]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Checkout Cancel URL</label>
              <input type="text" value={planForm.cancelUrl} onChange={e => setPlanForm(f => ({ ...f, cancelUrl: e.target.value }))} placeholder="https://scale360.wibiz.ai/" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8620A]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Description</label>
              <textarea value={planForm.description} onChange={e => setPlanForm(f => ({ ...f, description: e.target.value }))} rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-[#E8620A]" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="planActive" checked={planForm.isActive} onChange={e => setPlanForm(f => ({ ...f, isActive: e.target.checked }))} className="rounded border-gray-300" />
              <label htmlFor="planActive" className="text-sm text-gray-700">Active (visible for checkout)</label>
            </div>
            <div className="flex gap-2 justify-end pt-2 border-t border-gray-100">
              <button onClick={() => setEditingPlan(null)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">Cancel</button>
              <button
                onClick={() => savePlanMut.mutate({ ...planForm, id: editingPlan?.id, recurringPriceId: planForm.recurringPriceId || null, setupFeePriceId: planForm.setupFeePriceId || null, yearlyRecurringPriceId: (planForm as any).yearlyRecurringPriceId || null, testRecurringPriceId: (planForm as any).testRecurringPriceId || null, testSetupFeePriceId: (planForm as any).testSetupFeePriceId || null, testYearlyRecurringPriceId: (planForm as any).testYearlyRecurringPriceId || null, yearlyAmountCents: (planForm as any).yearlyAmountCents ?? 0, successUrl: planForm.successUrl || null, cancelUrl: planForm.cancelUrl || null, description: planForm.description || null })}
                disabled={savePlanMut.isPending || !planForm.name}
                className="px-4 py-2 text-sm bg-[#E8620A] text-white rounded-lg font-semibold hover:bg-[#c94e08] disabled:opacity-50 transition-colors flex items-center gap-1.5"
              >
                {savePlanMut.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Save Plan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recent Checkout Sessions */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <span className="text-sm font-semibold text-gray-700">Recent Checkout Sessions</span>
            <span className="ml-2 text-xs text-gray-400">Stripe Checkout sessions initiated from Scale360 landing pages</span>
          </div>
          <button onClick={() => sessionsQuery.refetch()} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
        {sessionsQuery.isLoading ? (
          <div className="py-10 flex items-center justify-center text-gray-400 gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading...</div>
        ) : (sessionsQuery.data ?? []).length === 0 ? (
          <div className="py-10 text-center text-gray-400 text-sm">No checkout sessions yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Customer</th>
                  <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Plan</th>
                  <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Ref</th>
                  <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Mode</th>
                  <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Status</th>
                  <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Date</th>
                  <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Session ID</th>
                </tr>
              </thead>
              <tbody>
                {(sessionsQuery.data ?? []).map((s: any) => (
                  <tr key={s.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="px-5 py-3">
                      <div className="font-medium text-gray-800 text-xs">{s.customerName ?? "—"}</div>
                      <div className="text-[10px] text-gray-400">{s.customerEmail ?? ""}</div>
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-600 font-mono uppercase">{s.planKey ?? "—"}</td>
                    <td className="px-5 py-3 text-xs text-gray-500 font-mono">{s.repCode ?? "—"}</td>
                    <td className="px-5 py-3">
                      {s.isTest
                        ? <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-600">TEST</span>
                        : <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-700">LIVE</span>}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        s.status === "completed" ? "bg-green-100 text-green-700" :
                        s.status === "created" ? "bg-blue-100 text-blue-600" :
                        "bg-gray-100 text-gray-500"
                      }`}>{s.status ?? "created"}</span>
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-400">{s.createdAt ? new Date(s.createdAt).toLocaleDateString() : "—"}</td>
                    <td className="px-5 py-3 text-[10px] text-gray-300 font-mono truncate max-w-[140px]">{s.stripeSessionId ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function AdminDashboardInner() {
  const [activeTab, setActiveTab] = useState<TabKey>("scale360");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const navigate = (tab: TabKey) => { setActiveTab(tab); setMobileSidebarOpen(false); };
  const logoutMutation = trpc.adminAuth.logout.useMutation({
    onSettled: () => {
      sessionStorage.removeItem("bc360_admin_unlocked");
      window.location.reload();
    },
  });
  const [showRepForm, setShowRepForm] = useState(false);
  const [selectedLead, setSelectedLead] = useState<number | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [repSearch, setRepSearch] = useState("");
  const [repVerticalFilter, setRepVerticalFilter] = useState("");
  const repTableScrollRef = useRef<HTMLDivElement>(null);
  const [repTableCanScrollRight, setRepTableCanScrollRight] = useState(false);
  const [leadAgentFilter, setLeadAgentFilter] = useState("");
  // Edit/Delete state
  const [editingRep, setEditingRep] = useState<any | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<any | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: "lead" | "rep" | "commission"; id: number; verticalName?: string } | null>(null);
  // Set Credentials state
  const [credRep, setCredRep] = useState<any | null>(null);
  // Suspend / Reassign state
  const [suspendTarget, setSuspendTarget] = useState<any | null>(null);
  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null);
  const [actionMenuPos, setActionMenuPos] = useState<{ top: number; left: number } | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const actionMenuPortalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        actionMenuRef.current && !actionMenuRef.current.contains(target) &&
        (!actionMenuPortalRef.current || !actionMenuPortalRef.current.contains(target))
      ) {
        setOpenActionMenu(null);
      }
    }
    function handleScrollOrResize() {
      setOpenActionMenu(null);
    }
    if (openActionMenu !== null) {
      document.addEventListener("mousedown", handleOutsideClick);
      window.addEventListener("resize", handleScrollOrResize);
      repTableScrollRef.current?.addEventListener("scroll", handleScrollOrResize);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      window.removeEventListener("resize", handleScrollOrResize);
      repTableScrollRef.current?.removeEventListener("scroll", handleScrollOrResize);
    };
  }, [openActionMenu]);
  const [reassignTarget, setReassignTarget] = useState<any | null>(null);
  const [reassignUplineCode, setReassignUplineCode] = useState("");
  const [repairTarget, setRepairTarget] = useState<any | null>(null);
  const [manualGhlId, setManualGhlId] = useState("");
  const [credUsername, setCredUsername] = useState("");
  const [credPassword, setCredPassword] = useState("");
  const [credShowPw, setCredShowPw] = useState(false);
  const [showDeletedAgents, setShowDeletedAgents] = useState(false);
  const [showManualCommissionForm, setShowManualCommissionForm] = useState(false);
  const [manualCommissionForm, setManualCommissionForm] = useState(EMPTY_MANUAL_COMMISSION_FORM);

  const leadsQuery = trpc.lead.list.useQuery({ includeAll: true }); // admin sees all leads including no-opp-ID for cleanup
  const statsQuery = trpc.lead.pipelineStats.useQuery({});
  const repsQuery = trpc.rep.leaderboard.useQuery();
  const deletedRepsQuery = trpc.rep.listDeleted.useQuery();
  useEffect(() => {
    const el = repTableScrollRef.current;
    if (!el) return;
    const updateScrollState = () => {
      setRepTableCanScrollRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
    };
    updateScrollState();
    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [repsQuery.data, repSearch, repVerticalFilter]);
  const pendingUpgradesQuery = trpc.repUpgrade.listPending.useQuery(undefined, { refetchInterval: 60_000 });
  const [commVerticalFilter, setCommVerticalFilter] = useState("");
  const [commSearch, setCommSearch] = useState("");
  const [commStatusFilter, setCommStatusFilter] = useState<CommissionStatusFilter>("");
  const [commDateFrom, setCommDateFrom] = useState("");
  const [commDateTo, setCommDateTo] = useState("");
  // Admins see test-mode commissions so QA can verify a test enrollment
  // calculated correctly; each is badged below. Associates never receive them,
  // since a fake client carrying a real-looking payout is indistinguishable
  // from money they are genuinely owed.
  const commissionsQuery = trpc.commission.list.useQuery({ verticalName: commVerticalFilter || undefined, includeTest: true });
  const visibleCommissions = useMemo(
    () => filterCommissionRows(commissionsQuery.data ?? [], {
      query: commSearch,
      status: commStatusFilter,
      dateFrom: commDateFrom,
      dateTo: commDateTo,
    }),
    [commissionsQuery.data, commSearch, commStatusFilter, commDateFrom, commDateTo]
  );
  const filteredCommissionSummary = useMemo(
    () => summarizeCommissionRows(visibleCommissions),
    [visibleCommissions]
  );
  const commissionDateRangeInvalid = !!commDateFrom && !!commDateTo && commDateFrom > commDateTo;
  const hasCommissionFilters = !!(
    commSearch.trim() || commVerticalFilter || commStatusFilter || commDateFrom || commDateTo
  );
  const applyCommissionPayoutPeriod = (half: "first" | "second") => {
    const range = currentMonthPayoutRange(half);
    setCommDateFrom(range.dateFrom);
    setCommDateTo(range.dateTo);
  };
  const clearCommissionFilters = () => {
    setCommSearch("");
    setCommVerticalFilter("");
    setCommStatusFilter("");
    setCommDateFrom("");
    setCommDateTo("");
  };
  const exportFilteredCommissions = () => {
    if (visibleCommissions.length === 0 || commissionDateRangeInvalid) return;
    const csv = buildCommissionExportCsv(visibleCommissions);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const range = commDateFrom || commDateTo
      ? `${commDateFrom || "start"}_to_${commDateTo || "end"}`
      : "all_dates";
    link.href = url;
    link.download = `commissions_${range}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${visibleCommissions.length} commission${visibleCommissions.length === 1 ? "" : "s"} for Excel.`);
  };
  const activeCommissionAssociates = useMemo(
    () => (repsQuery.data ?? [])
      .filter((rep: any) => rep.status === "active")
      .sort((a: any, b: any) =>
        `${a.firstName ?? ""} ${a.lastName ?? ""}`.localeCompare(
          `${b.firstName ?? ""} ${b.lastName ?? ""}`
        )
      ) as CommissionAssociate[],
    [repsQuery.data]
  );
  const verticalsQuery = trpc.vertical.list.useQuery();
  const allVerticalsQuery = trpc.vertical.listAll.useQuery(undefined, { refetchInterval: 60_000 });
  const toggleVerticalMutation = trpc.vertical.toggleActive.useMutation({
    onSuccess: () => { allVerticalsQuery.refetch(); toast.success("Vertical updated."); },
    onError: (e: any) => toast.error(e.message),
  });
  const attributionLogsQuery = trpc.attribution.getLogs.useQuery({ limit: 200 });
  const webhookLogsQuery = trpc.webhookLog.list.useQuery({ limit: 300 });
  const attributionIssuesQuery = trpc.attribution.getIssues.useQuery();
  const resyncOneMutation = trpc.attribution.resyncOne.useMutation({ onSuccess: () => { attributionIssuesQuery.refetch(); attributionLogsQuery.refetch(); toast.success("Attribution re-synced"); }});
  const resyncAllMutation = trpc.attribution.resyncAll.useMutation({ onSuccess: (r) => { attributionIssuesQuery.refetch(); attributionLogsQuery.refetch(); toast.success(`Re-sync complete: ${r.resolved} resolved, ${r.unresolved} unresolved, ${r.noRepCode} no rep code`); }});
  const [ghlLookupContactId, setGhlLookupContactId] = useState("");
  const [ghlLookupInput, setGhlLookupInput] = useState("");
  const ghlFieldLookupQuery = trpc.attribution.lookupGhlContactFields.useQuery(
    { ghlContactId: ghlLookupContactId },
    { enabled: ghlLookupContactId.length > 5 }
  );
  // Manual assignment state
  const [assignSearch, setAssignSearch] = useState("");
  const [assignSearchInput, setAssignSearchInput] = useState("");
  const [assignRepCode, setAssignRepCode] = useState<Record<number, string>>({});
  const assignLeadSearchQuery = trpc.attribution.searchLeads.useQuery(
    { query: assignSearch },
    { enabled: assignSearch.length >= 2 }
  );
  const assignRepMutation = trpc.attribution.assignRep.useMutation({
    onSuccess: (r, vars) => {
      attributionIssuesQuery.refetch();
      attributionLogsQuery.refetch();
      assignLeadSearchQuery.refetch();
      setAssignRepCode(prev => { const n = {...prev}; delete n[vars.leadId]; return n; });
      toast.success(`Assigned: ${r.referrerAgentName} (${r.referrerAgentEmail})`);
    },
    onError: (e) => toast.error(e.message),
  });
  const utils = trpc.useUtils();

  const updateCommission = trpc.commission.updateStatus.useMutation({
    onSuccess: () => {
      toast.success("Commission status updated");
      utils.commission.list.invalidate();
      utils.commission.summary.invalidate();
    },
  });
  const createManualCommission = trpc.commission.createManual.useMutation({
    onSuccess: () => {
      toast.success("Manual commission created as pending");
      setShowManualCommissionForm(false);
      setManualCommissionForm(EMPTY_MANUAL_COMMISSION_FORM);
      utils.commission.list.invalidate();
      utils.commission.summary.invalidate();
    },
    onError: (e) => toast.error(e.message || "Could not create manual commission"),
  });
  const deleteLeadMutation = trpc.lead.delete.useMutation({
    onSuccess: (data) => {
      const parts: string[] = ["Lead deleted."];
      if (data.opportunityDeleted) parts.push("Pipeline opportunity removed.");
      if (data.contactDeleted) parts.push("Wibiz contact also deleted.");
      else if (data.contactSkipReason) parts.push(`Contact preserved: ${data.contactSkipReason}`);
      toast.success(parts.join(" "));
      utils.lead.list.invalidate();
      utils.lead.pipelineStats.invalidate();
      setDeleteConfirm(null);
    },
    onError: (e) => toast.error(e.message || "Failed to delete lead"),
  });
  const bulkDeleteNoOppIdMutation = trpc.lead.bulkDeleteNoOppId.useMutation({
    onSuccess: (data) => {
      toast.success(`Deleted ${data.deleted} lead${data.deleted !== 1 ? "s" : ""} with no opportunity ID.`);
      utils.lead.list.invalidate();
      utils.lead.pipelineStats.invalidate();
    },
    onError: (e) => toast.error(e.message || "Bulk delete failed"),
  });
  const deleteRepMutation = trpc.rep.delete.useMutation({
    onSuccess: () => {
      toast.success("Agent removed. You can restore them from the Deleted Agents section.");
      utils.rep.leaderboard.invalidate();
      utils.rep.list.invalidate();
      utils.rep.listDeleted.invalidate();
      setDeleteConfirm(null);
    },
    onError: (e: any) => toast.error(e.message || "Failed to delete agent"),
  });
  const setCredentialsMutation = trpc.rep.setCredentials.useMutation({
    onSuccess: () => {
      toast.success("Credentials set successfully");
      utils.rep.leaderboard.invalidate();
      setCredRep(null); setCredUsername(""); setCredPassword("");
    },
    onError: (e) => toast.error(e.message),
  });
  const editRepMutation = trpc.rep.edit.useMutation({
    onSuccess: () => {
      toast.success("Rep updated");
      utils.rep.leaderboard.invalidate();
      setEditingRep(null);
    },
    onError: () => toast.error("Failed to update rep"),
  });
  const reinstateRepMutation = trpc.rep.reinstate.useMutation({
    onSuccess: () => { toast.success("Agent reinstated."); utils.rep.list.invalidate(); },
    onError: (e: any) => toast.error(e.message),
  });
  const restoreRepMutation = trpc.rep.restore.useMutation({
    onSuccess: () => {
      toast.success("Agent restored. You can reinstate them from the Agents list.");
      utils.rep.leaderboard.invalidate();
      utils.rep.list.invalidate();
      utils.rep.listDeleted.invalidate();
    },
    onError: (e: any) => toast.error(e.message || "Failed to restore agent"),
  });
  const suspendRepMutation = trpc.rep.suspend.useMutation({
    onSuccess: () => { toast.success("Agent suspended. Dashboard access removed."); utils.rep.list.invalidate(); setSuspendTarget(null); },
    onError: (e: any) => toast.error(e.message),
  });
  const reassignRepMutation = trpc.rep.reassign.useMutation({
    onSuccess: (r) => { toast.success(`Reassigned. New upline: ${r.newUplineRepCode}`); utils.rep.list.invalidate(); setReassignTarget(null); setReassignUplineCode(""); },
    onError: (e: any) => toast.error(e.message),
  });
  const repairGhlMutation = trpc.rep.repairGhlContact.useMutation({
    onSuccess: (r) => {
      toast.success(`Wibiz contact linked (${r.action}): ${r.ghlContactId}`);
      utils.rep.list.invalidate();
      setRepairTarget(null);
      setManualGhlId("");
    },
    onError: (e: any) => toast.error(e.message || "Repair failed"),
  });
  const updateStageInDrawer = trpc.lead.updateStage.useMutation({
    onSuccess: () => {
      toast.success("Stage updated");
      utils.lead.list.invalidate();
      utils.lead.pipelineStats.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
  const moveStageInDrawer = trpc.lead.moveStage.useMutation({
    onSuccess: () => {
      toast.success("Stage moved & synced to Wibiz");
      utils.lead.list.invalidate();
      utils.lead.pipelineStats.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
  // NOTE: ghlStagesQuery is declared after `leads` below

  const deleteCommissionMutation = trpc.commission.delete.useMutation({
    onSuccess: () => {
      toast.success("Commission deleted");
      utils.commission.list.invalidate();
      utils.commission.summary.invalidate();
      setDeleteConfirm(null);
    },
    onError: () => toast.error("Failed to delete commission"),
  });

  const copyToClipboard = (text: string, code: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
    toast.success("Copied to clipboard!");
  };

  function exportVerticalCsv(verticalName: string, leadsData: any[]) {
    const headers = ["ID","First Name","Last Name","Email","Phone","Business","Stage","Rep Code","Vertical","Created"];
    const rows = leadsData.map(l => [
      l.id, l.firstName ?? "", l.lastName ?? "", l.email ?? "", l.phone ?? "",
      l.businessName ?? "", l.stage ?? "", l.repCode ?? "", l.verticalName ?? "",
      new Date(l.createdAt).toLocaleDateString(),
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${verticalName.replace(/\s+/g, "_")}_leads_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  const stats = statsQuery.data;
  const leads = leadsQuery.data ?? [];
  // Fetch pipeline stages for the currently selected lead
  const selectedLeadObjForStages = selectedLead !== null ? leads.find((l: any) => l.id === selectedLead) ?? null : null;
  // For Share360 subtypes pass pipelineId directly so the correct track's stages are shown
  const pipelineIdForLead = (() => {
    const vn = selectedLeadObjForStages?.verticalName;
    if (vn === "Share360 Nonprofit") return "czZqnGQZru1yUcz7vHM6";
    if (vn === "Share360 Business") return "le4RyNh4YBlLMVeVslHt";
    // Share360's two tracks work above because they carry distinct verticalName
    // values. All Legacy360 pipelines share the single verticalName "Legacy360",
    // so leaving this undefined made the stage lookup resolve by vertical name
    // alone and always return whichever Legacy360 pipeline is listed first
    // (Living Trust). The drawer then offered Living Trust stages on 125 Plan and
    // Advantage leads, and moving one to a stage its pipeline does not have left
    // the card with no column to render in, so it vanished from the board.
    // Same root cause as the consultation-calendar lookup fixed earlier.
    // Mirrors the resolution already used in TeamMemberDashboard.tsx, including
    // skipping new_agent: that roster comes from the certifications table, not
    // from GHL opportunities, so it has no GHL stages to offer.
    if (vn === "Legacy360") {
      const key = legacy360SubTypeKeyForLead(selectedLeadObjForStages as any);
      if (key === "new_agent") return undefined;
      const direct = (selectedLeadObjForStages as any)?.ghlPipelineId;
      if (direct && LEGACY360_PIPELINE_ID_MAP[direct]) return direct;
      return LEGACY360_KEY_TO_PIPELINE_ID[key];
    }
    return undefined;
  })();
  const ghlStagesQuery = trpc.lead.getGhlStages.useQuery(
    { verticalName: selectedLeadObjForStages?.verticalName ?? undefined, pipelineId: pipelineIdForLead },
    { enabled: selectedLead !== null }
  );

  // Sidebar nav items
  const verticalNavItems = VERTICALS.map(v => ({
    key: v.key as TabKey,
    label: v.label,
    icon: v.icon,
    color: v.color,
    count: leads.filter(l => l.verticalName === v.verticalName || l.verticalName?.startsWith(v.label)).length,
  }));

  const { data: pendingDocs = [] } = trpc.clientPortal.adminListDocuments.useQuery(
    { status: "pending" },
    { refetchInterval: 60_000 }
  );
  const pendingDocCount = pendingDocs.length;

  const pendingUpgradeCount = (pendingUpgradesQuery.data as any[])?.length ?? 0;

  const managementNavItems = [
    { key: "all_leads" as TabKey, label: "All Leads", icon: Table2, color: "#84ab4a", badge: leads.filter((l: any) => !!l.ghlOpportunityId).length },
    { key: "reps" as TabKey, label: "Reps", icon: Users, color: "#84ab4a", badge: 0 },
    { key: "role_upgrades" as TabKey, label: "Role Upgrades", icon: ArrowUpCircle, color: "#84ab4a", badge: pendingUpgradeCount },
    { key: "commissions" as TabKey, label: "Commissions", icon: DollarSign, color: "#84ab4a", badge: 0 },
    { key: "team" as TabKey, label: "Team Members", icon: UserCheck, color: "#6366F1", badge: 0 },
    { key: "ra_subscriptions" as TabKey, label: "Associate Subscriptions", icon: CreditCard, color: "#0FA8A8", badge: 0 },
    { key: "verticals" as TabKey, label: "Verticals", icon: Building2, color: "#84ab4a", badge: 0 },
    { key: "identity" as TabKey, label: "Identity Verify", icon: Shield, color: "#7B4FD4", badge: 0 },
    { key: "agreements" as TabKey, label: "Agreements", icon: FileText, color: "#0F7B6C", badge: 0 },
    { key: "share360_enrollment" as TabKey, label: "Share360 Enrollment", icon: Globe, color: "#1A6FD4", badge: 0 },
    { key: "client_docs" as TabKey, label: "Client Docs", icon: FileText, color: "#0FA8A8", badge: pendingDocCount },
    { key: "certifications" as TabKey, label: "Certifications", icon: Award, color: "#F59E0B", badge: 0 },
    { key: "quiz_questions" as TabKey, label: "Quiz Questions", icon: BookOpen, color: "#F59E0B", badge: 0 },
    { key: "materials" as TabKey, label: "Marketing Materials", icon: BookOpen, color: "#84ab4a", badge: 0 },
    { key: "sync" as TabKey, label: "Wibiz Sync", icon: Zap, color: "#84ab4a", badge: 0 },
    { key: "bc360_calendar" as TabKey, label: "BC360 Calendar", icon: CalendarCheck, color: "#84ab4a", badge: 0 },
    { key: "grace_faq" as TabKey, label: "Grace & Dawn FAQ", icon: MessageSquare, color: "#84ab4a", badge: 0 },
    { key: "grace_phone" as TabKey, label: "Voice Phone", icon: Phone, color: "#E8620A", badge: 0 },
    { key: "appointments" as TabKey, label: "Training Appointments", icon: Calendar, color: "#0FA8A8", badge: 0 },
    { key: "announcements" as TabKey, label: "Announcements", icon: Megaphone, color: "#E8620A", badge: 0 },
    { key: "flywheel" as TabKey, label: "Media Flywheel", icon: GalleryHorizontal, color: "#84ab4a", badge: 0 },
    { key: "associates_landing" as TabKey, label: "Associates Landing Page", icon: LayoutTemplate, color: "#7B4FD4", badge: 0 },
    { key: "verticallinks" as TabKey, label: "Referral Links", icon: Link2, color: "#84ab4a", badge: 0 },
    { key: "settings" as TabKey, label: "Settings", icon: Settings, color: "#6366F1", badge: 0 },
    { key: "attribution" as TabKey, label: "Attribution", icon: Activity, color: "#E8620A", badge: 0 },
    { key: "webhook_logs" as TabKey, label: "Webhook Logs", icon: Activity, color: "#6366F1", badge: 0 },
    { key: "billing" as TabKey, label: "Billing (Scale360)", icon: CreditCard, color: "#E8620A", badge: 0 },
    { key: "fundable360_financing" as TabKey, label: "Fundable360 Financing", icon: CreditCard, color: "#156082", badge: 0 },
  ];

  const activeVertical = VERTICALS.find(v => v.key === activeTab);

  return (
    <div className="flex min-h-screen bg-[#F5F6F8]">
      {/* Mobile overlay */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`w-60 bg-[#0D1E35] flex flex-col fixed inset-y-0 left-0 z-40 overflow-y-auto transition-transform duration-200 ${mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="p-4 border-b border-white/7 flex-shrink-0">
          <div className="flex items-center justify-between gap-2.5">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-[#84ab4a]/15 border border-[#84ab4a]/30 flex items-center justify-center flex-shrink-0">
                <Shield className="w-4 h-4 text-[#84ab4a]" />
              </div>
              <div>
                <div className="text-sm font-bold text-white">BizConnect360</div>
                <div className="text-[10px] text-white/40 uppercase tracking-wider">Admin Portal</div>
              </div>
            </div>
            <button
              className="lg:hidden text-white/40 hover:text-white transition-colors p-1 flex-shrink-0"
              onClick={() => setMobileSidebarOpen(false)}
              aria-label="Close menu"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {/* Verticals section */}
          <div className="text-[9px] text-white/30 uppercase tracking-widest px-2 py-2 font-semibold">Verticals</div>
          {verticalNavItems.map(({ key, label, icon: Icon, color, count }) => (
            <button
              key={key}
              onClick={() => navigate(key)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === key
                  ? "border-l-2 pl-2.5"
                  : "text-white/60 hover:text-white hover:bg-white/5"
              }`}
              style={activeTab === key ? { background: `${color}18`, color, borderColor: color } : {}}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 text-left">{label}</span>
              {count > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: `${color}25`, color }}>
                  {count}
                </span>
              )}
            </button>
          ))}
          {/* Management section */}
          <div className="text-[9px] text-white/30 uppercase tracking-widest px-2 py-2 font-semibold mt-3">Management</div>
          {managementNavItems.map(({ key, label, icon: Icon, color, badge }) => (
            <button
              key={key}
              onClick={() => navigate(key)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === key
                  ? "border-l-2 pl-2.5"
                  : "text-white/60 hover:text-white hover:bg-white/5"
              }`}
              style={activeTab === key ? { background: `${color}18`, color, borderColor: color } : {}}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 text-left">{label}</span>
              {badge > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-400/20 text-amber-400">
                  {badge}
                </span>
              )}
            </button>
          ))}
          {/* Resources section */}
          <div className="text-[9px] text-white/30 uppercase tracking-widest px-2 py-2 font-semibold mt-3">Resources</div>
          <Link href="/ghl-guide">
            <button className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-white/60 hover:text-white hover:bg-white/5 transition-all">
              <BookOpen className="w-4 h-4 flex-shrink-0" />
              Setup Guide
            </button>
          </Link>
          <Link href="/">
            <button className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-white/60 hover:text-white hover:bg-white/5 transition-all">
              <ArrowRight className="w-4 h-4 flex-shrink-0" />
              Portal Home
            </button>
          </Link>
        </nav>
        <div className="p-3 border-t border-white/7 space-y-2">
          <div className="flex items-center gap-2.5 bg-[#84ab4a]/9 border border-[#84ab4a]/22 rounded-lg p-2.5">
            <div className="w-7 h-7 rounded-md bg-[#84ab4a]/15 border border-[#84ab4a]/30 flex items-center justify-center text-[10px] font-bold text-[#84ab4a]">A</div>
            <div>
              <div className="text-xs font-semibold text-white">Admin</div>
              <div className="text-[10px] text-white/40">Full Access · All Verticals</div>
            </div>
          </div>
          <button
            onClick={() => logoutMutation.mutate()}
            disabled={logoutMutation.isPending}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-white/50 hover:text-white hover:bg-white/5 transition-all"
          >
            <LogOut className="w-3.5 h-3.5 flex-shrink-0" />
            Lock Dashboard
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="lg:ml-60 flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="bg-white border-b border-gray-200 px-4 lg:px-6 py-3.5 flex items-center justify-between sticky top-0 z-30 shadow-sm">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
              onClick={() => setMobileSidebarOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            {activeVertical && (
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: activeVertical.bg }}>
                <activeVertical.icon className="w-4 h-4" style={{ color: activeVertical.color }} />
              </div>
            )}
            <div>
              <h1 className="text-base font-bold text-gray-900">
                {activeVertical ? `${activeVertical.label} Dashboard` : (
                  activeTab === "all_leads" ? "All Leads" :
                  activeTab === "reps" ? "Sales Reps" :
                  activeTab === "role_upgrades" ? "Role Upgrade Requests" :
                  activeTab === "grace_faq" ? "Grace — FAQ Manager" :
                  activeTab === "grace_phone" ? "Grace — Phone Testing" :
                  activeTab === "commissions" ? "Commissions" :
                  activeTab === "verticals" ? "Verticals Overview" :
                  activeTab === "attribution" ? "Attribution" :
                  activeTab === "billing" ? "Billing - Scale360 Stripe Settings" :
                  activeTab === "fundable360_financing" ? "Fundable360 - Financing Tracker" :
                  activeTab === "agreements" ? "Agreement Portal" :
                  "Wibiz Sync"
                )}
              </h1>
              <p className="text-xs text-gray-400 uppercase tracking-wide">
                BizConnect360 · Admin · Wibiz Dashboard
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <div className="hidden sm:flex items-center gap-1.5 bg-[#84ab4a]/9 border border-[#84ab4a]/28 text-[#84ab4a] px-3 py-1.5 rounded-full text-xs font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-[#84ab4a]" />
              Live
            </div>
            <Button size="sm" variant="outline" onClick={() => { utils.lead.list.invalidate(); utils.rep.leaderboard.invalidate(); }}>
              <RefreshCw className="w-3.5 h-3.5 sm:mr-1.5" /> <span className="hidden sm:inline">Refresh</span>
            </Button>
            {activeVertical && (
              <div className="relative hidden lg:block">
                <input
                  type="text"
                  placeholder="Filter by agent code…"
                  value={leadAgentFilter}
                  onChange={e => setLeadAgentFilter(e.target.value.toUpperCase())}
                  className="text-sm border border-gray-200 rounded-xl px-3 py-1.5 w-44 bg-white focus:outline-none focus:border-[#84ab4a]/60 focus:ring-2 focus:ring-[#84ab4a]/20"
                />
                {leadAgentFilter && (
                  <button
                    onClick={() => setLeadAgentFilter("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                  >
                    ✕
                  </button>
                )}
              </div>
            )}
            {activeTab === "reps" && (
              <Button size="sm" className="bg-[#84ab4a] hover:bg-[#6e9038] text-white border-0" onClick={() => setShowRepForm(true)}>
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Register Rep
              </Button>
            )}
          </div>
        </header>

        <div className="flex-1 p-4 lg:p-6">
          {/* ── VERTICAL TABS (Scale360, Fundable360, Share360, Legacy360, Media360) ── */}
          {activeVertical && (
            <VerticalKanbanTab
              key={activeVertical.key}
              vertical={activeVertical}
              leads={leadAgentFilter ? leads.filter((l: any) => l.repCode === leadAgentFilter) : leads}
              onSelectLead={(id) => setSelectedLead(id)}
              onDeleteLead={(id) => { const l = leads.find((x: any) => x.id === id); setDeleteConfirm({ type: "lead", id, verticalName: l?.verticalName ?? undefined }); }}
            />
          )}

          {/* ── ALL LEADS TAB ── */}
          {activeTab === "all_leads" && (
            <AllLeadsTable
              leads={leads}
              onSelectLead={(id) => setSelectedLead(id)}
              onDeleteLead={(id) => { const l = leads.find((x: any) => x.id === id); setDeleteConfirm({ type: "lead", id, verticalName: l?.verticalName ?? undefined }); }}
              onBulkDeleteNoOppId={(repCode) => bulkDeleteNoOppIdMutation.mutate({ repCode })}
              isBulkDeleting={bulkDeleteNoOppIdMutation.isPending}
            />
          )}

          {/* ── REPS TAB ── */}
          {activeTab === "reps" && (
            <div>
              <div className="flex items-center justify-between mb-5 gap-3">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Sales Reps</h2>
                  <p className="text-sm text-gray-400">{repsQuery.data?.length ?? 0} registered reps</p>
                </div>
                <div className="flex items-center gap-2">
                  {/* Vertical filter */}
                  <select
                    value={repVerticalFilter}
                    onChange={e => setRepVerticalFilter(e.target.value)}
                    className="text-sm border border-gray-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:border-[#84ab4a]/60 focus:ring-2 focus:ring-[#84ab4a]/20"
                  >
                    <option value="">All Verticals</option>
                    {["Fundable360", "Scale360", "Share360", "Legacy360", "Media360"].map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                  <div className="relative w-64">
                    <input
                      type="text"
                      placeholder="Search by name, email, or rep code…"
                      value={repSearch}
                      onChange={e => setRepSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-[#84ab4a]/60 focus:ring-2 focus:ring-[#84ab4a]/20 bg-white"
                    />
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                  </div>
                </div>
              </div>
              {/* Summary strip */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                {[
                  { label: "Total Reps", value: repsQuery.data?.length ?? 0, color: "#84ab4a" },
                  { label: "Active Reps", value: repsQuery.data?.filter(r => r.status === "active").length ?? 0, color: "#0FA8A8" },
                  { label: "Missing Wibiz", value: repsQuery.data?.filter(r => !(r as any).ghlContactId).length ?? 0, color: "#D97706" },
                  { label: "Total Leads", value: repsQuery.data?.reduce((a, r) => a + r.totalLeads, 0) ?? 0, color: "#1A6FD4" },
                  { label: "Total Commissions", value: repsQuery.data?.reduce((a, r) => a + r.totalCommission, 0) ?? 0, color: "#C8920A", isCurrency: true },
                ].map(({ label, value, color, isCurrency }) => (
                  <div key={label} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: color }} />
                    <div className="text-2xl font-bold font-mono text-gray-900">{isCurrency ? fmt(Number(value)) : value}</div>
                    <div className="text-xs text-gray-400 mt-1 font-medium">{label}</div>
                  </div>
                ))}
              </div>
              {/* Reps table */}
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden relative">
                <div ref={repTableScrollRef} className="overflow-auto max-h-[min(65vh,640px)]">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50 sticky top-0 z-10">
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide w-8 bg-gray-50">#</th>
                        <th className="text-left px-3 sm:px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide sticky left-0 z-20 w-28 sm:w-40 lg:w-52 bg-gray-50 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">Rep</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Level</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Rep Code</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Approved Verticals</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Referral Link</th>
                        <th className="text-center px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Leads</th>
                        <th className="text-center px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Active</th>
                        <th className="text-right px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Commission</th>
                        <th className="text-center px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Status</th>
                        <th className="text-center px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Wibiz</th>
                        <th className="text-center px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {repsQuery.data?.filter(rep => {
                        // Vertical filter: match rep.vertical or any in allowedVerticals
                        if (repVerticalFilter) {
                          const allowed = (() => { try { return rep.allowedVerticals ? JSON.parse(rep.allowedVerticals as string) : null; } catch { return null; } })();
                          const inPrimary = rep.vertical === repVerticalFilter;
                          const inAllowed = Array.isArray(allowed) && allowed.includes(repVerticalFilter);
                          if (!inPrimary && !inAllowed) return false;
                        }
                        if (!repSearch.trim()) return true;
                        const q = repSearch.toLowerCase();
                        return (
                          `${rep.firstName} ${rep.lastName}`.toLowerCase().includes(q) ||
                          rep.email?.toLowerCase().includes(q) ||
                          rep.repCode?.toLowerCase().includes(q)
                        );
                      }).map((rep, i) => {
                        const allowedVerts = (() => { try { return rep.allowedVerticals ? JSON.parse(rep.allowedVerticals as string) : null; } catch { return null; } })();
                        return (
                          <tr key={rep.id} className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer" onClick={() => setSelectedAgent(rep)}>
                            <td className="px-5 py-3.5 text-center">
                              <span className={`text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center mx-auto ${i === 0 ? "bg-yellow-100 text-yellow-700" : i === 1 ? "bg-gray-100 text-gray-600" : i === 2 ? "bg-orange-100 text-orange-700" : "bg-gray-50 text-gray-400"}`}>
                                {i + 1}
                              </span>
                            </td>
                            <td className="px-3 sm:px-5 py-3.5 sticky left-0 z-[5] w-28 sm:w-40 lg:w-52 bg-white shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                              <div className="font-semibold text-gray-900 truncate" title={`${rep.firstName} ${rep.lastName}`}>{rep.firstName} {rep.lastName}</div>
                              <div className="text-xs text-gray-400 truncate" title={rep.email ?? undefined}>{rep.email}</div>
                              {rep.vertical && <div className="text-[10px] mt-0.5 truncate" style={{ color: VERTICAL_COLORS[rep.vertical] ?? "#84ab4a" }}>{rep.vertical}</div>}
                            </td>
                            <td className="px-5 py-3.5">
                              <AgentLevelBadge level={(rep as any).agentLevel ?? "Associate"} />
                            </td>
                            <td className="px-5 py-3.5">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm font-bold text-[#84ab4a] bg-[#84ab4a]/10 px-2 py-0.5 rounded-md">{rep.repCode}</span>
                                <button onClick={() => copyToClipboard(rep.repCode, rep.repCode)} className="text-gray-300 hover:text-gray-600 transition-colors">
                                  {copiedCode === rep.repCode ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                                </button>
                              </div>
                            </td>
                            <td className="px-5 py-3.5">
                              {allowedVerts === null ? (
                                <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">All Verticals</span>
                              ) : allowedVerts.length === 0 ? (
                                <span className="text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-semibold">No Access</span>
                              ) : (
                                <div className="flex flex-wrap gap-1">
                                  {allowedVerts.map((v: string) => (
                                    <span key={v} className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: `${VERTICAL_COLORS[v] ?? "#84ab4a"}15`, color: VERTICAL_COLORS[v] ?? "#84ab4a" }}>
                                      {v}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="px-5 py-3.5 max-w-[220px]">
                              {(() => {
                                const inviteLink = `${window.location.origin}/associates?ref=${rep.repCode}`;
                                return (
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-xs text-gray-500 font-mono truncate">{inviteLink}</span>
                                    <button onClick={() => copyToClipboard(inviteLink, `link-${rep.repCode}`)} className="text-gray-300 hover:text-gray-600 transition-colors flex-shrink-0">
                                      {copiedCode === `link-${rep.repCode}` ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                                    </button>
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="px-5 py-3.5 text-center font-mono font-bold text-gray-700">{rep.totalLeads}</td>
                            <td className="px-5 py-3.5 text-center">
                              <span className="font-mono font-bold text-[#84ab4a]">{rep.activeClients}</span>
                            </td>
                            <td className="px-5 py-3.5 text-right font-mono font-bold text-gray-900">{fmt(rep.totalCommission)}</td>
                            <td className="px-5 py-3.5 text-center">
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                rep.status === "active" ? "bg-green-100 text-green-700"
                                : rep.status === "inactive" ? "bg-red-100 text-red-600"
                                : "bg-gray-100 text-gray-500"
                              }`}>
                                {rep.status === "inactive" ? "Suspended" : rep.status}
                              </span>
                            </td>
                            <td className="px-5 py-3.5 text-center">
                              {rep.ghlContactId ? (
                                <span title={rep.ghlContactId} className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                                  <Check className="w-3 h-3" /> Linked
                                </span>
                              ) : (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setRepairTarget(rep); setManualGhlId(""); }}
                                  title="No Wibiz contact — click to repair"
                                  className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 px-2 py-0.5 rounded-full transition-colors"
                                >
                                  <AlertCircle className="w-3 h-3" /> Missing
                                </button>
                              )}
                            </td>
                            <td className="px-5 py-3.5 text-center">
                              <div className="relative inline-block" ref={openActionMenu === rep.id ? actionMenuRef : null}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (openActionMenu === rep.id) {
                                      setOpenActionMenu(null);
                                      return;
                                    }
                                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                    setActionMenuPos({ top: rect.bottom + 4, left: rect.right - 192 });
                                    setOpenActionMenu(rep.id);
                                  }}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white border border-gray-200 rounded-lg shadow-sm hover:border-[#84ab4a]/50 hover:text-[#84ab4a] transition-colors"
                                >
                                  Actions <ChevronDown className="w-3 h-3" />
                                </button>
                                {openActionMenu === rep.id && actionMenuPos && createPortal(
                                  <div
                                    ref={actionMenuPortalRef}
                                    className="fixed w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden"
                                    style={{ top: actionMenuPos.top, left: actionMenuPos.left }}
                                  >
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenActionMenu(null);
                                        window.open(`/rep/${encodeURIComponent(rep.repCode)}`, "_blank", "noopener,noreferrer");
                                      }}
                                      className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                      <Eye className="w-3.5 h-3.5 text-[#84ab4a]" /> View as Agent
                                    </button>
                                    <div className="border-t border-gray-100" />
                                    <button onClick={(e) => { e.stopPropagation(); setOpenActionMenu(null); setEditingRep(rep); }} className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                                      <Pencil className="w-3.5 h-3.5 text-blue-500" /> Edit Details
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); setOpenActionMenu(null); setCredRep(rep); setCredUsername(rep.username ?? ""); setCredPassword(""); }} className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                                      <KeyRound className="w-3.5 h-3.5 text-purple-500" /> Set Credentials
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); setOpenActionMenu(null); setReassignTarget(rep); }} className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                                      <Network className="w-3.5 h-3.5 text-[#84ab4a]" /> Reassign / Hierarchy
                                    </button>
                                    {!rep.ghlContactId && (
                                      <button onClick={(e) => { e.stopPropagation(); setOpenActionMenu(null); setRepairTarget(rep); setManualGhlId(""); }} className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-amber-700 hover:bg-amber-50 transition-colors">
                                        <AlertCircle className="w-3.5 h-3.5" /> Repair Wibiz Contact
                                      </button>
                                    )}
                                    <div className="border-t border-gray-100" />
                                    {rep.status === "inactive" ? (
                                      <button onClick={(e) => { e.stopPropagation(); setOpenActionMenu(null); reinstateRepMutation.mutate({ id: rep.id }); }} className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-green-700 hover:bg-green-50 transition-colors">
                                        <RotateCcw className="w-3.5 h-3.5" /> Reinstate Agent
                                      </button>
                                    ) : (
                                      <button onClick={(e) => { e.stopPropagation(); setOpenActionMenu(null); setSuspendTarget(rep); }} className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-orange-700 hover:bg-orange-50 transition-colors">
                                        <Ban className="w-3.5 h-3.5" /> Suspend Agent
                                      </button>
                                    )}
                                    <button onClick={(e) => { e.stopPropagation(); setOpenActionMenu(null); setDeleteConfirm({ type: "rep", id: rep.id }); }} className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors">
                                      <Trash2 className="w-3.5 h-3.5" /> Delete
                                    </button>
                                  </div>,
                                  document.body
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {(!repsQuery.data || repsQuery.data.length === 0) && (
                    <div className="py-16 text-center text-gray-400">
                      <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="font-medium">No reps registered yet</p>
                      <p className="text-sm mt-1">Click "Register New Rep" to add your first sales rep</p>
                    </div>
                  )}
                </div>
                {repTableCanScrollRight && (
                  <div className="pointer-events-none absolute top-0 right-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent" />
                )}
              </div>

              {/* ── Deleted Agents ── */}
              <div className="mt-6 bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-gray-50 transition-colors"
                  onClick={() => setShowDeletedAgents(v => !v)}
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-gray-600">
                    <Trash2 className="w-4 h-4 text-red-400" />
                    Deleted Agents
                    {(deletedRepsQuery.data?.length ?? 0) > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600">
                        {deletedRepsQuery.data!.length}
                      </span>
                    )}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showDeletedAgents ? "rotate-180" : ""}`} />
                </button>
                {showDeletedAgents && (
                  <div className="border-t border-gray-100">
                    {!deletedRepsQuery.data || deletedRepsQuery.data.length === 0 ? (
                      <div className="py-10 text-center text-gray-400">
                        <Trash2 className="w-8 h-8 mx-auto mb-2 opacity-20" />
                        <p className="text-sm">No deleted agents</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-100 bg-gray-50/50">
                              <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Agent</th>
                              <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Rep Code</th>
                              <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Deleted By</th>
                              <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Deleted At</th>
                              <th className="text-center px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {deletedRepsQuery.data.map((rep: any) => (
                              <tr key={rep.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                                <td className="px-5 py-3">
                                  <div className="font-medium text-gray-800">{rep.firstName} {rep.lastName}</div>
                                  <div className="text-xs text-gray-400">{rep.email}</div>
                                </td>
                                <td className="px-5 py-3 font-mono text-xs text-gray-500">{rep.repCode}</td>
                                <td className="px-5 py-3 text-xs text-gray-500">{rep.deletedBy ?? "—"}</td>
                                <td className="px-5 py-3 text-xs text-gray-500">
                                  {rep.deletedAt ? new Date(rep.deletedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                                </td>
                                <td className="px-5 py-3 text-center">
                                  <button
                                    onClick={() => restoreRepMutation.mutate({ id: rep.id })}
                                    disabled={restoreRepMutation.isPending}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#84ab4a] border border-[#84ab4a]/30 rounded-lg hover:bg-[#84ab4a]/5 transition-colors disabled:opacity-50"
                                  >
                                    <RotateCcw className="w-3 h-3" />
                                    Restore
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── COMMISSIONS TAB ── */}
          {activeTab === "commissions" && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  {
                    label: "Total Commissions",
                    value: filteredCommissionSummary.totalAmount,
                    detail: `${filteredCommissionSummary.totalCount} commission${filteredCommissionSummary.totalCount === 1 ? "" : "s"} · ${fmtCommission(filteredCommissionSummary.outstandingAmount)} outstanding`,
                    color: "#1A6FD4",
                  },
                  {
                    label: "Pending",
                    value: filteredCommissionSummary.pending.amount,
                    detail: `${filteredCommissionSummary.pending.count} commission${filteredCommissionSummary.pending.count === 1 ? "" : "s"}`,
                    color: "#C8920A",
                  },
                  {
                    label: "Approved",
                    value: filteredCommissionSummary.approved.amount,
                    detail: `${filteredCommissionSummary.approved.count} commission${filteredCommissionSummary.approved.count === 1 ? "" : "s"}`,
                    color: "#0FA8A8",
                  },
                  {
                    label: "Paid",
                    value: filteredCommissionSummary.paid.amount,
                    detail: `${filteredCommissionSummary.paid.count} commission${filteredCommissionSummary.paid.count === 1 ? "" : "s"}`,
                    color: "#84ab4a",
                  },
                ].map(({ label, value, detail, color }) => (
                  <div key={label} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: color }} />
                    <div className="text-2xl font-bold font-mono text-gray-900">{fmtCommission(Number(value))}</div>
                    <div className="text-xs text-gray-700 mt-1 font-semibold">{label}</div>
                    <div className="text-[11px] text-gray-400 mt-0.5">{detail}</div>
                  </div>
                ))}
              </div>
              <CommissionGridTable />
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 space-y-3">
                  <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-700">All Commissions</span>
                      <div className="text-[11px] text-gray-400 mt-0.5">
                        {visibleCommissions.length} of {(commissionsQuery.data ?? []).length} shown
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end xl:justify-end">
                      <div className="relative min-w-0 sm:w-64">
                      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                      <Input
                        value={commSearch}
                        onChange={event => setCommSearch(event.target.value)}
                        placeholder="Search commissions..."
                        aria-label="Search commissions"
                        className="h-8 pl-8 text-xs"
                      />
                      </div>
                      <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        From
                        <Input
                          type="date"
                          value={commDateFrom}
                          onChange={event => setCommDateFrom(event.target.value)}
                          aria-label="Commission date from"
                          className="mt-1 h-8 w-full text-xs normal-case sm:w-36"
                        />
                      </label>
                      <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        To
                        <Input
                          type="date"
                          value={commDateTo}
                          onChange={event => setCommDateTo(event.target.value)}
                          aria-label="Commission date to"
                          className="mt-1 h-8 w-full text-xs normal-case sm:w-36"
                        />
                      </label>
                      <select
                        value={commStatusFilter}
                        onChange={event => setCommStatusFilter(event.target.value as CommissionStatusFilter)}
                        aria-label="Commission status"
                        className="h-8 px-3 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-[#84ab4a]/60 focus:ring-2 focus:ring-[#84ab4a]/20"
                      >
                        <option value="">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="paid">Paid</option>
                      </select>
                      <select
                        value={commVerticalFilter}
                        onChange={event => setCommVerticalFilter(event.target.value)}
                        aria-label="Commission vertical"
                        className="h-8 px-3 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-[#84ab4a]/60 focus:ring-2 focus:ring-[#84ab4a]/20"
                      >
                        <option value="">All Verticals</option>
                        {ALL_VERTICALS.map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                      <button
                        type="button"
                        onClick={exportFilteredCommissions}
                        disabled={visibleCommissions.length === 0 || commissionDateRangeInvalid}
                        title="Export the currently filtered commissions for Excel"
                        className="inline-flex h-8 items-center justify-center gap-1.5 px-3 text-xs font-semibold text-[#6f923f] border border-[#84ab4a]/40 bg-white hover:bg-[#84ab4a]/5 rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <Download className="w-3.5 h-3.5" /> Export Excel
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowManualCommissionForm(true)}
                        className="inline-flex h-8 items-center justify-center gap-1.5 px-3 text-xs font-semibold text-white bg-[#84ab4a] hover:bg-[#759a40] rounded-lg transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" /> Create Manual Commission
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-medium text-gray-400">Payout period:</span>
                    <button
                      type="button"
                      onClick={() => applyCommissionPayoutPeriod("first")}
                      className="px-2.5 py-1 text-[11px] font-semibold text-gray-600 border border-gray-200 rounded-lg hover:border-[#84ab4a]/50 hover:text-[#6f923f]"
                    >
                      This Month 1st-15th
                    </button>
                    <button
                      type="button"
                      onClick={() => applyCommissionPayoutPeriod("second")}
                      className="px-2.5 py-1 text-[11px] font-semibold text-gray-600 border border-gray-200 rounded-lg hover:border-[#84ab4a]/50 hover:text-[#6f923f]"
                    >
                      This Month 16th-End
                    </button>
                    {hasCommissionFilters && (
                      <button
                        type="button"
                        onClick={clearCommissionFilters}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-gray-500 hover:text-red-600"
                      >
                        <X className="w-3 h-3" /> Clear Filters
                      </button>
                    )}
                    {commissionDateRangeInvalid && (
                      <span className="text-[11px] font-medium text-red-600">The From date must be on or before the To date.</span>
                    )}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/50">
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Rep</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Client Name</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Business Name</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Description</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Vertical</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Date</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Rep Name</th>
                        <th className="text-right px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Amount</th>
                        <th className="text-center px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Status</th>
                        <th className="text-center px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleCommissions.map((c) => {
                        const vertical = (c as any).verticalName as string | null;
                        return (
                        <tr key={c.id} className="border-b border-gray-50">
                          <td className="px-5 py-3.5 font-mono text-xs font-bold text-[#84ab4a]">{c.repCode}</td>
                          <td className="px-5 py-3.5 text-gray-700 font-medium">
                            {(c as any).clientName ?? "—"}
                            {(c as any).isManualCommission && (
                              <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 align-middle">MANUAL</span>
                            )}
                            {(c as any).isTest && (
                              <span
                                className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600 align-middle"
                                title="This commission came from a Stripe test-mode enrollment. No real money was involved and it is hidden from the associate."
                              >TEST</span>
                            )}
                          </td>
                          <td className="px-5 py-3.5 text-gray-700 font-medium">{(c as any).businessName ?? "—"}</td>
                          <td className="px-5 py-3.5 text-gray-600 text-xs max-w-[260px]">
                            {(c as any).isManualCommission ? (c.notes || "-") : "-"}
                            {(c as any).isManualCommission && c.createdBy && (
                              <div className="text-[10px] text-gray-400 mt-1">Created by {c.createdBy}</div>
                            )}
                          </td>
                          <td className="px-5 py-3.5">
                            {vertical ? (
                              <span
                                className="text-xs px-2 py-0.5 rounded-full font-semibold"
                                style={{ color: VERTICAL_COLORS[vertical] ?? "#6B7280", background: `${VERTICAL_COLORS[vertical] ?? "#6B7280"}1A` }}
                              >
                                {vertical}
                              </span>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-5 py-3.5 text-gray-500 text-xs">
                            <div className="flex items-center gap-1.5">
                              <span>{c.createdAt ? new Date(c.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</span>
                              {(c as any).isManualTrigger && (
                                <span title="Manually triggered by an admin — this date is when the commission was recorded, which may lag the client's actual transaction date.">
                                  <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-gray-700 font-medium">{(c as any).repName ?? "—"}</td>
                          <td className="px-5 py-3.5 text-right font-mono font-bold text-gray-900">{fmtCommission(Number(c.amount))}</td>
                          <td className="px-5 py-3.5 text-center">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${c.status === "paid" ? "bg-green-100 text-green-700" : c.status === "approved" ? "bg-blue-100 text-blue-700" : "bg-yellow-100 text-yellow-700"}`}>
                              {c.status}
                            </span>
                            {c.status === "paid" && c.paidAt && (
                              <div className="text-[10px] text-gray-400 mt-1">
                                Paid {new Date(c.paidAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              {c.status === "pending" && (
                                <button onClick={() => updateCommission.mutate({ id: c.id, status: "approved" })} className="text-xs text-blue-600 hover:underline font-semibold">Approve</button>
                              )}
                              {c.status === "approved" && (
                                <button onClick={() => updateCommission.mutate({ id: c.id, status: "paid" })} className="text-xs text-green-600 hover:underline font-semibold">Mark Paid</button>
                              )}
                              <button onClick={() => setDeleteConfirm({ type: "commission", id: c.id })} className="p-1 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Delete commission">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {visibleCommissions.length === 0 && (
                    <div className="py-16 text-center text-gray-400">
                      <DollarSign className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="font-medium">{hasCommissionFilters ? "No commissions match your filters" : "No commissions yet"}</p>
                      <p className="text-sm mt-1">
                        {hasCommissionFilters
                          ? "Adjust the date range, status, vertical, or search, then try again."
                          : "Commissions are auto-generated when leads reach Payment Received stage"}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── VERTICALS OVERVIEW TAB ── */}
          {activeTab === "verticals" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-1">Verticals Overview</h2>
                <p className="text-sm text-gray-400">BC360 has one account on the Wibiz platform. All five verticals live inside it as separate pipelines.</p>
              </div>
              {/* Account architecture note */}
              <div className="bg-[#0D1E35] rounded-2xl p-5 text-sm text-white/70 leading-relaxed">
                <div className="text-[#84ab4a] font-bold text-xs uppercase tracking-wider mb-3">Account Architecture</div>
                <p className="mb-2">BC360 has <strong className="text-white">one account</strong> on the WiBiz platform. All five verticals live inside it as separate pipelines. Separation is achieved through pipelines, custom fields, tags, and user permissions.</p>
                <p className="text-white/50 text-xs">Admin Users (Butch, Hudson) have full visibility: all pipelines, all leads, all verticals, all reps. Team members only see the vertical they work in.</p>
              </div>
              {/* Pipeline cards */}
              <div className="grid grid-cols-2 gap-4">
                {VERTICALS.map((v) => {
                  const verticalLeads = leads.filter(l => l.verticalName === v.verticalName || l.verticalName?.startsWith(v.label));
                  const dbVertical = (allVerticalsQuery.data ?? []).find((dv: any) => dv.name === v.label || dv.name === v.verticalName);
                  const isVerticalActive = dbVertical ? dbVertical.isActive : true;
                  return (
                    <div key={v.key} className={`bg-white border rounded-2xl shadow-sm overflow-hidden transition-shadow ${isVerticalActive ? "border-gray-100 hover:shadow-md cursor-pointer" : "border-red-100 opacity-70"}`} onClick={() => isVerticalActive && setActiveTab(v.key)}>
                      <div className="h-1" style={{ background: isVerticalActive ? v.color : "#D1D5DB" }} />
                      <div className="p-5">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: isVerticalActive ? v.bg : "#F3F4F6" }}>
                              <v.icon className="w-4.5 h-4.5" style={{ color: isVerticalActive ? v.color : "#9CA3AF" }} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-bold text-gray-900">{v.label}</h3>
                                {!isVerticalActive && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 uppercase tracking-wider">Off</span>}
                              </div>
                              <div className="text-xs text-gray-400">{v.pipelines.length} pipeline{v.pipelines.length > 1 ? "s" : ""}</div>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1.5">
                            <div className="text-2xl font-bold font-mono" style={{ color: isVerticalActive ? v.color : "#9CA3AF" }}>{verticalLeads.length}</div>
                            <div className="text-xs text-gray-400">leads</div>
                            <button
                              onClick={(e) => { e.stopPropagation(); exportVerticalCsv(v.label, verticalLeads); }}
                              className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-lg border border-gray-200 text-gray-500 hover:text-[#84ab4a] hover:border-[#84ab4a]/40 transition-colors"
                              title="Export leads as CSV"
                            >
                              <Download className="w-3 h-3" /> Export CSV
                            </button>
                            {dbVertical && (
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleVerticalMutation.mutate({ id: dbVertical.id, isActive: !isVerticalActive }); }}
                                className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-lg border transition-colors ${isVerticalActive ? "border-green-200 text-green-600 hover:bg-red-50 hover:text-red-600 hover:border-red-200" : "border-gray-200 text-gray-400 hover:bg-green-50 hover:text-green-600 hover:border-green-200"}`}
                                title={isVerticalActive ? "Disable this vertical" : "Enable this vertical"}
                                disabled={toggleVerticalMutation.isPending}
                              >
                                {isVerticalActive ? <><ToggleRight className="w-3 h-3" /> Active</> : <><ToggleLeft className="w-3 h-3" /> Inactive</>}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="space-y-2">
                          {v.pipelines.map((p) => (
                            <div key={p.id} className="bg-gray-50 rounded-lg px-3 py-2">
                              <div className="text-xs font-semibold text-gray-700 mb-1">{p.name}</div>
                              <div className="flex flex-wrap gap-1">
                                {p.stages.slice(0, 5).map((s) => (
                                  <span key={s} className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: `${v.color}12`, color: v.color }}>
                                    {s}
                                  </span>
                                ))}
                                {p.stages.length > 5 && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium bg-gray-200 text-gray-500">
                                    +{p.stages.length - 5} more
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── WIBIZ SYNC TAB ── */}
          {activeTab === "sync" && (
            <>
              <DocSyncPanel />
              <UplineSyncPanel />
              <CrmSyncTab copyToClipboard={copyToClipboard} copiedCode={copiedCode} />
            </>
          )}

          {/* ── GRACE FAQ TAB ── */}
          {activeTab === "grace_faq" && (
            <GraceFaqAdminTab />
          )}

          {/* ── ROLE UPGRADES TAB ── */}
          {activeTab === "grace_phone" && (
            <GracePhoneAdminTab />
          )}
          {activeTab === "role_upgrades" && (
            <RoleUpgradesTab />
          )}

          {/* ── FUNDABLE360 FINANCING TAB ── */}
          {activeTab === "fundable360_financing" && (
            <Fundable360FinancingTab />
          )}

          {/* ── TEAM MEMBERS TAB ── */}
          {activeTab === "team" && (
            <TeamMembersAdminTab />
          )}

          {/* ── RA SUBSCRIPTIONS TAB ── */}
          {activeTab === "ra_subscriptions" && (
            <RaSubscriptionsAdminTab />
          )}

          {/* ── IDENTITY VERIFICATION TAB ── */}
          {activeTab === "identity" && (
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-1">Identity Verification</h2>
              <p className="text-sm text-gray-400 mb-5">Review and approve agent identity submissions. Agents must be verified before accessing advanced features.</p>
              <IdentityVerificationPanel />
            </div>
          )}

          {/* ── SIGNED AGREEMENTS TAB ── */}
          {activeTab === "agreements" && (
            <AgreementPortalPanel />
          )}

          {/* ── SHARE360 ENROLLMENT TAB ── */}
          {activeTab === "share360_enrollment" && (
            <Share360EnrollmentAdminTab />
          )}

          {activeTab === "client_docs" && (
            <ClientDocumentReview />
          )}
          {activeTab === "certifications" && (
            <CertificationPipeline />
          )}
          {activeTab === "quiz_questions" && (
            <AdminQuizQuestionsTab />
          )}
          {activeTab === "materials" && (
            <AdminMarketingMaterials />
          )}
          {activeTab === "bc360_calendar" && (
            <AdminBc360CalendarTab />
          )}
          {activeTab === "appointments" && (
            <AdminAppointmentsTab />
          )}
          {activeTab === "announcements" && (
            <AdminAnnouncementsTab />
          )}
          {activeTab === "flywheel" && (
            <AdminFlywheelTab />
          )}
          {activeTab === "associates_landing" && (
            <AdminAssociateLandingTab />
          )}
          {activeTab === "verticallinks" && (
            <AdminVerticalLinksTab />
          )}
          {activeTab === "settings" && (
            <AdminSettingsTab />
          )}

          {/* ── ATTRIBUTION TAB ── */}
          {/* ── WEBHOOK LOGS TAB ── */}
          {activeTab === "webhook_logs" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Webhook Logs</h2>
                  <p className="text-sm text-gray-400">All inbound payloads from Wibiz and Legacy360 endpoints. Admin-only.</p>
                </div>
                <button onClick={() => webhookLogsQuery.refetch()} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 px-3 py-1.5 border border-gray-200 rounded-lg">
                  <Activity className="w-3.5 h-3.5" /> Refresh
                </button>
              </div>
              {/* Stats bar */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-2">
                {[
                  { label: "Total", value: webhookLogsQuery.data?.length ?? 0, color: "#6366F1" },
                  { label: "Backend Events", value: webhookLogsQuery.data?.filter((l: any) => l.endpoint === "ghl").length ?? 0, color: "#1A6FD4" },
                  { label: "Legacy360", value: webhookLogsQuery.data?.filter((l: any) => l.endpoint === "legacy360").length ?? 0, color: "#C8920A" },
                  { label: "Errors", value: webhookLogsQuery.data?.filter((l: any) => l.attributionStatus === "error").length ?? 0, color: "#E8620A" },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: color }} />
                    <div className="text-2xl font-bold font-mono text-gray-900">{value}</div>
                    <div className="text-xs text-gray-400 mt-1 font-medium">{label}</div>
                  </div>
                ))}
              </div>
              {/* Log table */}
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                {webhookLogsQuery.isLoading ? (
                  <div className="py-12 text-center text-gray-400 text-sm">Loading webhook logs...</div>
                ) : (webhookLogsQuery.data?.length ?? 0) === 0 ? (
                  <div className="py-16 text-center text-gray-400">
                    <Activity className="w-10 h-10 mx-auto mb-3 opacity-30" />
                    <p className="font-medium">No webhook events yet</p>
                    <p className="text-sm mt-1">Events are logged automatically when Wibiz sends data to this server.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-100 bg-gray-50">
                          <th className="text-left px-4 py-2.5 font-semibold text-gray-500">Time</th>
                          <th className="text-left px-4 py-2.5 font-semibold text-gray-500">Endpoint</th>
                          <th className="text-left px-4 py-2.5 font-semibold text-gray-500">Event</th>
                          <th className="text-left px-4 py-2.5 font-semibold text-gray-500">Vertical</th>
                          <th className="text-left px-4 py-2.5 font-semibold text-gray-500">Rep Code</th>
                          <th className="text-left px-4 py-2.5 font-semibold text-gray-500">Lead ID</th>
                          <th className="text-left px-4 py-2.5 font-semibold text-gray-500">Status</th>
                          <th className="text-left px-4 py-2.5 font-semibold text-gray-500">ms</th>
                        </tr>
                      </thead>
                      <tbody>
                        {webhookLogsQuery.data?.map((entry: any) => {
                          const isError = entry.attributionStatus === "error";
                          const isResolved = entry.attributionStatus === "resolved";
                          return (
                            <tr key={entry.id} className={`border-b border-gray-50 hover:bg-gray-50 ${isError ? "bg-red-50" : ""}`}>
                              <td className="px-4 py-2.5 text-gray-400 font-mono whitespace-nowrap">
                                {new Date(entry.createdAt).toLocaleString()}
                              </td>
                              <td className="px-4 py-2.5">
                                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${entry.endpoint === "legacy360" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>
                                  {entry.endpoint}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-gray-600 font-mono">{entry.eventType ?? "—"}</td>
                              <td className="px-4 py-2.5 text-gray-600">{entry.verticalName ?? "—"}</td>
                              <td className="px-4 py-2.5 font-mono text-gray-700">{entry.repCodeExtracted ?? "—"}</td>
                              <td className="px-4 py-2.5 font-mono text-gray-700">{entry.leadId ?? "—"}</td>
                              <td className="px-4 py-2.5">
                                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                  isError ? "bg-red-100 text-red-700" :
                                  isResolved ? "bg-green-100 text-green-700" :
                                  entry.attributionStatus === "unresolved" ? "bg-orange-100 text-orange-700" :
                                  "bg-gray-100 text-gray-500"
                                }`}>
                                  {entry.attributionStatus ?? "—"}
                                </span>
                                {isError && entry.error && (
                                  <div className="text-[10px] text-red-500 mt-0.5 truncate max-w-[200px]">{entry.error}</div>
                                )}
                              </td>
                              <td className="px-4 py-2.5 font-mono text-gray-400">{entry.processingMs ?? "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "billing" && <AdminBillingPanel />}

          {activeTab === "attribution" && (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  {
                    label: "Resolved",
                    value: attributionLogsQuery.data?.filter(l => l.status === "resolved").length ?? 0,
                    color: "#84ab4a",
                  },
                  {
                    label: "Unresolved",
                    value: attributionLogsQuery.data?.filter(l => l.status === "unresolved").length ?? 0,
                    color: "#E8620A",
                  },
                  {
                    label: "No Rep Code",
                    value: attributionLogsQuery.data?.filter(l => l.status === "no_rep_code").length ?? 0,
                    color: "#6B7280",
                  },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: color }} />
                    <div className="text-2xl font-bold font-mono text-gray-900">{value}</div>
                    <div className="text-xs text-gray-400 mt-1 font-medium">{label}</div>
                  </div>
                ))}
              </div>

              {/* Attribution Issues */}
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <span className="text-sm font-semibold text-gray-700">Attribution Issues</span>
                    <span className="ml-2 text-xs text-gray-400">Leads with Wibiz agent ID extracted but referrer email not yet resolved</span>
                  </div>
                  <button
                    onClick={() => resyncAllMutation.mutate()}
                    disabled={resyncAllMutation.isPending || (attributionIssuesQuery.data?.length ?? 0) === 0}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E8620A] text-white text-xs font-semibold hover:bg-[#d4540a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {resyncAllMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    Re-sync All ({attributionIssuesQuery.data?.length ?? 0})
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/50">
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Lead</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Vertical</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Agent ID</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Override Rep</th>
                        <th className="text-center px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attributionIssuesQuery.data?.map((lead) => (
                        <tr key={lead.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                          <td className="px-5 py-3.5">
                            <div className="font-medium text-gray-800">{lead.firstName} {lead.lastName ?? ""}</div>
                            <div className="text-xs text-gray-400">{lead.email ?? "—"}</div>
                          </td>
                          <td className="px-5 py-3.5 text-gray-600 text-xs">{lead.verticalName ?? "—"}</td>
                          <td className="px-5 py-3.5 font-mono text-xs font-bold text-[#E8620A]">
                            {lead.f360ReferrerAgentId ?? lead.f360AgentId ?? lead.repCode ?? "—"}
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex gap-1.5 items-center">
                              <select
                                value={assignRepCode[lead.id] ?? ""}
                                onChange={e => setAssignRepCode(prev => ({ ...prev, [lead.id]: e.target.value }))}
                                className="border border-gray-200 rounded-md px-2 py-1 text-xs focus:outline-none focus:border-[#84ab4a] min-w-[120px]"
                              >
                                <option value="">— override —</option>
                                {repsQuery.data?.filter(r => r.repCode?.startsWith("BC-")).map(r => (
                                  <option key={r.id} value={r.repCode}>
                                    {r.repCode} — {r.firstName} {r.lastName}
                                  </option>
                                ))}
                              </select>
                              {assignRepCode[lead.id] && (
                                <button
                                  onClick={() => assignRepMutation.mutate({ leadId: lead.id, repCode: assignRepCode[lead.id] })}
                                  disabled={assignRepMutation.isPending}
                                  className="px-2 py-1 bg-[#84ab4a] text-white text-xs font-semibold rounded-md hover:bg-[#6d9039] disabled:opacity-40 transition-colors"
                                >
                                  {assignRepMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Assign"}
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            <button
                              onClick={() => resyncOneMutation.mutate({ leadId: lead.id })}
                              disabled={resyncOneMutation.isPending}
                              className="flex items-center gap-1 mx-auto px-2.5 py-1 rounded-md bg-[#84ab4a]/10 text-[#84ab4a] text-xs font-semibold hover:bg-[#84ab4a]/20 disabled:opacity-50 transition-colors"
                            >
                              {resyncOneMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                              Re-sync
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(attributionIssuesQuery.data?.length ?? 0) === 0 && (
                    <div className="py-16 text-center text-gray-400">
                      <CheckCircle2 className="w-10 h-10 mx-auto mb-3 opacity-30 text-[#84ab4a]" />
                      <p className="font-medium text-[#84ab4a]">All leads are properly attributed</p>
                      <p className="text-sm mt-1">No leads with missing referrer information found</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Manual Agent Assignment */}
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100">
                  <span className="text-sm font-semibold text-gray-700">Manual Agent Assignment</span>
                  <span className="ml-2 text-xs text-gray-400">Search any lead and assign the correct rep — use this for leads that predated the fix or were missed by auto-attribution</span>
                </div>
                <div className="p-5 space-y-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={assignSearchInput}
                      onChange={e => setAssignSearchInput(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && setAssignSearch(assignSearchInput.trim())}
                      placeholder="Search lead by name or email..."
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#84ab4a]"
                    />
                    <button
                      onClick={() => setAssignSearch(assignSearchInput.trim())}
                      disabled={assignSearchInput.trim().length < 2}
                      className="px-4 py-2 bg-[#84ab4a] text-white text-sm font-semibold rounded-lg hover:bg-[#6d9039] disabled:opacity-50 transition-colors"
                    >
                      Search
                    </button>
                  </div>
                  {assignLeadSearchQuery.isFetching && (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Loader2 className="w-4 h-4 animate-spin" /> Searching...
                    </div>
                  )}
                  {assignLeadSearchQuery.data && assignLeadSearchQuery.data.length > 0 && (
                    <div className="rounded-lg border border-gray-100 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-100">
                            <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-400 uppercase tracking-wide">Lead</th>
                            <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-400 uppercase tracking-wide">Vertical</th>
                            <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-400 uppercase tracking-wide">Current Attribution</th>
                            <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-400 uppercase tracking-wide">Assign Rep</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assignLeadSearchQuery.data.map((lead) => (
                            <tr key={lead.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-800">{lead.firstName} {lead.lastName ?? ""}</div>
                                <div className="text-xs text-gray-400">{lead.email ?? "—"}</div>
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-500">{lead.verticalName ?? "—"}</td>
                              <td className="px-4 py-3 text-xs">
                                {lead.referrerAgentEmail ? (
                                  <div>
                                    <div className="font-medium text-[#84ab4a]">{lead.referrerAgentName ?? ""}</div>
                                    <div className="text-gray-400">{lead.referrerAgentEmail}</div>
                                  </div>
                                ) : (
                                  <span className="text-orange-500 font-medium">
                                    {lead.f360AgentId ?? lead.f360ReferrerAgentId ?? lead.repCode ?? "Unattributed"}
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex gap-2 items-center">
                                  <select
                                    value={assignRepCode[lead.id] ?? ""}
                                    onChange={e => setAssignRepCode(prev => ({ ...prev, [lead.id]: e.target.value }))}
                                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#84ab4a] min-w-[140px]"
                                  >
                                    <option value="">— Select Rep —</option>
                                    {repsQuery.data?.filter(r => r.repCode?.startsWith("BC-")).map(r => (
                                      <option key={r.id} value={r.repCode}>
                                        {r.repCode} — {r.firstName} {r.lastName}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    onClick={() => assignRepMutation.mutate({ leadId: lead.id, repCode: assignRepCode[lead.id] })}
                                    disabled={!assignRepCode[lead.id] || assignRepMutation.isPending}
                                    className="px-3 py-1.5 bg-[#84ab4a] text-white text-xs font-semibold rounded-lg hover:bg-[#6d9039] disabled:opacity-40 transition-colors whitespace-nowrap"
                                  >
                                    {assignRepMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Assign"}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {assignLeadSearchQuery.data?.length === 0 && assignSearch.length >= 2 && !assignLeadSearchQuery.isFetching && (
                    <div className="text-sm text-gray-400 text-center py-4">No leads found matching "{assignSearch}"</div>
                  )}
                </div>
              </div>

              {/* GHL Field UUID Lookup */}
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100">
                  <span className="text-sm font-semibold text-gray-700">Wibiz Custom Field Lookup</span>
                  <span className="ml-2 text-xs text-gray-400">Enter a Wibiz contact ID to inspect all custom field UUIDs and values</span>
                </div>
                <div className="p-5 space-y-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={ghlLookupInput}
                      onChange={(e) => setGhlLookupInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && setGhlLookupContactId(ghlLookupInput.trim())}
                      placeholder="Wibiz Contact ID (e.g. VzT0IRrSvZmTC0beBxMP)"
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#84ab4a]"
                    />
                    <button
                      onClick={() => setGhlLookupContactId(ghlLookupInput.trim())}
                      disabled={ghlLookupInput.trim().length < 5}
                      className="px-4 py-2 bg-[#84ab4a] text-white text-sm font-semibold rounded-lg hover:bg-[#6d9039] disabled:opacity-50 transition-colors"
                    >
                      Lookup
                    </button>
                  </div>
                  {ghlFieldLookupQuery.isFetching && (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Loader2 className="w-4 h-4 animate-spin" /> Fetching from Wibiz...
                    </div>
                  )}
                  {ghlFieldLookupQuery.data && !ghlFieldLookupQuery.isFetching && (
                    ghlFieldLookupQuery.data.found ? (
                      <div className="space-y-2">
                        <div className="text-sm font-medium text-gray-700">
                          {ghlFieldLookupQuery.data.contactName ?? "Unknown"} — <span className="text-gray-400 text-xs">{ghlFieldLookupQuery.data.contactEmail ?? ""}</span>
                        </div>
                        <div className="rounded-lg border border-gray-100 overflow-hidden">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-100">
                                <th className="text-left px-3 py-2 font-bold text-gray-400 uppercase tracking-wide">UUID</th>
                                <th className="text-left px-3 py-2 font-bold text-gray-400 uppercase tracking-wide">Field Key</th>
                                <th className="text-left px-3 py-2 font-bold text-gray-400 uppercase tracking-wide">Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ghlFieldLookupQuery.data.fields.map((f: any, i: number) => (
                                <tr key={i} className={`border-b border-gray-50 ${f.value ? "" : "opacity-40"}`}>
                                  <td className="px-3 py-2 font-mono text-[10px] text-gray-500 select-all">{f.id ?? "—"}</td>
                                  <td className="px-3 py-2 font-mono text-[10px] text-blue-600">{f.key ?? <span className="text-gray-300 italic">no key</span>}</td>
                                  <td className="px-3 py-2 text-gray-700 max-w-[200px] truncate">{f.value != null ? String(f.value) : <span className="text-gray-300 italic">empty</span>}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {ghlFieldLookupQuery.data.fields.length === 0 && (
                            <div className="py-6 text-center text-gray-400 text-sm">No custom fields on this contact</div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-orange-500">Contact not found in Wibiz</div>
                    )
                  )}
                </div>
              </div>

              {/* Attribution Log */}
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700">Attribution Log</span>
                  <button onClick={() => { attributionLogsQuery.refetch(); attributionIssuesQuery.refetch(); }} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600">
                    <RefreshCw className="w-3 h-3" /> Refresh
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/50">
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Status</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Lead</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Vertical</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Rep Code</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Resolved To</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Trigger</th>
                        <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attributionLogsQuery.data?.map((entry) => (
                        <tr key={entry.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                          <td className="px-5 py-3">
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold ${
                              entry.status === "resolved" ? "bg-green-100 text-green-700" :
                              entry.status === "unresolved" ? "bg-orange-100 text-orange-700" :
                              "bg-gray-100 text-gray-500"
                            }`}>
                              {entry.status === "resolved" ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                              {entry.status === "resolved" ? "Resolved" : entry.status === "unresolved" ? "Unresolved" : "No Rep Code"}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <div className="font-medium text-gray-800 text-xs">{entry.leadName ?? "—"}</div>
                            <div className="text-[10px] text-gray-400">{entry.leadEmail ?? ""}</div>
                          </td>
                          <td className="px-5 py-3 text-xs text-gray-500">{entry.verticalName ?? "—"}</td>
                          <td className="px-5 py-3 font-mono text-xs font-bold text-[#84ab4a]">{entry.attemptedRepCode ?? "—"}</td>
                          <td className="px-5 py-3 text-xs text-gray-600">
                            {entry.resolvedEmail ? (
                              <div>
                                <div className="font-medium">{entry.resolvedName ?? ""}</div>
                                <div className="text-[10px] text-gray-400">{entry.resolvedEmail}</div>
                              </div>
                            ) : entry.notes ? (
                              <span className="text-orange-500 text-[10px]">{entry.notes}</span>
                            ) : "—"}
                          </td>
                          <td className="px-5 py-3">
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${entry.triggeredBy === "resync" ? "bg-blue-50 text-blue-600" : "bg-gray-50 text-gray-400"}`}>
                              {entry.triggeredBy}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-xs text-gray-400">
                            {new Date(entry.createdAt).toLocaleDateString()} {new Date(entry.createdAt).toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(attributionLogsQuery.data?.length ?? 0) === 0 && (
                    <div className="py-16 text-center text-gray-400">
                      <Activity className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="font-medium">No attribution events yet</p>
                      <p className="text-sm mt-1">Events are logged automatically when leads are created via webhook</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Dialog
        open={showManualCommissionForm}
        onOpenChange={(open) => {
          if (createManualCommission.isPending) return;
          setShowManualCommissionForm(open);
          if (!open) setManualCommissionForm(EMPTY_MANUAL_COMMISSION_FORM);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#84ab4a]" />
              Create Manual Commission
            </DialogTitle>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (!manualCommissionForm.verticalName) return;
              createManualCommission.mutate({
                repCode: manualCommissionForm.repCode,
                verticalName: manualCommissionForm.verticalName,
                amount: manualCommissionForm.amount,
                description: manualCommissionForm.description,
                clientName: manualCommissionForm.clientName.trim() || undefined,
                businessName: manualCommissionForm.businessName.trim() || undefined,
              });
            }}
          >
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Associate</label>
              <AssociateCommissionSearch
                associates={activeCommissionAssociates}
                value={manualCommissionForm.repCode}
                onChange={repCode => setManualCommissionForm(form => ({ ...form, repCode }))}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Client name</label>
                <Input
                  value={manualCommissionForm.clientName}
                  onChange={event => setManualCommissionForm(form => ({ ...form, clientName: event.target.value }))}
                  maxLength={200}
                  placeholder="Optional client name"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Business name</label>
                <Input
                  value={manualCommissionForm.businessName}
                  onChange={event => setManualCommissionForm(form => ({ ...form, businessName: event.target.value }))}
                  maxLength={200}
                  placeholder="Optional business name"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Vertical</label>
              <select
                value={manualCommissionForm.verticalName}
                onChange={(event) => setManualCommissionForm((form) => ({
                  ...form,
                  verticalName: event.target.value as (typeof MANUAL_COMMISSION_VERTICALS)[number],
                }))}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-[#84ab4a] focus:ring-2 focus:ring-[#84ab4a]/20"
                required
              >
                <option value="">Select a vertical</option>
                {MANUAL_COMMISSION_VERTICALS.map((vertical) => <option key={vertical} value={vertical}>{vertical}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Amount (USD)</label>
              <Input
                value={manualCommissionForm.amount}
                onChange={(event) => setManualCommissionForm((form) => ({ ...form, amount: event.target.value }))}
                inputMode="decimal"
                placeholder="1342.54"
                pattern="\d{1,8}(\.\d{1,2})?"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
              <textarea
                value={manualCommissionForm.description}
                onChange={(event) => setManualCommissionForm((form) => ({ ...form, description: event.target.value }))}
                rows={3}
                maxLength={500}
                placeholder="Example: Share360 onboarding promotion bonus"
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:border-[#84ab4a] focus:ring-2 focus:ring-[#84ab4a]/20"
                required
              />
              <div className="text-[10px] text-gray-400 text-right mt-1">{manualCommissionForm.description.length}/500</div>
            </div>
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
              The commission will be created as Pending and included in the associate and admin totals.
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                disabled={createManualCommission.isPending}
                onClick={() => setShowManualCommissionForm(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  createManualCommission.isPending ||
                  !manualCommissionForm.repCode ||
                  !manualCommissionForm.verticalName ||
                  !manualCommissionForm.amount ||
                  !manualCommissionForm.description.trim()
                }
                className="bg-[#84ab4a] hover:bg-[#759a40] text-white"
              >
                {createManualCommission.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Pending Commission
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rep Onboarding Modal */}
      <Dialog open={showRepForm} onOpenChange={setShowRepForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#84ab4a]" />
              Register New Sales Rep
            </DialogTitle>
          </DialogHeader>
          <RepOnboardingForm
            onClose={() => setShowRepForm(false)}
            onSuccess={() => {
              setShowRepForm(false);
              utils.rep.leaderboard.invalidate();
              utils.rep.nextCode.invalidate();
              setActiveTab("reps");
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Lead Detail Drawer — rich slide-over panel */}
      {selectedLead !== null && (() => {
        const lead = leads.find((l: any) => l.id === selectedLead) ?? null;
        return (
          <LeadDetailDrawer
            lead={lead}
            onClose={() => setSelectedLead(null)}
            isAdmin={true}
            canManageShare360InternalSteps={true}
            authorName="Admin"
            onGhlStageChange={(leadId, ghlStageId, ghlStageName) => {
              moveStageInDrawer.mutate({ leadId, ghlStageId, ghlStageName, changedBy: "admin", isAdmin: true });
            }}
            ghlStages={ghlStagesQuery.data ?? []}
            onStageChange={(leadId, newStage) => {
              updateStageInDrawer.mutate({ leadId, stage: newStage as any, changedBy: "admin", isAdmin: true });
            }}
            availableStages={STAGES.map(s => ({ key: s.key, label: s.label, color: s.color }))}
            calendarUrls={lead ? consultationCalendarsForLead(lead as any) : []}
            onDeleteLead={(id) => setDeleteConfirm({ type: "lead", id, verticalName: lead?.verticalName ?? undefined })}
          />
        );
      })()}

      {/* Agent Detail Drawer */}
      <AgentDetailDrawer
        key={selectedAgent?.id ?? "none"}
        rep={selectedAgent}
        onClose={() => setSelectedAgent(null)}
        onRepUpdated={() => { utils.rep.leaderboard.invalidate(); setSelectedAgent(null); }}
        onDelete={(id) => setDeleteConfirm({ type: "rep", id })}
      />

      {/* Set Credentials Modal */}
      <Dialog open={credRep !== null} onOpenChange={(open) => { if (!open) { setCredRep(null); setCredUsername(""); setCredPassword(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-purple-600" />
              Set Login Credentials
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 mb-4">
            Set a username and password for <span className="font-semibold text-gray-800">{credRep?.firstName} {credRep?.lastName}</span> ({credRep?.repCode}).
            {credRep?.username && <span className="ml-1 text-xs text-purple-600">(Current: {credRep.username})</span>}
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">Username</label>
              <Input
                placeholder="e.g. john.smith"
                value={credUsername}
                onChange={(e) => setCredUsername(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">Password</label>
              <div className="relative">
                <Input
                  type={credShowPw ? "text" : "password"}
                  placeholder="Min 6 characters"
                  value={credPassword}
                  onChange={(e) => setCredPassword(e.target.value)}
                  autoComplete="new-password"
                  className="pr-10"
                />
                <button type="button" onClick={() => setCredShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {credShowPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => { setCredRep(null); setCredUsername(""); setCredPassword(""); }}>Cancel</Button>
              <Button
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                disabled={credUsername.trim().length < 3 || credPassword.length < 6 || setCredentialsMutation.isPending}
                onClick={() => setCredentialsMutation.mutate({ repId: credRep.id, username: credUsername.trim(), password: credPassword })}
              >
                {setCredentialsMutation.isPending ? "Saving..." : "Save Credentials"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Edit Rep Modal */}
      <Dialog open={editingRep !== null} onOpenChange={(open) => { if (!open) setEditingRep(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-600" />
              Edit Rep — {editingRep?.firstName} {editingRep?.lastName}
            </DialogTitle>
          </DialogHeader>
          {editingRep && (
            <EditRepForm
              key={editingRep.id}
              rep={editingRep}
              onSave={(data) => editRepMutation.mutate({ id: editingRep.id, ...data })}
              onCancel={() => setEditingRep(null)}
              isPending={editRepMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Suspend agent confirmation */}
      <Dialog open={suspendTarget !== null} onOpenChange={(open) => { if (!open) setSuspendTarget(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-orange-600 flex items-center gap-2">
              <Ban className="w-5 h-5" /> Suspend Agent
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 mt-2">
            Suspend <span className="font-semibold">{suspendTarget?.firstName} {suspendTarget?.lastName}</span> ({suspendTarget?.repCode})?
            Their dashboard access will be removed immediately. You can reinstate them at any time.
          </p>
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="outline" onClick={() => setSuspendTarget(null)}>Cancel</Button>
            <Button
              className="bg-orange-600 hover:bg-orange-700 text-white"
              onClick={() => suspendTarget && suspendRepMutation.mutate({ id: suspendTarget.id })}
              disabled={suspendRepMutation.isPending}
            >
              {suspendRepMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
              Suspend Agent
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reassign / Set Hierarchy modal */}
      <Dialog open={reassignTarget !== null} onOpenChange={(open) => { if (!open) { setReassignTarget(null); setReassignUplineCode(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Network className="w-5 h-5 text-[#84ab4a]" /> Reassign / Set Hierarchy
            </DialogTitle>
          </DialogHeader>
          {reassignTarget && (
            <div className="space-y-4 mt-2">
              <div className="bg-gray-50 rounded-xl p-3 text-sm">
                <div className="font-semibold text-gray-800">{reassignTarget.firstName} {reassignTarget.lastName}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  <span className="font-mono text-[#84ab4a]">{reassignTarget.repCode}</span> · {reassignTarget.agentLevel}
                </div>
                {reassignTarget.uplineRepCode && (
                  <div className="text-xs text-gray-400 mt-1">Current upline: <span className="font-mono">{reassignTarget.uplineRepCode}</span></div>
                )}
                {reassignTarget.mgaRepCode && (
                  <div className="text-xs text-gray-400">Current Agency (MGA): <span className="font-mono">{reassignTarget.mgaRepCode}</span></div>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">New Upline Rep Code</label>
                <p className="text-xs text-gray-400 mb-2">Enter the rep code of the Agency or Senior Associate this agent should report to. The Agency (MGA) code will be recalculated automatically.</p>
                <Input
                  value={reassignUplineCode}
                  onChange={e => setReassignUplineCode(e.target.value.toUpperCase().trim())}
                  placeholder="e.g. BC360-001"
                  className="font-mono"
                />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => { setReassignTarget(null); setReassignUplineCode(""); }}>Cancel</Button>
                <Button
                  className="bg-[#84ab4a] hover:bg-[#6e9038] text-white"
                  onClick={() => reassignRepMutation.mutate({ repId: reassignTarget.id, newUplineRepCode: reassignUplineCode })}
                  disabled={!reassignUplineCode || reassignRepMutation.isPending}
                >
                  {reassignRepMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Network className="w-4 h-4" />}
                  Reassign
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Repair Wibiz Contact Modal */}
      <Dialog open={repairTarget !== null} onOpenChange={(open) => { if (!open) { setRepairTarget(null); setManualGhlId(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertCircle className="w-5 h-5" />
              Repair Wibiz Contact — {repairTarget?.firstName} {repairTarget?.lastName}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-gray-600">
              This rep has no linked Wibiz contact. BC360 will first search Wibiz by their email (<span className="font-mono text-xs">{repairTarget?.email}</span>). If the search fails, it will attempt to create a new contact.
            </p>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Manual Wibiz Contact ID <span className="text-gray-400 normal-case font-normal">(optional — paste if auto-search keeps failing)</span>
              </label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-300"
                placeholder="e.g. aBcDeFgHiJkLmN..."
                value={manualGhlId}
                onChange={e => setManualGhlId(e.target.value.trim())}
              />
              <p className="text-xs text-gray-400 mt-1">Find this in Wibiz → Contacts → open the contact → copy the ID from the URL.</p>
            </div>
            <div className="flex gap-2 justify-end pt-1">
              <Button variant="outline" onClick={() => { setRepairTarget(null); setManualGhlId(""); }}>Cancel</Button>
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => repairTarget && repairGhlMutation.mutate({ repId: repairTarget.id, manualGhlContactId: manualGhlId || undefined })}
                disabled={repairGhlMutation.isPending}
              >
                {repairGhlMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertCircle className="w-4 h-4" />}
                {manualGhlId ? "Link This Contact ID" : "Auto-Repair"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteConfirm !== null} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Confirm Delete
            </DialogTitle>
          </DialogHeader>
          {deleteConfirm?.type === "lead" ? (
            <div className="mt-2 space-y-3">
              <p className="text-sm text-gray-600">
                Deleting this lead will also remove its related pipeline opportunity. The Wibiz contact record will only be deleted if it is not connected to any other active lead or client records. <strong>This action cannot be undone.</strong>
              </p>
              {deleteConfirm.verticalName?.startsWith("Share360") && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <strong>Share360 note:</strong> Deleting a Share360 lead permanently blocks that business contact from being re-submitted. If you need to fix the attribution, business name, or other details, use <strong>Edit</strong> instead — it is always safer than delete&nbsp;+&nbsp;re-enter.
                </div>
              )}
            </div>
          ) : deleteConfirm?.type === "rep" ? (
            <div className="mt-2 space-y-2">
              <p className="text-sm text-gray-600">
                This agent will be removed from the active agents list and will lose portal access immediately. All their data (leads, commissions, documents) is preserved and untouched.
              </p>
              <p className="text-sm text-[#84ab4a] font-medium">
                This is reversible — you can restore them from the Deleted Agents section below the agents table.
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-600 mt-2">
              Are you sure you want to delete this {deleteConfirm?.type}? This action cannot be undone.
            </p>
          )}
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white border-0"
              onClick={() => {
                if (!deleteConfirm) return;
                if (deleteConfirm.type === "lead") deleteLeadMutation.mutate({ id: deleteConfirm.id });
                else if (deleteConfirm.type === "rep") deleteRepMutation.mutate({ id: deleteConfirm.id });
                else if (deleteConfirm.type === "commission") deleteCommissionMutation.mutate({ id: deleteConfirm.id });
              }}
              disabled={deleteLeadMutation.isPending || deleteRepMutation.isPending || deleteCommissionMutation.isPending}
            >
              {(deleteLeadMutation.isPending || deleteRepMutation.isPending || deleteCommissionMutation.isPending) ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
